import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Search, Trash2, Edit, Plus, Save, X, Upload, Image, Download, Eye, Stethoscope, Globe, BadgeCheck, Plane, Paintbrush, Copy, Fingerprint, MapPin, Printer, Barcode, QrCode, HelpCircle } from 'lucide-react';
import LazyImage from './LazyImage';
import SearchableSelect from './SearchableSelect';
import { PermissionsProvider, usePermissions, PermissionGate } from './PassportPermissions';

import { useNavigate } from 'react-router-dom';
import Tesseract from 'tesseract.js';
import CustomDateInput from './CustomDateInput';
import { Calendar } from 'lucide-react';
import config from '../config';
import { 
    ImageViewerModal,
    ImageCropModal,
    SettingsModal, 
    BatchUploadModal, 
    QuickEntryModal,
    FieldMessage,
    NotesSection
} from './PassportModals';
import SmartRepeatQuickModal from './SmartRepeatQuickModal';
import { checkDeleteCode } from '../deleteConfirm';

const BodyPortal = ({ children, enabled = true }) => {
    if (!enabled || typeof document === 'undefined' || !document.body) {
        return children;
    }
    return ReactDOM.createPortal(children, document.body);
};


const createSafeDefaultSettings = (settings = {}) => {
    const base = {
        enabled: {
            CompanyID: false,
            TravelDuration: false,
            PassportStatus: false,
            PreviousCompanies: false,
            AbilityTest: false,
            PassportProfessionID: false,
            PassportProfessionText: false,
            TravelProfessionID: false,
            MilitaryStatus: false,
            DelegateID: false,
            LicenseType: false,
            MedicalExamStatus: false,
            DefaultNote: false
        },
        values: {
            CompanyID: '',
            TravelDuration: '',
            PassportStatus: '',
            PreviousCompanies: [],
            AbilityTest: '',
            PassportProfessionID: '',
            PassportProfessionText: '',
            PassportProfessionInputMode: 'select',
            TravelProfessionID: '',
            MilitaryStatus: '',
            DelegateID: '',
            LicenseType: '',
            MedicalExamStatus: '',
            DefaultNote: ''
        }
    };

    const next = {
        ...base,
        ...(settings || {}),
        enabled: { ...base.enabled, ...(settings?.enabled || {}) },
        values: { ...base.values, ...(settings?.values || {}) }
    };

    if (!Array.isArray(next.values.PreviousCompanies)) {
        next.values.PreviousCompanies = [];
    }

    const mode = next.values.PassportProfessionInputMode === 'text' || next.enabled.PassportProfessionText
        ? 'text'
        : 'select';
    next.values.PassportProfessionInputMode = mode;

    // منع تشغيل مهنة الجواز كنص واختيار في نفس الوقت
    if (mode === 'text') {
        next.enabled.PassportProfessionID = false;
        next.values.PassportProfessionID = '';
    } else {
        next.enabled.PassportProfessionText = false;
        next.values.PassportProfessionText = '';
    }

    return next;
};

const DefaultSettingsModal = ({
    isOpen,
    onClose,
    defaultSettings,
    setDefaultSettings,
    companies = [],
    professions = [],
    delegates = [],
    travelDurations = [],
    passportStatuses = [],
    abilityTests = [],
    militaryStatuses = [],
    licenseTypes = [],
    canShowPassportProfessionMode = true,
    onSave
}) => {
    const [localSettings, setLocalSettings] = React.useState(() => createSafeDefaultSettings(defaultSettings));
    const [isSaving, setIsSaving] = React.useState(false);

    React.useEffect(() => {
        if (isOpen) setLocalSettings(createSafeDefaultSettings(defaultSettings));
    }, [isOpen, defaultSettings]);

    if (!isOpen) return null;

    const updateEnabled = (key, checked) => {
        setLocalSettings(prev => {
            const next = createSafeDefaultSettings(prev);
            next.enabled[key] = checked;

            if (key === 'PassportProfessionID' && checked) {
                next.values.PassportProfessionInputMode = 'select';
                next.enabled.PassportProfessionText = false;
                next.values.PassportProfessionText = '';
            }
            if (key === 'PassportProfessionText' && checked) {
                next.values.PassportProfessionInputMode = 'text';
                next.enabled.PassportProfessionID = false;
                next.values.PassportProfessionID = '';
            }

            return createSafeDefaultSettings(next);
        });
    };

    const updateValue = (key, value) => {
        setLocalSettings(prev => createSafeDefaultSettings({
            ...prev,
            values: { ...prev.values, [key]: value }
        }));
    };

    const setPassportProfessionMode = (mode) => {
        setLocalSettings(prev => {
            const next = createSafeDefaultSettings(prev);
            next.values.PassportProfessionInputMode = mode === 'text' ? 'text' : 'select';
            if (next.values.PassportProfessionInputMode === 'text') {
                next.enabled.PassportProfessionID = false;
                next.values.PassportProfessionID = '';
            } else {
                next.enabled.PassportProfessionText = false;
                next.values.PassportProfessionText = '';
            }
            return createSafeDefaultSettings(next);
        });
    };

    const handleSave = async () => {
        const cleaned = createSafeDefaultSettings(localSettings);
        try {
            setIsSaving(true);
            setDefaultSettings && setDefaultSettings(cleaned);
            if (onSave) await onSave(cleaned);
            onClose && onClose();
        } finally {
            setIsSaving(false);
        }
    };

    const optionFromList = (list, idKey, labelKey) => list.map(item => ({
        value: item[idKey],
        label: item[labelKey],
        subtitle: item[idKey] ? `ID: ${item[idKey]}` : ''
    }));

    const renderToggleSelect = (key, label, options, placeholder = 'اختر...') => (
        <div className="border border-gray-200 rounded-2xl p-4 bg-white">
            <label className="flex items-center gap-2 font-bold text-gray-800 mb-3">
                <input
                    type="checkbox"
                    checked={!!localSettings.enabled[key]}
                    onChange={(e) => updateEnabled(key, e.target.checked)}
                    className="rounded text-blue-600 focus:ring-blue-500"
                />
                {label}
            </label>
            {localSettings.enabled[key] && (
                <SearchableSelect
                    options={options}
                    value={localSettings.values[key]}
                    onChange={(value) => updateValue(key, value)}
                    placeholder={placeholder}
                    emptyMessage="لا توجد نتائج"
                />
            )}
        </div>
    );

    const renderToggleBasicSelect = (key, label, values) => (
        <div className="border border-gray-200 rounded-2xl p-4 bg-white">
            <label className="flex items-center gap-2 font-bold text-gray-800 mb-3">
                <input
                    type="checkbox"
                    checked={!!localSettings.enabled[key]}
                    onChange={(e) => updateEnabled(key, e.target.checked)}
                    className="rounded text-blue-600 focus:ring-blue-500"
                />
                {label}
            </label>
            {localSettings.enabled[key] && (
                <select
                    value={localSettings.values[key] || ''}
                    onChange={(e) => updateValue(key, e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">اختر...</option>
                    {values.map(value => <option key={value} value={value}>{value}</option>)}
                </select>
            )}
        </div>
    );

    const professionMode = localSettings.values.PassportProfessionInputMode === 'text' ? 'text' : 'select';

    return (
        <BodyPortal>
            <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4" dir="rtl">
                <div className="bg-gray-50 rounded-3xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
                    <div className="bg-gradient-to-r from-blue-700 to-indigo-700 text-white px-6 py-4 flex items-center justify-between gap-4">
                        <div>
                            <h2 className="text-xl font-black">⚙️ ضبط النموذج الافتراضي</h2>
                            <p className="text-sm text-white/80 mt-1">اختار القيم الافتراضية وطريقة إدخال مهنة الجواز بدون تداخل بين النص والقائمة.</p>
                        </div>
                        <button type="button" onClick={onClose} className="bg-white/15 hover:bg-white/25 p-2 rounded-full">
                            <X size={22} />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-5 space-y-5">
                        {canShowPassportProfessionMode && (
                            <div className="bg-white border border-amber-200 rounded-3xl p-5 shadow-sm">
                                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                                    <div>
                                        <h3 className="font-black text-gray-900">طريقة حقل المهنة بالجواز</h3>
                                        <p className="text-xs text-amber-700 mt-1 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                                            تنبيه: تغيير هذا الاختيار يُحفظ في قاعدة البيانات ويُطبق على كل المستخدمين والأجهزة، وليس على هذا المتصفح فقط.
                                        </p>
                                    </div>
                                    <span className="text-xs font-black px-3 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200">إعداد عام</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setPassportProfessionMode('select')}
                                        className={`rounded-2xl border p-4 text-right transition ${professionMode === 'select' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                                    >
                                        <div className="font-black text-gray-900">اختيار من قائمة فقط</div>
                                        <div className="text-sm text-gray-500 mt-1">يظهر الحقل كقائمة قابلة للبحث، ولا يقبل نصًا خارج جدول المهن.</div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPassportProfessionMode('text')}
                                        className={`rounded-2xl border p-4 text-right transition ${professionMode === 'text' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                                    >
                                        <div className="font-black text-gray-900">كتابة نصية فقط</div>
                                        <div className="text-sm text-gray-500 mt-1">يظهر الحقل ككتابة يدوية فقط، ولا يتم حفظ النص في جدول المهن.</div>
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {renderToggleSelect('CompanyID', 'الشركة الافتراضية', optionFromList(companies, 'CompanyID', 'CompanyName'), 'ابحث عن الشركة...')}
                            {renderToggleBasicSelect('TravelDuration', 'مدة السفر الافتراضية', travelDurations)}
                            {renderToggleBasicSelect('PassportStatus', 'حالة الجواز الافتراضية', passportStatuses)}
                            {renderToggleBasicSelect('AbilityTest', 'اختبار القدرات الافتراضي', abilityTests)}

                            <div className="border border-gray-200 rounded-2xl p-4 bg-white lg:col-span-2">
                                <label className="flex items-center gap-2 font-bold text-gray-800 mb-3">
                                    <input
                                        type="checkbox"
                                        checked={professionMode === 'select' ? !!localSettings.enabled.PassportProfessionID : !!localSettings.enabled.PassportProfessionText}
                                        onChange={(e) => updateEnabled(professionMode === 'select' ? 'PassportProfessionID' : 'PassportProfessionText', e.target.checked)}
                                        className="rounded text-blue-600 focus:ring-blue-500"
                                    />
                                    قيمة افتراضية لمهنة الجواز
                                </label>
                                {professionMode === 'select' ? (
                                    localSettings.enabled.PassportProfessionID && (
                                        <SearchableSelect
                                            options={optionFromList(professions, 'ProfessionID', 'ProfessionName')}
                                            value={localSettings.values.PassportProfessionID}
                                            onChange={(value) => updateValue('PassportProfessionID', value)}
                                            placeholder="ابحث واختر مهنة الجواز..."
                                            emptyMessage="لا توجد مهن"
                                        />
                                    )
                                ) : (
                                    localSettings.enabled.PassportProfessionText && (
                                        <input
                                            type="text"
                                            value={localSettings.values.PassportProfessionText || ''}
                                            onChange={(e) => updateValue('PassportProfessionText', e.target.value)}
                                            className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                            placeholder="اكتب المهنة الافتراضية نصيًا"
                                        />
                                    )
                                )}
                                <p className="text-xs text-gray-500 mt-2">
                                    النظام يمنع تشغيل النص والقائمة معًا. الوضع الحالي: {professionMode === 'select' ? 'اختياري من القائمة' : 'نصي فقط'}.
                                </p>
                            </div>

                            {renderToggleSelect('TravelProfessionID', 'مهنة السفر الافتراضية', optionFromList(professions, 'ProfessionID', 'ProfessionName'), 'ابحث عن مهنة السفر...')}
                            {renderToggleBasicSelect('MilitaryStatus', 'الموقف من التجنيد الافتراضي', militaryStatuses)}
                            {renderToggleSelect('DelegateID', 'المندوب الافتراضي', optionFromList(delegates, 'DelegateID', 'DelegateName'), 'ابحث عن المندوب...')}
                            {renderToggleBasicSelect('LicenseType', 'نوع الرخصة الافتراضي', licenseTypes)}
                            {renderToggleBasicSelect('MedicalExamStatus', 'حالة الكشف الطبي الافتراضية', ['لم يتم الكشف', 'لائق', 'غير لائق', 'إعادة كشف', 'انتظار النتيجة'])}
                        </div>

                        <div className="border border-gray-200 rounded-2xl p-4 bg-white">
                            <label className="flex items-center gap-2 font-bold text-gray-800 mb-3">
                                <input
                                    type="checkbox"
                                    checked={!!localSettings.enabled.PreviousCompanies}
                                    onChange={(e) => updateEnabled('PreviousCompanies', e.target.checked)}
                                    className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                الشركات السابقة الافتراضية
                            </label>
                            {localSettings.enabled.PreviousCompanies && (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-44 overflow-y-auto bg-gray-50 border border-gray-200 rounded-2xl p-3">
                                    {companies.map(company => {
                                        const value = String(company.CompanyID);
                                        const checked = localSettings.values.PreviousCompanies.includes(value);
                                        return (
                                            <label key={company.CompanyID} className="flex items-center gap-2 bg-white border border-gray-100 rounded-xl px-3 py-2 cursor-pointer hover:bg-blue-50">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => {
                                                        const current = localSettings.values.PreviousCompanies || [];
                                                        updateValue('PreviousCompanies', checked ? current.filter(id => id !== value) : [...current, value]);
                                                    }}
                                                    className="rounded text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className="text-sm text-gray-700">{company.CompanyName}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="border border-gray-200 rounded-2xl p-4 bg-white">
                            <label className="flex items-center gap-2 font-bold text-gray-800 mb-3">
                                <input
                                    type="checkbox"
                                    checked={!!localSettings.enabled.DefaultNote}
                                    onChange={(e) => updateEnabled('DefaultNote', e.target.checked)}
                                    className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                ملاحظة افتراضية
                            </label>
                            {localSettings.enabled.DefaultNote && (
                                <textarea
                                    value={localSettings.values.DefaultNote || ''}
                                    onChange={(e) => updateValue('DefaultNote', e.target.value)}
                                    rows={3}
                                    className="w-full border border-gray-300 rounded-2xl px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                    placeholder="اكتب ملاحظة تضاف تلقائيًا للجواز الجديد..."
                                />
                            )}
                        </div>
                    </div>

                    <div className="bg-white border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row gap-3 justify-end">
                        <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                            إلغاء
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving}
                            className={`px-5 py-2.5 rounded-xl text-white font-black flex items-center justify-center gap-2 ${isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                        >
                            <Save size={18} />
                            {isSaving ? 'جاري الحفظ...' : 'حفظ ضبط النموذج'}
                        </button>
                    </div>
                </div>
            </div>
        </BodyPortal>
    );
};


const getBlacklistItems = (items) => Array.isArray(items) ? items.filter(Boolean) : [];

const scopeLabel = (scopeType) => {
    if (scopeType === 'Office') return 'منع مكتب';
    if (scopeType === 'Company') return 'منع شركة';
    if (scopeType === 'Warning') return 'تحذير فقط';
    return 'غير محدد';
};

const getBlacklistSummary = (items = []) => {
    const active = getBlacklistItems(items).filter(item => item.Status === 'Active');
    const officeBlocks = active.filter(item => item.ScopeType === 'Office');
    const companyBlocks = active.filter(item => item.ScopeType === 'Company');
    const warnings = active.filter(item => item.ScopeType === 'Warning');

    if (officeBlocks.length > 0) {
        return {
            severity: 'danger',
            label: 'منع مكتب',
            title: officeBlocks[0].ReasonDetails || 'ممنوع من المكتب بالكامل',
            count: active.length
        };
    }
    if (companyBlocks.length > 0) {
        return {
            severity: 'company',
            label: companyBlocks.length > 1 ? `منع ${companyBlocks.length} شركات` : 'منع شركة',
            title: companyBlocks.map(item => item.CompanyName || 'شركة غير محددة').join('، '),
            count: active.length
        };
    }
    if (warnings.length > 0) {
        return {
            severity: 'warning',
            label: 'تحذير',
            title: warnings[0].ReasonDetails || 'يوجد تحذير إداري',
            count: active.length
        };
    }
    return null;
};

const BlacklistSummaryBadge = ({ items = [], onClick }) => {
    const summary = getBlacklistSummary(items);
    if (!summary) return null;

    const styles = {
        danger: 'bg-red-100 text-red-800 border-red-200',
        company: 'bg-orange-100 text-orange-800 border-orange-200',
        warning: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };

    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
            className={`text-[11px] px-2 py-1 rounded-full border font-bold inline-flex items-center gap-1 ${styles[summary.severity] || styles.warning}`}
            title={summary.title}
        >
            <span>⛔</span>
            <span>{summary.label}</span>
            {summary.count > 1 && <span className="opacity-70">+{summary.count - 1}</span>}
        </button>
    );
};

const BLACKLIST_REASON_OPTIONS = [
    'ترك العمل',
    'مشكلة مع الشركة / الكفيل',
    'هروب',
    'سوء سلوك',
    'رفض استكمال العقد',
    'مشكلة مالية',
    'بيانات غير صحيحة',
    'بناءً على طلب الشركة',
    'تحذير إداري',
    'سبب آخر'
];

const formatBlacklistDate = (value) => {
    if (!value) return '';
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return String(value);
        return date.toLocaleString('ar-EG');
    } catch {
        return String(value);
    }
};

const PassportBlacklistModal = ({
    isOpen,
    onClose,
    passport,
    companies = [],
    showAlert,
    getCurrentUserId,
    onChanged,
    apiBaseUrl
}) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [isExporting, setIsExporting] = React.useState(false);
    const [activeItems, setActiveItems] = React.useState([]);
    const [historyItems, setHistoryItems] = React.useState([]);
    const [globalItems, setGlobalItems] = React.useState([]);
    const [globalFilters, setGlobalFilters] = React.useState({
        status: 'Active',
        scopeType: '',
        companyId: '',
        search: '',
        fromDate: '',
        toDate: ''
    });
    const [globalPage, setGlobalPage] = React.useState(1);
    const [globalPageSize, setGlobalPageSize] = React.useState(25);
    const [globalPagination, setGlobalPagination] = React.useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });
    const [companySearch, setCompanySearch] = React.useState('');
    const [form, setForm] = React.useState({
        ScopeType: 'Company',
        CompanyIDs: [],
        ReasonCategory: 'بناءً على طلب الشركة',
        ReasonDetails: ''
    });

    const selectedPassportId = passport?.PassportID;
    const isGlobalMode = !selectedPassportId;

    React.useEffect(() => {
        if (!isOpen) return;
        setCompanySearch('');
        setForm(prev => ({
            ...prev,
            ScopeType: 'Company',
            CompanyIDs: [],
            ReasonCategory: 'بناءً على طلب الشركة',
            ReasonDetails: ''
        }));
    }, [isOpen, selectedPassportId]);

    React.useEffect(() => {
        if (!isOpen || !isGlobalMode) return;
        setGlobalPage(1);
    }, [isOpen, isGlobalMode, globalFilters.status, globalFilters.scopeType, globalFilters.companyId, globalFilters.search, globalFilters.fromDate, globalFilters.toDate, globalPageSize]);

    const buildGlobalParams = React.useCallback((extra = {}) => {
        const params = new URLSearchParams();
        Object.entries(globalFilters).forEach(([key, value]) => {
            if (value !== '' && value !== null && value !== undefined) params.append(key, value);
        });
        Object.entries(extra).forEach(([key, value]) => {
            if (value !== '' && value !== null && value !== undefined) params.set(key, value);
        });
        return params;
    }, [globalFilters]);

    const loadData = React.useCallback(async () => {
        if (!isOpen) return;
        try {
            setIsLoading(true);
            if (selectedPassportId) {
                const response = await fetch(`${apiBaseUrl}/api/passports/${selectedPassportId}/blacklist`);
                const result = await response.json();
                if (!response.ok || !result.success) throw new Error(result.error || 'فشل في تحميل سجل المنع');
                setActiveItems(result.active || []);
                setHistoryItems(result.history || []);
            } else {
                const params = buildGlobalParams({ page: globalPage, pageSize: globalPageSize });
                const response = await fetch(`${apiBaseUrl}/api/passports/blacklist?${params.toString()}`);
                const result = await response.json();
                if (!response.ok || !result.success) throw new Error(result.error || 'فشل في تحميل قائمة المنع');
                setGlobalItems(result.data || []);
                setGlobalPagination(result.pagination || {
                    page: globalPage,
                    pageSize: globalPageSize,
                    total: (result.data || []).length,
                    totalPages: 1
                });
            }
        } catch (error) {
            console.error('خطأ في تحميل قائمة المنع:', error);
            showAlert && showAlert('حدث خطأ أثناء تحميل قائمة المنع: ' + error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    }, [isOpen, selectedPassportId, apiBaseUrl, buildGlobalParams, globalPage, globalPageSize]);

    React.useEffect(() => {
        const timeout = setTimeout(loadData, selectedPassportId ? 0 : 250);
        return () => clearTimeout(timeout);
    }, [loadData, selectedPassportId]);

    const updateGlobalFilter = (key, value) => {
        setGlobalFilters(prev => ({ ...prev, [key]: value }));
        setGlobalPage(1);
    };

    const toggleCompany = (companyId) => {
        const value = String(companyId);
        setForm(prev => ({
            ...prev,
            CompanyIDs: prev.CompanyIDs.includes(value)
                ? prev.CompanyIDs.filter(id => id !== value)
                : [...prev.CompanyIDs, value]
        }));
    };

    const filteredBlacklistCompanies = React.useMemo(() => {
        const query = String(companySearch || '').trim().toLowerCase();
        if (!query) return companies;
        return companies.filter(company => String(company.CompanyName || '').toLowerCase().includes(query));
    }, [companies, companySearch]);

    const handleAdd = async () => {
        if (!selectedPassportId) return;
        if (form.ScopeType === 'Company' && form.CompanyIDs.length === 0) {
            showAlert && showAlert('اختار شركة واحدة على الأقل', 'error');
            return;
        }
        try {
            setIsLoading(true);
            const response = await fetch(`${apiBaseUrl}/api/passports/${selectedPassportId}/blacklist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ScopeType: form.ScopeType,
                    CompanyIDs: form.ScopeType === 'Company' ? form.CompanyIDs.map(id => parseInt(id, 10)) : [],
                    ReasonCategory: form.ReasonCategory,
                    ReasonDetails: form.ReasonDetails.trim(),
                    AddedByUserID: getCurrentUserId ? getCurrentUserId() : null
                })
            });
            const result = await response.json();
            if (!response.ok || !result.success) throw new Error(result.error || 'فشل في إضافة المنع');
            showAlert && showAlert('تم حفظ حالة المنع/التحذير بنجاح', 'success');
            setForm(prev => ({ ...prev, ReasonDetails: '' }));
            await loadData();
            onChanged && onChanged();
        } catch (error) {
            console.error('خطأ في إضافة المنع:', error);
            showAlert && showAlert('حدث خطأ: ' + error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemove = async (item) => {
        const reason = window.prompt(
            `اكتب سبب رفع المنع عن العميل:\n${item.PersonName || passport?.NameArabic || ''}\n${item.CompanyName ? 'الشركة: ' + item.CompanyName : item.ScopeLabel || ''}`
        );
        if (reason === null) return;
        if (!reason.trim()) {
            showAlert && showAlert('لازم تكتب سبب رفع المنع', 'error');
            return;
        }

        try {
            setIsLoading(true);
            const response = await fetch(`${apiBaseUrl}/api/passports/blacklist/${item.BlacklistID}/remove`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    RemoveReason: reason.trim(),
                    RemovedByUserID: getCurrentUserId ? getCurrentUserId() : null
                })
            });
            const result = await response.json();
            if (!response.ok || !result.success) throw new Error(result.error || 'فشل في رفع المنع');
            showAlert && showAlert('تم رفع المنع مع حفظ السبب في السجل', 'success');
            await loadData();
            onChanged && onChanged();
        } catch (error) {
            console.error('خطأ في رفع المنع:', error);
            showAlert && showAlert('حدث خطأ: ' + error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const statusBadge = (item) => {
        if (item.Status === 'Removed') return 'bg-gray-100 text-gray-700 border-gray-200';
        if (item.ScopeType === 'Office') return 'bg-red-100 text-red-800 border-red-200';
        if (item.ScopeType === 'Company') return 'bg-orange-100 text-orange-800 border-orange-200';
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    };

    const getStatusText = (item) => item.Status === 'Removed' ? 'مرفوع' : 'نشط';

    const escapeHtml = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const getExportRows = (items) => items.map((item, index) => ({
        '#': index + 1,
        'اسم العميل': item.PersonName || '',
        'رقم الجواز': item.PassportNumber || '',
        'الرقم القومي': item.NationalID || '',
        'رقم الهاتف': item.PhoneNumber || '',
        'نوع المنع': item.ScopeLabel || '',
        'الشركة': item.CompanyName || (item.ScopeType === 'Office' ? 'المكتب كله' : ''),
        'تصنيف السبب': item.ReasonCategory || '',
        'تفاصيل السبب': item.ReasonDetails || '',
        'الحالة': getStatusText(item),
        'تاريخ الإضافة': formatBlacklistDate(item.AddedAt),
        'أضيف بواسطة': item.AddedByUserName || '',
        'تاريخ الرفع': formatBlacklistDate(item.RemovedAt),
        'سبب الرفع': item.RemoveReason || '',
        'رفع بواسطة': item.RemovedByUserName || ''
    }));

    const fetchAllGlobalItemsForOutput = async () => {
        const params = buildGlobalParams({ page: 1, pageSize: 10000, export: 1 });
        const response = await fetch(`${apiBaseUrl}/api/passports/blacklist?${params.toString()}`);
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.error || 'فشل في تحميل بيانات التصدير');
        return result.data || [];
    };

    const getReportTitle = () => {
        const company = globalFilters.companyId ? companies.find(c => String(c.CompanyID) === String(globalFilters.companyId))?.CompanyName : '';
        const statusText = globalFilters.status === 'Active' ? 'النشطة' : globalFilters.status === 'Removed' ? 'المرفوعة' : 'كل الحالات';
        const scopeText = globalFilters.scopeType ? scopeLabel(globalFilters.scopeType) : 'كل أنواع المنع';
        return `تقرير قائمة المنع - ${company || 'كل الشركات'} - ${scopeText} - ${statusText}`;
    };

    const handleExportExcel = async () => {
        try {
            setIsExporting(true);
            const items = await fetchAllGlobalItemsForOutput();
            const rows = getExportRows(items);
            if (!rows.length) {
                showAlert && showAlert('لا توجد بيانات لتصديرها حسب الفلتر الحالي', 'warning');
                return;
            }
            const headers = Object.keys(rows[0]);
            const tableRows = rows.map(row => `<tr>${headers.map(h => `<td>${escapeHtml(row[h])}</td>`).join('')}</tr>`).join('');
            const html = `\ufeff<html><head><meta charset="utf-8" /></head><body dir="rtl"><h2>${escapeHtml(getReportTitle())}</h2><p>تاريخ التصدير: ${escapeHtml(new Date().toLocaleString('ar-EG'))}</p><table border="1"><thead><tr>${headers.map(h => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead><tbody>${tableRows}</tbody></table></body></html>`;
            const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `blacklist_report_${new Date().toISOString().slice(0, 10)}.xls`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            showAlert && showAlert(`تم تصدير ${rows.length} سجل إلى Excel`, 'success');
        } catch (error) {
            console.error('خطأ في تصدير قائمة المنع:', error);
            showAlert && showAlert('حدث خطأ أثناء التصدير: ' + error.message, 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const handlePrintReport = async () => {
        try {
            setIsExporting(true);
            const items = await fetchAllGlobalItemsForOutput();
            const rows = getExportRows(items);
            if (!rows.length) {
                showAlert && showAlert('لا توجد بيانات للطباعة حسب الفلتر الحالي', 'warning');
                return;
            }
            const headers = ['#', 'اسم العميل', 'رقم الجواز', 'الرقم القومي', 'رقم الهاتف', 'نوع المنع', 'الشركة', 'تصنيف السبب', 'تفاصيل السبب', 'الحالة', 'تاريخ الإضافة', 'أضيف بواسطة', 'سبب الرفع'];
            const tableRows = rows.map(row => `<tr>${headers.map(h => `<td>${escapeHtml(row[h])}</td>`).join('')}</tr>`).join('');
            const printWindow = window.open('', '_blank', 'width=1200,height=800');
            if (!printWindow) {
                showAlert && showAlert('المتصفح منع نافذة الطباعة. اسمح بالنوافذ المنبثقة ثم جرّب مرة أخرى.', 'warning');
                return;
            }
            printWindow.document.write(`
                <html dir="rtl" lang="ar">
                <head>
                    <meta charset="utf-8" />
                    <title>${escapeHtml(getReportTitle())}</title>
                    <style>
                        body { font-family: Tahoma, Arial, sans-serif; margin: 22px; color: #111827; }
                        .header { text-align: center; border-bottom: 3px solid #b91c1c; padding-bottom: 12px; margin-bottom: 18px; }
                        .header h1 { margin: 0 0 8px; color: #991b1b; font-size: 22px; }
                        .meta { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 12px; font-size: 12px; color: #374151; }
                        table { width: 100%; border-collapse: collapse; font-size: 11px; }
                        th { background: #991b1b; color: white; padding: 7px; border: 1px solid #7f1d1d; }
                        td { padding: 6px; border: 1px solid #d1d5db; vertical-align: top; }
                        tr:nth-child(even) td { background: #f9fafb; }
                        .footer { margin-top: 28px; display: flex; justify-content: space-between; font-size: 12px; }
                        @media print { body { margin: 10mm; } .no-print { display: none; } }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>${escapeHtml(getReportTitle())}</h1>
                        <div>عدد الحالات: ${rows.length}</div>
                    </div>
                    <div class="meta">
                        <div>تاريخ الطباعة: ${escapeHtml(new Date().toLocaleString('ar-EG'))}</div>
                        <div>الفلاتر مطبقة حسب الاختيارات الحالية داخل النظام</div>
                    </div>
                    <table>
                        <thead><tr>${headers.map(h => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead>
                        <tbody>${tableRows}</tbody>
                    </table>
                    <div class="footer">
                        <div>توقيع المسؤول: ____________________</div>
                        <div>ختم المكتب: ____________________</div>
                    </div>
                    <script>window.onload = function(){ window.focus(); window.print(); };</script>
                </body>
                </html>
            `);
            printWindow.document.close();
        } catch (error) {
            console.error('خطأ في طباعة قائمة المنع:', error);
            showAlert && showAlert('حدث خطأ أثناء الطباعة: ' + error.message, 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const renderItem = (item, showPersonName = false) => (
        <div key={item.BlacklistID} className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-bold px-2 py-1 rounded-full border ${statusBadge(item)}`}>
                            {item.Status === 'Removed' ? 'مرفوع' : item.ScopeLabel}
                        </span>
                        {item.CompanyName && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">{item.CompanyName}</span>}
                        {showPersonName && <span className="font-bold text-gray-900">{item.PersonName || 'بدون اسم'}</span>}
                    </div>
                    <div className="text-sm text-gray-800 font-semibold break-words">{item.ReasonCategory || 'سبب غير مصنف'}</div>
                    <div className="text-sm text-gray-600 whitespace-pre-wrap break-words">{item.ReasonDetails || 'لا يوجد تفاصيل'}</div>
                    <div className="text-xs text-gray-400">
                        تمت الإضافة: {formatBlacklistDate(item.AddedAt)} {item.AddedByUserName ? `- بواسطة ${item.AddedByUserName}` : ''}
                    </div>
                    {item.Status === 'Removed' && (
                        <div className="text-xs text-gray-500 bg-gray-50 rounded-xl p-2 mt-2">
                            <div className="font-bold text-gray-700">سبب الرفع:</div>
                            <div className="whitespace-pre-wrap">{item.RemoveReason || 'غير مسجل'}</div>
                            <div className="mt-1">{formatBlacklistDate(item.RemovedAt)} {item.RemovedByUserName ? `- بواسطة ${item.RemovedByUserName}` : ''}</div>
                        </div>
                    )}
                </div>
                {item.Status === 'Active' && (
                    <button
                        type="button"
                        onClick={() => handleRemove(item)}
                        className="bg-green-50 hover:bg-green-100 text-green-700 px-3 py-2 rounded-xl text-sm font-bold border border-green-100"
                    >
                        رفع المنع
                    </button>
                )}
            </div>
        </div>
    );

    const totalPages = Math.max(globalPagination.totalPages || 1, 1);
    const currentPage = Math.min(globalPage, totalPages);
    const fromRecord = globalPagination.total ? ((currentPage - 1) * globalPageSize) + 1 : 0;
    const toRecord = globalPagination.total ? Math.min(currentPage * globalPageSize, globalPagination.total) : 0;

    const getPaginationButtons = () => {
        const pages = [];
        const start = Math.max(1, currentPage - 2);
        const end = Math.min(totalPages, currentPage + 2);
        if (start > 1) {
            pages.push(1);
            if (start > 2) pages.push('...start');
        }
        for (let pageNumber = start; pageNumber <= end; pageNumber += 1) pages.push(pageNumber);
        if (end < totalPages) {
            if (end < totalPages - 1) pages.push('...end');
            pages.push(totalPages);
        }
        return pages;
    };

    const renderGlobalTable = () => (
        <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-100 text-gray-700">
                        <tr>
                            <th className="px-3 py-3 text-right whitespace-nowrap">العميل</th>
                            <th className="px-3 py-3 text-right whitespace-nowrap">الجواز/القومي</th>
                            <th className="px-3 py-3 text-right whitespace-nowrap">النوع</th>
                            <th className="px-3 py-3 text-right whitespace-nowrap">الشركة</th>
                            <th className="px-3 py-3 text-right whitespace-nowrap">السبب</th>
                            <th className="px-3 py-3 text-right whitespace-nowrap">تاريخ الإضافة</th>
                            <th className="px-3 py-3 text-right whitespace-nowrap">الحالة</th>
                            <th className="px-3 py-3 text-center whitespace-nowrap">إجراء</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {globalItems.length ? globalItems.map(item => (
                            <tr key={item.BlacklistID} className="hover:bg-red-50/40 align-top">
                                <td className="px-3 py-3 min-w-[160px]">
                                    <div className="font-black text-gray-900">{item.PersonName || 'بدون اسم'}</div>
                                    <div className="text-xs text-gray-500" dir="ltr">{item.PhoneNumber || ''}</div>
                                </td>
                                <td className="px-3 py-3 min-w-[140px]">
                                    <div className="font-bold text-gray-700">{item.PassportNumber || '—'}</div>
                                    <div className="text-xs text-gray-500">{item.NationalID || '—'}</div>
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap">
                                    <span className={`text-xs font-bold px-2 py-1 rounded-full border ${statusBadge(item)}`}>
                                        {item.ScopeLabel}
                                    </span>
                                </td>
                                <td className="px-3 py-3 min-w-[140px]">{item.CompanyName || (item.ScopeType === 'Office' ? 'المكتب كله' : '—')}</td>
                                <td className="px-3 py-3 min-w-[220px]">
                                    <div className="font-bold text-gray-800">{item.ReasonCategory || '—'}</div>
                                    <div className="text-xs text-gray-500 whitespace-pre-wrap max-w-[360px]">{item.ReasonDetails || ''}</div>
                                    {item.Status === 'Removed' && (
                                        <div className="mt-2 text-xs bg-gray-50 rounded-lg p-2 text-gray-600">
                                            <span className="font-bold">سبب الرفع: </span>{item.RemoveReason || 'غير مسجل'}
                                        </div>
                                    )}
                                </td>
                                <td className="px-3 py-3 min-w-[120px]">
                                    <div>{formatBlacklistDate(item.AddedAt)}</div>
                                    <div className="text-xs text-gray-500">{item.AddedByUserName || ''}</div>
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap">
                                    <span className={`text-xs px-2 py-1 rounded-full font-bold ${item.Status === 'Active' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                        {getStatusText(item)}
                                    </span>
                                </td>
                                <td className="px-3 py-3 text-center whitespace-nowrap">
                                    {item.Status === 'Active' ? (
                                        <button
                                            type="button"
                                            onClick={() => handleRemove(item)}
                                            className="bg-green-50 hover:bg-green-100 text-green-700 px-3 py-2 rounded-xl text-xs font-bold border border-green-100"
                                        >
                                            رفع المنع
                                        </button>
                                    ) : <span className="text-gray-400 text-xs">—</span>}
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="8" className="px-4 py-10 text-center text-gray-400">لا توجد نتائج في قائمة المنع</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    if (!isOpen) return null;

    return (
        <BodyPortal>
            <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4" dir="rtl">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl max-h-[92vh] overflow-hidden flex flex-col">
                    <div className="bg-gradient-to-r from-red-700 to-orange-600 text-white px-6 py-4 flex items-center justify-between gap-4">
                        <div>
                            <h2 className="text-xl font-black">⛔ إدارة قائمة المنع الذكية</h2>
                            <p className="text-sm text-white/80 mt-1">
                                {passport ? `${passport.NameArabic || 'بدون اسم'} - جواز ${passport.PassportNumber || 'غير مسجل'}` : 'عرض وبحث كل حالات المنع والتحذيرات مع التصدير والطباعة'}
                            </p>
                        </div>
                        <button onClick={onClose} className="bg-white/15 hover:bg-white/25 p-2 rounded-full">
                            <X size={22} />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto bg-gray-50 p-5 space-y-5">
                        {isLoading && (
                            <div className="bg-white border border-gray-200 rounded-2xl p-3 text-center text-gray-600">
                                جاري التحميل...
                            </div>
                        )}

                        {passport ? (
                            <>
                                {activeItems.length > 0 && (
                                    <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                                        <div className="font-black text-red-800 mb-2">تنبيهات نشطة على هذا العميل</div>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                            {activeItems.map(item => renderItem(item))}
                                        </div>
                                    </div>
                                )}

                                <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
                                    <h3 className="font-black text-gray-900 mb-4">إضافة منع أو تحذير جديد</h3>
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2">نوع الحالة</label>
                                            <select
                                                value={form.ScopeType}
                                                onChange={(e) => setForm(prev => ({ ...prev, ScopeType: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                            >
                                                <option value="Company">منع من شركة معينة</option>
                                                <option value="Office">منع من المكتب كله</option>
                                                <option value="Warning">تحذير فقط</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2">تصنيف السبب</label>
                                            <select
                                                value={form.ReasonCategory}
                                                onChange={(e) => setForm(prev => ({ ...prev, ReasonCategory: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                            >
                                                {BLACKLIST_REASON_OPTIONS.map(reason => <option key={reason} value={reason}>{reason}</option>)}
                                            </select>
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                type="button"
                                                disabled={isLoading}
                                                onClick={handleAdd}
                                                className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl py-2.5 font-black flex items-center justify-center gap-2"
                                            >
                                                <Save size={18} />
                                                حفظ الحالة
                                            </button>
                                        </div>
                                    </div>

                                    {form.ScopeType === 'Company' && (
                                        <div className="mt-4">
                                            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                                                <label className="block text-sm font-bold text-gray-700">الشركات الممنوع منها</label>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-red-700 bg-red-50 border border-red-100 rounded-full px-3 py-1">
                                                        المحدد: {form.CompanyIDs.length}
                                                    </span>
                                                    {form.CompanyIDs.length > 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setForm(prev => ({ ...prev, CompanyIDs: [] }))}
                                                            className="text-xs font-bold text-gray-600 hover:text-red-700 border border-gray-200 rounded-lg px-3 py-1 bg-white"
                                                        >
                                                            إلغاء تحديد الكل
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="relative mb-3">
                                                <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <input
                                                    type="text"
                                                    value={companySearch}
                                                    onChange={(e) => setCompanySearch(e.target.value)}
                                                    className="w-full border border-gray-300 rounded-xl pr-10 pl-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                                    placeholder="ابحث باسم الشركة..."
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-52 overflow-y-auto border border-gray-200 rounded-2xl p-3 bg-gray-50">
                                                {filteredBlacklistCompanies.map(company => (
                                                    <label key={company.CompanyID} className="flex items-center gap-2 bg-white border border-gray-100 rounded-xl px-3 py-2 cursor-pointer hover:bg-red-50">
                                                        <input
                                                            type="checkbox"
                                                            checked={form.CompanyIDs.includes(String(company.CompanyID))}
                                                            onChange={() => toggleCompany(company.CompanyID)}
                                                            className="rounded text-red-600 focus:ring-red-500"
                                                        />
                                                        <span className="text-sm font-medium text-gray-700">{company.CompanyName}</span>
                                                    </label>
                                                ))}
                                                {filteredBlacklistCompanies.length === 0 && (
                                                    <div className="text-sm text-gray-400 col-span-full text-center py-4">
                                                        لا توجد شركة مطابقة للبحث
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-4">
                                        <label className="block text-sm font-bold text-gray-700 mb-2">
                                            تفاصيل السبب أو الملاحظة <span className="font-normal text-gray-400">(اختياري)</span>
                                        </label>
                                        <textarea
                                            value={form.ReasonDetails}
                                            onChange={(e) => setForm(prev => ({ ...prev, ReasonDetails: e.target.value }))}
                                            rows={4}
                                            className="w-full border border-gray-300 rounded-2xl px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                            placeholder="يمكن ترك هذا الحقل فارغًا، أو كتابة تفاصيل إضافية عند الحاجة..."
                                        />
                                    </div>
                                </div>

                                <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
                                    <div className="font-black text-gray-900 mb-3">السجل الكامل</div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                        {historyItems.length ? historyItems.map(item => renderItem(item)) : <div className="text-gray-400 text-sm">لا يوجد سجل منع سابق لهذا العميل</div>}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm space-y-4">
                                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                                    <div>
                                        <h3 className="font-black text-gray-900">قائمة المنع العامة</h3>
                                        <p className="text-sm text-gray-500 mt-1">يتم تحميل الصفحة الحالية فقط لتجنب البطء، والتصدير/الطباعة حسب الفلتر الحالي.</p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={handleExportExcel}
                                            disabled={isExporting || isLoading}
                                            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl font-bold flex items-center gap-2"
                                        >
                                            <Download size={18} />
                                            تصدير Excel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handlePrintReport}
                                            disabled={isExporting || isLoading}
                                            className="bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white px-4 py-2 rounded-xl font-bold flex items-center gap-2"
                                        >
                                            <Printer size={18} />
                                            طباعة منسقة
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
                                    <input
                                        value={globalFilters.search}
                                        onChange={(e) => updateGlobalFilter('search', e.target.value)}
                                        className="border border-gray-300 rounded-xl px-3 py-2 xl:col-span-2"
                                        placeholder="بحث باسم/جواز/رقم قومي/هاتف/سبب"
                                    />
                                    <select value={globalFilters.status} onChange={(e) => updateGlobalFilter('status', e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2">
                                        <option value="Active">النشط فقط</option>
                                        <option value="Removed">المرفوع فقط</option>
                                        <option value="">الكل</option>
                                    </select>
                                    <select value={globalFilters.scopeType} onChange={(e) => updateGlobalFilter('scopeType', e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2">
                                        <option value="">كل الأنواع</option>
                                        <option value="Office">منع مكتب</option>
                                        <option value="Company">منع شركة</option>
                                        <option value="Warning">تحذير</option>
                                    </select>
                                    <select value={globalFilters.companyId} onChange={(e) => updateGlobalFilter('companyId', e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2">
                                        <option value="">كل الشركات</option>
                                        {companies.map(company => <option key={company.CompanyID} value={company.CompanyID}>{company.CompanyName}</option>)}
                                    </select>
                                    <select value={globalPageSize} onChange={(e) => { setGlobalPageSize(parseInt(e.target.value, 10)); setGlobalPage(1); }} className="border border-gray-300 rounded-xl px-3 py-2">
                                        <option value={10}>عرض 10</option>
                                        <option value={25}>عرض 25</option>
                                        <option value={50}>عرض 50</option>
                                        <option value={100}>عرض 100</option>
                                    </select>
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">من تاريخ الإضافة</label>
                                        <input type="date" value={globalFilters.fromDate} onChange={(e) => updateGlobalFilter('fromDate', e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2" />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">إلى تاريخ الإضافة</label>
                                        <input type="date" value={globalFilters.toDate} onChange={(e) => updateGlobalFilter('toDate', e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2" />
                                    </div>
                                </div>

                                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                    <div className="text-sm text-gray-600">
                                        عرض <span className="font-black text-gray-900">{fromRecord}</span> إلى <span className="font-black text-gray-900">{toRecord}</span> من <span className="font-black text-gray-900">{globalPagination.total || 0}</span> سجل
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <button
                                            type="button"
                                            onClick={() => setGlobalPage(prev => Math.max(prev - 1, 1))}
                                            disabled={currentPage <= 1 || isLoading}
                                            className="px-3 py-2 rounded-xl border border-gray-200 bg-white disabled:opacity-40 text-sm font-bold"
                                        >
                                            السابق
                                        </button>
                                        {getPaginationButtons().map(pageNumber => pageNumber.toString().startsWith('...') ? (
                                            <span key={pageNumber} className="px-2 text-gray-400">...</span>
                                        ) : (
                                            <button
                                                key={pageNumber}
                                                type="button"
                                                onClick={() => setGlobalPage(pageNumber)}
                                                disabled={isLoading}
                                                className={`min-w-[38px] px-3 py-2 rounded-xl border text-sm font-black ${pageNumber === currentPage ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-red-50'}`}
                                            >
                                                {pageNumber}
                                            </button>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => setGlobalPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage >= totalPages || isLoading}
                                            className="px-3 py-2 rounded-xl border border-gray-200 bg-white disabled:opacity-40 text-sm font-bold"
                                        >
                                            التالي
                                        </button>
                                    </div>
                                </div>

                                {renderGlobalTable()}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </BodyPortal>
    );
};


// Error Boundary للحماية من الأخطاء
class PassportErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('خطأ في مكون الجوازات:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50" dir="rtl">
                    <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                        <div className="text-center">
                            <div className="bg-red-100 rounded-full p-3 mx-auto w-16 h-16 flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14 2 9.27l6.91-1.01L12 2z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">حدث خطأ غير متوقع</h2>
                            <p className="text-gray-600 mb-6">
                                نعتذر عن هذا الإزعاج. يُرجى إعادة تحميل الصفحة أو المحاولة لاحقاً.
                            </p>
                            <button 
                                onClick={() => window.location.reload()}
                                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
                            >
                                إعادة تحميل الصفحة
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}


const ImageMagnifier = ({ src, alt, className, magnifierHeight = 100, magnifierWidth = 100, zoomLevel = 2.5 }) => {
    const [showMagnifier, setShowMagnifier] = useState(false);
    const [magnifierXY, setMagnifierXY] = useState({ x: 0, y: 0 });
    const [imgXY, setImgXY] = useState({ x: 0, y: 0 });
    const [imgSize, setImgSize] = useState({ width: 0, height: 0 });

    const handleMouseMove = (e) => {
        const elem = e.currentTarget;
        const { top, left, width, height } = elem.getBoundingClientRect();
        
        // حساب موقع الماوس بالنسبة للصورة
        const x = e.clientX - left;
        const y = e.clientY - top;
        
        setImgXY({ x, y });
        setImgSize({ width, height });
        
        // موقع المكبر
        setMagnifierXY({
            x: e.clientX - magnifierWidth / 2,
            y: e.clientY - magnifierHeight / 2
        });
    };

    return (
        <div className="relative inline-block w-full">
            <img
                src={src}
                alt={alt}
                className={`${className} cursor-crosshair`}
                onMouseEnter={() => setShowMagnifier(true)}
                onMouseLeave={() => setShowMagnifier(false)}
                onMouseMove={handleMouseMove}
            />
            
            {/* المكبر */}
            {showMagnifier && (
                <div
                    className="fixed border-4 border-gray-800 rounded-full pointer-events-none z-[999] shadow-2xl"
                    style={{
                        height: `${magnifierHeight}px`,
                        width: `${magnifierWidth}px`,
                        top: `${magnifierXY.y}px`,
                        left: `${magnifierXY.x}px`,
                        backgroundImage: `url('${src}')`,
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: `${imgSize.width * zoomLevel}px ${imgSize.height * zoomLevel}px`,
                        backgroundPositionX: `${-imgXY.x * zoomLevel + magnifierWidth / 2}px`,
                        backgroundPositionY: `${-imgXY.y * zoomLevel + magnifierHeight / 2}px`,
                        backgroundColor: 'white'
                    }}
                >
                    {/* خطوط متقاطعة للمساعدة في التركيز */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-full h-0.5 bg-red-500 opacity-70"></div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="h-full w-0.5 bg-red-500 opacity-70"></div>
                    </div>
                    
                    {/* نص يوضح مستوى التكبير */}
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded">
                        {zoomLevel}x
                    </div>
                    </div>
            )}
        </div>
    );
};
// الرقم الظاهر للمستخدم: رقم الملف المتسلسل، مع الاحتفاظ بـ PassportID كمعرف داخلي فقط
const getPassportFileNumber = (passport = {}) => passport.OfficeSerial || passport.SerialNumber || '';

// تحسين أداء المكونات مع React.memo
const PassportRow = React.memo(({ passport, companies, delegates, professions, warningSettings, onView, onEdit, onNewTrip, onViewHistory, onAddNote, onQuickReceiveUpdate, onSmartRepeat, onPrintBarcode, onSavedPrintProfiles, showAlert, listMemberships, blacklistInfo, onOpenBlacklist, canManageBlacklist, showStatusColumn, onSecureDelivery, secureDeliveryEnabled }) => {
    const { hasPermission, canDeliver, canRevert } = usePermissions();
    const fileNumber = getPassportFileNumber(passport);
    return (
        
        <tr className="hover:bg-blue-50 align-top">
            <td className="px-3 py-3 font-semibold text-gray-700 whitespace-nowrap">{fileNumber}</td>
            <td className="px-3 py-3 font-semibold text-gray-900">
                <div className="flex flex-wrap items-center gap-1.5 min-w-[10rem] max-w-[16rem]">
                    <span>{passport.NameArabic}</span>
                    <BlacklistSummaryBadge items={blacklistInfo} onClick={canManageBlacklist ? () => onOpenBlacklist && onOpenBlacklist(passport) : undefined} />
                    {listMemberships && listMemberships[passport.PassportID]?.length ? (<div className="mt-1 flex items-center gap-1 flex-wrap"><span className="text-[11px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">ضمن قائمة: {listMemberships[passport.PassportID][0].ListName}</span>{listMemberships[passport.PassportID].length>1 && (<span className="text-[11px] text-gray-500">+{listMemberships[passport.PassportID].length - 1} أخرى</span>)}</div>) : null}
                    {(() => {
                        const passportWarning = checkExpiryWarning(
                            passport.PassportExpiryDate, 
                            passport.TravelDuration, 
                            warningSettings, 
                            'passport'
                        );
                        const licenseWarning = checkExpiryWarning(
                            passport.LicenseExpiryDate, 
                            passport.TravelDuration, 
                            warningSettings, 
                            'license'
                        );
                        
                        return (
                            <div className="flex gap-1 flex-wrap">
                                {passportWarning.hasWarning && (
                                    <span 
                                        className={`px-2 py-1 rounded-full text-xs font-medium cursor-help ${
                                            passportWarning.level === 'critical' 
                                                ? 'bg-red-100 text-red-800 border border-red-200' 
                                                : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                        }`}
                                        title={passportWarning.message}
                                    >
                                        🛂 {passportWarning.level === 'critical' ? 'منتهي' : 'ينتهي قريباً'}
                                    </span>
                                )}
                                {licenseWarning.hasWarning && (
                                    <span 
                                        className={`px-2 py-1 rounded-full text-xs font-medium cursor-help ${
                                            licenseWarning.level === 'critical' 
                                                ? 'bg-red-100 text-red-800 border border-red-200' 
                                                : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                        }`}
                                        title={licenseWarning.message}
                                    >
                                        🚗 {licenseWarning.level === 'critical' ? 'منتهية' : 'تنتهي قريباً'}
                                    </span>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </td>
            <td className="px-3 py-3">
                {companies.find(c => c.CompanyID === passport.CompanyID)?.CompanyName}
            </td>
            <td className="px-3 py-3">
                {passport.TravelProfessionName || professions.find(p => p.ProfessionID === passport.TravelProfessionID)?.ProfessionName || '—'}
            </td>
            <td className="px-3 py-3 whitespace-nowrap font-mono text-[13px]" dir="ltr">
                {(() => {
                    try {
                        if (!passport.PhoneNumbers) return '';
                        if (/^\d/.test(passport.PhoneNumbers.trim())) {
                            return passport.PhoneNumbers.trim();
                        }
                        const parsed = JSON.parse(passport.PhoneNumbers);
                        return Array.isArray(parsed) ? (parsed[0] || '') : passport.PhoneNumbers;
                    } catch (error) {
                        return passport.PhoneNumbers || '';
                    }
                })()}
            </td>
            <td className="px-3 py-3">
                {delegates.find(d => d.DelegateID === passport.DelegateID)?.DelegateName}
            </td>
            <td className="px-3 py-3 whitespace-nowrap font-mono text-[13px]" dir="ltr">{passport.PassportNumber}</td>
            <td className="px-3 py-3">
    <div className="flex flex-col items-center gap-1 min-w-[6.5rem]">
        {passport.PassportReceiveStatus !== 'لم يستلم' && passport.PassportReceiveStatus !== 'اختر الحالة' ? (
            <div className="flex flex-col items-center">
                <span className="text-blue-700 font-semibold">
                    {passport.PassportReceiveStatus}
                </span>
                {passport.ReceiveDate && (
                    <span className="text-sm text-gray-600 max-md:text-center">
                        {new Date(passport.ReceiveDate).toLocaleDateString('ar-EG')}
                    </span>
                )}
                <PermissionGate permissions="تراجع عن التسليم">
                    <button 
                        onClick={() => {
                            const doAction = () => onQuickReceiveUpdate(passport.PassportID, 'لم يستلم');
                            secureDeliveryEnabled ? onSecureDelivery('تراجع عن التسليم', `جواز: ${passport.NameArabic}`, doAction) : doAction();
                        }}
                        className="text-red-600 hover:text-red-800 text-sm mt-1 flex items-center gap-1"
                    >
                        <X size={16} className="inline-block" />
                        تراجع
                    </button>
                </PermissionGate>
            </div>
        ) : (
            <PermissionGate permissions="تسليم الجوازات">
                <div className="flex flex-col items-stretch gap-1 w-full">
                    <button
                        onClick={() => {
                            const doAction = () => onQuickReceiveUpdate(passport.PassportID, 'استلم العميل');
                            secureDeliveryEnabled ? onSecureDelivery('تسليم للعميل', `جواز: ${passport.NameArabic}`, doAction) : doAction();
                        }}
                        className="bg-green-100 text-green-600 px-2.5 py-1 text-xs font-semibold whitespace-nowrap rounded-full hover:bg-green-200 transition"
                    >
                        تسليم للعميل
                    </button>
                    <button
                        onClick={() => {
                            const doAction = () => onQuickReceiveUpdate(passport.PassportID, 'استلم المندوب');
                            secureDeliveryEnabled ? onSecureDelivery('تسليم للمندوب', `جواز: ${passport.NameArabic}`, doAction) : doAction();
                        }}
                        className="bg-blue-100 text-blue-600 px-2.5 py-1 text-xs font-semibold whitespace-nowrap rounded-full hover:bg-blue-200 transition"
                    >
                        تسليم للمندوب
                    </button>
                    <button
                        onClick={() => {
                            const doAction = () => {
                                const fn = window.prompt('ادخل اسم المستلم'); if (!fn) return;
                                const fp = window.prompt('ادخل رقم هاتف المستلم (11 رقم)'); if (!fp) return;
                                if (!/^01[0-9]{9}$/.test(fp)) { alert('رقم الهاتف يجب أن يبدأ بـ 01 ويكون 11 رقم'); return; }
                                onQuickReceiveUpdate(passport.PassportID, 'استلم صديق', fn, fp);
                            };
                            secureDeliveryEnabled ? onSecureDelivery('تسليم لصديق', `جواز: ${passport.NameArabic}`, doAction) : doAction();
                        }}
                        className="bg-yellow-100 text-yellow-600 px-2.5 py-1 text-xs font-semibold whitespace-nowrap rounded-full hover:bg-yellow-200 transition"
                    >
                        تسليم لصديق
                    </button>
                </div>
            </PermissionGate>
        )}
    </div>
</td>
            {showStatusColumn && (
            <td className="px-3 py-3">
                <div className="flex flex-col gap-1.5">
                    <StatusIndicator date={passport.MedicalExamDate} icon={Stethoscope} label="كشف طبي" />
                    <StatusIndicator date={passport.NetWorkDate} icon={Globe} label="عمل نت" />
                   <StatusIndicator date={passport.FingerprintDate} icon={Fingerprint} label="بصمة" />
                    <StatusIndicator date={passport.MedicalInsuranceDate} icon={BadgeCheck} label="التأمين الطبي" />
                    <StatusIndicator date={passport.ConsulateEntryDate} icon={MapPin} label="دخل القنصلية" />
                    <StatusIndicator date={passport.VisaDate} icon={BadgeCheck} label="أشر" />
                    <StatusIndicator date={passport.TravelDate} icon={Plane} label="سافر" />
                </div>
            </td>
            )}
            <td className="px-3 py-3">
    <div className="grid grid-cols-3 gap-0.5 justify-items-center w-[7.5rem] mx-auto">
        <button
            onClick={() => onView(passport)}
            className="text-green-600 hover:text-green-800 p-1.5 hover:bg-green-50 rounded-full transition duration-200"
            title="عرض"
        >
            <Eye size={18} />
        </button>
        <PermissionGate permissions="تعديل الجوازات">
            <button
                onClick={() => onEdit(passport)}
                className="text-blue-600 hover:text-blue-800 p-1.5 hover:bg-blue-50 rounded-full transition duration-200"
                title="تعديل"
            >
                <Edit size={18} />
            </button>
        </PermissionGate>
        <PermissionGate permissions="إدارة الملاحظات">
    <div className="relative group">
        <button
            onClick={() => onAddNote(passport.PassportID, passport.NameArabic)}
            className="text-green-600 hover:text-green-800 p-1.5 hover:bg-green-50 rounded-full transition duration-200"
            title="إضافة ملاحظة"
        >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10,9 9,9 8,9"/>
        </svg>
    </button>
    
    {/* Tooltip للملاحظات */}
    {(() => {
        try {
            const notes = passport.Notes ? JSON.parse(passport.Notes) : [];
            const validNotes = Array.isArray(notes) ? notes.filter(note => note.text && note.text.trim()) : [];
            
            if (validNotes.length > 0) {
                return (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-50 pointer-events-none">
                        <div className="bg-gray-800 text-white text-sm rounded-lg p-3 shadow-lg max-w-xs">
                            <div className="font-bold text-center mb-2 text-yellow-300">📝 الملاحظات المسجلة</div>
                            <div className="space-y-2 max-h-32 overflow-y-auto">
                                {validNotes.slice(0, 3).map((note, index) => (
                                    <div key={index} className="border-b border-gray-600 pb-1 last:border-b-0">
                                        <div className="text-xs text-gray-300">
                                            {new Date(note.date).toLocaleDateString('ar-EG')}
                                        </div>
                                        <div className="text-white">
                                            {note.text.length > 50 ? note.text.substring(0, 50) + '...' : note.text}
                                        </div>
                                    </div>
                                ))}
                                {validNotes.length > 3 && (
                                    <div className="text-center text-yellow-300 text-xs">
                                        و {validNotes.length - 3} ملاحظة أخرى...
                                    </div>
                                )}
                            </div>
                            <div className="text-center text-xs text-gray-400 mt-2">
                                انقر لإضافة ملاحظة جديدة
                            </div>
                            {/* مثلث صغير في الأسفل */}
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                        </div>
                    </div>
                );
            } else {
                return (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-50 pointer-events-none">
                        <div className="bg-blue-600 text-white text-sm rounded-lg p-2 shadow-lg">
                            <div className="text-center">لا توجد ملاحظات - انقر للإضافة</div>
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-blue-600"></div>
                        </div>
                    </div>
                );
            }
        } catch (error) {
            return null;
        }
    })()}
</div>
</PermissionGate>
{canManageBlacklist && (
    <button
        onClick={() => onOpenBlacklist && onOpenBlacklist(passport)}
        className="text-red-600 hover:text-red-800 p-1.5 hover:bg-red-50 rounded-full transition duration-200"
        title="إدارة قائمة المنع"
    >
        ⛔
    </button>
)}
<PermissionGate permissions="إنشاء سفرة جديدة">
    <button
        onClick={() => onNewTrip(passport.PassportID, passport.NameArabic)}
        className="text-green-600 hover:text-green-800 p-1.5 hover:bg-green-50 rounded-full transition duration-200"
        title="سفرة جديدة"
    >
        <Plus size={18} />
    </button>
</PermissionGate>
<PermissionGate permissions="طباعة الطرق المحفوظة">
    <QuickSavedPrintProfilesButton
        passport={passport}
        companies={companies}
        delegates={delegates}
        professions={professions}
        showAlert={showAlert}
        iconOnly
    />
</PermissionGate>
<PermissionGate permissions="عرض التاريخ">
    <button
        onClick={() => onViewHistory(passport.PassportID)}
        className="text-purple-600 hover:text-purple-800 p-1.5 hover:bg-purple-50 rounded-full transition duration-200"
        title="عرض التاريخ"
    >
        <Eye size={18} />
    </button>
</PermissionGate>
<PermissionGate permissions="التكرار الذكي">
<button
    onClick={() => onSmartRepeat(passport)}
    className="text-orange-600 hover:text-orange-800 p-1.5 hover:bg-orange-50 rounded-full transition duration-200"
    title="تكرار ذكي"
>
    <Copy size={18} />
</button>
</PermissionGate>
                </div>
            </td>
        </tr>
    );
});




// دالة للتحقق من انتهاء صلاحية الجواز/الرخصة
// دالة محسنة للتحقق من انتهاء صلاحية الجواز/الرخصة
// دالة محدثة للتحقق من انتهاء صلاحية الجواز/الرخصة
const checkExpiryWarning = (date, travelDuration, settings, type = 'passport') => {
    if (!date || !travelDuration || !settings) {
        return { hasWarning: false, message: '', level: 'none', remainingMonths: null };
    }

    // التحقق من تفعيل التحذيرات
    const isWarningEnabled = type === 'passport' 
        ? settings.enablePassportWarnings 
        : settings.enableLicenseWarnings;
    
    if (!isWarningEnabled) {
        return { hasWarning: false, message: '', level: 'none', remainingMonths: null };
    }

    const expiryDate = new Date(date);
    const today = new Date();
    
    if (isNaN(expiryDate.getTime())) {
        return { hasWarning: false, message: '', level: 'none', remainingMonths: null };
    }

    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = Math.ceil(diffDays / 30);

    // استخدام الإعدادات الجديدة المنفصلة
    const settingsKey = type === 'passport' ? 'passportSettings' : 'licenseSettings';
    const durationConfig = settings[settingsKey]?.[travelDuration];
    
    if (!durationConfig) {
        return { hasWarning: false, message: '', level: 'none', remainingMonths: diffMonths };
    }

    const minMonths = durationConfig.minMonths;
    const documentType = type === 'passport' ? 'الجواز' : 'الرخصة';
    const documentIcon = type === 'passport' ? '🛂' : '🚗';

    // التحقق من انتهاء الصلاحية
    if (diffDays <= 0) {
        return {
            hasWarning: true,
            level: 'critical',
            message: `${documentIcon} ${documentType} منتهي الصلاحية منذ ${Math.abs(diffDays)} يوم`,
            remainingMonths: diffMonths,
            remainingDays: diffDays
        };
    } 
    // التحقق من اقتراب انتهاء الصلاحية
    else if (diffMonths <= minMonths) {
        let message;
        if (diffDays <= 30) {
            message = `${documentIcon} ${documentType} ينتهي خلال ${diffDays} يوم`;
        } else {
            message = `${documentIcon} ${documentType} ينتهي خلال ${diffMonths} شهر`;
        }
        
        return {
            hasWarning: true,
            level: 'warning',
            message: message,
            remainingMonths: diffMonths,
            remainingDays: diffDays
        };
    }

    return { 
        hasWarning: false, 
        message: '', 
        level: 'none', 
        remainingMonths: diffMonths,
        remainingDays: diffDays 
    };
};


const StatusIndicator = ({ date, color, label, icon: Icon }) => {
    if (!date) return null;

    const getStatusColor = () => {
        switch (label) {
            case 'كشف طبي':
                return 'text-green-600 bg-green-50';
            case 'عمل نت':
                return 'text-blue-600 bg-blue-50';
            case 'بصمة':
                return 'text-orange-600 bg-orange-50';
            case 'دخل القنصلية':
                return 'text-teal-600 bg-teal-50';
                case 'تأشير':
                    return 'text-amber-600 bg-amber-50';
            case 'سافر':
                return 'text-purple-600 bg-purple-50';
            default:
                return '';
        }
    };

    return (
        <div className="flex items-center gap-2">
            <div className={`p-1 rounded-lg ${getStatusColor()}`}
                 title={`${label}: ${new Date(date).toLocaleDateString('ar-EG')}`}>
                <Icon size={16} />
            </div>
            <span className="text-xs text-gray-600">{label}</span>
        </div>
    );
};

// أدوات عرض الموبايل فقط - لا تغيّر شكل الكمبيوتر ولا منطق البيانات
const getPassportPhone = (passport) => {
    try {
        if (!passport.PhoneNumbers) return 'غير مسجل';
        if (/^\d/.test(passport.PhoneNumbers.trim())) {
            return passport.PhoneNumbers.trim();
        }
        const parsed = JSON.parse(passport.PhoneNumbers);
        return Array.isArray(parsed) ? (parsed[0] || 'غير مسجل') : (passport.PhoneNumbers || 'غير مسجل');
    } catch (error) {
        return passport.PhoneNumbers || 'غير مسجل';
    }
};

const findNameById = (items, idField, nameField, id, fallback = 'غير محدد') => {
    return items.find(item => item[idField] === id)?.[nameField] || fallback;
};

const PassportMobileCard = React.memo(({ passport, companies, delegates, professions, warningSettings, onView, onEdit, onNewTrip, onViewHistory, onAddNote, onQuickReceiveUpdate, onSmartRepeat, onPrintBarcode, onSavedPrintProfiles, showAlert, listMemberships, blacklistInfo, onOpenBlacklist, canManageBlacklist, showStatusColumn, onSecureDelivery, secureDeliveryEnabled }) => {
    const [showActions, setShowActions] = useState(false);
    const companyName = findNameById(companies, 'CompanyID', 'CompanyName', passport.CompanyID);
    const professionName = passport.TravelProfessionName || findNameById(professions, 'ProfessionID', 'ProfessionName', passport.TravelProfessionID);
    const delegateName = findNameById(delegates, 'DelegateID', 'DelegateName', passport.DelegateID);
    const phone = getPassportPhone(passport);
    const receiveStatus = passport.PassportReceiveStatus && passport.PassportReceiveStatus !== 'اختر الحالة'
        ? passport.PassportReceiveStatus
        : 'لم يستلم';
    const fileNumber = getPassportFileNumber(passport);

    const passportWarning = checkExpiryWarning(
        passport.PassportExpiryDate,
        passport.TravelDuration,
        warningSettings,
        'passport'
    );
    const licenseWarning = checkExpiryWarning(
        passport.LicenseExpiryDate,
        passport.TravelDuration,
        warningSettings,
        'license'
    );

    const runDeliveryAction = (title, status, extraAction) => {
        const doAction = extraAction || (() => onQuickReceiveUpdate(passport.PassportID, status));
        secureDeliveryEnabled ? onSecureDelivery(title, `جواز: ${passport.NameArabic}`, doAction) : doAction();
        setShowActions(false);
    };

    const actionButtonClass = "w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-100 text-gray-800 transition text-right";

    return (
        <div className="bg-white rounded-[1.35rem] shadow-sm border border-gray-100 overflow-hidden md:hidden mx-2 transition active:scale-[0.995]">
            <div className="h-1 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600"></div>
            <div className="p-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded-full font-semibold">ملف #{fileNumber}</span>
                            <BlacklistSummaryBadge items={blacklistInfo} onClick={canManageBlacklist ? () => onOpenBlacklist && onOpenBlacklist(passport) : undefined} />
                            {listMemberships && listMemberships[passport.PassportID]?.length ? (
                                <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-full">
                                    ضمن قائمة: {listMemberships[passport.PassportID][0].ListName}
                                </span>
                            ) : null}
                        </div>
                        <h3 className="text-base font-black text-gray-900 leading-7 break-words">{passport.NameArabic || 'بدون اسم'}</h3>
                        <p className="text-sm text-gray-500 mt-0.5">جواز: <span className="font-bold text-gray-700">{passport.PassportNumber || 'غير مسجل'}</span></p>
                    </div>
                    <span className={`shrink-0 text-xs px-3 py-1.5 rounded-full font-semibold ${receiveStatus === 'لم يستلم' ? 'bg-yellow-50 text-yellow-700' : 'bg-green-50 text-green-700'}`}>
                        {receiveStatus}
                    </span>
                </div>

                {(passportWarning.hasWarning || licenseWarning.hasWarning) && (
                    <div className="flex gap-2 flex-wrap">
                        {passportWarning.hasWarning && (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${passportWarning.level === 'critical' ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'}`}>
                                🛂 {passportWarning.level === 'critical' ? 'الجواز منتهي' : 'الجواز ينتهي قريباً'}
                            </span>
                        )}
                        {licenseWarning.hasWarning && (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${licenseWarning.level === 'critical' ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'}`}>
                                🚗 {licenseWarning.level === 'critical' ? 'الرخصة منتهية' : 'الرخصة تنتهي قريباً'}
                            </span>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-slate-50 rounded-2xl p-2.5 border border-slate-100">
                        <div className="text-gray-400 text-xs mb-1">الشركة</div>
                        <div className="font-semibold text-gray-800 break-words">{companyName}</div>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-2.5 border border-slate-100">
                        <div className="text-gray-400 text-xs mb-1">المندوب</div>
                        <div className="font-semibold text-gray-800 break-words">{delegateName}</div>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-2.5 border border-slate-100">
                        <div className="text-gray-400 text-xs mb-1">مهنة السفر</div>
                        <div className="font-semibold text-gray-800 break-words">{professionName}</div>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-2.5 border border-slate-100">
                        <div className="text-gray-400 text-xs mb-1">الهاتف</div>
                        <div className="font-semibold text-gray-800 break-words" dir="ltr">{phone}</div>
                    </div>
                </div>

                {showStatusColumn && (
                    <details className="bg-slate-50 rounded-2xl p-2.5 border border-slate-100 group">
                        <summary className="list-none cursor-pointer flex items-center justify-between text-sm font-black text-gray-800">
                            <span>مراحل المتابعة</span>
                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full group-open:hidden">عرض</span>
                            <span className="hidden text-xs text-gray-500 bg-white px-2 py-1 rounded-full group-open:inline">إخفاء</span>
                        </summary>
                        <div className="grid grid-cols-2 gap-2 mt-3">
                            <StatusIndicator date={passport.MedicalExamDate} icon={Stethoscope} label="كشف طبي" />
                            <StatusIndicator date={passport.NetWorkDate} icon={Globe} label="عمل نت" />
                            <StatusIndicator date={passport.FingerprintDate} icon={Fingerprint} label="بصمة" />
                            <StatusIndicator date={passport.MedicalInsuranceDate} icon={BadgeCheck} label="التأمين الطبي" />
                            <StatusIndicator date={passport.ConsulateEntryDate} icon={MapPin} label="دخل القنصلية" />
                            <StatusIndicator date={passport.VisaDate} icon={BadgeCheck} label="أشر" />
                            <StatusIndicator date={passport.TravelDate} icon={Plane} label="سافر" />
                        </div>
                    </details>
                )}

                <button
                    onClick={() => setShowActions(true)}
                    className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-2xl py-3 font-black shadow-sm active:scale-[0.99] transition"
                >
                    الإجراءات
                </button>
            </div>

            {showActions && (
                <BodyPortal>
                    <div
                        className="fixed inset-0 bg-black/45 z-[9999] md:hidden"
                        style={{ width: '100vw', height: '100dvh' }}
                        onClick={() => setShowActions(false)}
                    >
                    <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-[2rem] w-screen max-h-[86dvh] overflow-y-auto p-4 pb-6 shadow-2xl" onClick={(e) => e.stopPropagation()} dir="rtl">
                        <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-4"></div>
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">إجراءات الجواز</h3>
                                <p className="text-sm text-gray-500 mt-1">{passport.NameArabic || 'بدون اسم'} - ملف #{fileNumber}</p>
                            </div>
                            <button onClick={() => setShowActions(false)} className="bg-gray-100 text-gray-600 rounded-full p-2">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-2">
                            <button className={actionButtonClass} onClick={() => { onView(passport); setShowActions(false); }}>
                                <span>عرض بيانات الجواز</span>
                                <Eye size={18} className="text-green-600" />
                            </button>

                            <PermissionGate permissions="تعديل الجوازات">
                                <button className={actionButtonClass} onClick={() => { onEdit(passport); setShowActions(false); }}>
                                    <span>تعديل بيانات الجواز</span>
                                    <Edit size={18} className="text-blue-600" />
                                </button>
                            </PermissionGate>

                            <PermissionGate permissions="إدارة الملاحظات">
                                <button className={actionButtonClass} onClick={() => { onAddNote(passport.PassportID, passport.NameArabic); setShowActions(false); }}>
                                    <span>إضافة ملاحظة</span>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-600">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                        <polyline points="14,2 14,8 20,8"/>
                                        <line x1="16" y1="13" x2="8" y2="13"/>
                                        <line x1="16" y1="17" x2="8" y2="17"/>
                                        <polyline points="10,9 9,9 8,9"/>
                                    </svg>
                                </button>
                            </PermissionGate>

                            {canManageBlacklist && (
                                <button className={actionButtonClass} onClick={() => { onOpenBlacklist && onOpenBlacklist(passport); setShowActions(false); }}>
                                    <span>إدارة قائمة المنع</span>
                                    <span className="text-red-600 font-black">⛔</span>
                                </button>
                            )}

                            <PermissionGate permissions="إنشاء سفرة جديدة">
                                <button className={actionButtonClass} onClick={() => { onNewTrip(passport.PassportID, passport.NameArabic); setShowActions(false); }}>
                                    <span>إنشاء سفرة جديدة</span>
                                    <Plus size={18} className="text-green-600" />
                                </button>
                            </PermissionGate>

                            <PermissionGate permissions="عرض التاريخ">
                                <button className={actionButtonClass} onClick={() => { onViewHistory(passport.PassportID); setShowActions(false); }}>
                                    <span>عرض تاريخ العميل</span>
                                    <Eye size={18} className="text-purple-600" />
                                </button>
                            </PermissionGate>

                            <PermissionGate permissions="طباعة الطرق المحفوظة">
                                <QuickSavedPrintProfilesButton
                                    passport={passport}
                                    companies={companies}
                                    delegates={delegates}
                                    professions={professions}
                                    showAlert={showAlert}
                                    buttonClassName={actionButtonClass}
                                    label="طباعة بطريقة محفوظة"
                                    onAfterPrint={() => setShowActions(false)}
                                />
                            </PermissionGate>

                            <PermissionGate permissions="التكرار الذكي">
                                <button className={actionButtonClass} onClick={() => { onSmartRepeat(passport); setShowActions(false); }}>
                                    <span>تكرار ذكي</span>
                                    <Copy size={18} className="text-orange-600" />
                                </button>
                            </PermissionGate>
                        </div>

                        <div className="border-t border-gray-100 mt-4 pt-4">
                            <div className="font-bold text-gray-900 mb-2">استلام وتسليم الجواز</div>
                            {receiveStatus !== 'لم يستلم' ? (
                                <div className="bg-blue-50 rounded-xl p-3">
                                    <div className="text-blue-700 font-semibold">الحالة الحالية: {receiveStatus}</div>
                                    {passport.ReceiveDate && (
                                        <div className="text-sm text-gray-600 mt-1">تاريخ الاستلام: {new Date(passport.ReceiveDate).toLocaleDateString('ar-EG')}</div>
                                    )}
                                    <PermissionGate permissions="تراجع عن التسليم">
                                        <button
                                            onClick={() => runDeliveryAction('تراجع عن التسليم', 'لم يستلم')}
                                            className="mt-3 w-full bg-red-50 text-red-700 rounded-xl py-2.5 font-semibold"
                                        >
                                            تراجع عن التسليم
                                        </button>
                                    </PermissionGate>
                                </div>
                            ) : (
                                <PermissionGate permissions="تسليم الجوازات">
                                    <div className="grid grid-cols-1 gap-2">
                                        <button
                                            onClick={() => runDeliveryAction('تسليم للعميل', 'استلم العميل')}
                                            className="w-full bg-green-50 text-green-700 rounded-xl py-3 font-semibold"
                                        >
                                            تسليم للعميل
                                        </button>
                                        <button
                                            onClick={() => runDeliveryAction('تسليم للمندوب', 'استلم المندوب')}
                                            className="w-full bg-blue-50 text-blue-700 rounded-xl py-3 font-semibold"
                                        >
                                            تسليم للمندوب
                                        </button>
                                        <button
                                            onClick={() => runDeliveryAction('تسليم لصديق', 'استلم صديق', () => {
                                                const fn = window.prompt('ادخل اسم المستلم'); if (!fn) return;
                                                const fp = window.prompt('ادخل رقم هاتف المستلم (11 رقم)'); if (!fp) return;
                                                if (!/^01[0-9]{9}$/.test(fp)) { alert('رقم الهاتف يجب أن يبدأ بـ 01 ويكون 11 رقم'); return; }
                                                onQuickReceiveUpdate(passport.PassportID, 'استلم صديق', fn, fp);
                                            })}
                                            className="w-full bg-yellow-50 text-yellow-700 rounded-xl py-3 font-semibold"
                                        >
                                            تسليم لصديق
                                        </button>
                                    </div>
                                </PermissionGate>
                            )}
                        </div>
                    </div>
                </div>
                </BodyPortal>
            )}
        </div>
    );
});


// إنشاء كائنات الصوت للتنبيهات
function playSound(type) {
    try {
        // استخدام Web Audio API
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        if (type === 'error') {
            oscillator.frequency.value = 440; // صوت مرتفع للخطأ
            gainNode.gain.value = 0.1;
            oscillator.start();
            setTimeout(() => oscillator.stop(), 200);
        } else if (type === 'warning') {
            oscillator.frequency.value = 330; // صوت منخفض للتحذير
            gainNode.gain.value = 0.1;
            oscillator.start();
            setTimeout(() => oscillator.stop(), 300);
        }
    } catch (error) {
        console.warn('لا يمكن تشغيل الصوت:', error);
    }
}

// مكون رسالة التنبيه
const AlertMessage = ({ message, type, onClose }) => {
    return (
        <div className={`fixed top-8 left-1/2 transform -translate-x-1/2 z-50 p-3 rounded-lg shadow-sm flex items-center gap-2 transition-all duration-300 ${
            type === 'error' ? 'bg-red-50 text-red-600 border-red-200' : 
            type === 'warning' ? 'bg-yellow-50 text-yellow-600 border-yellow-200' : 
            'bg-green-50 text-green-600 border-green-200'
        } border`}>
            <span className="text-sm font-medium">{message}</span>
            <button 
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
            >
                <X size={16} />
            </button>
        </div>
    );
};

// =========================
// طباعة ملصقات وباركود الجوازات عبر Web Print SDK المحلي
// =========================
const BARCODE_STORAGE_KEY = 'masar_passport_barcode_websdk_settings_v2_independent_layout';
const BARCODE_AUTO_PRINT_STORAGE_KEY = 'masar_passport_barcode_auto_print_local_v1';

const DEFAULT_BARCODE_SETTINGS = {
    printEngine: 'server-bridge',
    printerGuideRead: false,
    sdkPort: 18080,
    logicalName: 'Printer1',
    printAgentId: '',
    printAgentPrinterName: '',
    labelWidthMm: 38,
    labelHeightMm: 24,
    dpi: 203,

    showName: true,
    showId: true,
    showBarcode: true,
    codeType: 'barcode',
    contentMode: 'search',

    // إعدادات عامة للطابعة
    density: 15,
    speed: 3,
    gapDots: 16,
    mediaType: 'G',
    orientation: 'T',
    printCopies: 1,

    // عنصر الاسم: كل عنصر له مكانه وحجمه وخطه المستقل
    nameXmm: 0.5,
    nameYmm: 1.0,
    nameWidthMm: 37,
    nameHeightMm: 4.5,
    nameFontSize: 8,
    nameBold: true,
    nameAlign: 'center',

    // عنصر ID
    idXmm: 0.5,
    idYmm: 5.4,
    idWidthMm: 37,
    idHeightMm: 5.0,
    idFontSize: 13,
    idBold: true,
    idAlign: 'center',

    // عنصر الباركود الخطي
    barcodeXmm: 2.5,
    barcodeYmm: 10.8,
    barcodeHeightMm: 10.5,
    barcodeNarrow: 2,
    barcodeWide: 5,
    barcodePreviewWidthMm: 33,
    barcodeAlign: 'center',

    // عنصر QR
    qrXmm: 13,
    qrYmm: 7,
    qrSize: 7,

    activeProfileId: '',
    profileName: 'ملصق 38×24 - افتراضي',
    autoPrintOnCreate: false,
    autoPrintProfileId: '',
    savedProfiles: [],
    includeFields: [
        'company', 'nameArabic', 'passportNumber', 'profession', 'delegate',
        'phones', 'previousCompanies', 'medicalStatus', 'netWorkDate',
        'applicationNumber', 'fingerprintDate', 'visaDate', 'travelDate'
    ]
};

const getBarcodeProfileSnapshot = (settings = {}) => {
    const {
        savedProfiles,
        activeProfileId,
        profileName,
        printerGuideRead,
        autoPrintOnCreate,
        autoPrintProfileId,
        ...snapshot
    } = settings || {};

    // مهم: طريقة الطباعة المحفوظة تحفظ الآن كل شيء يخص الطباعة نفسها:
    // نوع طريقة الطباعة + جهاز Agent + اسم الطابعة + مقاس الليبل + الكثافة/السرعة + أماكن العناصر.
    // هذا يجعل تحميل أي طريقة يرجع نفس الطابعة ونفس الإعدادات بدل الاعتماد على إعداد عام قديم.
    return snapshot;
};

const normalizeBarcodeProfiles = (profiles) => Array.isArray(profiles)
    ? profiles.filter(profile => profile && profile.id && profile.name && profile.settings)
    : [];

const migrateOldBarcodeSettings = (savedSettings = {}) => {
    const migrated = { ...savedSettings };
    const dpi = Number(savedSettings.dpi) || 203;
    const dotsToMm = (dots, fallbackMm = 0) => {
        if (dots === undefined || dots === null || dots === '') return fallbackMm;
        return Number((Number(dots) * 25.4 / dpi).toFixed(2));
    };

    // دعم أي إعدادات قديمة كانت محفوظة بالنقط بدل المليمتر
    if (savedSettings.nameX !== undefined && savedSettings.nameXmm === undefined) migrated.nameXmm = dotsToMm(savedSettings.nameX, DEFAULT_BARCODE_SETTINGS.nameXmm);
    if (savedSettings.nameY !== undefined && savedSettings.nameYmm === undefined) migrated.nameYmm = dotsToMm(savedSettings.nameY, DEFAULT_BARCODE_SETTINGS.nameYmm);
    if (savedSettings.idX !== undefined && savedSettings.idXmm === undefined) migrated.idXmm = dotsToMm(savedSettings.idX, DEFAULT_BARCODE_SETTINGS.idXmm);
    if (savedSettings.idY !== undefined && savedSettings.idYmm === undefined) migrated.idYmm = dotsToMm(savedSettings.idY, DEFAULT_BARCODE_SETTINGS.idYmm);
    if (savedSettings.barcodeX !== undefined && savedSettings.barcodeXmm === undefined) migrated.barcodeXmm = dotsToMm(savedSettings.barcodeX, DEFAULT_BARCODE_SETTINGS.barcodeXmm);
    if (savedSettings.barcodeY !== undefined && savedSettings.barcodeYmm === undefined) migrated.barcodeYmm = dotsToMm(savedSettings.barcodeY, DEFAULT_BARCODE_SETTINGS.barcodeYmm);
    if (savedSettings.barcodeHeight !== undefined && savedSettings.barcodeHeightMm === undefined) migrated.barcodeHeightMm = dotsToMm(savedSettings.barcodeHeight, DEFAULT_BARCODE_SETTINGS.barcodeHeightMm);
    if (savedSettings.qrX !== undefined && savedSettings.qrXmm === undefined) migrated.qrXmm = dotsToMm(savedSettings.qrX, DEFAULT_BARCODE_SETTINGS.qrXmm);
    if (savedSettings.qrY !== undefined && savedSettings.qrYmm === undefined) migrated.qrYmm = dotsToMm(savedSettings.qrY, DEFAULT_BARCODE_SETTINGS.qrYmm);

    return migrated;
};

const mergeBarcodeSettings = (savedSettings = {}) => {
    const migrated = migrateOldBarcodeSettings(savedSettings || {});
    return {
        ...DEFAULT_BARCODE_SETTINGS,
        ...migrated,
        savedProfiles: normalizeBarcodeProfiles(migrated?.savedProfiles)
    };
};

const normalizeBarcodeLayoutSettings = (settings = {}) => {
    const next = mergeBarcodeSettings(settings || {});
    const round = (value, precision = 1) => Number(Number(value).toFixed(precision));
    const clamp = (value, min, max) => Math.min(Math.max(Number(value) || min, min), Math.max(min, max));
    const labelWidth = Math.max(10, Number(next.labelWidthMm) || DEFAULT_BARCODE_SETTINGS.labelWidthMm);
    const labelHeight = Math.max(8, Number(next.labelHeightMm) || DEFAULT_BARCODE_SETTINGS.labelHeightMm);
    const safeMargin = 0.4;

    const fitBox = ({ xKey, yKey, widthKey, heightKey, minWidth = 1, minHeight = 1 }) => {
        const x = clamp(next[xKey], 0, Math.max(0, labelWidth - safeMargin));
        const y = clamp(next[yKey], 0, Math.max(0, labelHeight - safeMargin));
        const maxWidth = Math.max(minWidth, labelWidth - x - safeMargin);
        const maxHeight = Math.max(minHeight, labelHeight - y - safeMargin);
        next[xKey] = round(x);
        next[yKey] = round(y);
        if (widthKey) next[widthKey] = round(clamp(next[widthKey], minWidth, maxWidth));
        if (heightKey) next[heightKey] = round(clamp(next[heightKey], minHeight, maxHeight));
    };

    fitBox({ xKey: 'nameXmm', yKey: 'nameYmm', widthKey: 'nameWidthMm', heightKey: 'nameHeightMm', minWidth: 5, minHeight: 2 });
    fitBox({ xKey: 'idXmm', yKey: 'idYmm', widthKey: 'idWidthMm', heightKey: 'idHeightMm', minWidth: 5, minHeight: 2 });
    fitBox({ xKey: 'barcodeXmm', yKey: 'barcodeYmm', widthKey: 'barcodePreviewWidthMm', heightKey: 'barcodeHeightMm', minWidth: 8, minHeight: 4 });

    const qrVisualSizeMm = Math.max(10, (Number(next.qrSize) || DEFAULT_BARCODE_SETTINGS.qrSize) * 2.8);
    next.qrXmm = round(clamp(next.qrXmm, 0, Math.max(0, labelWidth - qrVisualSizeMm - safeMargin)));
    next.qrYmm = round(clamp(next.qrYmm, 0, Math.max(0, labelHeight - qrVisualSizeMm - safeMargin)));

    next.barcodeNarrow = Math.max(1, Math.min(4, Number(next.barcodeNarrow) || DEFAULT_BARCODE_SETTINGS.barcodeNarrow));
    const rawWide = Number(next.barcodeWide) || DEFAULT_BARCODE_SETTINGS.barcodeWide;
    // مهم: لو الخط العريض مساوي للرفيع فالباركود قد يطبع كشكل أعمدة غير قابل للقراءة.
    // نخلي النسبة آمنة تلقائيًا، خصوصًا لطابعات 203dpi.
    next.barcodeWide = Math.max(next.barcodeNarrow + 1, Math.min(8, rawWide));
    if (next.barcodeWide <= next.barcodeNarrow) next.barcodeWide = Math.min(8, next.barcodeNarrow * 2 + 1);
    next.printCopies = Math.max(1, Number(next.printCopies) || 1);
    next.dpi = Math.max(100, Number(next.dpi) || DEFAULT_BARCODE_SETTINGS.dpi);

    // لا نغيّر نوع الرمز تلقائيًا.
    // لو المستخدم اختار Barcode مع بيانات مكتوبة، سيظل Barcode كما اختاره،
    // وسيظهر تنبيه أن الباركود الخطي يطبع الكود القصير فقط.

    return next;
};

const BARCODE_PRINT_ENGINE_OPTIONS = [
    { value: 'agent', label: 'Masar Print Agent - طابعة USB على أي جهاز' },
    { value: 'server-bridge', label: 'طباعة مركزية من جهاز السيرفر' },
    { value: 'websdk', label: 'طباعة محلية من نفس الجهاز' }
];

const BARCODE_QUICK_SETUP_STEPS = [
    'ثبّت تعريف الطابعة USB على جهاز Windows المتصل بها.',
    'شغّل Masar Print Agent على نفس الجهاز المتصل بالطابعة، وسيظهر الجهاز والطابعات داخل النظام.',
    'من ضبط الباركود اختر طريقة الطباعة: Masar Print Agent.',
    'اختَر Agent ID والطابعة المطلوبة من القائمة، أو اكتبهم يدويًا لو لم تظهر القائمة فورًا.',
    'أي مستخدم من موبايل أو كمبيوتر سيرسل أمر الطباعة للـ Queue، والـ Agent المتصل بالطابعة هو الذي يطبع.',
    'اضبط عرض وارتفاع الليبل ثم اضبط عناصر الاسم والـ ID والباركود، وبعد الوصول للشكل المناسب احفظها كطريقة طباعة محفوظة.'
];

const BARCODE_FIELD_OPTIONS = [
    { key: 'company', label: 'الشركة', scanLabel: 'شركة' },
    { key: 'nameArabic', label: 'الاسم بالعربي', scanLabel: 'اسم' },
    { key: 'passportNumber', label: 'رقم الجواز', scanLabel: 'جواز' },
    { key: 'profession', label: 'المهنة بالجواز', scanLabel: 'مهنة' },
    { key: 'delegate', label: 'المندوب', scanLabel: 'مندوب' },
    { key: 'phones', label: 'أرقام الهواتف', scanLabel: 'هاتف' },
    { key: 'previousCompanies', label: 'الشركات السابقة', scanLabel: 'سابق' },
    { key: 'medicalStatus', label: 'حالة الكشف الطبي', scanLabel: 'طبي' },
    { key: 'netWorkDate', label: 'تاريخ عمل النت', scanLabel: 'نت' },
    { key: 'applicationNumber', label: 'رقم الطلب', scanLabel: 'طلب' },
    { key: 'fingerprintDate', label: 'تاريخ البصمة', scanLabel: 'بصمة' },
    { key: 'visaDate', label: 'تاريخ التأشير', scanLabel: 'تأشير' },
    { key: 'travelDate', label: 'تاريخ السفر', scanLabel: 'سفر' }
];

// السعة المباشرة الآمنة لمولّد QR الحالي في الصفحة عند Version 5 / Level L.
// بدل ما يقطع النص العربي في منتصف الحرف ويظهر الرمز � عند المسح،
// بنحسب الطول ونمنع القصّ الصامت ونستخدم صيغة مختصرة للبيانات المكتوبة.
const QR_DIRECT_TEXT_MAX_BYTES = 106;


const BARCODE_SETTINGS_ALL_SECTIONS_PERMISSION = 'نافذة الباركود - عرض كل مناطق النافذة';

const BARCODE_SETTINGS_SECTION_PERMISSIONS = [
    BARCODE_SETTINGS_ALL_SECTIONS_PERMISSION,
    'نافذة الباركود - المعاينة والسحب',
    'نافذة الباركود - اختبار وطباعة',
    'نافذة الباركود - حفظ واستعادة الإعدادات',
    'نافذة الباركود - طرق الطباعة المحفوظة',
    'نافذة الباركود - الطباعة التلقائية',
    'نافذة الباركود - إعدادات الليبل والطابعة',
    'نافذة الباركود - عنصر الاسم',
    'نافذة الباركود - عنصر ID',
    'نافذة الباركود - عنصر الباركود أو QR',
    'نافذة الباركود - محتوى الرمز',
    'نافذة الباركود - دليل التشغيل',
    'نافذة الباركود - تنبيه Masar Print Agent'
];

const barcodeSafeText = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    const text = String(value).replace(/\s+/g, ' ').trim();
    return text || fallback;
};

const barcodeSafeMultilineText = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    const text = String(value)
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .split('\n')
        .map(line => line.replace(/[ \t]+/g, ' ').trim())
        .filter(Boolean)
        .join('\n')
        .trim();
    return text || fallback;
};

const barcodeUtf8ByteLength = (value) => {
    const text = String(value || '');
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text).length;
    try { return unescape(encodeURIComponent(text)).length; } catch { return text.length; }
};

const trimTextToUtf8Bytes = (value, maxBytes) => {
    const suffix = '…';
    const clean = String(value || '');
    const safeMax = Math.max(1, Number(maxBytes) || 1);
    if (barcodeUtf8ByteLength(clean) <= safeMax) return clean;
    const chars = Array.from(clean);
    let low = 0;
    let high = chars.length;
    while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        const candidate = chars.slice(0, mid).join('') + suffix;
        if (barcodeUtf8ByteLength(candidate) <= safeMax) low = mid;
        else high = mid - 1;
    }
    return chars.slice(0, Math.max(0, low)).join('') + suffix;
};

const formatBarcodeDate = (value) => {
    if (!value) return '';
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return String(value);
        return date.toLocaleDateString('ar-EG');
    } catch {
        return String(value);
    }
};

const barcodeFindName = (items, idField, nameField, id, fallback = '') => {
    const item = Array.isArray(items) ? items.find(x => String(x?.[idField] ?? '') === String(id ?? '')) : null;
    return item?.[nameField] || fallback;
};

const barcodeGetPhoneText = (phoneNumbers) => {
    if (!phoneNumbers) return '';
    try {
        if (typeof phoneNumbers === 'string' && /^\d/.test(phoneNumbers.trim())) return phoneNumbers.trim();
        const parsed = typeof phoneNumbers === 'string' ? JSON.parse(phoneNumbers) : phoneNumbers;
        if (Array.isArray(parsed)) return parsed.filter(Boolean).join(' - ');
        return barcodeSafeText(parsed);
    } catch {
        return barcodeSafeText(phoneNumbers);
    }
};

const barcodeGetPreviousCompaniesText = (passport, companies) => {
    const raw = passport?.PreviousCompanies;
    if (!raw) return '';
    try {
        const parsed = Array.isArray(raw) ? raw : JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed
                .map(id => barcodeFindName(companies, 'CompanyID', 'CompanyName', id, id))
                .filter(Boolean)
                .join(' - ');
        }
    } catch {}
    return barcodeSafeText(raw);
};

const getCompanyForBarcode = (passport, companies) => {
    return Array.isArray(companies) ? companies.find(c => String(c?.CompanyID ?? '') === String(passport?.CompanyID ?? '')) : null;
};

const cleanCompanyCodeForBarcode = (value) => {
    const cleaned = barcodeSafeText(value).replace(/\s+/g, '').toUpperCase();
    return cleaned || '';
};

const getCompanyCodeForBarcode = (passport, companies) => {
    const company = getCompanyForBarcode(passport, companies);
    // الرمز الأساسي يأتي من صفحة الشركات: Companies.CompanyCode
    // لو الشركة ليس لها رمز، نستخدم C كافتراضي حتى لا يتوقف الباركود القديم.
    const directCode = cleanCompanyCodeForBarcode(company?.CompanyCode || company?.Code || company?.Symbol || company?.CompanySymbol || company?.ShortCode);
    return directCode || 'C';
};

const buildPassportBarcodeCode = (passport, companies) => `${getCompanyCodeForBarcode(passport, companies)}-${getPassportFileNumber(passport) || ''}`;

const buildPassportBarcodeFieldMap = (passport, companies, delegates, professions) => ({
    company: barcodeFindName(companies, 'CompanyID', 'CompanyName', passport?.CompanyID),
    nameArabic: passport?.NameArabic || '',
    passportNumber: passport?.PassportNumber || '',
    profession: passport?.PassportProfessionName || passport?.PassportProfessionText || barcodeFindName(professions, 'ProfessionID', 'ProfessionName', passport?.PassportProfessionID),
    delegate: barcodeFindName(delegates, 'DelegateID', 'DelegateName', passport?.DelegateID),
    phones: barcodeGetPhoneText(passport?.PhoneNumbers),
    previousCompanies: barcodeGetPreviousCompaniesText(passport, companies),
    medicalStatus: passport?.MedicalExamStatus || '',
    netWorkDate: formatBarcodeDate(passport?.NetWorkDate),
    applicationNumber: passport?.ApplicationNumber || '',
    fingerprintDate: formatBarcodeDate(passport?.FingerprintDate),
    visaDate: formatBarcodeDate(passport?.VisaDate),
    travelDate: formatBarcodeDate(passport?.TravelDate)
});

const getBarcodeSelectedFieldLines = (passport, companies, delegates, professions, settings) => {
    const fieldMap = buildPassportBarcodeFieldMap(passport, companies, delegates, professions);
    const selectedKeys = Array.isArray(settings?.includeFields) ? settings.includeFields : [];
    return BARCODE_FIELD_OPTIONS
        .filter(field => selectedKeys.includes(field.key))
        .map(field => {
            const value = barcodeSafeMultilineText(fieldMap[field.key]);
            return value ? `${field.scanLabel || field.label}=${value}` : '';
        })
        .filter(Boolean);
};

const fitBarcodeTextLinesForQr = (lines, fallbackCode = '') => {
    const maxBytes = QR_DIRECT_TEXT_MAX_BYTES;
    const cleanLines = Array.isArray(lines) ? lines.filter(Boolean) : [];
    if (!cleanLines.length) return barcodeSafeText(fallbackCode, 'C-1');

    const fullText = cleanLines.join('\n');
    if (barcodeUtf8ByteLength(fullText) <= maxBytes) return fullText;

    const fitted = [];
    for (const line of cleanLines) {
        const prefix = fitted.length ? `${fitted.join('\n')}\n` : '';
        const remainingBytes = maxBytes - barcodeUtf8ByteLength(prefix);
        if (remainingBytes <= 4) break;
        if (barcodeUtf8ByteLength(line) <= remainingBytes) {
            fitted.push(line);
        } else {
            fitted.push(trimTextToUtf8Bytes(line, remainingBytes));
            break;
        }
    }

    return fitted.filter(Boolean).join('\n') || trimTextToUtf8Bytes(fullText, maxBytes) || barcodeSafeText(fallbackCode, 'C-1');
};

const getBarcodeContentInfo = (passport, companies, delegates, professions, settings) => {
    const code = buildPassportBarcodeCode(passport, companies);
    if (settings.contentMode === 'text') {
        const lines = getBarcodeSelectedFieldLines(passport, companies, delegates, professions, settings);
        const rawContent = lines.length ? lines.join('\n') : code;
        const content = fitBarcodeTextLinesForQr(lines, code);
        return {
            content,
            rawContent,
            byteLength: barcodeUtf8ByteLength(rawContent),
            printedByteLength: barcodeUtf8ByteLength(content),
            maxBytes: QR_DIRECT_TEXT_MAX_BYTES,
            isTooLong: barcodeUtf8ByteLength(rawContent) > QR_DIRECT_TEXT_MAX_BYTES,
            selectedCount: Array.isArray(settings.includeFields) ? settings.includeFields.length : 0
        };
    }

    // مهم: لا نطبع أي IP أو URL داخل الباركود/QR.
    // الهاتف سيقرأ هذا كنص فقط، أما Masar Agent على جهازك فيحوّله داخليًا إلى رابط localhost وينفذ الأمر.
    if (settings.contentMode === 'edit') {
        const content = `EDIT:${code}`;
        return { content, rawContent: content, byteLength: barcodeUtf8ByteLength(content), printedByteLength: barcodeUtf8ByteLength(content), maxBytes: QR_DIRECT_TEXT_MAX_BYTES, isTooLong: false, selectedCount: 0 };
    }
    if (settings.contentMode === 'view') {
        const content = `OPEN:${code}`;
        return { content, rawContent: content, byteLength: barcodeUtf8ByteLength(content), printedByteLength: barcodeUtf8ByteLength(content), maxBytes: QR_DIRECT_TEXT_MAX_BYTES, isTooLong: false, selectedCount: 0 };
    }
    if (settings.contentMode === 'search') {
        const content = `SEARCH:${code}`;
        return { content, rawContent: content, byteLength: barcodeUtf8ByteLength(content), printedByteLength: barcodeUtf8ByteLength(content), maxBytes: QR_DIRECT_TEXT_MAX_BYTES, isTooLong: false, selectedCount: 0 };
    }
    return { content: code, rawContent: code, byteLength: barcodeUtf8ByteLength(code), printedByteLength: barcodeUtf8ByteLength(code), maxBytes: QR_DIRECT_TEXT_MAX_BYTES, isTooLong: false, selectedCount: 0 };
};

const buildBarcodeContent = (passport, companies, delegates, professions, settings) => {
    return getBarcodeContentInfo(passport, companies, delegates, professions, settings).content;
};


// الباركود الخطي لازم يطبع بيانات قصيرة فقط حتى يكون قابلًا للمسح.
// أي URL أو نص عربي أو بيانات كثيرة يجب أن تدخل QR فقط، وليس Barcode 1D.
const getPrintableSymbolData = (codeType, code, content) => {
    const safeCode = barcodeSafeText(code, 'TEST-1').replace(/[^\x20-\x7E]/g, '').trim() || 'TEST-1';
    const safeContent = barcodeSafeMultilineText(content, safeCode);
    const firstContentLine = String(safeContent || '').split(/\r?\n/)[0].trim();
    const isScannerCommand = /^(SEARCH|FIND|OPEN|VIEW|EDIT)\s*[:=|\-\s]/i.test(firstContentLine);
    if (codeType === 'qr') return safeContent || safeCode;
    // Barcode 1D يطبع أوامر المسدس القصيرة فقط بدون URL، أما البيانات المكتوبة الطويلة/العربية فتحتاج QR.
    return isScannerCommand ? firstContentLine.slice(0, 64) : safeCode;
};

// معاينة حقيقية تقريبية للـ Code128 بدل الأعمدة الوهمية التي كانت تظهر ولا تُقرأ بالماسح.
const CODE128_PATTERNS = [
    '212222','222122','222221','121223','121322','131222','122213','122312','132212','221213','221312','231212','112232','122132','122231','113222','123122','123221','223211','221132','221231','213212','223112','312131','311222','321122','321221','312212','322112','322211','212123','212321','232121','111323','131123','131321','112313','132113','132311','211313','231113','231311','112133','112331','132131','113123','113321','133121','313121','211331','231131','213113','213311','213131','311123','311321','331121','312113','312311','332111','314111','221411','431111','111224','111422','121124','121421','141122','141221','112214','112412','122114','122411','142112','142211','241211','221114','413111','241112','134111','111242','121142','121241','114212','124112','124211','411212','421112','421211','212141','214121','412121','111143','111341','131141','114113','114311','411113','411311','113141','114131','311141','411131','211412','211214','211232','2331112'
];

const buildCode128BModules = (value) => {
    const text = barcodeSafeText(value, 'TEST-1').replace(/[^\x20-\x7E]/g, '').slice(0, 48) || 'TEST-1';
    const values = [104]; // Start Code B
    for (let i = 0; i < text.length; i += 1) {
        values.push(Math.max(0, Math.min(95, text.charCodeAt(i) - 32)));
    }
    let checksum = values[0];
    for (let i = 1; i < values.length; i += 1) checksum += values[i] * i;
    values.push(checksum % 103);
    values.push(106); // Stop

    const bars = [];
    let x = 10; // quiet zone
    values.forEach((codeValue) => {
        const pattern = CODE128_PATTERNS[codeValue] || CODE128_PATTERNS[0];
        for (let i = 0; i < pattern.length; i += 1) {
            const width = Number(pattern[i]) || 1;
            if (i % 2 === 0) bars.push({ x, width });
            x += width;
        }
    });
    return { text, bars, totalWidth: x + 10 };
};

const Code128Preview = React.memo(({ value }) => {
    const data = React.useMemo(() => buildCode128BModules(value), [value]);
    return (
        <svg viewBox={`0 0 ${data.totalWidth} 64`} preserveAspectRatio="none" className="w-full h-full block bg-white" role="img" aria-label={`Barcode ${data.text}`}>
            <rect x="0" y="0" width={data.totalWidth} height="64" fill="white" />
            {data.bars.map((bar, index) => <rect key={index} x={bar.x} y="3" width={bar.width} height="58" fill="black" />)}
        </svg>
    );
});


// QR حقيقي وليس شكلًا وهميًا. نفس المصفوفة تُستخدم في المعاينة وفي الصورة المرسلة إلى Masar Print Agent.
const QR_VERSION_TABLE = [
    null,
    { version: 1, size: 21, dataCodewords: 19, ecCodewords: 7, alignment: [] },
    { version: 2, size: 25, dataCodewords: 34, ecCodewords: 10, alignment: [6, 18] },
    { version: 3, size: 29, dataCodewords: 55, ecCodewords: 15, alignment: [6, 22] },
    { version: 4, size: 33, dataCodewords: 80, ecCodewords: 20, alignment: [6, 26] },
    { version: 5, size: 37, dataCodewords: 108, ecCodewords: 26, alignment: [6, 30] }
];

let qrGfTables = null;

const getQrGfTables = () => {
    if (qrGfTables) return qrGfTables;
    const exp = new Array(512).fill(0);
    const log = new Array(256).fill(0);
    let x = 1;
    for (let i = 0; i < 255; i += 1) {
        exp[i] = x;
        log[x] = i;
        x <<= 1;
        if (x & 0x100) x ^= 0x11D;
    }
    for (let i = 255; i < 512; i += 1) exp[i] = exp[i - 255];
    qrGfTables = { exp, log };
    return qrGfTables;
};

const qrGfMul = (a, b) => {
    if (!a || !b) return 0;
    const { exp, log } = getQrGfTables();
    return exp[log[a] + log[b]];
};

const makeQrGeneratorPoly = (degree) => {
    // Reed-Solomon divisor polynomial, بنفس ترتيب QR القياسي.
    let result = new Array(degree).fill(0);
    result[degree - 1] = 1;
    let root = 1;
    for (let i = 0; i < degree; i += 1) {
        for (let j = 0; j < degree; j += 1) {
            result[j] = qrGfMul(result[j], root);
            if (j + 1 < degree) result[j] ^= result[j + 1];
        }
        root = qrGfMul(root, 2);
    }
    return result;
};

const makeQrErrorCorrection = (data, degree) => {
    const divisor = makeQrGeneratorPoly(degree);
    const result = new Array(degree).fill(0);
    data.forEach((byte) => {
        const factor = byte ^ result.shift();
        result.push(0);
        for (let i = 0; i < degree; i += 1) result[i] ^= qrGfMul(divisor[i], factor);
    });
    return result;
};

const appendQrBits = (bits, value, length) => {
    for (let i = length - 1; i >= 0; i -= 1) bits.push((value >>> i) & 1);
};

const encodeQrBytes = (text) => {
    const safe = barcodeSafeText(text, 'C-1');
    if (typeof TextEncoder !== 'undefined') return Array.from(new TextEncoder().encode(safe));
    return Array.from(safe).map(ch => ch.charCodeAt(0) & 0xFF);
};

const pickQrVersion = (byteLength) => {
    for (let version = 1; version < QR_VERSION_TABLE.length; version += 1) {
        const info = QR_VERSION_TABLE[version];
        if ((4 + 8 + byteLength * 8 + 4) <= info.dataCodewords * 8) return info;
    }
    return QR_VERSION_TABLE[QR_VERSION_TABLE.length - 1];
};

const makeQrDataCodewords = (bytes, info) => {
    const maxBytes = Math.max(0, Math.floor((info.dataCodewords * 8 - 4 - 8 - 4) / 8));
    const safeBytes = bytes.slice(0, maxBytes);
    const bits = [];
    appendQrBits(bits, 0b0100, 4); // Byte mode
    appendQrBits(bits, safeBytes.length, 8);
    safeBytes.forEach(byte => appendQrBits(bits, byte, 8));
    const totalBits = info.dataCodewords * 8;
    appendQrBits(bits, 0, Math.min(4, Math.max(0, totalBits - bits.length)));
    while (bits.length % 8 !== 0) bits.push(0);
    const codewords = [];
    for (let i = 0; i < bits.length; i += 8) {
        let value = 0;
        for (let j = 0; j < 8; j += 1) value = (value << 1) | bits[i + j];
        codewords.push(value);
    }
    const pad = [0xEC, 0x11];
    let padIndex = 0;
    while (codewords.length < info.dataCodewords) {
        codewords.push(pad[padIndex % 2]);
        padIndex += 1;
    }
    return codewords;
};

const getQrFormatBits = (errorCorrectionLevelBits = 1, mask = 0) => {
    const data = (errorCorrectionLevelBits << 3) | mask; // L = 01, mask 0
    let rem = data;
    for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ (((rem >>> 9) & 1) ? 0x537 : 0);
    return ((data << 10) | (rem & 0x3FF)) ^ 0x5412;
};

const makeQrMatrix = (text) => {
    try {
        const bytes = encodeQrBytes(text);
        const info = pickQrVersion(bytes.length);
        const size = info.size;
        const modules = Array.from({ length: size }, () => new Array(size).fill(false));
        const reserved = Array.from({ length: size }, () => new Array(size).fill(false));
        const setFunction = (row, col, value) => {
            if (row < 0 || row >= size || col < 0 || col >= size) return;
            modules[row][col] = !!value;
            reserved[row][col] = true;
        };
        const drawFinder = (row, col) => {
            for (let r = -1; r <= 7; r += 1) {
                for (let c = -1; c <= 7; c += 1) {
                    const rr = row + r;
                    const cc = col + c;
                    if (rr < 0 || rr >= size || cc < 0 || cc >= size) continue;
                    const black = r >= 0 && r <= 6 && c >= 0 && c <= 6 && (r === 0 || r === 6 || c === 0 || c === 6 || (r >= 2 && r <= 4 && c >= 2 && c <= 4));
                    setFunction(rr, cc, black);
                }
            }
        };
        drawFinder(0, 0);
        drawFinder(0, size - 7);
        drawFinder(size - 7, 0);

        for (let i = 8; i < size - 8; i += 1) {
            setFunction(6, i, i % 2 === 0);
            setFunction(i, 6, i % 2 === 0);
        }

        const centers = info.alignment || [];
        centers.forEach((rCenter) => centers.forEach((cCenter) => {
            const overlapsFinder = (rCenter === 6 && cCenter === 6) || (rCenter === 6 && cCenter === size - 7) || (rCenter === size - 7 && cCenter === 6);
            if (overlapsFinder) return;
            for (let r = -2; r <= 2; r += 1) {
                for (let c = -2; c <= 2; c += 1) {
                    setFunction(rCenter + r, cCenter + c, Math.max(Math.abs(r), Math.abs(c)) !== 1);
                }
            }
        }));

        // مناطق معلومات الصيغة + الوحدة الداكنة الثابتة
        for (let i = 0; i < 9; i += 1) {
            if (i !== 6) {
                reserved[8][i] = true;
                reserved[i][8] = true;
            }
        }
        for (let i = 0; i < 8; i += 1) {
            reserved[8][size - 1 - i] = true;
            reserved[size - 1 - i][8] = true;
        }
        setFunction(size - 8, 8, true);

        const dataCodewords = makeQrDataCodewords(bytes, info);
        const ec = makeQrErrorCorrection(dataCodewords, info.ecCodewords);
        const allCodewords = dataCodewords.concat(ec);
        const dataBits = [];
        allCodewords.forEach(byte => appendQrBits(dataBits, byte, 8));

        let bitIndex = 0;
        let upward = true;
        for (let right = size - 1; right >= 1; right -= 2) {
            if (right === 6) right -= 1;
            for (let vert = 0; vert < size; vert += 1) {
                const row = upward ? size - 1 - vert : vert;
                for (let j = 0; j < 2; j += 1) {
                    const col = right - j;
                    if (reserved[row][col]) continue;
                    let bit = bitIndex < dataBits.length ? dataBits[bitIndex] : 0;
                    bitIndex += 1;
                    if (((row + col) & 1) === 0) bit ^= 1; // mask 0
                    modules[row][col] = !!bit;
                }
            }
            upward = !upward;
        }

        const formatBits = getQrFormatBits(1, 0);
        const getBit = (i) => ((formatBits >>> i) & 1) !== 0;
        for (let i = 0; i <= 5; i += 1) setFunction(i, 8, getBit(i));
        setFunction(7, 8, getBit(6));
        setFunction(8, 8, getBit(7));
        setFunction(8, 7, getBit(8));
        for (let i = 9; i < 15; i += 1) setFunction(8, 14 - i, getBit(i));
        for (let i = 0; i < 8; i += 1) setFunction(8, size - 1 - i, getBit(i));
        for (let i = 8; i < 15; i += 1) setFunction(size - 15 + i, 8, getBit(i));
        setFunction(size - 8, 8, true);

        return { modules, size, version: info.version, truncated: bytes.length > (dataCodewords.length - 2) };
    } catch (error) {
        console.warn('QR generation failed:', error);
        return null;
    }
};

const makeQrSvgCells = (qr, quietZone = 4) => {
    if (!qr?.modules) return { cells: [], viewSize: 0 };
    const cells = [];
    for (let r = 0; r < qr.size; r += 1) {
        for (let c = 0; c < qr.size; c += 1) {
            if (qr.modules[r][c]) cells.push(<rect key={`${r}-${c}`} x={c + quietZone} y={r + quietZone} width="1" height="1" fill="black" />);
        }
    }
    return { cells, viewSize: qr.size + quietZone * 2 };
};

const buildQrPngDataUrl = (value, modulePixels = 8, quietZone = 4) => {
    if (typeof document === 'undefined') return '';
    const qr = makeQrMatrix(value);
    if (!qr?.modules) return '';
    const safeModulePixels = Math.max(3, Math.min(18, Math.round(Number(modulePixels) || 8)));
    const imageModules = qr.size + quietZone * 2;
    const canvas = document.createElement('canvas');
    canvas.width = imageModules * safeModulePixels;
    canvas.height = imageModules * safeModulePixels;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000000';
    for (let r = 0; r < qr.size; r += 1) {
        for (let c = 0; c < qr.size; c += 1) {
            if (qr.modules[r][c]) {
                ctx.fillRect((c + quietZone) * safeModulePixels, (r + quietZone) * safeModulePixels, safeModulePixels, safeModulePixels);
            }
        }
    }
    return canvas.toDataURL('image/png');
};

const QRVisualPreview = React.memo(({ value }) => {
    const qr = React.useMemo(() => makeQrMatrix(value), [value]);
    const { cells, viewSize } = React.useMemo(() => makeQrSvgCells(qr, 4), [qr]);
    if (!qr?.modules) {
        return <div className="w-full h-full bg-white text-[10px] flex items-center justify-center text-red-600 font-bold">QR</div>;
    }
    return (
        <svg viewBox={`0 0 ${viewSize} ${viewSize}`} className="w-full h-full block bg-white" shapeRendering="crispEdges" role="img" aria-label="QR code preview">
            <rect x="0" y="0" width={viewSize} height={viewSize} fill="white" />
            {cells}
        </svg>
    );
});

const mmToDots = (mm, dpi = 203) => Math.max(1, Math.round((Number(mm) || 0) * (Number(dpi) || 203) / 25.4));
const clampNumber = (value, min, max) => Math.max(min, Math.min(Number(value) || 0, max));

const makeTextBitmap = (text, {
    dpi = 203,
    widthDots = 260,
    heightDots = 32,
    fontSizePt = 10,
    bold = true,
    align = 'center',
    rtl = true
} = {}) => {
    if (typeof document === 'undefined') return null;
    const cleanText = barcodeSafeText(text);
    if (!cleanText) return null;

    const canvas = document.createElement('canvas');
    const scale = 2;
    const safeWidth = Math.max(20, Math.round(Number(widthDots) || 260));
    const safeHeight = Math.max(16, Math.round(Number(heightDots) || 32));
    let fontPx = Math.max(9, Math.round((Number(fontSizePt) || 10) * (Number(dpi) || 203) / 72));

    canvas.width = safeWidth * scale;
    canvas.height = safeHeight * scale;

    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, safeWidth, safeHeight);
    ctx.fillStyle = '#000000';
    ctx.textBaseline = 'middle';
    ctx.direction = rtl ? 'rtl' : 'ltr';

    const padding = 2;
    let fittedText = cleanText.length > 80 ? `${cleanText.slice(0, 79)}…` : cleanText;
    const setFont = () => {
        ctx.font = `${bold ? '700' : '400'} ${fontPx}px Arial, Tahoma, sans-serif`;
    };
    setFont();

    while (ctx.measureText(fittedText).width > safeWidth - (padding * 2) && fontPx > 8) {
        fontPx -= 1;
        setFont();
    }

    if (align === 'right') {
        ctx.textAlign = 'right';
        ctx.fillText(fittedText, safeWidth - padding, safeHeight / 2);
    } else if (align === 'left') {
        ctx.textAlign = 'left';
        ctx.fillText(fittedText, padding, safeHeight / 2);
    } else {
        ctx.textAlign = 'center';
        ctx.fillText(fittedText, safeWidth / 2, safeHeight / 2);
    }

    return {
        dataUrl: canvas.toDataURL('image/png'),
        widthDots: safeWidth,
        heightDots: safeHeight
    };
};

const buildBixolonFunctionJson = (functions, id) => {
    const body = { id, functions: {} };
    functions.forEach((item, index) => {
        body.functions[`func${index + 1}`] = { [item.name]: item.args || [] };
    });
    return JSON.stringify(body);
};

const buildBixolonLabelData = ({ passport, companies, delegates, professions, settings, testMode = false }) => {
    settings = normalizeBarcodeLayoutSettings(settings || {});
    const effectiveCodeType = settings.codeType || 'barcode';
    const id = Date.now() % 100000000;
    const dpi = Number(settings.dpi) || 203;
    const widthDots = mmToDots(settings.labelWidthMm, dpi);
    const heightDots = mmToDots(settings.labelHeightMm, dpi);
    const code = testMode ? 'TEST-1' : buildPassportBarcodeCode(passport, companies);
    const content = testMode ? 'TEST-1' : buildBarcodeContent(passport, companies, delegates, professions, settings);
    const symbolData = getPrintableSymbolData(effectiveCodeType, code, content);
    const name = testMode ? 'اختبار طباعة مسار' : barcodeSafeText(passport?.NameArabic, 'بدون اسم');
    const xMax = Math.max(1, widthDots - 2);
    const yMax = Math.max(1, heightDots - 2);
    const xDots = (mm) => clampNumber(mmToDots(mm, dpi), 0, xMax);
    const yDots = (mm) => clampNumber(mmToDots(mm, dpi), 0, yMax);

    const functions = [
        { name: 'setLabelId', args: [id] },
        { name: 'checkLabelStatus', args: [] },
        { name: 'clearBuffer', args: [] },
        { name: 'setPrintingType', args: ['d'] },
        { name: 'setWidth', args: [widthDots] },
        { name: 'setLength', args: [heightDots, Number(settings.gapDots) || 16, settings.mediaType || 'G', 0] },
        { name: 'setMargin', args: [0, 0] },
        { name: 'setOrientation', args: [settings.orientation || 'T'] },
        { name: 'setSpeed', args: [Number(settings.speed) || 3] },
        { name: 'setDensity', args: [Number(settings.density) || 15] },
        { name: 'setCharacterset', args: [0, 19] }
    ];

    if (settings.showName) {
        const nameBitmap = makeTextBitmap(name, {
            dpi,
            widthDots: mmToDots(settings.nameWidthMm, dpi),
            heightDots: mmToDots(settings.nameHeightMm, dpi),
            fontSizePt: Number(settings.nameFontSize) || 8,
            bold: !!settings.nameBold,
            align: settings.nameAlign || 'center',
            rtl: true
        });
        if (nameBitmap?.dataUrl) {
            functions.push({ name: 'drawBitmap', args: [nameBitmap.dataUrl, xDots(settings.nameXmm), yDots(settings.nameYmm), nameBitmap.widthDots, 0] });
        }
    }

    if (settings.showId) {
        const idBitmap = makeTextBitmap(code, {
            dpi,
            widthDots: mmToDots(settings.idWidthMm, dpi),
            heightDots: mmToDots(settings.idHeightMm, dpi),
            fontSizePt: Number(settings.idFontSize) || 13,
            bold: !!settings.idBold,
            align: settings.idAlign || 'center',
            rtl: false
        });
        if (idBitmap?.dataUrl) {
            functions.push({ name: 'drawBitmap', args: [idBitmap.dataUrl, xDots(settings.idXmm), yDots(settings.idYmm), idBitmap.widthDots, 0] });
        }
    }

    if (settings.showBarcode) {
        if (effectiveCodeType === 'qr') {
            functions.push({
                name: 'drawQRCode',
                args: [symbolData || code, xDots(settings.qrXmm), yDots(settings.qrYmm), 1, 'M', Number(settings.qrSize) || 6, 0]
            });
        } else {
            functions.push({
                name: 'draw1DBarcode',
                args: [symbolData || code, xDots(settings.barcodeXmm), yDots(settings.barcodeYmm), 1, Number(settings.barcodeNarrow) || 2, Number(settings.barcodeWide) || 5, mmToDots(settings.barcodeHeightMm, dpi), 0, 0]
            });
        }
    }

    functions.push({ name: 'printBuffer', args: [] });
    return buildBixolonFunctionJson(functions, id);
};



const makeBoundaryTestBitmap = (settings = {}) => {
    const safeSettings = normalizeBarcodeLayoutSettings(settings || {});
    const dpi = Number(safeSettings.dpi) || 203;
    const widthMm = Number(safeSettings.labelWidthMm) || 38;
    const heightMm = Number(safeSettings.labelHeightMm) || 24;
    const widthDots = Math.max(20, mmToDots(widthMm, dpi));
    const heightDots = Math.max(20, mmToDots(heightMm, dpi));

    if (typeof document === 'undefined') return null;
    const canvas = document.createElement('canvas');
    canvas.width = widthDots;
    canvas.height = heightDots;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const dot = (mm) => mmToDots(mm, dpi);
    const marginDots = Math.max(3, dot(1));
    const innerMarginDots = Math.max(2, dot(3));
    const centerX = Math.round(widthDots / 2);
    const centerY = Math.round(heightDots / 2);
    const cornerSize = Math.max(6, dot(3));

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, widthDots, heightDots);
    ctx.strokeStyle = '#000000';
    ctx.fillStyle = '#000000';
    ctx.lineWidth = Math.max(1, Math.round(dpi / 203));

    // إطار خارجي يوضح بداية ونهاية الاستيكر الفعلية.
    ctx.strokeRect(marginDots, marginDots, Math.max(1, widthDots - (marginDots * 2)), Math.max(1, heightDots - (marginDots * 2)));

    // إطار أمان داخلي 3mm: أي باركود أو QR يفضل أن يبقى داخله.
    ctx.setLineDash([Math.max(3, dot(0.8)), Math.max(3, dot(0.8))]);
    ctx.strokeRect(innerMarginDots, innerMarginDots, Math.max(1, widthDots - (innerMarginDots * 2)), Math.max(1, heightDots - (innerMarginDots * 2)));

    // خط منتصف أفقي ورأسي لاختبار الزحزحة.
    ctx.beginPath();
    ctx.moveTo(centerX, marginDots);
    ctx.lineTo(centerX, heightDots - marginDots);
    ctx.moveTo(marginDots, centerY);
    ctx.lineTo(widthDots - marginDots, centerY);
    ctx.stroke();
    ctx.setLineDash([]);

    // علامات أركان واضحة.
    const drawCorner = (x, y) => {
        ctx.fillRect(x, y, cornerSize, Math.max(2, dot(0.7)));
        ctx.fillRect(x, y, Math.max(2, dot(0.7)), cornerSize);
    };
    drawCorner(marginDots, marginDots);
    drawCorner(widthDots - marginDots - cornerSize, marginDots);
    drawCorner(marginDots, heightDots - marginDots - cornerSize);
    drawCorner(widthDots - marginDots - cornerSize, heightDots - marginDots - cornerSize);

    // علامة مركز صغيرة.
    ctx.fillRect(centerX - dot(2), centerY - Math.max(1, dot(0.25)), dot(4), Math.max(2, dot(0.5)));
    ctx.fillRect(centerX - Math.max(1, dot(0.25)), centerY - dot(2), Math.max(2, dot(0.5)), dot(4));

    // كتابة صغيرة لاختبار الاتجاه والمقاس.
    ctx.font = `${Math.max(10, dot(2.2))}px Arial, Tahoma, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`BOUNDARY ${widthMm}x${heightMm}mm`, centerX, Math.max(dot(4.5), marginDots + dot(2.5)));
    ctx.font = `${Math.max(8, dot(1.8))}px Arial, Tahoma, sans-serif`;
    ctx.fillText('CENTER', centerX, centerY + dot(3.2));

    return {
        dataUrl: canvas.toDataURL('image/png'),
        widthDots,
        heightDots
    };
};

const buildBoundaryTestBixolonLabelData = ({ settings }) => {
    const safeSettings = normalizeBarcodeLayoutSettings(settings || {});
    const id = Date.now() % 100000000;
    const dpi = Number(safeSettings.dpi) || 203;
    const widthDots = mmToDots(safeSettings.labelWidthMm, dpi);
    const heightDots = mmToDots(safeSettings.labelHeightMm, dpi);
    const boundaryBitmap = makeBoundaryTestBitmap(safeSettings);

    const functions = [
        { name: 'setLabelId', args: [id] },
        { name: 'checkLabelStatus', args: [] },
        { name: 'clearBuffer', args: [] },
        { name: 'setPrintingType', args: ['d'] },
        { name: 'setWidth', args: [widthDots] },
        { name: 'setLength', args: [heightDots, Number(safeSettings.gapDots) || 16, safeSettings.mediaType || 'G', 0] },
        { name: 'setMargin', args: [0, 0] },
        { name: 'setOrientation', args: [safeSettings.orientation || 'T'] },
        { name: 'setSpeed', args: [Number(safeSettings.speed) || 3] },
        { name: 'setDensity', args: [Number(safeSettings.density) || 15] },
        { name: 'setCharacterset', args: [0, 19] }
    ];

    if (boundaryBitmap?.dataUrl) {
        functions.push({ name: 'drawBitmap', args: [boundaryBitmap.dataUrl, 0, 0, boundaryBitmap.widthDots, 0] });
    }

    functions.push({ name: 'printBuffer', args: [] });
    return buildBixolonFunctionJson(functions, id);
};
const postToBixolonWebSdk = async (logicalName, port, labelJson) => {
    const safeName = encodeURIComponent(logicalName || 'Printer1');
    const base = `http://127.0.0.1:${Number(port) || 18080}/WebPrintSDK/`;
    const urls = [`${base}${safeName}`, `${base}${safeName}.bxl`];
    let lastError = null;

    for (const url of urls) {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
                body: labelJson
            });
            const text = await response.text().catch(() => '');
            if (response.ok && !/error/i.test(text)) {
                return { success: true, responseText: text };
            }
            lastError = text || `HTTP ${response.status}`;
        } catch (error) {
            lastError = error.message;
            try {
                await fetch(url, {
                    method: 'POST',
                    mode: 'no-cors',
                    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
                    body: labelJson
                });
                return { success: true, responseText: 'تم إرسال الطلب للطابعة بدون قراءة نتيجة التنفيذ' };
            } catch (silentError) {
                lastError = silentError.message || lastError;
            }
        }
    }

    throw new Error(lastError || 'تعذر الاتصال بخدمة Web Print SDK المحلية');
};


// رابط API ديناميكي للطباعة والطرق المحفوظة:
// يمنع مشكلة أن النسخة تكون مبنية على IP شبكة قديمة أو localhost.
// عند فتح النظام من أي شبكة، يتم توجيه طلبات الطباعة إلى نفس جهاز السيرفر الذي فتحت منه الصفحة.
const getRuntimeApiBaseUrl = () => {
    const configuredBase = String(config.API_BASE_URL || '').replace(/\/+$/, '');

    if (typeof window === 'undefined' || !window.location) {
        return configuredBase;
    }

    const currentHost = window.location.hostname;
    const currentHostApiBase = window.location.origin; // السيرفر على نفس الموقع

    if (!configuredBase) {
        return currentHostApiBase;
    }

    try {
        const configuredUrl = new URL(configuredBase, window.location.origin);
        const localHosts = ['localhost', '127.0.0.1', '0.0.0.0'];
        const isBrowserOnRealNetwork = currentHost && !localHosts.includes(currentHost);
        const isConfiguredLocalhost = localHosts.includes(configuredUrl.hostname);
        const privateIpRegex = /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.)/;

        if (isBrowserOnRealNetwork && isConfiguredLocalhost) {
            return currentHostApiBase;
        }

        if (
            isBrowserOnRealNetwork &&
            privateIpRegex.test(configuredUrl.hostname) &&
            privateIpRegex.test(currentHost) &&
            configuredUrl.hostname !== currentHost
        ) {
            return currentHostApiBase;
        }

        return configuredBase;
    } catch (error) {
        return configuredBase || currentHostApiBase;
    }
};

const buildBarcodeApiUrl = (path) => `${getRuntimeApiBaseUrl()}${path}`;


const postToServerPrintBridge = async (logicalName, port, labelJson) => {
    const response = await fetch(buildBarcodeApiUrl('/api/passports/barcode/print-server'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            logicalName: logicalName || 'Printer1',
            sdkPort: Number(port) || 18080,
            labelJson
        })
    });

    const text = await response.text().catch(() => '');
    let payload = null;
    try {
        payload = text ? JSON.parse(text) : null;
    } catch {
        payload = null;
    }

    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || text || `HTTP ${response.status}`);
    }

    return payload || { success: true, responseText: text };
};

const buildMasarAgentLabelData = ({ passport, companies, delegates, professions, settings, testMode = false }) => {
    const safeSettings = normalizeBarcodeLayoutSettings(settings || {});
    const effectiveCodeType = safeSettings.codeType || 'barcode';
    const code = testMode ? 'TEST-1' : buildPassportBarcodeCode(passport, companies);
    const content = testMode ? 'TEST-1' : buildBarcodeContent(passport, companies, delegates, professions, safeSettings);
    const symbolData = getPrintableSymbolData(effectiveCodeType, code, content);
    const name = testMode ? 'اختبار طباعة مسار' : barcodeSafeText(passport?.NameArabic, 'بدون اسم');
    const dpi = Number(safeSettings.dpi) || 203;
    const qrModuleDots = Math.max(4, Math.min(12, Math.round(Number(safeSettings.qrSize) || 7)));
    const qrVisualSizeMm = Number(Math.max(16, qrModuleDots * 2.8).toFixed(1));
    const nativeLabelJson = buildBixolonLabelData({
        passport,
        companies,
        delegates,
        professions,
        settings: {
            ...safeSettings,
            qrSize: qrModuleDots
        },
        testMode
    });
    const qrBitmapDataUrl = effectiveCodeType === 'qr'
        ? buildQrPngDataUrl(symbolData || code, Math.max(6, qrModuleDots), 4)
        : '';

    return {
        version: 2,
        type: 'passport_barcode_label',
        printMode: 'native-first',
        code,
        content: symbolData || code,
        symbolData: symbolData || code,
        fullContent: content || code,
        barcodeData: code,
        qrData: symbolData || code,
        qrBitmapDataUrl,
        qrSizeMm: qrVisualSizeMm,
        qrModuleDots,
        barcodeWidthMm: Number(safeSettings.barcodePreviewWidthMm) || 33,
        // مهم: الـ Agent لازم يستخدم هذا الـ nativeLabelJson أولًا حتى تطلع الطباعة مثل الضبط الحقيقي،
        // بدل ما يرسم QR/Barcode قديم أو مصغر من عنده.
        nativeLabelJson,
        name,
        label: {
            widthMm: Number(safeSettings.labelWidthMm) || 38,
            heightMm: Number(safeSettings.labelHeightMm) || 24,
            dpi
        },
        settings: {
            showName: !!safeSettings.showName,
            showId: !!safeSettings.showId,
            showBarcode: !!safeSettings.showBarcode,
            codeType: effectiveCodeType || 'barcode',
            nameXmm: Number(safeSettings.nameXmm) || 0,
            nameYmm: Number(safeSettings.nameYmm) || 0,
            nameWidthMm: Number(safeSettings.nameWidthMm) || 37,
            nameHeightMm: Number(safeSettings.nameHeightMm) || 4.5,
            nameFontSize: Number(safeSettings.nameFontSize) || 8,
            nameBold: !!safeSettings.nameBold,
            nameAlign: safeSettings.nameAlign || 'center',
            idXmm: Number(safeSettings.idXmm) || 0,
            idYmm: Number(safeSettings.idYmm) || 5,
            idWidthMm: Number(safeSettings.idWidthMm) || 37,
            idHeightMm: Number(safeSettings.idHeightMm) || 5,
            idFontSize: Number(safeSettings.idFontSize) || 13,
            idBold: !!safeSettings.idBold,
            idAlign: safeSettings.idAlign || 'center',
            barcodeXmm: Number(safeSettings.barcodeXmm) || 2,
            barcodeYmm: Number(safeSettings.barcodeYmm) || 10,
            barcodeHeightMm: Number(safeSettings.barcodeHeightMm) || 10,
            barcodeNarrow: Number(safeSettings.barcodeNarrow) || 2,
            barcodeWide: Number(safeSettings.barcodeWide) || 5,
            qrXmm: Number(safeSettings.qrXmm) || 13,
            qrYmm: Number(safeSettings.qrYmm) || 7,
            qrSize: Number(safeSettings.qrSize) || 6
        },
        passport: testMode ? null : {
            passportId: passport?.PassportID || null, // داخلي فقط للعلاقات وليس للطباعة أو البحث
            officeSerial: getPassportFileNumber(passport) || '',
            passportNumber: passport?.PassportNumber || '',
            company: barcodeFindName(companies, 'CompanyID', 'CompanyName', passport?.CompanyID),
            delegate: barcodeFindName(delegates, 'DelegateID', 'DelegateName', passport?.DelegateID)
        }
    };
};



const buildMasarAgentBoundaryTestData = (settings = {}) => {
    const safeSettings = normalizeBarcodeLayoutSettings(settings || {});
    const dpi = Number(safeSettings.dpi) || 203;
    const labelWidthMm = Number(safeSettings.labelWidthMm) || 38;
    const labelHeightMm = Number(safeSettings.labelHeightMm) || 24;
    const nativeLabelJson = buildBoundaryTestBixolonLabelData({ settings: safeSettings });
    const boundaryBitmap = makeBoundaryTestBitmap(safeSettings);

    return {
        version: 4,
        type: 'label_boundary_test',
        printMode: 'boundary-test',
        code: `BOUNDARY-${labelWidthMm}x${labelHeightMm}`,
        content: `اختبار حدود الاستيكر ${labelWidthMm}x${labelHeightMm}mm`,
        name: 'اختبار حدود الاستيكر',
        nativeLabelJson,
        labelBitmapDataUrl: boundaryBitmap?.dataUrl || '',
        boundaryBitmapDataUrl: boundaryBitmap?.dataUrl || '',
        labelBitmapWidthDots: boundaryBitmap?.widthDots || 0,
        labelBitmapHeightDots: boundaryBitmap?.heightDots || 0,
        label: {
            widthMm: labelWidthMm,
            heightMm: labelHeightMm,
            dpi
        },
        settings: {
            // مهم جدًا: لو كان هناك Agent قديم مازال يعمل، لا تجعله يطبع باركود عادي باسم BOUNDARY.
            showName: false,
            showId: false,
            showBarcode: false,
            density: Number(safeSettings.density) || 15,
            speed: Number(safeSettings.speed) || 3,
            gapDots: Number(safeSettings.gapDots) || 16,
            mediaType: safeSettings.mediaType || 'G',
            orientation: safeSettings.orientation || 'T',
            printCopies: 1,
            boundaryMarginMm: 1,
            safeMarginMm: 3
        }
    };
};
const getBarcodeApiCandidateBases = () => {
    const bases = [];
    const addBase = (value) => {
        const clean = String(value || '').replace(/\/+$/, '');
        if (clean && !bases.includes(clean)) bases.push(clean);
    };

    addBase(getRuntimeApiBaseUrl());

    if (typeof window !== 'undefined' && window.location?.hostname) {
        addBase(window.location.origin);
    }

    addBase(config.PASSPORT_API_URL);
    addBase(config.API_BASE_URL);

    return bases;
};

const fetchMasarPrintAgents = async () => {
    const errors = [];
    for (const base of getBarcodeApiCandidateBases()) {
        try {
            const response = await fetch(`${base}/api/passports/barcode/agents`, { cache: 'no-store' });
            const payload = await response.json().catch(() => null);
            if (!response.ok || payload?.success === false) {
                throw new Error(payload?.error || `HTTP ${response.status}`);
            }
            return dedupeMasarPrintAgents(Array.isArray(payload?.agents) ? payload.agents : []);
        } catch (error) {
            errors.push(`${base}: ${error.message}`);
        }
    }
    throw new Error(errors.join(' | ') || 'تعذر تحميل أجهزة الطباعة');
};

const isMasarAgentOnline = (agent) => {
    if (!agent) return false;
    if (String(agent.status || '').toLowerCase() === 'offline') return false;
    if (agent.isOnline === false) return false;
    return true;
};

const normalizeMasarPrinterName = (printer) => String(printer?.name || printer?.printerName || printer || '').trim();

const normalizeMasarAgentPrinters = (printers) => Array.isArray(printers)
    ? printers.map(printer => ({
        name: normalizeMasarPrinterName(printer),
        isDefault: typeof printer === 'object' ? !!printer?.isDefault : false
    })).filter(printer => printer.name)
    : [];

const getMasarAgentDisplayKey = (agent = {}) => {
    const name = String(agent.agentName || agent.computerName || agent.agentId || '').trim().toLowerCase();
    const computer = String(agent.computerName || '').trim().toLowerCase();
    const ip = String(agent.ipAddress || '').replace(/^::ffff:/, '').trim().toLowerCase();
    // لو نفس البرنامج اتسجل بأكثر من AgentID، غالبًا الاسم والكمبيوتر أو IP هيبقوا نفسهم.
    return `${name}|${computer || ip}`;
};

const dedupeMasarPrintAgents = (agents) => {
    const mapped = Array.isArray(agents) ? agents.map(agent => ({
        ...agent,
        printers: normalizeMasarAgentPrinters(agent?.printers)
    })).filter(agent => agent && agent.agentId) : [];

    const byKey = new Map();
    mapped.forEach(agent => {
        const key = getMasarAgentDisplayKey(agent) || String(agent.agentId);
        const current = byKey.get(key);
        if (!current) {
            byKey.set(key, agent);
            return;
        }
        const currentScore = (isMasarAgentOnline(current) ? 10000000000000 : 0) + (new Date(current.lastSeenAt || current.updatedAt || 0).getTime() || 0) + ((current.printers || []).length * 1000);
        const nextScore = (isMasarAgentOnline(agent) ? 10000000000000 : 0) + (new Date(agent.lastSeenAt || agent.updatedAt || 0).getTime() || 0) + ((agent.printers || []).length * 1000);
        if (nextScore >= currentScore) byKey.set(key, agent);
    });

    return Array.from(byKey.values()).sort((a, b) => {
        if (isMasarAgentOnline(a) !== isMasarAgentOnline(b)) return isMasarAgentOnline(a) ? -1 : 1;
        return (new Date(b.lastSeenAt || b.updatedAt || 0).getTime() || 0) - (new Date(a.lastSeenAt || a.updatedAt || 0).getTime() || 0);
    });
};

const postToMasarPrintAgent = async (settings, payload, copies = 1, batchId = '') => {
    const safeSettings = mergeBarcodeSettings(settings || {});
    const agentId = barcodeSafeText(safeSettings.printAgentId);
    if (!agentId) {
        throw new Error('اختار جهاز Masar Print Agent من إعدادات الطباعة أولًا');
    }

    // تحقق سريع قبل إنشاء Job حتى لا يتم إرسال الأمر لجهاز قديم Offline محفوظ سابقًا.
    try {
        const agents = await fetchMasarPrintAgents();
        const selectedAgent = agents.find(agent => String(agent?.agentId || '') === String(agentId));
        if (!selectedAgent) {
            throw new Error('جهاز الطباعة المختار غير موجود في قائمة الأجهزة. اضغط تحديث أجهزة الطباعة واختر الجهاز المتصل.');
        }
        if (!isMasarAgentOnline(selectedAgent)) {
            throw new Error(`جهاز الطباعة المختار غير متصل الآن: ${selectedAgent.agentName || selectedAgent.agentId}. افتح Masar Print Agent واضغط Save & Start ثم حدّث الأجهزة.`);
        }
    } catch (error) {
        throw new Error(error?.message || 'تعذر التحقق من اتصال جهاز الطباعة');
    }

    const response = await fetch(buildBarcodeApiUrl('/api/passports/barcode/print-agent'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            agentId,
            printerName: barcodeSafeText(safeSettings.printAgentPrinterName),
            copies: Math.max(1, Number(copies) || 1),
            batchId: barcodeSafeText(batchId),
            payload
        })
    });

    const text = await response.text().catch(() => '');
    let result = null;
    try { result = text ? JSON.parse(text) : null; } catch { result = null; }
    if (!response.ok || result?.success === false) {
        throw new Error(result?.error || text || `HTTP ${response.status}`);
    }
    return result || { success: true };
};

const postLabelToSelectedPrintEngine = async (settings, labelJson, agentPayload = null, batchId = '') => {
    const safeSettings = mergeBarcodeSettings(settings || {});
    if (safeSettings.printEngine === 'agent') {
        return postToMasarPrintAgent(safeSettings, agentPayload || { labelJson, note: 'fallback_label_json' }, 1, batchId);
    }
    if (safeSettings.printEngine === 'server-bridge') {
        return postToServerPrintBridge(safeSettings.logicalName, safeSettings.sdkPort, labelJson);
    }
    return postToBixolonWebSdk(safeSettings.logicalName, safeSettings.sdkPort, labelJson);
};

const getBarcodePrintFailureMessage = (settings, error) => {
    const safeSettings = mergeBarcodeSettings(settings || {});
    const mainHelp = safeSettings.printEngine === 'agent'
        ? `تأكد من:
1- تشغيل Masar Print Agent على الجهاز المتصل بالطابعة USB.
2- أن الجهاز ظاهر Online داخل ضبط الباركود.
3- أن تعريف الطابعة مثبت على Windows وأن اسم الطابعة المختار صحيح.
4- أن جهاز الطابعة والسيرفر على نفس الشبكة.`
        : safeSettings.printEngine === 'server-bridge'
            ? `تأكد من:
1- أن جهاز السيرفر مفتوح والنظام الخلفي يعمل.
2- أن Web Print SDK أو أداة الطابعة شغالة على جهاز السيرفر فقط.
3- أن الطابعة متصلة بجهاز السيرفر وأن Logical Name هو ${safeSettings.logicalName || 'Printer1'}.
4- أن العينة الرسمية لطابعة الليبل تطبع من جهاز السيرفر.`
            : `تأكد من:
1- تشغيل Web Print SDK أو أداة الطباعة الرسمية على نفس الجهاز الذي يطبع.
2- أن Logical Name هو ${safeSettings.logicalName || 'Printer1'}.
3- أن العينة الرسمية لطابعة الليبل تطبع من نفس الجهاز.`;

    const errorText = error?.message || error || 'غير معروف';
    const networkHint = safeSettings.printEngine === 'server-bridge' && /Failed to fetch|NetworkError|Load failed/i.test(String(errorText))
        ? `

ملاحظة مهمة:
الخطأ Failed to fetch معناه أن هذا الجهاز لا يصل إلى API جهاز السيرفر.
جرّب من نفس الجهاز فتح هذا الرابط:
${buildBarcodeApiUrl('/api/passports/barcode/print-server/health')}
إذا لم يفتح، فالمشكلة شبكة أو Firewall أو IP السيرفر وليس إعدادات الليبل.`
        : '';

    return `فشل الطباعة المباشرة

${mainHelp}${networkHint}

الخطأ: ${errorText}`;
};

const getLocalBarcodeAutoPrintSettings = (fallback = {}) => {
    const safeFallback = {
        autoPrintOnCreate: !!fallback.autoPrintOnCreate,
        autoPrintProfileId: fallback.autoPrintProfileId || ''
    };

    try {
        const saved = localStorage.getItem(BARCODE_AUTO_PRINT_STORAGE_KEY);
        if (!saved) return safeFallback;
        const parsed = JSON.parse(saved);
        return {
            autoPrintOnCreate: !!parsed?.autoPrintOnCreate,
            autoPrintProfileId: parsed?.autoPrintProfileId || ''
        };
    } catch {
        return safeFallback;
    }
};

const saveLocalBarcodeAutoPrintSettings = (settings = {}) => {
    const safe = {
        autoPrintOnCreate: !!settings.autoPrintOnCreate,
        autoPrintProfileId: settings.autoPrintProfileId || ''
    };

    try {
        localStorage.setItem(BARCODE_AUTO_PRINT_STORAGE_KEY, JSON.stringify(safe));
    } catch (error) {
        console.warn('تعذر حفظ تفعيل الطباعة التلقائية محلياً:', error);
    }

    return safe;
};

const stripLocalOnlyBarcodeSettings = (settings = {}) => {
    const { autoPrintOnCreate, autoPrintProfileId, ...rest } = settings || {};
    return rest;
};

const getStoredBarcodeSettings = () => {
    try {
        const saved = localStorage.getItem(BARCODE_STORAGE_KEY);
        const baseSettings = saved ? mergeBarcodeSettings(JSON.parse(saved)) : mergeBarcodeSettings();
        const localAutoSettings = getLocalBarcodeAutoPrintSettings(baseSettings);
        return mergeBarcodeSettings({
            ...baseSettings,
            ...localAutoSettings
        });
    } catch {
        return mergeBarcodeSettings({
            ...mergeBarcodeSettings(),
            ...getLocalBarcodeAutoPrintSettings()
        });
    }
};

const saveStoredBarcodeSettings = (settings) => {
    const next = mergeBarcodeSettings(settings);
    saveLocalBarcodeAutoPrintSettings(next);
    try {
        localStorage.setItem(BARCODE_STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
        console.warn('تعذر حفظ إعدادات الباركود محلياً:', error);
    }
    return next;
};

const getBarcodeDefaultSettingsSnapshot = (settings = {}) => {
    const { savedProfiles, ...snapshot } = mergeBarcodeSettings(settings || {});
    // الطباعة التلقائية اختيار محلي لكل جهاز/متصفح فقط، ولا تُحفظ في قاعدة البيانات.
    return stripLocalOnlyBarcodeSettings(snapshot);
};

const normalizeBarcodeProfileFromApi = (profile) => {
    if (!profile) return null;
    const settings = profile.settings || profile.Settings || profile.SettingsJson || {};
    return {
        id: profile.id || profile.ProfileID,
        name: profile.name || profile.ProfileName,
        settings,
        updatedAt: profile.updatedAt || profile.UpdatedAt || profile.updated_at || new Date().toISOString()
    };
};

const fetchBarcodePrintConfigFromServer = async () => {
    const response = await fetch(buildBarcodeApiUrl('/api/passports/barcode/print-config'));
    const text = await response.text().catch(() => '');
    let payload = null;
    try {
        payload = text ? JSON.parse(text) : null;
    } catch {
        payload = null;
    }

    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || text || `HTTP ${response.status}`);
    }

    return {
        settings: payload?.settings || null,
        profiles: normalizeBarcodeProfiles((payload?.profiles || []).map(normalizeBarcodeProfileFromApi))
    };
};

const saveBarcodeDefaultSettingsToServer = async (settings) => {
    const response = await fetch(buildBarcodeApiUrl('/api/passports/barcode/print-settings'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: getBarcodeDefaultSettingsSnapshot(settings) })
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || `HTTP ${response.status}`);
    }
    return payload?.settings || getBarcodeDefaultSettingsSnapshot(settings);
};

const saveBarcodePrintProfileToServer = async (profile) => {
    const safeProfile = normalizeBarcodeProfileFromApi(profile);
    const response = await fetch(buildBarcodeApiUrl('/api/passports/barcode/print-profiles'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            id: safeProfile.id,
            name: safeProfile.name,
            settings: safeProfile.settings
        })
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || `HTTP ${response.status}`);
    }
    return normalizeBarcodeProfileFromApi(payload?.profile) || safeProfile;
};

const deleteBarcodePrintProfileFromServer = async (profileId) => {
    const response = await fetch(buildBarcodeApiUrl(`/api/passports/barcode/print-profiles/${encodeURIComponent(profileId)}`), {
        method: 'DELETE'
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || `HTTP ${response.status}`);
    }
    return true;
};

export const getSyncedBarcodeSettings = async () => {
    const localSettings = getStoredBarcodeSettings();
    try {
        const serverConfig = await fetchBarcodePrintConfigFromServer();
        const serverProfiles = normalizeBarcodeProfiles(serverConfig.profiles);
        const localProfiles = normalizeBarcodeProfiles(localSettings.savedProfiles);
        let finalProfiles = serverProfiles;

        // ترحيل الطرق القديمة المحفوظة في المتصفح إلى قاعدة البيانات مرة واحدة.
        if (serverProfiles.length === 0 && localProfiles.length > 0 && !localStorage.getItem('masar_barcode_profiles_migrated_to_db')) {
            const migratedProfiles = [];
            for (const profile of localProfiles) {
                try {
                    migratedProfiles.push(await saveBarcodePrintProfileToServer(profile));
                } catch (error) {
                    console.warn('تعذر ترحيل طريقة طباعة محفوظة إلى قاعدة البيانات:', error);
                }
            }
            if (migratedProfiles.length > 0) {
                finalProfiles = normalizeBarcodeProfiles(migratedProfiles);
                localStorage.setItem('masar_barcode_profiles_migrated_to_db', '1');
            }
        }

        const localAutoSettings = getLocalBarcodeAutoPrintSettings(localSettings);
        const sharedServerSettings = stripLocalOnlyBarcodeSettings(serverConfig.settings || {});
        const next = mergeBarcodeSettings({
            ...localSettings,
            ...sharedServerSettings,
            ...localAutoSettings,
            savedProfiles: finalProfiles
        });
        return saveStoredBarcodeSettings(next);
    } catch (error) {
        console.warn('تعذر تحميل طرق الطباعة من قاعدة البيانات، سيتم استخدام النسخة المحلية:', error);
        return localSettings;
    }
};

export const getBarcodeProfileSettingsById = (baseSettings, profileId) => {
    const settings = mergeBarcodeSettings(baseSettings || getStoredBarcodeSettings());
    const savedProfiles = normalizeBarcodeProfiles(settings.savedProfiles);
    const profile = savedProfiles.find(item => item.id === profileId);
    if (!profile) return settings;

    const profileSettings = profile.settings || {};

    // مهم جدًا:
    // طريقة الطباعة المحفوظة يجب أن تحمل معها طريقة الطباعة والطابعة والـ Agent.
    // كان الكود القديم يستبدل اتصال الطريقة المحفوظة بالإعداد العام الحالي،
    // وده سبب خطأ "اختار جهاز Masar Print Agent" عند الطباعة من زر الاختيار
    // رغم أن اختبار الطباعة وحدود الاستيكر يعملان من صفحة الإعدادات.
    // المنطق الجديد: استخدم اتصال الطريقة المحفوظة أولًا، ولو الطريقة قديمة
    // ولا تحتوي على Agent/Printer نستخدم الاتصال الحالي كـ fallback فقط.
    const fallbackPrintConnection = {
        printEngine: settings.printEngine || DEFAULT_BARCODE_SETTINGS.printEngine,
        sdkPort: settings.sdkPort || DEFAULT_BARCODE_SETTINGS.sdkPort,
        logicalName: settings.logicalName || DEFAULT_BARCODE_SETTINGS.logicalName,
        printAgentId: settings.printAgentId || DEFAULT_BARCODE_SETTINGS.printAgentId,
        printAgentPrinterName: settings.printAgentPrinterName || DEFAULT_BARCODE_SETTINGS.printAgentPrinterName
    };

    const profilePrintConnection = {
        printEngine: profileSettings.printEngine || fallbackPrintConnection.printEngine,
        sdkPort: profileSettings.sdkPort || fallbackPrintConnection.sdkPort,
        logicalName: profileSettings.logicalName || fallbackPrintConnection.logicalName,
        printAgentId: profileSettings.printAgentId || fallbackPrintConnection.printAgentId,
        printAgentPrinterName: profileSettings.printAgentPrinterName || fallbackPrintConnection.printAgentPrinterName
    };

    return mergeBarcodeSettings({
        ...settings,
        ...profileSettings,
        ...profilePrintConnection,
        savedProfiles,
        activeProfileId: profile.id,
        profileName: profile.name
    });
};

const getAutoBarcodePrintSettings = () => {
    const stored = getStoredBarcodeSettings();
    if (!stored.autoPrintOnCreate) return null;
    const profileId = stored.autoPrintProfileId || stored.activeProfileId;
    return getBarcodeProfileSettingsById(stored, profileId);
};

export const printPassportBarcodeWithSettings = async ({
    passport,
    companies,
    delegates,
    professions,
    settings,
    testMode = false,
    batchId = '',
    shouldCancel = null
}) => {
    const safeSettings = mergeBarcodeSettings(settings || getStoredBarcodeSettings());
    const copies = Math.max(1, Number(safeSettings.printCopies) || 1);
    const labelJson = buildBixolonLabelData({ passport, companies, delegates, professions, settings: safeSettings, testMode });
    const agentPayload = buildMasarAgentLabelData({ passport, companies, delegates, professions, settings: safeSettings, testMode });
    let sentCopies = 0;

    for (let i = 0; i < copies; i += 1) {
        // بعد طباعة النسخة الجارية لا نرسل نسخة إضافية عند طلب إيقاف الدفعة.
        if (i > 0 && typeof shouldCancel === 'function' && shouldCancel()) break;
        await postLabelToSelectedPrintEngine(safeSettings, labelJson, agentPayload, batchId);
        sentCopies += 1;
    }
    return { success: true, sentCopies };
};

const buildNewPassportAutoPrintPayload = (passportData = {}, saveResult = {}) => {
    const officeSerial = saveResult?.officeSerial ?? saveResult?.OfficeSerial ?? '';
    return {
        ...passportData,
        PassportID: saveResult?.passportId || saveResult?.id || saveResult?.PassportID || passportData?.PassportID || null,
        OfficeSerial: officeSerial,
        SerialNumber: officeSerial === '' || officeSerial === null || officeSerial === undefined ? '' : String(officeSerial)
    };
};

const autoPrintNewPassportIfEnabled = async ({ passport, companies, delegates, professions, showAlert, sourceLabel = 'جواز جديد' }) => {
    const autoSettings = getAutoBarcodePrintSettings();
    if (!autoSettings) return false;

    const officeSerial = String(passport?.OfficeSerial ?? '').trim();
    if (!officeSerial) {
        throw new Error('لم يصل رقم الملف الظاهر OfficeSerial، لذلك تم إيقاف الطباعة حتى لا يُطبع باركود ناقص أو رقم داخلي');
    }

    const printablePassport = {
        ...passport,
        OfficeSerial: officeSerial,
        SerialNumber: officeSerial
    };
    await printPassportBarcodeWithSettings({ passport: printablePassport, companies, delegates, professions, settings: autoSettings });
    showAlert?.(`تمت الطباعة التلقائية للباركود: ${sourceLabel}`, 'success');
    return true;
};

const QuickSavedPrintProfilesButton = React.memo(({
    passport,
    companies = [],
    delegates = [],
    professions = [],
    showAlert,
    iconOnly = false,
    buttonClassName = '',
    label = 'طباعة بطريقة محفوظة',
    onAfterPrint
}) => {
    const [open, setOpen] = React.useState(false);
    const [printingId, setPrintingId] = React.useState('');
    const [settings, setSettings] = React.useState(() => getStoredBarcodeSettings());
    const [loadingProfiles, setLoadingProfiles] = React.useState(false);
    const buttonRef = React.useRef(null);
    const [menuPosition, setMenuPosition] = React.useState({ top: 0, left: 0, width: 288 });

    const refreshSettings = async () => {
        setLoadingProfiles(true);
        try {
            const synced = await getSyncedBarcodeSettings();
            setSettings(synced);
            return synced;
        } finally {
            setLoadingProfiles(false);
        }
    };
    const profiles = normalizeBarcodeProfiles(settings.savedProfiles);

    const printWithProfile = async (profile) => {
        setPrintingId(profile.id);
        let effectiveSettings = settings;
        try {
            const synced = await getSyncedBarcodeSettings();
            setSettings(synced);
            effectiveSettings = getBarcodeProfileSettingsById(synced, profile.id);
            await printPassportBarcodeWithSettings({ passport, companies, delegates, professions, settings: effectiveSettings });
            showAlert?.(`تم إرسال الطباعة بطريقة: ${profile.name}`, 'success');
            setOpen(false);
            onAfterPrint?.();
        } catch (error) {
            console.error('Quick saved profile print error:', error);
            showAlert?.(`فشل الطباعة: ${error.message}`, 'error');
            alert(getBarcodePrintFailureMessage(effectiveSettings, error));
        } finally {
            setPrintingId('');
        }
    };

    const handleButtonClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const rect = buttonRef.current?.getBoundingClientRect?.();
        const width = iconOnly ? 288 : Math.min(360, Math.max(280, rect?.width || 280));
        const left = rect ? Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)) : 8;
        const top = rect ? Math.min(rect.bottom + 8, window.innerHeight - 80) : 80;
        setMenuPosition({ top, left, width });
        setOpen(prev => !prev);
        refreshSettings();
    };

    const buttonContent = iconOnly ? (
        <Barcode size={18} />
    ) : (
        <>
            <span>{label}</span>
            <Barcode size={18} className="text-green-700" />
        </>
    );

    const desktopButtonClass = iconOnly
        ? 'text-green-700 hover:text-green-900 p-1.5 hover:bg-green-50 rounded-full transition duration-200'
        : buttonClassName;

    return (
        <div className={iconOnly ? 'inline-flex' : 'block'} onClick={(e) => e.stopPropagation()}>
            <button
                ref={buttonRef}
                type="button"
                onClick={handleButtonClick}
                className={desktopButtonClass}
                title="طرق الطباعة المحفوظة"
            >
                {buttonContent}
            </button>

            {open && (
                <BodyPortal>
                    <div className="fixed inset-0 z-[10055]" onClick={() => setOpen(false)}></div>
                    <div
                        className="fixed z-[10060] bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden"
                        style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px`, width: `${menuPosition.width}px` }}
                        dir="rtl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-green-700 text-white px-3 py-2 flex items-center justify-between gap-2">
                            <div className="text-sm font-black">طرق الطباعة المحفوظة</div>
                            <button type="button" onClick={() => setOpen(false)} className="p-1 hover:bg-white/20 rounded-lg">
                                <X size={16} />
                            </button>
                        </div>
                        <div className="p-2 max-h-64 overflow-auto space-y-1">
                            {loadingProfiles && (
                                <div className="bg-blue-50 border border-blue-100 text-blue-800 rounded-xl p-2 text-xs font-bold text-center">
                                    جاري تحميل طرق الطباعة من قاعدة البيانات...
                                </div>
                            )}
                            {profiles.length > 0 ? profiles.map(profile => (
                                <button
                                    key={profile.id}
                                    type="button"
                                    onClick={() => printWithProfile(profile)}
                                    disabled={!!printingId}
                                    className="w-full text-right flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl hover:bg-green-50 border border-transparent hover:border-green-100 disabled:opacity-60"
                                >
                                    <span className="min-w-0">
                                        <span className="block font-bold text-gray-900 truncate">{profile.name}</span>
                                        <span className="block text-[11px] text-gray-500">اختيارها يطبع مباشرة</span>
                                    </span>
                                    <span className="shrink-0 bg-green-600 text-white px-2.5 py-1 rounded-lg text-xs font-bold">
                                        {printingId === profile.id ? 'جاري...' : 'طباعة'}
                                    </span>
                                </button>
                            )) : (
                                <div className="bg-yellow-50 border border-yellow-100 text-yellow-800 rounded-xl p-3 text-sm leading-6">
                                    لا توجد طرق محفوظة. افتح زر الباركود بجوار إعدادات التحذيرات، ثم اضبط الملصق واضغط "حفظ كطريقة".
                                </div>
                            )}
                        </div>
                    </div>
                </BodyPortal>
            )}
        </div>
    );
});

const BarcodeSavedProfilesModal = ({ isOpen, onClose, passport, companies, delegates, professions, showAlert, onOpenSettings }) => {
    const [settings, setSettings] = React.useState(() => getStoredBarcodeSettings());
    const [printingId, setPrintingId] = React.useState('');
    const [loadingProfiles, setLoadingProfiles] = React.useState(false);

    React.useEffect(() => {
        if (!isOpen) return;
        let mounted = true;
        setLoadingProfiles(true);
        getSyncedBarcodeSettings()
            .then((synced) => { if (mounted) setSettings(synced); })
            .catch(() => { if (mounted) setSettings(getStoredBarcodeSettings()); })
            .finally(() => {
                if (mounted) {
                    setLoadingProfiles(false);
                }
            });
        return () => { mounted = false; };
    }, [isOpen]);

    if (!isOpen || !passport) return null;

    const profiles = normalizeBarcodeProfiles(settings.savedProfiles);
    const printWithProfile = async (profile) => {
        setPrintingId(profile.id);
        let effectiveSettings = settings;
        try {
            const synced = await getSyncedBarcodeSettings();
            setSettings(synced);
            effectiveSettings = getBarcodeProfileSettingsById(synced, profile.id);
            await printPassportBarcodeWithSettings({ passport, companies, delegates, professions, settings: effectiveSettings });
            showAlert?.(`تم إرسال الطباعة بطريقة: ${profile.name}`, 'success');
            onClose?.();
        } catch (error) {
            console.error('Saved profile print error:', error);
            showAlert?.(`فشل الطباعة: ${error.message}`, 'error');
            alert(getBarcodePrintFailureMessage(effectiveSettings, error));
        } finally {
            setPrintingId('');
        }
    };

    const printCurrentDefault = async () => {
        setPrintingId('default');
        let effectiveSettings = settings;
        try {
            effectiveSettings = await getSyncedBarcodeSettings();
            setSettings(effectiveSettings);
            await printPassportBarcodeWithSettings({ passport, companies, delegates, professions, settings: effectiveSettings });
            showAlert?.('تم إرسال الطباعة بالطريقة الافتراضية الحالية', 'success');
            onClose?.();
        } catch (error) {
            console.error('Default profile print error:', error);
            showAlert?.(`فشل الطباعة: ${error.message}`, 'error');
            alert(getBarcodePrintFailureMessage(effectiveSettings, error));
        } finally {
            setPrintingId('');
        }
    };

    return (
        <BodyPortal>
            <div className="fixed inset-0 bg-black/50 z-[10020] flex items-center justify-center p-4" dir="rtl">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                    <div className="bg-slate-900 text-white p-4 flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-black flex items-center gap-2"><Printer size={20} /> طرق الطباعة المحفوظة</h2>
                            <p className="text-xs text-slate-200 mt-1">اختر طريقة محفوظة وسيتم الطباعة مباشرة.</p>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-white/15 rounded-xl"><X size={20} /></button>
                    </div>
                    <div className="p-4 space-y-3">
                        <div className="bg-slate-50 border rounded-xl p-3 text-sm">
                            <div className="font-bold text-gray-900">{passport.NameArabic || 'بدون اسم'}</div>
                            <div className="text-xs text-gray-500 mt-1">الكود: {buildPassportBarcodeCode(passport, companies)}</div>
                        </div>

                        <button
                            onClick={printCurrentDefault}
                            disabled={!!printingId}
                            className="w-full flex items-center justify-between gap-3 bg-blue-50 hover:bg-blue-100 border border-blue-100 rounded-xl px-4 py-3 text-blue-900 disabled:opacity-50"
                        >
                            <span className="font-bold">طباعة بالطريقة الافتراضية الحالية</span>
                            <Printer size={18} />
                        </button>

                        {loadingProfiles && (
                            <div className="bg-blue-50 border border-blue-100 text-blue-800 rounded-xl p-3 text-sm font-bold text-center">
                                جاري تحميل طرق الطباعة من قاعدة البيانات...
                            </div>
                        )}

                        {profiles.length > 0 ? (
                            <div className="space-y-2 max-h-[45vh] overflow-auto">
                                {profiles.map(profile => (
                                    <button
                                        key={profile.id}
                                        onClick={() => printWithProfile(profile)}
                                        disabled={!!printingId}
                                        className="w-full text-right flex items-center justify-between gap-3 bg-white hover:bg-green-50 border rounded-xl px-4 py-3 disabled:opacity-50"
                                    >
                                        <span>
                                            <span className="block font-black text-gray-900">{profile.name}</span>
                                            <span className="block text-xs text-gray-500 mt-1">اضغط للطباعة مباشرة</span>
                                        </span>
                                        <span className="shrink-0 bg-green-600 text-white rounded-lg px-3 py-1.5 text-xs font-bold">
                                            {printingId === profile.id ? 'جاري...' : 'طباعة'}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-yellow-50 border border-yellow-100 text-yellow-800 rounded-xl p-3 text-sm leading-6">
                                لا توجد طرق طباعة محفوظة حتى الآن. افتح ضبط الملصق، ظبط المقاسات والمواقع، ثم اضغط "حفظ كطريقة".
                            </div>
                        )}

                        <div className="flex gap-2 pt-2 border-t">
                            <button onClick={() => { onClose?.(); onOpenSettings?.(passport); }} className="flex-1 bg-slate-800 text-white rounded-xl py-2.5 font-bold">
                                ضبط الملصق
                            </button>
                            <button onClick={onClose} className="flex-1 bg-gray-100 text-gray-700 rounded-xl py-2.5 font-bold">
                                إغلاق
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </BodyPortal>
    );
};

const BarcodeSettingHint = ({ children }) => (
    <div className="md:col-span-4 text-xs leading-5 bg-white/80 border border-white rounded-xl p-3 text-gray-600">{children}</div>
);

const BarcodeElementCard = React.memo(({ title, color, enabledKey, settings, updateSetting, children, note }) => (
    <div className={`border rounded-3xl p-4 md:p-5 shadow-sm ${color || 'bg-white'}`}>
        <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="font-black text-gray-900 flex items-center gap-2">{title}</h3>
            {enabledKey && (
                <label className="text-xs flex items-center gap-2 bg-white border rounded-full px-3 py-1.5 font-bold shadow-sm">
                    <input type="checkbox" checked={!!settings?.[enabledKey]} onChange={(e) => updateSetting(enabledKey, e.target.checked)} />
                    إظهار
                </label>
            )}
        </div>
        {note && <div className="text-xs text-gray-500 mb-3 leading-5 bg-white/80 border border-white rounded-2xl p-3">{note}</div>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{children}</div>
    </div>
));

export const BarcodePrintModal = ({ isOpen, onClose, passport, companies, delegates, professions, showAlert, settingsOnly = false, pageMode = false }) => {
    const { hasPermission } = usePermissions();
    const [settings, setSettings] = React.useState(() => getStoredBarcodeSettings());
    const [printing, setPrinting] = React.useState(false);
    const [showPrinterGuide, setShowPrinterGuide] = React.useState(false);
    const [dragState, setDragState] = React.useState(null);
    const [loadingProfiles, setLoadingProfiles] = React.useState(false);
    const [printAgents, setPrintAgents] = React.useState([]);
    const [loadingAgents, setLoadingAgents] = React.useState(false);
    const contentScrollRef = React.useRef(null);
    const pendingModalViewRestoreRef = React.useRef(null);
    const selectedAutoProfile = normalizeBarcodeProfiles(settings.savedProfiles).find(profile => profile.id === settings.autoPrintProfileId);
    const canOpenBarcodeSettingsWindow = hasPermission('ضبط الباركود وطرق الطباعة');

    const readCurrentUserPermissions = React.useCallback(() => {
        try {
            const storageKeys = ['user', 'currentUser', 'loggedUser', 'loggedInUser'];
            for (const storageKey of storageKeys) {
                const savedUser = localStorage.getItem(storageKey);
                if (!savedUser) continue;
                const parsedUser = JSON.parse(savedUser);
                const rawPermissions = parsedUser?.Permissions ?? parsedUser?.permissions ?? parsedUser?.UserPermissions;
                if (!rawPermissions) continue;
                const parsedPermissions = typeof rawPermissions === 'string' ? JSON.parse(rawPermissions || '{}') : rawPermissions;
                if (parsedPermissions && typeof parsedPermissions === 'object') return parsedPermissions;
            }
        } catch (error) {
            console.warn('تعذر قراءة صلاحيات المستخدم من التخزين المحلي:', error);
        }
        return {};
    }, []);

    const hasExplicitStoredPermission = React.useCallback((permissionName) => {
        const permissions = readCurrentUserPermissions();
        return Object.values(permissions || {}).some(sectionPermissions => (
            sectionPermissions &&
            typeof sectionPermissions === 'object' &&
            sectionPermissions[permissionName] === true
        ));
    }, [readCurrentUserPermissions]);

    const canShowAllBarcodeSettingsSections = hasExplicitStoredPermission(BARCODE_SETTINGS_ALL_SECTIONS_PERMISSION);
    const canShowBarcodeSettingsSection = (permissionName) => canOpenBarcodeSettingsWindow && (
        canShowAllBarcodeSettingsSections || hasExplicitStoredPermission(permissionName)
    );
    const canShowBarcodePreview = canShowBarcodeSettingsSection('نافذة الباركود - المعاينة والسحب');
    const canShowBarcodePrintActions = canShowBarcodeSettingsSection('نافذة الباركود - اختبار وطباعة');
    const canShowBarcodeSaveActions = canShowBarcodeSettingsSection('نافذة الباركود - حفظ واستعادة الإعدادات');
    const canShowBarcodeProfiles = canShowBarcodeSettingsSection('نافذة الباركود - طرق الطباعة المحفوظة');
    const canShowBarcodeAutoPrint = canShowBarcodeSettingsSection('نافذة الباركود - الطباعة التلقائية');
    const canShowBarcodeLabelPrinter = canShowBarcodeSettingsSection('نافذة الباركود - إعدادات الليبل والطابعة');
    const canShowBarcodeName = canShowBarcodeSettingsSection('نافذة الباركود - عنصر الاسم');
    const canShowBarcodeId = canShowBarcodeSettingsSection('نافذة الباركود - عنصر ID');
    const canShowBarcodeCode = canShowBarcodeSettingsSection('نافذة الباركود - عنصر الباركود أو QR');
    const canShowBarcodeContent = canShowBarcodeSettingsSection('نافذة الباركود - محتوى الرمز');
    const canShowBarcodeGuide = canShowBarcodeSettingsSection('نافذة الباركود - دليل التشغيل');
    const canShowBarcodeAgentHint = canShowBarcodeSettingsSection('نافذة الباركود - تنبيه Masar Print Agent');
    const hasBarcodeSidebarContent = canShowBarcodePreview || canShowBarcodePrintActions || canShowBarcodeSaveActions;
    const hasBarcodeMainContent = canShowBarcodeProfiles || canShowBarcodeAutoPrint || canShowBarcodeLabelPrinter || canShowBarcodeName || canShowBarcodeId || canShowBarcodeCode || canShowBarcodeContent || canShowBarcodeAgentHint;

    const captureModalViewState = React.useCallback(() => {
        const scroller = contentScrollRef.current;
        const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
        const pageScroller = pageMode && typeof window !== 'undefined';
        const canRestoreFocus = activeElement
            && typeof activeElement.focus === 'function'
            && activeElement !== document.body
            && (pageScroller || (scroller && scroller.contains(activeElement)));

        return {
            scrollTop: pageScroller ? window.scrollY : (scroller ? scroller.scrollTop : 0),
            scrollLeft: pageScroller ? window.scrollX : (scroller ? scroller.scrollLeft : 0),
            activeElement: canRestoreFocus ? activeElement : null,
            selectionStart: canRestoreFocus && typeof activeElement.selectionStart === 'number' ? activeElement.selectionStart : null,
            selectionEnd: canRestoreFocus && typeof activeElement.selectionEnd === 'number' ? activeElement.selectionEnd : null
        };
    }, [pageMode]);

    const restoreModalViewState = React.useCallback((snapshot) => {
        if (!snapshot) return;

        const restore = () => {
            if (pageMode && typeof window !== 'undefined') {
                window.scrollTo(snapshot.scrollLeft || 0, snapshot.scrollTop || 0);
            } else {
                const scroller = contentScrollRef.current;
                if (scroller) {
                    scroller.scrollTop = snapshot.scrollTop || 0;
                    scroller.scrollLeft = snapshot.scrollLeft || 0;
                }
            }

            const activeElement = snapshot.activeElement;
            if (activeElement && typeof document !== 'undefined' && document.contains(activeElement)) {
                try {
                    activeElement.focus({ preventScroll: true });
                    if (snapshot.selectionStart !== null && typeof activeElement.setSelectionRange === 'function') {
                        activeElement.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd ?? snapshot.selectionStart);
                    }
                } catch {
                    // بعض عناصر select أو checkbox لا تدعم setSelectionRange، تجاهل فقط.
                }
            }
        };

        restore();
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(restore);
        setTimeout(restore, 0);
    }, [pageMode]);

    const keepBarcodeModalView = React.useCallback((action) => {
        pendingModalViewRestoreRef.current = captureModalViewState();
        action();
    }, [captureModalViewState]);

    React.useLayoutEffect(() => {
        if (!pendingModalViewRestoreRef.current) return;
        const snapshot = pendingModalViewRestoreRef.current;
        pendingModalViewRestoreRef.current = null;
        restoreModalViewState(snapshot);
    }, [settings, restoreModalViewState]);

    const updateAutoPrintSetting = (key, value) => {
        keepBarcodeModalView(() => {
            setSettings(prev => {
                const next = mergeBarcodeSettings({ ...prev, [key]: value });
                saveStoredBarcodeSettings(next);
                return next;
            });
        });
    };

    const refreshPrintAgents = React.useCallback(async (silent = false) => {
        setLoadingAgents(true);
        try {
            const agents = await fetchMasarPrintAgents();
            setPrintAgents(agents);

            const onlineAgents = agents.filter(isMasarAgentOnline);
            const selectedAgent = onlineAgents[0] || null;
            if (selectedAgent) {
                const normalizedSelectedPrinters = normalizeMasarAgentPrinters(selectedAgent.printers);
                const printerName = selectedAgent.defaultPrinter
                    || normalizedSelectedPrinters.find(printer => printer.isDefault)?.name
                    || normalizedSelectedPrinters[0]?.name
                    || '';

                keepBarcodeModalView(() => {
                    setSettings(prev => {
                        // لا تحتفظ بجهاز قديم Offline حتى لو كان موجودًا في قاعدة البيانات.
                        // هذا كان يسبب إرسال Job لجهاز غير متصل، فيظهر نجاح من النظام ولا تتم الطباعة.
                        const hasCurrentOnlineAgent = onlineAgents.some(agent => String(agent?.agentId || '') === String(prev.printAgentId || ''));
                        const nextAgentId = hasCurrentOnlineAgent ? prev.printAgentId : (selectedAgent.agentId || '');
                        const currentAgent = onlineAgents.find(agent => String(agent?.agentId || '') === String(nextAgentId || '')) || selectedAgent;
                        const currentPrinters = normalizeMasarAgentPrinters(currentAgent.printers);
                        const currentPrinterNames = currentPrinters.map(printer => printer.name).filter(Boolean);
                        const previousPrinterIsValid = currentPrinterNames.includes(prev.printAgentPrinterName);
                        const nextPrinterName = previousPrinterIsValid
                            ? prev.printAgentPrinterName
                            : (currentAgent.defaultPrinter || printerName || currentPrinterNames[0] || '');

                        const next = saveStoredBarcodeSettings({
                            ...prev,
                            printEngine: 'agent',
                            printAgentId: nextAgentId,
                            printAgentPrinterName: nextPrinterName
                        });
                        return next;
                    });
                });

                if (!silent) {
                    showAlert?.(`تم تحميل جهاز الطباعة المتصل: ${selectedAgent.agentName || selectedAgent.agentId}`, 'success');
                }
            } else {
                keepBarcodeModalView(() => {
                    setSettings(prev => saveStoredBarcodeSettings({
                        ...prev,
                        printEngine: 'agent',
                        printAgentId: '',
                        printAgentPrinterName: ''
                    }));
                });
                if (!silent) {
                    showAlert?.(agents.length
                        ? 'كل أجهزة Masar Print Agent الموجودة Offline. افتح البرنامج على جهاز الطابعة واضغط Save & Start ثم حدّث الأجهزة.'
                        : 'لم يتم العثور على أي Masar Print Agent متصل. تأكد أن البرنامج مفتوح على جهاز الطابعة.', 'warning');
                }
            }
        } catch (error) {
            showAlert?.(`تعذر تحميل أجهزة الطباعة: ${error.message}`, 'error');
        } finally {
            setLoadingAgents(false);
        }
    }, [showAlert, keepBarcodeModalView]);

    React.useEffect(() => {
        if (!isOpen) return;
        let mounted = true;
        setLoadingProfiles(true);
        getSyncedBarcodeSettings()
            .then((synced) => { if (mounted) setSettings(synced); })
            .catch(() => { if (mounted) setSettings(getStoredBarcodeSettings()); })
            .finally(() => {
                if (mounted) {
                    setLoadingProfiles(false);
                    refreshPrintAgents(true);
                }
            });
        return () => { mounted = false; };
    }, [isOpen, refreshPrintAgents]);

    if (!isOpen || !passport || !canOpenBarcodeSettingsWindow) return null;

    const labelCode = buildPassportBarcodeCode(passport, companies);
    const barcodeContentInfo = getBarcodeContentInfo(passport, companies, delegates, professions, settings);
    const barcodeContent = barcodeContentInfo.content;
    const effectivePreviewCodeType = settings.codeType || 'barcode';
    const printableSymbolData = getPrintableSymbolData(effectivePreviewCodeType, labelCode, barcodeContent);
    const selectedFieldsCount = settings.includeFields?.length || 0;
    const isLongLinearBarcode = effectivePreviewCodeType === 'barcode' && labelCode.length > 24;
    const savedProfiles = normalizeBarcodeProfiles(settings.savedProfiles);
    const selectedProfile = savedProfiles.find(profile => profile.id === settings.activeProfileId);
    const selectedPrintAgent = printAgents.find(agent => String(agent.agentId || '') === String(settings.printAgentId || '')) || null;
    const selectedPrintAgentPrinters = normalizeMasarAgentPrinters(selectedPrintAgent?.printers);

    const previewScale = Math.min(7, 285 / Math.max(1, Number(settings.labelWidthMm) || 38), 190 / Math.max(1, Number(settings.labelHeightMm) || 24));
    const mm = (value) => `${(Number(value) || 0) * previewScale}px`;
    const previewFont = (value) => `${Math.max(6, (Number(value) || 8) * previewScale / 3)}px`;

    const updateSetting = (key, value) => {
        keepBarcodeModalView(() => setSettings(prev => ({ ...prev, [key]: value })));
    };
    const updateNumericSetting = (key, value) => updateSetting(key, String(value ?? '').replace(',', '.'));
    const getNumericValue = (key, fallback = 0) => {
        const value = Number(String(settings[key] ?? '').replace(',', '.'));
        return Number.isFinite(value) ? value : fallback;
    };

    const getLayoutIssues = () => {
        const labelWidth = getNumericValue('labelWidthMm', 38);
        const labelHeight = getNumericValue('labelHeightMm', 24);
        const issues = [];
        const checkBox = (enabled, label, xKey, yKey, widthKey, heightKey) => {
            if (!enabled) return;
            const x = getNumericValue(xKey, 0);
            const y = getNumericValue(yKey, 0);
            const width = getNumericValue(widthKey, 0);
            const height = getNumericValue(heightKey, 0);
            if (x < 0 || y < 0 || x + width > labelWidth || y + height > labelHeight) {
                issues.push(`${label} خارج حدود الليبل أو قريب من القطع`);
            }
        };
        checkBox(settings.showName, 'الاسم', 'nameXmm', 'nameYmm', 'nameWidthMm', 'nameHeightMm');
        checkBox(settings.showId, 'ID', 'idXmm', 'idYmm', 'idWidthMm', 'idHeightMm');
        if (settings.showBarcode && effectivePreviewCodeType === 'barcode') {
            checkBox(true, 'الباركود', 'barcodeXmm', 'barcodeYmm', 'barcodePreviewWidthMm', 'barcodeHeightMm');
            if (labelCode.length > 24 && getNumericValue('barcodeNarrow', 2) >= 2) {
                issues.push('كود الباركود طويل؛ قلّل رمز الشركة أو استخدم QR');
            }
        }
        if (settings.showBarcode && effectivePreviewCodeType === 'qr') {
            const sizeMm = Math.max(10, getNumericValue('qrSize', 6) * 2.8);
            const x = getNumericValue('qrXmm', 0);
            const y = getNumericValue('qrYmm', 0);
            if (x + sizeMm > labelWidth || y + sizeMm > labelHeight) {
                issues.push('QR خارج حدود الليبل أو قريب من القطع');
            }
        }
        return issues;
    };

    const layoutIssues = getLayoutIssues();

    const fixLayoutEdges = () => {
        keepBarcodeModalView(() => {
            setSettings(prev => {
                const next = normalizeBarcodeLayoutSettings(prev);
                saveStoredBarcodeSettings(next);
                return next;
            });
        });
        showAlert?.('تم ضبط العناصر داخل حدود الليبل لتقليل مشكلة الأجزاء المتاكلة', 'success');
    };

    const sanitizeNumericSetting = (key, min = 0, fallback = 0) => {
        keepBarcodeModalView(() => {
            setSettings(prev => {
                const raw = String(prev[key] ?? '').replace(',', '.');
                const value = Number(raw);
                const safe = Number.isFinite(value) ? Math.max(min, value) : fallback;
                return { ...prev, [key]: Number(safe.toFixed(2)) };
            });
        });
    };
    const adjustNumericSetting = (key, delta, min = 0, precision = 1) => {
        keepBarcodeModalView(() => {
            setSettings(prev => {
                const current = Number(String(prev[key] ?? '').replace(',', '.'));
                const nextValue = Math.max(min, (Number.isFinite(current) ? current : min) + delta);
                return { ...prev, [key]: Number(nextValue.toFixed(precision)) };
            });
        });
    };
    const setPresetSize = (width, height) => {
        keepBarcodeModalView(() => {
            setSettings(prev => ({
                ...prev,
                labelWidthMm: width,
                labelHeightMm: height,
                profileName: `ملصق ${width}×${height}`
            }));
        });
    };

    const elementPositionKeys = {
        name: { x: 'nameXmm', y: 'nameYmm', width: 'nameWidthMm', height: 'nameHeightMm' },
        id: { x: 'idXmm', y: 'idYmm', width: 'idWidthMm', height: 'idHeightMm' },
        barcode: { x: 'barcodeXmm', y: 'barcodeYmm', width: 'barcodePreviewWidthMm', height: 'barcodeHeightMm' },
        qr: { x: 'qrXmm', y: 'qrYmm', width: null, height: null }
    };

    const moveElement = (element, dx, dy) => {
        const keys = elementPositionKeys[element];
        if (!keys) return;
        keepBarcodeModalView(() => {
            setSettings(prev => ({
                ...prev,
                [keys.x]: Number(Math.max(0, (Number(prev[keys.x]) || 0) + dx).toFixed(1)),
                [keys.y]: Number(Math.max(0, (Number(prev[keys.y]) || 0) + dy).toFixed(1))
            }));
        });
    };

    const centerElementHorizontally = (element) => {
        const keys = elementPositionKeys[element];
        if (!keys) return;
        const labelWidth = getNumericValue('labelWidthMm', 38);
        const elementWidth = element === 'qr'
            ? Math.max(7, getNumericValue('qrSize', 4) * 2.8)
            : getNumericValue(keys.width, 10);
        updateSetting(keys.x, Number(Math.max(0, (labelWidth - elementWidth) / 2).toFixed(1)));
    };

    const startDragElement = (element, event) => {
        const keys = elementPositionKeys[element];
        if (!keys) return;
        event.preventDefault();
        event.stopPropagation();
        setDragState({
            element,
            startX: event.clientX,
            startY: event.clientY,
            initialX: getNumericValue(keys.x, 0),
            initialY: getNumericValue(keys.y, 0)
        });
    };

    const handlePreviewPointerMove = (event) => {
        if (!dragState) return;
        const keys = elementPositionKeys[dragState.element];
        if (!keys) return;
        const labelWidth = getNumericValue('labelWidthMm', 38);
        const labelHeight = getNumericValue('labelHeightMm', 24);
        const elementWidth = dragState.element === 'qr'
            ? Math.max(7, getNumericValue('qrSize', 4) * 2.8)
            : getNumericValue(keys.width, 10);
        const elementHeight = dragState.element === 'qr'
            ? Math.max(7, getNumericValue('qrSize', 4) * 2.8)
            : getNumericValue(keys.height, 5);
        const nextX = Math.min(Math.max(0, dragState.initialX + ((event.clientX - dragState.startX) / previewScale)), Math.max(0, labelWidth - elementWidth));
        const nextY = Math.min(Math.max(0, dragState.initialY + ((event.clientY - dragState.startY) / previewScale)), Math.max(0, labelHeight - elementHeight));
        keepBarcodeModalView(() => {
            setSettings(prev => ({ ...prev, [keys.x]: Number(nextX.toFixed(1)), [keys.y]: Number(nextY.toFixed(1)) }));
        });
    };

    const stopPreviewDrag = () => setDragState(null);

    const toggleField = (fieldKey) => {
        keepBarcodeModalView(() => {
            setSettings(prev => {
                const current = Array.isArray(prev.includeFields) ? prev.includeFields : [];
                return current.includes(fieldKey)
                    ? { ...prev, includeFields: current.filter(k => k !== fieldKey) }
                    : { ...prev, includeFields: [...current, fieldKey] };
            });
        });
    };

    const saveSettings = async () => {
        const next = normalizeBarcodeLayoutSettings(settings);
        saveStoredBarcodeSettings(next);
        keepBarcodeModalView(() => setSettings(next));
        try {
            await saveBarcodeDefaultSettingsToServer(next);
            showAlert?.('تم حفظ إعدادات الليبل والطابعة كافتراضي عام. تفعيل الطباعة التلقائية محفوظ محليًا على هذا الجهاز فقط.', 'success');
        } catch (error) {
            console.error('خطأ حفظ الإعدادات العامة في قاعدة البيانات:', error);
            showAlert?.(`تم الحفظ محليًا فقط، ولم يتم الحفظ في قاعدة البيانات: ${error.message}`, 'warning');
        }
    };

    const savePrintProfile = async ({ overwrite = false } = {}) => {
        const normalizedCurrentSettings = normalizeBarcodeLayoutSettings(settings);
        const name = barcodeSafeText(normalizedCurrentSettings.profileName, 'طريقة طباعة جديدة');
        const selectedId = normalizedCurrentSettings.activeProfileId || '';
        const id = overwrite && selectedId ? selectedId : `profile_${Date.now()}`;

        if (overwrite && selectedId) {
            const profileName = selectedProfile?.name || name;
            if (!window.confirm(`تحديث طريقة الطباعة المحفوظة "${profileName}" بالإعدادات الحالية؟`)) return;
        }

        const profile = {
            id,
            name,
            settings: getBarcodeProfileSnapshot({ ...normalizedCurrentSettings, profileName: name }),
            updatedAt: new Date().toISOString()
        };
        try {
            const savedProfile = await saveBarcodePrintProfileToServer(profile);
            const nextProfiles = [...savedProfiles.filter(item => item.id !== id), savedProfile];
            const next = mergeBarcodeSettings({ ...settings, profileName: savedProfile.name, activeProfileId: savedProfile.id, savedProfiles: nextProfiles });
            keepBarcodeModalView(() => setSettings(next));
            saveStoredBarcodeSettings(next);
            showAlert?.(overwrite ? `تم تحديث طريقة الطباعة: ${savedProfile.name}` : `تم حفظ طريقة طباعة جديدة: ${savedProfile.name}`, 'success');
        } catch (error) {
            console.error('خطأ حفظ طريقة الطباعة في قاعدة البيانات:', error);
            const nextProfiles = [...savedProfiles.filter(item => item.id !== id), profile];
            const next = mergeBarcodeSettings({ ...settings, profileName: name, activeProfileId: id, savedProfiles: nextProfiles });
            keepBarcodeModalView(() => setSettings(next));
            saveStoredBarcodeSettings(next);
            showAlert?.(`تعذر الحفظ في قاعدة البيانات وتم الحفظ محليًا فقط: ${error.message}`, 'warning');
        }
    };

    const loadPrintProfile = (profileId) => {
        if (!profileId) {
            keepBarcodeModalView(() => {
                setSettings(prev => {
                    const next = mergeBarcodeSettings({ ...prev, activeProfileId: '' });
                    saveStoredBarcodeSettings(next);
                    return next;
                });
            });
            return;
        }
        const profile = savedProfiles.find(item => item.id === profileId);
        if (!profile) return;
        const next = mergeBarcodeSettings({
            ...settings,
            ...profile.settings,
            savedProfiles,
            activeProfileId: profile.id,
            profileName: profile.name
        });
        keepBarcodeModalView(() => setSettings(next));
        saveStoredBarcodeSettings(next);
        showAlert?.(`تم تحميل طريقة الطباعة: ${profile.name}${next.printAgentPrinterName ? ' - الطابعة: ' + next.printAgentPrinterName : ''}`, 'success');
    };

    const deletePrintProfileById = async (profileId) => {
        if (!profileId) return;
        const targetProfile = savedProfiles.find(item => item.id === profileId);
        const profileName = targetProfile?.name || settings.profileName || 'الطريقة الحالية';
        if (!window.confirm(`حذف طريقة الطباعة "${profileName}" من قاعدة البيانات لكل المستخدمين؟`)) return;
        try {
            await deleteBarcodePrintProfileFromServer(profileId);
        } catch (error) {
            console.error('خطأ حذف طريقة الطباعة من قاعدة البيانات:', error);
            showAlert?.(`تعذر الحذف من قاعدة البيانات: ${error.message}`, 'error');
            return;
        }
        const nextProfiles = savedProfiles.filter(item => item.id !== profileId);
        const isDeletingActiveProfile = settings.activeProfileId === profileId;
        const next = mergeBarcodeSettings({
            ...settings,
            activeProfileId: isDeletingActiveProfile ? '' : settings.activeProfileId,
            profileName: isDeletingActiveProfile ? 'ملصق 38×24 - افتراضي' : settings.profileName,
            savedProfiles: nextProfiles
        });
        keepBarcodeModalView(() => setSettings(next));
        saveStoredBarcodeSettings(next);
        showAlert?.('تم حذف طريقة الطباعة من قاعدة البيانات', 'success');
    };

    const deletePrintProfile = async () => deletePrintProfileById(settings.activeProfileId);

    const resetSettings = () => {
        const next = mergeBarcodeSettings({ ...DEFAULT_BARCODE_SETTINGS, savedProfiles });
        keepBarcodeModalView(() => setSettings(next));
        saveStoredBarcodeSettings(next);
        showAlert?.('تم استعادة الإعدادات الافتراضية مع الاحتفاظ بطرق الطباعة المحفوظة', 'success');
    };

    const printViaBixolon = async (testMode = false) => {
        setPrinting(true);
        try {
            const copies = Math.max(1, Number(settings.printCopies) || 1);
            const labelJson = buildBixolonLabelData({ passport, companies, delegates, professions, settings, testMode });
            const agentPayload = buildMasarAgentLabelData({ passport, companies, delegates, professions, settings, testMode });
            agentPayload.nativeLabelJson = labelJson;
            for (let i = 0; i < copies; i += 1) {
                await postLabelToSelectedPrintEngine(settings, labelJson, agentPayload);
            }
            showAlert?.(testMode ? 'تم إرسال اختبار الطباعة للطابعة' : 'تم إرسال الملصق للطابعة بنجاح', 'success');
        } catch (error) {
            console.error('Web Print SDK print error:', error);
            showAlert?.(`فشل الطباعة المباشرة: ${error.message}`, 'error');
            alert(getBarcodePrintFailureMessage(settings, error));
        } finally {
            setPrinting(false);
        }
    };


    const printBoundaryTest = async () => {
        setPrinting(true);
        try {
            const safeSettings = normalizeBarcodeLayoutSettings(settings || {});
            const labelJson = buildBoundaryTestBixolonLabelData({ settings: safeSettings });
            const agentPayload = buildMasarAgentBoundaryTestData(safeSettings);
            agentPayload.nativeLabelJson = labelJson;
            await postLabelToSelectedPrintEngine(safeSettings, labelJson, agentPayload);
            showAlert?.('تم إرسال اختبار حدود الاستيكر V17. يجب أن يطبع إطار وخط منتصف، وليس باركود BOUNDARY. إذا ظهر باركود BOUNDARY فمعناه أن Agent قديم مازال يعمل.', 'success');
        } catch (error) {
            console.error('Boundary test print error:', error);
            showAlert?.(`فشل اختبار حدود الاستيكر: ${error.message}`, 'error');
            alert(getBarcodePrintFailureMessage(settings, error));
        } finally {
            setPrinting(false);
        }
    };

    const renderNumberInput = (label, key, min = 0, step = 0.1, helper = '') => {
        const precision = step >= 1 ? 0 : 1;
        return (
            <label className="text-xs text-gray-600 block">
                <span className="font-semibold text-gray-700">{label}</span>
                {helper && <span className="block text-[11px] text-gray-400 mt-0.5 leading-4">{helper}</span>}
                <div className="mt-1 flex rounded-lg overflow-hidden border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-100">
                    <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => adjustNumericSetting(key, -Number(step || 1), min, precision)}
                        className="px-2 bg-gray-50 hover:bg-gray-100 border-l text-gray-700 font-black"
                        title="تقليل"
                    >
                        −
                    </button>
                    <input
                        type="text"
                        inputMode="decimal"
                        value={settings[key] ?? ''}
                        onChange={(e) => updateNumericSetting(key, e.target.value)}
                        onBlur={() => sanitizeNumericSetting(key, min, DEFAULT_BARCODE_SETTINGS[key] ?? min)}
                        className="w-full px-2 py-1.5 text-sm text-center outline-none"
                    />
                    <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => adjustNumericSetting(key, Number(step || 1), min, precision)}
                        className="px-2 bg-gray-50 hover:bg-gray-100 border-r text-gray-700 font-black"
                        title="زيادة"
                    >
                        +
                    </button>
                </div>
            </label>
        );
    };

    const renderAlignSelect = (label, key) => (
        <label className="text-xs text-gray-600">
            {label}
            <select value={settings[key]} onChange={(e) => updateSetting(key, e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
                <option value="center">منتصف</option>
                <option value="right">يمين</option>
                <option value="left">يسار</option>
            </select>
        </label>
    );

    const renderMovePanel = (element, colorClass = 'bg-white') => (
        <div className={`rounded-xl border p-3 ${colorClass} md:col-span-4`}>
            <div className="text-xs font-black text-gray-800 mb-2">تحريك سريع للعنصر</div>
            <div className="flex flex-wrap gap-2 items-center">
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => moveElement(element, 0, -0.5)} className="px-3 py-1.5 rounded-lg bg-white border hover:bg-gray-50 text-xs font-bold">↑ أعلى</button>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => moveElement(element, 0, 0.5)} className="px-3 py-1.5 rounded-lg bg-white border hover:bg-gray-50 text-xs font-bold">↓ أسفل</button>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => moveElement(element, -0.5, 0)} className="px-3 py-1.5 rounded-lg bg-white border hover:bg-gray-50 text-xs font-bold">→ يمين</button>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => moveElement(element, 0.5, 0)} className="px-3 py-1.5 rounded-lg bg-white border hover:bg-gray-50 text-xs font-bold">← شمال</button>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => centerElementHorizontally(element)} className="px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-black text-xs font-bold">توسيط أفقي</button>
            </div>
            <div className="text-[11px] text-gray-500 mt-2">تقدر كمان تسحب العنصر من المعاينة مباشرة بالماوس.</div>
        </div>
    );

    const barcodeWorkspace = (
        <div className={pageMode ? 'bg-white/95 backdrop-blur shadow-2xl w-full min-h-screen rounded-none md:rounded-[2rem] border border-slate-200 overflow-visible' : 'bg-white rounded-none md:rounded-2xl shadow-2xl w-full max-w-7xl h-[100dvh] md:h-auto md:max-h-[94vh] overflow-hidden flex flex-col'}>
                    <div className={pageMode ? "bg-gradient-to-l from-slate-950 via-blue-950 to-indigo-900 text-white p-5 md:p-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" : "bg-gradient-to-r from-slate-900 to-blue-900 text-white p-4 flex items-center justify-between gap-3"}>
                        <div>
                            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 text-xs font-bold mb-3">
                                <span className="w-2 h-2 rounded-full bg-emerald-300"></span> صفحة مستقلة لضبط الطباعة
                            </div>
                            <h2 className="text-2xl md:text-3xl font-black flex items-center gap-2"><Barcode size={26} /> {settingsOnly ? 'ضبط الباركود وطرق الطباعة' : 'طباعة ملصق / باركود الجواز'}</h2>
                            <p className="text-blue-100 text-sm md:text-base mt-2 max-w-3xl leading-7">صمّم الاستيكر بدقة، احفظ أكثر من طريقة طباعة، واختبر قبل الطباعة مع حماية من الأجزاء المتاكلة.</p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            {canShowBarcodeGuide && (
                                <button onClick={() => setShowPrinterGuide(prev => !prev)} className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-sm font-bold flex items-center gap-2" title="دليل تشغيل الطابعة">
                                    <HelpCircle size={18} /> دليل التشغيل
                                </button>
                            )}
                            <button onClick={onClose} className="px-3 py-2 hover:bg-white/20 rounded-xl flex items-center gap-2 font-bold">
                                {pageMode ? <span>رجوع للجوازات</span> : <X size={22} />}
                            </button>
                        </div>
                    </div>

                    {showPrinterGuide && canShowBarcodeGuide && (
                        <div className="mx-4 mt-4 border border-blue-100 bg-blue-50 rounded-2xl p-4 text-sm text-blue-950">
                            <div className="font-black mb-2 flex items-center gap-2"><HelpCircle size={18} /> تشغيل الطباعة المباشرة على أي جهاز</div>
                            <ol className="list-decimal pr-5 space-y-1">
                                {BARCODE_QUICK_SETUP_STEPS.map((step, index) => <li key={index}>{step}</li>)}
                            </ol>
                            <div className="mt-3 bg-white border border-blue-100 rounded-xl p-3 text-xs text-blue-900">
                                كل جهاز كمبيوتر له تعريف طابعة و Logical Name خاص به. بعد ضبط المقاس والمواقع احفظها كطريقة طباعة؛ وبكده تقدر تختارها بسرعة بدل إعادة الضبط كل مرة.
                            </div>
                        </div>
                    )}

                    <div
                        ref={contentScrollRef}
                        className={pageMode
                            ? 'p-3 md:p-5 grid grid-cols-1 xl:grid-cols-5 gap-5 bg-gradient-to-b from-slate-50 to-white overflow-visible'
                            : 'flex-1 overflow-y-auto overscroll-contain p-3 md:p-5 grid grid-cols-1 xl:grid-cols-5 gap-5 bg-gradient-to-b from-slate-50 to-white'}
                        style={pageMode ? { overflowAnchor: 'auto' } : { scrollBehavior: 'auto', overflowAnchor: 'none' }}
                    >
                        {hasBarcodeSidebarContent && (
                        <div className="xl:col-span-2 space-y-4">
                            {canShowBarcodePreview && (
                            <div className="border border-slate-200 rounded-3xl p-4 bg-white shadow-sm xl:sticky xl:top-4">
                                <h3 className="font-bold text-gray-900 mb-2">معاينة تقريبية بالسحب</h3>
                                <div className="text-xs text-gray-500 mb-3 leading-5 bg-white rounded-xl border p-2">
                                    اسحب مربع الاسم أو ID أو الباركود داخل الملصق. كل حركة هتعدل X و Y تلقائيًا. الألوان للتوضيح فقط ولا تطبع.
                                </div>
                                <div
                                    className="bg-white border border-dashed border-gray-400 rounded-xl mx-auto overflow-hidden relative select-none"
                                    onPointerMove={handlePreviewPointerMove}
                                    onPointerUp={stopPreviewDrag}
                                    onPointerLeave={stopPreviewDrag}
                                    style={{ width: mm(settings.labelWidthMm), height: mm(settings.labelHeightMm), minWidth: '210px', minHeight: '130px', touchAction: 'none' }}
                                >
                                    {settings.showName && (
                                        <div
                                            onPointerDown={(e) => startDragElement('name', e)}
                                            className="absolute border-2 border-blue-400 bg-blue-50/60 flex items-center justify-center overflow-hidden text-center cursor-move rounded-sm"
                                            title="اسحب لتحريك الاسم"
                                            style={{ left: mm(settings.nameXmm), top: mm(settings.nameYmm), width: mm(settings.nameWidthMm), height: mm(settings.nameHeightMm), fontSize: previewFont(settings.nameFontSize), fontWeight: settings.nameBold ? 800 : 400, outline: dragState?.element === 'name' ? '2px solid #1d4ed8' : 'none' }}
                                        >
                                            {passport.NameArabic || 'بدون اسم'}
                                        </div>
                                    )}
                                    {settings.showId && (
                                        <div
                                            onPointerDown={(e) => startDragElement('id', e)}
                                            className="absolute border-2 border-emerald-400 bg-emerald-50/60 flex items-center justify-center overflow-hidden text-center cursor-move rounded-sm"
                                            dir="ltr"
                                            title="اسحب لتحريك ID"
                                            style={{ left: mm(settings.idXmm), top: mm(settings.idYmm), width: mm(settings.idWidthMm), height: mm(settings.idHeightMm), fontSize: previewFont(settings.idFontSize), fontWeight: settings.idBold ? 900 : 400, outline: dragState?.element === 'id' ? '2px solid #047857' : 'none' }}
                                        >
                                            {labelCode}
                                        </div>
                                    )}
                                    {settings.showBarcode && effectivePreviewCodeType === 'barcode' && (
                                        <div
                                            onPointerDown={(e) => startDragElement('barcode', e)}
                                            className="absolute border-2 border-purple-400 bg-purple-50/60 flex items-center justify-center overflow-hidden cursor-move rounded-sm"
                                            title="اسحب لتحريك الباركود"
                                            style={{ left: mm(settings.barcodeXmm), top: mm(settings.barcodeYmm), width: mm(settings.barcodePreviewWidthMm), height: mm(settings.barcodeHeightMm), outline: dragState?.element === 'barcode' ? '2px solid #7e22ce' : 'none' }}
                                        >
                                            <Code128Preview value={labelCode} />
                                        </div>
                                    )}
                                    {settings.showBarcode && effectivePreviewCodeType === 'qr' && (
                                        <div
                                            onPointerDown={(e) => startDragElement('qr', e)}
                                            className="absolute border-2 border-purple-400 bg-white p-1 cursor-move rounded-sm"
                                            title="اسحب لتحريك QR"
                                            style={{ left: mm(settings.qrXmm), top: mm(settings.qrYmm), width: mm(Math.max(10, Number(settings.qrSize) * 2.8)), height: mm(Math.max(10, Number(settings.qrSize) * 2.8)), outline: dragState?.element === 'qr' ? '2px solid #7e22ce' : 'none' }}
                                        >
                                            <QRVisualPreview value={barcodeContent} />
                                        </div>
                                    )}
                                </div>
                                <div className="text-xs text-gray-500 mt-3 space-y-1">
                                    <div>مقاس الليبل: <b>{settings.labelWidthMm} × {settings.labelHeightMm} mm</b></div>
                                    <div>الذي سيُطبع داخل الرمز: <span className="font-bold text-gray-900">{effectivePreviewCodeType === 'barcode' ? labelCode : (settings.contentMode === 'text' ? barcodeContent : printableSymbolData)}</span></div>
                                </div>
                                {layoutIssues.length > 0 && (
                                    <div className="mt-3 bg-red-50 border border-red-200 text-red-800 text-xs rounded-xl p-3 leading-5">
                                        <div className="font-black mb-1">تنبيه قبل الطباعة:</div>
                                        <ul className="list-disc pr-4 space-y-1">
                                            {layoutIssues.map((issue, index) => <li key={index}>{issue}</li>)}
                                        </ul>
                                        <button type="button" onClick={fixLayoutEdges} className="mt-2 bg-red-600 hover:bg-red-700 text-white rounded-lg px-3 py-1.5 font-bold">
                                            إصلاح تلقائي للحدود
                                        </button>
                                    </div>
                                )}
                                {isLongLinearBarcode && <div className="mt-3 bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs rounded-xl p-3">كود الباركود طويل نسبيًا. الأفضل تقليل رمز الشركة أو استخدام QR.</div>}
                            </div>
                            )}

                            {(canShowBarcodePrintActions || canShowBarcodeSaveActions) && (
                            <div className="grid grid-cols-2 gap-2">
                                {canShowBarcodePrintActions && (
                                    <>
                                        <button onClick={() => printViaBixolon(true)} disabled={printing} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-2.5 font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                                            <Printer size={16} /> اختبار
                                        </button>
                                        <button onClick={() => printViaBixolon(false)} disabled={printing} className="bg-green-600 hover:bg-green-700 text-white rounded-xl py-2.5 font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                                            <Printer size={16} /> طباعة
                                        </button>
                                        <button onClick={printBoundaryTest} disabled={printing} className="col-span-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl py-2.5 font-bold disabled:opacity-50 flex items-center justify-center gap-2" title="يطبع إطار حول الاستيكر وخط منتصف لمعرفة هل المقاس والـ Gap مضبوطين أم لا">
                                            <Printer size={16} /> اختبار حدود الاستيكر
                                        </button>
                                    </>
                                )}
                                {canShowBarcodeSaveActions && (
                                    <>
                                        <button onClick={saveSettings} className="bg-gray-900 hover:bg-black text-white rounded-xl py-2.5 font-bold col-span-2">حفظ الإعدادات العامة</button>
                                        <button onClick={resetSettings} className="bg-white hover:bg-gray-50 text-gray-700 border rounded-xl py-2.5 font-bold col-span-2">استعادة الافتراضي</button>
                                    </>
                                )}
                            </div>
                            )}
                        </div>
                        )}

                        <div className={`${hasBarcodeSidebarContent ? 'xl:col-span-3' : 'xl:col-span-4'} space-y-4`}>
                            {(canShowBarcodeProfiles || canShowBarcodeAutoPrint) && (
                            <div className="border rounded-2xl p-4 bg-amber-50 border-amber-100 space-y-4">
                                {canShowBarcodeProfiles && (
                                <>
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                                    <div>
                                        <h3 className="font-black text-amber-950">طرق الطباعة المحفوظة</h3>
                                        <p className="text-xs text-amber-800 mt-1 leading-5">اختيار طريقة محفوظة يقوم بتحميلها للمعاينة فقط. لن يتم تعديلها إلا من زر "تحديث المختارة".</p>
                                    </div>
                                    <span className="text-[11px] bg-white border border-amber-200 text-amber-900 rounded-full px-3 py-1 font-bold self-start">عام لكل المستخدمين</span>
                                </div>
                                {loadingProfiles && (
                                    <div className="bg-blue-50 border border-blue-100 text-blue-800 rounded-xl p-2 text-xs font-bold text-center">
                                        جاري تحميل الطرق المحفوظة من قاعدة البيانات...
                                    </div>
                                )}
                                {selectedProfile && (
                                    <div className="bg-white border border-amber-200 rounded-xl p-3 text-xs text-amber-900 leading-5">
                                        أنت واقف الآن على طريقة: <b>{selectedProfile.name}</b>. زر "حفظ كطريقة جديدة" لن يغيرها، وزر "تحديث المختارة" هو الوحيد الذي يعدلها بعد تأكيدك.
                                    </div>
                                )}
                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-end">
                                    <label className="text-xs text-amber-900 lg:col-span-4">اسم الطريقة الحالية
                                        <input value={settings.profileName || ''} onChange={(e) => updateSetting('profileName', e.target.value)} className="mt-1 w-full border border-amber-200 rounded-xl px-3 py-2.5 text-sm bg-white" placeholder="مثال: ملصق 38×24 - شركة معينة" />
                                    </label>
                                    <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-4 gap-2">
                                        <button type="button" onClick={() => savePrintProfile({ overwrite: false })} className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-2.5 font-black">حفظ كطريقة جديدة</button>
                                        <button type="button" onClick={() => savePrintProfile({ overwrite: true })} disabled={!settings.activeProfileId} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-2.5 font-black disabled:opacity-50">تحديث المختارة</button>
                                        <button type="button" onClick={() => loadPrintProfile('')} className="bg-white hover:bg-gray-50 text-gray-700 border border-amber-200 rounded-xl py-2.5 font-black">تصميم جديد</button>
                                        <button type="button" onClick={deletePrintProfile} disabled={!settings.activeProfileId} className="bg-white hover:bg-red-50 text-red-700 border border-red-100 rounded-xl py-2.5 font-black disabled:opacity-50">حذف المختارة</button>
                                    </div>
                                </div>

                                <div className="bg-white border border-amber-200 rounded-2xl p-3">
                                    <div className="flex items-center justify-between gap-2 mb-3">
                                        <div className="font-black text-amber-950">قائمة الطرق المحفوظة</div>
                                        <span className="text-[11px] text-amber-700">اضغط تحميل فقط للتعديل أو المعاينة، والطباعة السريعة تتم من زر الباركود بجوار كل جواز.</span>
                                    </div>
                                    {savedProfiles.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                                            {savedProfiles.map(profile => {
                                                const isActive = settings.activeProfileId === profile.id;
                                                return (
                                                    <div key={profile.id} className={`rounded-2xl border p-3 ${isActive ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                                                        <div className="flex items-start justify-between gap-2">
                                                            <div className="min-w-0">
                                                                <div className="font-black text-gray-900 truncate">{profile.name}</div>
                                                                <div className="text-[11px] text-gray-500 mt-1">
                                                                    آخر تحديث: {profile.updatedAt ? new Date(profile.updatedAt).toLocaleString('ar-EG') : 'غير محدد'}
                                                                </div>
                                                            </div>
                                                            {isActive && <span className="shrink-0 bg-blue-600 text-white rounded-full px-2 py-1 text-[10px] font-black">محمّلة</span>}
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-2 mt-3">
                                                            <button type="button" onClick={() => loadPrintProfile(profile.id)} className="bg-white hover:bg-blue-50 border border-blue-100 text-blue-700 rounded-xl py-2 text-xs font-black">
                                                                تحميل للتعديل
                                                            </button>
                                                            <button type="button" onClick={() => deletePrintProfileById(profile.id)} className="bg-white hover:bg-red-50 border border-red-100 text-red-700 rounded-xl py-2 text-xs font-black">
                                                                حذف
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="bg-yellow-50 border border-yellow-100 text-yellow-800 rounded-xl p-3 text-sm leading-6">
                                            لا توجد طرق محفوظة حتى الآن. اكتب اسم الطريقة ثم اضغط "حفظ كطريقة جديدة".
                                        </div>
                                    )}
                                </div>

                                </>
                                )}

                                {canShowBarcodeAutoPrint && (
                                <div className="bg-white border border-amber-200 rounded-2xl p-3">
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
                                        <div>
                                            <div className="font-black text-amber-950">الطباعة التلقائية بعد تسجيل جواز جديد</div>
                                            <div className="text-xs text-amber-700 mt-1">تعمل بعد الحفظ من: إضافة جواز جديد، الإدخال السريع، والمعالجة المجمعة.</div>
                                        </div>
                                        <span className="text-[11px] bg-emerald-50 border border-emerald-100 text-emerald-800 rounded-full px-3 py-1 font-bold self-start">محلي لهذا الجهاز فقط</span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                                        <label className={`text-xs flex items-center gap-2 rounded-xl px-3 py-3 font-bold border ${settings.autoPrintOnCreate ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                                            <input type="checkbox" checked={!!settings.autoPrintOnCreate} onChange={(e) => updateAutoPrintSetting('autoPrintOnCreate', e.target.checked)} />
                                            تفعيل الطباعة تلقائيًا لهذا الجهاز
                                        </label>
                                        <label className="text-xs text-amber-900 md:col-span-2">طريقة الطباعة المستخدمة تلقائيًا
                                            <select
                                                value={settings.autoPrintProfileId || ''}
                                                onChange={(e) => updateAutoPrintSetting('autoPrintProfileId', e.target.value)}
                                                disabled={!settings.autoPrintOnCreate}
                                                className="mt-1 w-full border border-amber-200 rounded-lg px-3 py-2 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-400"
                                            >
                                                <option value="">الطريقة الافتراضية الحالية</option>
                                                {savedProfiles.map(profile => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
                                            </select>
                                            <span className="block text-[11px] text-amber-700 mt-1">الاختيار يتسجل فورًا في متصفح هذا الجهاز فقط، ولن يتفعل على باقي الأجهزة إلا لو المستخدم فعّله بنفسه.</span>
                                        </label>
                                    </div>
                                    {settings.autoPrintOnCreate && (
                                        <div className="mt-3 bg-emerald-50 border border-emerald-100 text-emerald-800 rounded-xl p-3 text-xs leading-5">
                                            الطباعة التلقائية مفعلة الآن على هذا الجهاز. الطريقة المستخدمة: <b>{selectedAutoProfile?.name || 'الطريقة الافتراضية الحالية'}</b>.
                                        </div>
                                    )}
                                </div>
                                )}
                            </div>
                            )}

                            {canShowBarcodeLabelPrinter && (
                            <BarcodeElementCard settings={settings} updateSetting={updateSetting} title="1) إعدادات الليبل والطابعة" color="bg-slate-50" note="هذا القسم خاص بالاستيكر كاملًا وبالاتصال بالطابعة. لا يغير مكان الاسم أو ID أو الباركود.">
                                <BarcodeSettingHint>
                                    <b>عرض/ارتفاع الليبل:</b> مقاس الاستيكر الحقيقي. عندك غالبًا 38 × 24 mm.
                                    <br />
                                    <b>طريقة الطباعة المركزية:</b> تجعل أي موبايل أو كمبيوتر على نفس الشبكة يرسل أمر الطباعة لجهاز السيرفر المتصل بالطابعة.
                                    <br />
                                    <b>Logical Name:</b> اسم الطابعة داخل أداة Web Print SDK على جهاز السيرفر، وغالبًا Printer1.
                                    <br />
                                    <b>DPI:</b> دقة الطابعة، غالبًا 203. لا تغيّره إلا لو طابعتك 300 DPI.
                                </BarcodeSettingHint>
                                <div className="md:col-span-4 flex flex-wrap gap-2">
                                    <button type="button" onClick={() => setPresetSize(38, 24)} className="px-3 py-2 rounded-xl bg-white border hover:bg-gray-50 text-xs font-bold">مقاس 38×24</button>
                                    <button type="button" onClick={() => setPresetSize(50, 20)} className="px-3 py-2 rounded-xl bg-white border hover:bg-gray-50 text-xs font-bold">مقاس 50×20</button>
                                    <button type="button" onClick={() => setPresetSize(40, 18)} className="px-3 py-2 rounded-xl bg-white border hover:bg-gray-50 text-xs font-bold">مقاس 40×18</button>
                                </div>
                                {renderNumberInput('عرض الليبل', 'labelWidthMm', 1, 0.1, 'عرض الاستيكر كاملًا بالمليمتر')}
                                {renderNumberInput('ارتفاع الليبل', 'labelHeightMm', 1, 0.1, 'ارتفاع الاستيكر كاملًا بالمليمتر')}
                                {renderNumberInput('DPI', 'dpi', 100, 1, '203 للطابعات الشائعة')}
                                {renderNumberInput('عدد النسخ', 'printCopies', 1, 1, 'عدد الاستيكرات عند الطباعة')}
                                {renderNumberInput('الكثافة', 'density', 0, 1, 'زيادة السواد/وضوح الطباعة')}
                                {renderNumberInput('السرعة', 'speed', 0, 1, 'لو الطباعة باهتة قلل السرعة')}
                                {renderNumberInput('Gap dots', 'gapDots', 0, 1, 'مسافة بين الليبلات بالنقط')}
                                {renderNumberInput('Port', 'sdkPort', 1, 1, 'من أداة Web Print SDK على جهاز السيرفر غالبًا 18080')}
                                <label className="text-xs text-gray-600 md:col-span-2"><span className="font-semibold text-gray-700">Logical Name</span>
                                    <span className="block text-[11px] text-gray-400 mt-0.5">يستخدم فقط مع Web Print SDK أو الطباعة المركزية القديمة، مثل Printer1</span>
                                    <input value={settings.logicalName || ''} onChange={(e) => updateSetting('logicalName', e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" placeholder="Printer1" />
                                </label>
                                <label className="text-xs text-gray-600 md:col-span-2"><span className="font-semibold text-gray-700">Masar Print Agent</span>
                                    <span className="block text-[11px] text-gray-400 mt-0.5">اختار الجهاز المتصل بالطابعة USB. لو القائمة فاضية شغّل Agent على جهاز الطابعة واضغط تحديث.</span>
                                    <div className="mt-1 flex gap-2">
                                        <select value={settings.printAgentId || ''} onChange={(e) => {
                                            const agent = printAgents.find(item => String(item.agentId) === String(e.target.value));
                                            const printers = normalizeMasarAgentPrinters(agent?.printers);
                                            const firstPrinter = agent?.defaultPrinter || printers.find(printer => printer.isDefault)?.name || printers[0]?.name || '';
                                            keepBarcodeModalView(() => {
                                                setSettings(prev => {
                                                    const next = saveStoredBarcodeSettings({
                                                        ...prev,
                                                        printEngine: 'agent',
                                                        printAgentId: e.target.value,
                                                        printAgentPrinterName: firstPrinter
                                                    });
                                                    return next;
                                                });
                                            });
                                        }} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
                                            <option value="">اختار Agent</option>
                                            {printAgents.map(agent => (
                                                <option key={agent.agentId} value={agent.agentId}>
                                                    {agent.agentName || agent.computerName || agent.agentId} - {isMasarAgentOnline(agent) ? 'Online' : 'Offline'}{agent.computerName ? ` - ${agent.computerName}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <button type="button" onClick={refreshPrintAgents} disabled={loadingAgents} className="px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-bold disabled:opacity-50">تحديث</button>
                                    </div>
                                </label>
                                <label className="text-xs text-gray-600 md:col-span-2"><span className="font-semibold text-gray-700">طابعة Agent</span>
                                    <span className="block text-[11px] text-gray-400 mt-0.5">اختار الطابعة التي سيتم حفظها داخل طريقة الطباعة الحالية.</span>
                                    <select
                                        value={settings.printAgentPrinterName || ''}
                                        onChange={(e) => updateSetting('printAgentPrinterName', e.target.value)}
                                        className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                                        disabled={!selectedPrintAgent || selectedPrintAgentPrinters.length === 0}
                                    >
                                        <option value="">{selectedPrintAgent ? 'اختار الطابعة' : 'اختار Agent أولًا'}</option>
                                        {selectedPrintAgentPrinters.map(printer => (
                                            <option key={printer.name} value={printer.name}>{printer.name}{printer.isDefault ? ' - Default' : ''}</option>
                                        ))}
                                    </select>
                                    {settings.printAgentPrinterName && (
                                        <span className="block text-[11px] text-emerald-700 mt-1">سيتم حفظ هذه الطابعة مع طريقة الطباعة.</span>
                                    )}
                                </label>
                                <label className="text-xs text-gray-600"><span className="font-semibold text-gray-700">طريقة الطباعة</span>
                                    <select value={settings.printEngine} onChange={(e) => updateSetting('printEngine', e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
                                        {BARCODE_PRINT_ENGINE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                                    </select>
                                </label>
                            </BarcodeElementCard>
                            )}

                            {canShowBarcodeName && (
                            <BarcodeElementCard settings={settings} updateSetting={updateSetting} title="2) عنصر الاسم فقط" color="bg-blue-50 border-blue-100" enabledKey="showName" note="كل أرقام هذا القسم تخص الاسم فقط. اسحب مربع الاسم الأزرق من المعاينة أو استخدم أزرار التحريك.">
                                {renderMovePanel('name', 'bg-blue-50/60')}
                                {renderNumberInput('X الاسم', 'nameXmm', 0, 0.1, 'المسافة من يسار الاستيكر')}
                                {renderNumberInput('Y الاسم', 'nameYmm', 0, 0.1, 'المسافة من أعلى الاستيكر')}
                                {renderNumberInput('عرض الاسم', 'nameWidthMm', 1, 0.1, 'عرض صندوق الاسم')}
                                {renderNumberInput('ارتفاع الاسم', 'nameHeightMm', 1, 0.1, 'ارتفاع صندوق الاسم')}
                                {renderNumberInput('حجم خط الاسم', 'nameFontSize', 5, 0.5, 'كبر أو صغر الاسم فقط')}
                                {renderAlignSelect('محاذاة الاسم', 'nameAlign')}
                                <label className="text-xs flex items-center gap-2 pt-6"><input type="checkbox" checked={!!settings.nameBold} onChange={(e) => updateSetting('nameBold', e.target.checked)} /> بولد الاسم</label>
                            </BarcodeElementCard>
                            )}

                            {canShowBarcodeId && (
                            <BarcodeElementCard settings={settings} updateSetting={updateSetting} title="3) عنصر ID فقط" color="bg-emerald-50 border-emerald-100" enabledKey="showId" note="كل أرقام هذا القسم تخص كود ID فقط مثل B-110. لا تؤثر على الاسم أو الباركود.">
                                {renderMovePanel('id', 'bg-emerald-50/60')}
                                {renderNumberInput('X ID', 'idXmm', 0, 0.1, 'المسافة من يسار الاستيكر')}
                                {renderNumberInput('Y ID', 'idYmm', 0, 0.1, 'المسافة من أعلى الاستيكر')}
                                {renderNumberInput('عرض ID', 'idWidthMm', 1, 0.1, 'عرض صندوق ID')}
                                {renderNumberInput('ارتفاع ID', 'idHeightMm', 1, 0.1, 'ارتفاع صندوق ID')}
                                {renderNumberInput('حجم خط ID', 'idFontSize', 5, 0.5, 'كبر أو صغر ID فقط')}
                                {renderAlignSelect('محاذاة ID', 'idAlign')}
                                <label className="text-xs flex items-center gap-2 pt-6"><input type="checkbox" checked={!!settings.idBold} onChange={(e) => updateSetting('idBold', e.target.checked)} /> بولد ID</label>
                            </BarcodeElementCard>
                            )}

                            {canShowBarcodeCode && (
                            <BarcodeElementCard settings={settings} updateSetting={updateSetting} title="4) عنصر الباركود أو QR فقط" color="bg-purple-50 border-purple-100" enabledKey="showBarcode" note="اختار Barcode للكود القصير مثل B-110. اختار QR لو هتخزن بيانات كثيرة أو عربي داخل الرمز.">
                                <label className="text-xs text-gray-600 md:col-span-2"><span className="font-semibold text-gray-700">نوع الرمز</span>
                                    <span className="block text-[11px] text-gray-400 mt-0.5">اختر Barcode أو QR يدويًا. Barcode الخطي يطبع كود قصير فقط، وQR مناسب للنصوص الطويلة أو العربي</span>
                                    <select value={settings.codeType} onChange={(e) => updateSetting('codeType', e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
                                        <option value="barcode">Barcode Code128</option>
                                        <option value="qr">QR Code</option>
                                    </select>
                                </label>
                                {effectivePreviewCodeType === 'barcode' ? (
                                    <>
                                        {renderMovePanel('barcode', 'bg-purple-50/60')}
                                        {renderNumberInput('X الباركود', 'barcodeXmm', 0, 0.1, 'المسافة من يسار الاستيكر')}
                                        {renderNumberInput('Y الباركود', 'barcodeYmm', 0, 0.1, 'المسافة من أعلى الاستيكر')}
                                        {renderNumberInput('ارتفاع الباركود', 'barcodeHeightMm', 1, 0.1, 'طول خطوط الباركود')}
                                        {renderNumberInput('عرض المعاينة', 'barcodePreviewWidthMm', 1, 0.1, 'لترتيب المعاينة فقط؛ العرض الحقيقي يتأثر بطول الكود')}
                                        {renderNumberInput('سُمك الخط الرفيع', 'barcodeNarrow', 1, 1, 'كلما زاد الرقم زاد عرض الخطوط')}
                                        {renderNumberInput('سُمك الخط العريض', 'barcodeWide', 2, 1, 'للقراءة الأفضل اجعله أكبر من الرفيع؛ مثال 5 مع رفيع 2')}
                                    </>
                                ) : (
                                    <>
                                        {renderMovePanel('qr', 'bg-purple-50/60')}
                                        {renderNumberInput('X QR', 'qrXmm', 0, 0.1, 'المسافة من يسار الاستيكر')}
                                        {renderNumberInput('Y QR', 'qrYmm', 0, 0.1, 'المسافة من أعلى الاستيكر')}
                                        {renderNumberInput('حجم QR', 'qrSize', 3, 1, 'ابدأ من 6 أو 7 حتى يظهر واضحًا للماسح')}
                                    </>
                                )}
                            </BarcodeElementCard>
                            )}

                            {canShowBarcodeContent && (
                            <div className="border rounded-2xl p-4">
                                <h3 className="font-black text-gray-900 mb-3">5) محتوى الرمز</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <label className="text-xs text-gray-600">وضع الرمز
                                        <select value={settings.contentMode} onChange={(e) => updateSetting('contentMode', e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                            <option value="search">بحث بالمسدس فقط - بدون IP</option>
                                            <option value="view">فتح معاينة بالمسدس فقط - بدون IP</option>
                                            <option value="edit">فتح تعديل بالمسدس فقط - بدون IP</option>
                                            <option value="text">بيانات مكتوبة فقط</option>
                                        </select>
                                    </label>
                                    <div className="text-xs text-gray-500 bg-gray-50 border rounded-xl p-3 leading-5">
                                        محتوى المسح الحالي: <span className="font-bold text-gray-800">{settings.contentMode === 'text' ? `${selectedFieldsCount} حقول مختارة - ${barcodeContentInfo.printedByteLength}/${barcodeContentInfo.maxBytes} byte` : barcodeContent}</span><br />
                                        {effectivePreviewCodeType === 'barcode' && settings.contentMode === 'text' ? (
                                            <span className="text-amber-700 font-bold">تنبيه: أنت اخترت Barcode خطي. البيانات المكتوبة الطويلة أو العربية لا تناسب Barcode 1D؛ اختر QR يدويًا لو تريد قراءتها بالموبايل.</span>
                                        ) : (
                                            <span className="text-green-700 font-bold">آمن: أوضاع البحث/المعاينة/التعديل تطبع أمرًا قصيرًا فقط مثل EDIT:H-2022 بدون أي IP أو رابط. الهاتف يراه كنص، ومسدس الباركود مع Masar Agent ينفذه.</span>
                                        )}
                                        {barcodeContentInfo.isTooLong && (
                                            <div className="mt-2 text-red-700 bg-red-50 border border-red-100 rounded-lg p-2 font-bold">
                                                النص المختار أطول من سعة QR المباشر، لذلك سيتم اختصاره بدون كسر الحروف. قلّل الحقول المختارة لو عايز يظهر كل النص عند المسح.
                                            </div>
                                        )}
                                        {settings.contentMode === 'text' && (
                                            <pre className="mt-2 max-h-24 overflow-auto whitespace-pre-wrap bg-white border rounded-lg p-2 text-[11px] text-gray-700">{barcodeContent}</pre>
                                        )}
                                    </div>
                                </div>

                                {settings.contentMode === 'text' && (
                                    <div className="mt-4 bg-slate-50 rounded-xl p-3 border">
                                        <div className="font-bold text-sm text-gray-800 mb-2">البيانات التي تدخل داخل الرمز</div>
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            <button type="button" onClick={() => updateSetting('includeFields', ['company', 'nameArabic', 'passportNumber'])} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold">اختيار أساسي آمن</button>
                                            <button type="button" onClick={() => updateSetting('includeFields', BARCODE_FIELD_OPTIONS.map(field => field.key))} className="px-3 py-1.5 rounded-lg bg-gray-700 text-white text-xs font-bold">تحديد الكل</button>
                                            <button type="button" onClick={() => updateSetting('includeFields', [])} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-bold border">إلغاء الكل</button>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                            {BARCODE_FIELD_OPTIONS.map(field => (
                                                <label key={field.key} className="text-xs flex items-center gap-2 bg-white border rounded-lg px-2 py-2">
                                                    <input type="checkbox" checked={settings.includeFields?.includes(field.key)} onChange={() => toggleField(field.key)} />
                                                    {field.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            )}

                            {canShowBarcodeAgentHint && (
                            <div className="border rounded-2xl p-4 bg-blue-50 border-blue-100 text-sm text-blue-900">
                                <div className="font-bold mb-1">مهم للتشغيل على أكثر من جهاز</div>
                                <div>أفضل وضع تجاري الآن هو Masar Print Agent: ثبته على أي جهاز عليه طابعة USB، وسيطبع كل المستخدمين من الموبايل أو الكمبيوتر بدون Web Print SDK وبدون شرط توصيل الطابعة بالسيرفر.</div>
                            </div>
                            )}

                            {!hasBarcodeMainContent && (
                                <div className="border border-dashed border-gray-300 rounded-2xl p-6 text-center text-gray-500 bg-gray-50">
                                    لا توجد مناطق مفعلة داخل نافذة ضبط الباركود لهذا المستخدم. فعّل "نافذة الباركود - عرض كل مناطق النافذة" أو فعّل منطقة واحدة على الأقل من صفحة المستخدمين، ثم سجّل خروج ودخول للمستخدم.
                                </div>
                            )}
                        </div>
                    </div>
        </div>
    );

    if (pageMode) {
        return (
            <div className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top_left,#dbeafe,transparent_35%),linear-gradient(180deg,#f8fafc,#ffffff)] py-0 md:py-5" dir="rtl">
                <div className="max-w-[1700px] mx-auto px-0 md:px-5">
                    {barcodeWorkspace}
                </div>
            </div>
        );
    }

    return (
        <BodyPortal>
            <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-0 md:p-4" dir="rtl">
                {barcodeWorkspace}
            </div>
        </BodyPortal>
    );
};


const PassportActivityLogModal = ({
    isOpen,
    onClose,
    logs,
    users,
    companies = [],
    delegates = [],
    professions = [],
    cleanupSettings,
    onSaveCleanupSettings,
    onManualCleanup,
    filters,
    setFilters,
    onRefresh,
    isLoading
}) => {
    const [manualDeleteMode, setManualDeleteMode] = React.useState('older_than_month');
    const [selectedCleanupMode, setSelectedCleanupMode] = React.useState(cleanupSettings?.cleanupMode || 'off');

    React.useEffect(() => {
        setSelectedCleanupMode(cleanupSettings?.cleanupMode || 'off');
    }, [cleanupSettings?.cleanupMode]);

    if (!isOpen) return null;

    const parseDetails = (details) => {
        if (!details) return null;
        if (typeof details === 'object') return details;
        try {
            return JSON.parse(details);
        } catch {
            return null;
        }
    };

    const formatDateTime = (date) => {
        if (!date) return '—';
        try {
            return new Date(date).toLocaleString('ar-EG', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return date;
        }
    };

    const normalizeId = (value) => String(value ?? '').trim();

    const parseListValue = (value) => {
        if (value === undefined || value === null || value === '') return [];
        if (Array.isArray(value)) return value;
        const text = String(value).trim();
        if (!text || text === '[]') return [];
        try {
            const parsed = JSON.parse(text);
            return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
            return text.split(',').map(item => item.trim()).filter(Boolean);
        }
    };

    const companyNameById = (id) => {
        const item = companies.find(company => normalizeId(company.CompanyID) === normalizeId(id));
        return item?.CompanyName || (id ? `شركة غير معروفة #${id}` : '—');
    };

    const delegateNameById = (id) => {
        const item = delegates.find(delegate => normalizeId(delegate.DelegateID) === normalizeId(id));
        return item?.DelegateName || (id ? `مندوب غير معروف #${id}` : '—');
    };

    const professionNameById = (id) => {
        const item = professions.find(profession => normalizeId(profession.ProfessionID) === normalizeId(id));
        return item?.ProfessionName || (id ? `مهنة غير معروفة #${id}` : '—');
    };

    const formatChangeValue = (field, value) => {
        if (value === undefined || value === null || value === '') return '—';
        const text = String(value).trim();
        if (!text || text === 'null' || text === 'undefined') return '—';

        switch (field) {
            case 'DelegateID':
                return delegateNameById(text);
            case 'CompanyID':
                return companyNameById(text);
            case 'PassportProfessionID':
            case 'TravelProfessionID':
                return professionNameById(text);
            case 'PreviousCompanies': {
                const ids = parseListValue(text);
                return ids.length ? ids.map(companyNameById).join('، ') : '—';
            }
            default:
                return text;
        }
    };

    const actionColors = {
        create: 'bg-green-50 text-green-700 border-green-200',
        update: 'bg-blue-50 text-blue-700 border-blue-200',
        delegate_change: 'bg-purple-50 text-purple-700 border-purple-200',
        delivery: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        delivery_revert: 'bg-orange-50 text-orange-700 border-orange-200',
        note: 'bg-yellow-50 text-yellow-700 border-yellow-200',
        image: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        new_trip: 'bg-pink-50 text-pink-700 border-pink-200',
        new_trip_created: 'bg-pink-50 text-pink-700 border-pink-200',
        delete: 'bg-red-50 text-red-700 border-red-200',
        cleanup_settings: 'bg-slate-50 text-slate-700 border-slate-200',
        cleanup_manual: 'bg-red-50 text-red-700 border-red-200'
    };

    const cleanupModeLabel = (mode) => {
        switch (mode) {
            case 'weekly':
                return 'أسبوعي - يحتفظ بآخر 7 أيام';
            case 'monthly':
                return 'شهري - يحتفظ بآخر 30 يوم';
            default:
                return 'متوقف';
        }
    };

    const manualDeleteModeLabel = (mode) => {
        switch (mode) {
            case 'all':
                return 'حذف السجل بالكامل';
            case 'older_than_week':
                return 'حذف الأقدم من أسبوع';
            case 'older_than_month':
                return 'حذف الأقدم من شهر';
            case 'older_than_3_months':
                return 'حذف الأقدم من 3 شهور';
            case 'older_than_year':
                return 'حذف الأقدم من سنة';
            default:
                return 'حذف يدوي';
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-[9999] flex items-center justify-center p-0 md:p-4" dir="rtl">
            <div className="bg-white rounded-none md:rounded-2xl shadow-2xl w-full md:max-w-6xl h-[100dvh] md:h-auto md:max-h-[90vh] flex flex-col overflow-hidden">
                <div className="px-4 py-3 md:px-6 md:py-4 border-b bg-gradient-to-l from-slate-900 to-slate-700 text-white flex items-center justify-between gap-3">
                    <div>
                        <h2 className="text-xl md:text-2xl font-bold">سجل حركات الجوازات</h2>
                        <p className="hidden md:block text-sm text-slate-200 mt-1">كل الحركات داخل صفحة إدارة جوازات السفر مع اسم المستخدم والتوقيت</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition">
                        <X size={22} />
                    </button>
                </div>

                <div className="p-3 md:p-4 border-b bg-gray-50 grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3">
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">المستخدم</label>
                        <select
                            value={filters.userId}
                            onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        >
                            <option value="">كل المستخدمين</option>
                            {users.map(user => (
                                <option key={user.UserId} value={user.UserId}>
                                    {user.FullName || user.Username}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs text-gray-500 mb-1">اليوم</label>
                        <input
                            type="date"
                            value={filters.date}
                            onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                    </div>

                    <div>
                        <label className="block text-xs text-gray-500 mb-1">نوع الحركة</label>
                        <select
                            value={filters.actionType}
                            onChange={(e) => setFilters(prev => ({ ...prev, actionType: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        >
                            <option value="">كل الحركات</option>
                            <option value="create">إضافة جواز</option>
                            <option value="update">تعديل بيانات</option>
                            <option value="delegate_change">تغيير مندوب</option>
                            <option value="delivery">تسليم</option>
                            <option value="delivery_revert">تراجع عن التسليم</option>
                            <option value="note">ملاحظات</option>
                            <option value="image">صور</option>
                            <option value="new_trip">سفرة جديدة</option>
                            <option value="delete">حذف جواز</option>
                            <option value="cleanup_settings">تعديل إعدادات التنظيف</option>
                            <option value="cleanup_manual">تنظيف يدوي للسجل</option>
                        </select>
                    </div>

                    <div className="col-span-2 md:col-span-1">
                        <label className="block text-xs text-gray-500 mb-1">بحث</label>
                        <div className="relative">
                            <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                value={filters.search}
                                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                placeholder="اسم، رقم جواز، حركة..."
                                className="w-full border border-gray-300 rounded-lg py-2 pr-9 pl-3 text-sm"
                            />
                        </div>
                    </div>

                    <div className="col-span-2 md:col-span-1 flex items-end gap-2">
                        <button
                            onClick={onRefresh}
                            disabled={isLoading}
                            className="flex-1 md:flex-none bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                        >
                            {isLoading ? 'جاري التحميل...' : 'تطبيق'}
                        </button>
                        <button
                            onClick={() => setFilters({ userId: '', date: '', actionType: '', search: '' })}
                            className="flex-1 md:flex-none bg-white hover:bg-gray-100 text-gray-700 border px-4 py-2 rounded-lg text-sm"
                        >
                            مسح
                        </button>
                    </div>
                </div>

                <details className="md:hidden border-b bg-white">
                    <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-slate-800 flex items-center justify-between">
                        <span>إعدادات السجل والتنظيف</span>
                        <span className="text-xs text-slate-500">اضغط للفتح</span>
                    </summary>
                    <div className="px-4 pb-4 space-y-3 bg-gray-50">
                        <div className="border border-blue-100 bg-blue-50 rounded-xl p-3">
                            <h3 className="font-semibold text-blue-900 text-sm">التنظيف التلقائي</h3>
                            <p className="text-xs text-blue-700 mt-1 leading-5">
                                الحالي: {cleanupModeLabel(cleanupSettings?.cleanupMode || 'off')}
                                {cleanupSettings?.lastCleanupAt ? ` • آخر تنظيف: ${formatDateTime(cleanupSettings.lastCleanupAt)}` : ''}
                            </p>
                            <div className="mt-3 space-y-2">
                                <select
                                    value={selectedCleanupMode}
                                    onChange={(e) => setSelectedCleanupMode(e.target.value)}
                                    className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white"
                                >
                                    <option value="off">متوقف</option>
                                    <option value="weekly">أسبوعي - يحتفظ بآخر 7 أيام</option>
                                    <option value="monthly">شهري - يحتفظ بآخر 30 يوم</option>
                                </select>
                                <button
                                    onClick={() => onSaveCleanupSettings(selectedCleanupMode)}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
                                >
                                    حفظ الإعداد
                                </button>
                            </div>
                        </div>

                        <div className="border border-red-100 bg-red-50 rounded-xl p-3">
                            <h3 className="font-semibold text-red-900 text-sm">حذف يدوي للسجل</h3>
                            <p className="text-xs text-red-700 mt-1">استخدمه عند الحاجة فقط.</p>
                            <div className="mt-3 space-y-2">
                                <select
                                    value={manualDeleteMode}
                                    onChange={(e) => setManualDeleteMode(e.target.value)}
                                    className="w-full border border-red-200 rounded-lg px-3 py-2 text-sm bg-white"
                                >
                                    <option value="older_than_week">حذف الأقدم من أسبوع</option>
                                    <option value="older_than_month">حذف الأقدم من شهر</option>
                                    <option value="older_than_3_months">حذف الأقدم من 3 شهور</option>
                                    <option value="older_than_year">حذف الأقدم من سنة</option>
                                    <option value="all">حذف السجل بالكامل</option>
                                </select>
                                <button
                                    onClick={() => {
                                        const label = manualDeleteModeLabel(manualDeleteMode);
                                        const warning = manualDeleteMode === 'all'
                                            ? `${label}

تحذير: سيتم حذف كل سجل حركات الجوازات. هل أنت متأكد؟`
                                            : `${label}

هل تريد تنفيذ الحذف الآن؟`;
                                        if (window.confirm(warning)) {
                                            onManualCleanup(manualDeleteMode);
                                        }
                                    }}
                                    className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm"
                                >
                                    حذف الآن
                                </button>
                            </div>
                        </div>
                    </div>
                </details>

                <div className="hidden md:block px-4 py-3 border-b bg-white">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="border border-blue-100 bg-blue-50 rounded-xl p-4">
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div>
                                    <h3 className="font-semibold text-blue-900">التنظيف التلقائي</h3>
                                    <p className="text-xs text-blue-700 mt-1">
                                        الحالي: {cleanupModeLabel(cleanupSettings?.cleanupMode || 'off')}
                                        {cleanupSettings?.lastCleanupAt ? ` • آخر تنظيف: ${formatDateTime(cleanupSettings.lastCleanupAt)}` : ''}
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <select
                                    value={selectedCleanupMode}
                                    onChange={(e) => setSelectedCleanupMode(e.target.value)}
                                    className="flex-1 border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white"
                                >
                                    <option value="off">متوقف</option>
                                    <option value="weekly">أسبوعي - يحتفظ بآخر 7 أيام</option>
                                    <option value="monthly">شهري - يحتفظ بآخر 30 يوم</option>
                                </select>
                                <button
                                    onClick={() => onSaveCleanupSettings(selectedCleanupMode)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
                                >
                                    حفظ الإعداد
                                </button>
                            </div>
                        </div>

                        <div className="border border-red-100 bg-red-50 rounded-xl p-4">
                            <div className="mb-3">
                                <h3 className="font-semibold text-red-900">حذف يدوي للسجل</h3>
                                <p className="text-xs text-red-700 mt-1">استخدمه عند الحاجة لتخفيف حجم قاعدة البيانات.</p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <select
                                    value={manualDeleteMode}
                                    onChange={(e) => setManualDeleteMode(e.target.value)}
                                    className="flex-1 border border-red-200 rounded-lg px-3 py-2 text-sm bg-white"
                                >
                                    <option value="older_than_week">حذف الأقدم من أسبوع</option>
                                    <option value="older_than_month">حذف الأقدم من شهر</option>
                                    <option value="older_than_3_months">حذف الأقدم من 3 شهور</option>
                                    <option value="older_than_year">حذف الأقدم من سنة</option>
                                    <option value="all">حذف السجل بالكامل</option>
                                </select>
                                <button
                                    onClick={() => {
                                        const label = manualDeleteModeLabel(manualDeleteMode);
                                        const warning = manualDeleteMode === 'all'
                                            ? `${label}\n\nتحذير: سيتم حذف كل سجل حركات الجوازات. هل أنت متأكد؟`
                                            : `${label}\n\nهل تريد تنفيذ الحذف الآن؟`;
                                        if (window.confirm(warning)) {
                                            onManualCleanup(manualDeleteMode);
                                        }
                                    }}
                                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm"
                                >
                                    حذف الآن
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-3 py-2 md:px-6 md:py-3 bg-white border-b flex justify-between items-center">
                    <span className="text-xs md:text-sm text-gray-500">عدد النتائج: {logs.length}</span>
                    <button
                        onClick={() => setFilters(prev => ({ ...prev, date: new Date().toISOString().split('T')[0] }))}
                        className="text-xs md:text-sm bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-100"
                    >
                        حركات اليوم فقط
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 p-3 md:p-4 space-y-3 bg-gray-50 md:bg-white">
                    {logs.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            {isLoading ? 'جاري تحميل السجل...' : 'لا توجد حركات مطابقة للفلاتر الحالية'}
                        </div>
                    ) : logs.map(log => {
                        const details = parseDetails(log.Details);
                        const changes = Array.isArray(details?.changes) ? details.changes : [];
                        return (
                            <div key={log.LogID} className="border border-gray-200 rounded-xl p-3 md:p-4 hover:shadow-md transition bg-white">
                                <div className="flex flex-wrap justify-between gap-2 md:gap-3">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
                                            <span className={`text-xs px-2.5 py-1 rounded-full border ${actionColors[log.ActionType] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                                                {log.ActionTitle}
                                            </span>
                                            <span className="text-sm font-semibold text-gray-900">
                                                {log.PassportName || 'جواز غير محدد'}
                                            </span>
                                            {log.PassportNumber && <span className="text-xs text-gray-500">({log.PassportNumber})</span>}
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            بواسطة: <span className="font-medium text-gray-700">{log.UserName || 'غير محدد'}</span>
                                            <span className="mx-2">•</span>
                                            {formatDateTime(log.CreatedAt)}
                                        </p>
                                    </div>
                                    {log.PassportID && (
                                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-lg self-start">
                                            ID: {log.PassportID}
                                        </span>
                                    )}
                                </div>

                                {details?.message && (
                                    <p className="mt-3 text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{details.message}</p>
                                )}

                                {changes.length > 0 && (
                                    <div className="mt-3 overflow-x-auto">
                                        <table className="w-full text-[11px] md:text-xs border border-gray-100 rounded-lg overflow-hidden">
                                            <thead className="bg-gray-50 text-gray-600">
                                                <tr>
                                                    <th className="text-right p-2">النقطة المعدلة</th>
                                                    <th className="text-right p-2">قبل</th>
                                                    <th className="text-right p-2">بعد</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {changes.slice(0, 8).map((change, index) => (
                                                    <tr key={`${log.LogID}-${change.field}-${index}`} className="border-t">
                                                        <td className="p-2 font-medium text-gray-700">{change.label || change.field}</td>
                                                        <td className="p-2 text-red-600 max-w-[160px] md:max-w-[260px] truncate" title={formatChangeValue(change.field, change.oldValue)}>
                                                            {formatChangeValue(change.field, change.oldValue)}
                                                        </td>
                                                        <td className="p-2 text-green-700 max-w-[160px] md:max-w-[260px] truncate" title={formatChangeValue(change.field, change.newValue)}>
                                                            {formatChangeValue(change.field, change.newValue)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {changes.length > 8 && (
                                            <p className="text-xs text-gray-400 mt-2">+ {changes.length - 8} تعديل آخر</p>
                                        )}
                                    </div>
                                )}

                                {details?.note && (
                                    <p className="mt-3 text-sm bg-yellow-50 text-yellow-800 border border-yellow-100 rounded-lg p-3">
                                        {details.note}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};


const NewTripCompanyModal = ({
    isOpen,
    onClose,
    onConfirm,
    companies = [],
    delegates = [],
    travelDurations = [],
    abilityTests = [],
    clientName = '',
    currentCompanyId = ''
}) => {
    const [step, setStep] = React.useState(0);
    const [selectedCompanyId, setSelectedCompanyId] = React.useState('');
    const [selectedTravelDuration, setSelectedTravelDuration] = React.useState('');
    const [selectedAbilityTest, setSelectedAbilityTest] = React.useState('');
    const [selectedDelegateId, setSelectedDelegateId] = React.useState(null); // null = لم يحدد بعد، none = بدون مندوب
    const [searchTerm, setSearchTerm] = React.useState('');
    const [delegateSearchTerm, setDelegateSearchTerm] = React.useState('');

    React.useEffect(() => {
        if (!isOpen) return;
        setStep(0);
        setSelectedCompanyId('');
        setSelectedTravelDuration('');
        setSelectedAbilityTest('');
        setSelectedDelegateId(null);
        setSearchTerm('');
        setDelegateSearchTerm('');
    }, [isOpen, clientName]);

    if (!isOpen) return null;

    const steps = [
        { label: 'الشركة', icon: '🏢' },
        { label: 'مدة السفر', icon: '⏱️' },
        { label: 'القدرات', icon: '✅' },
        { label: 'المندوب', icon: '👤' },
        { label: 'المراجعة', icon: '📋' }
    ];

    const allCompanies = Array.isArray(companies) ? companies : [];
    const allDelegates = Array.isArray(delegates) ? delegates : [];
    const normalizedSearch = String(searchTerm || '').trim().toLowerCase();
    const normalizedDelegateSearch = String(delegateSearchTerm || '').trim().toLowerCase();

    const visibleCompanies = allCompanies
        .filter(company => {
            if (!normalizedSearch) return true;
            return [company?.CompanyName, company?.CompanyCode, company?.Code, company?.Symbol, company?.CompanyID]
                .filter(Boolean).join(' ').toLowerCase().includes(normalizedSearch);
        })
        .sort((a, b) => String(a?.CompanyName || '').localeCompare(String(b?.CompanyName || ''), 'ar'));

    const visibleDelegates = allDelegates
        .filter(delegate => {
            if (!normalizedDelegateSearch) return true;
            return [delegate?.DelegateName, delegate?.DelegateID, delegate?.PhoneNumber, delegate?.Phone]
                .filter(Boolean).join(' ').toLowerCase().includes(normalizedDelegateSearch);
        })
        .sort((a, b) => String(a?.DelegateName || '').localeCompare(String(b?.DelegateName || ''), 'ar'));

    const selectedCompany = allCompanies.find(company => String(company?.CompanyID ?? '') === String(selectedCompanyId));
    const selectedCode = cleanCompanyCodeForBarcode(
        selectedCompany?.CompanyCode || selectedCompany?.Code || selectedCompany?.Symbol || selectedCompany?.CompanySymbol || selectedCompany?.ShortCode
    );
    const currentCompany = allCompanies.find(company => String(company?.CompanyID ?? '') === String(currentCompanyId ?? ''));
    const selectedDelegate = selectedDelegateId && selectedDelegateId !== 'none'
        ? allDelegates.find(delegate => String(delegate?.DelegateID ?? '') === String(selectedDelegateId))
        : null;

    const canContinue = (() => {
        if (step === 0) return !!selectedCompanyId;
        if (step === 1) return !!selectedTravelDuration;
        if (step === 2) return !!selectedAbilityTest;
        if (step === 3) return selectedDelegateId !== null;
        return true;
    })();

    const goNext = () => {
        if (!canContinue) return;
        setStep(current => Math.min(current + 1, steps.length - 1));
    };

    const goBack = () => setStep(current => Math.max(current - 1, 0));

    const finish = () => {
        if (!selectedCompanyId || !selectedTravelDuration || !selectedAbilityTest || selectedDelegateId === null) return;
        onConfirm && onConfirm({
            companyId: selectedCompanyId,
            travelDuration: selectedTravelDuration,
            abilityTest: selectedAbilityTest,
            delegateId: selectedDelegateId === 'none' ? null : selectedDelegateId
        });
    };

    const ChoiceButton = ({ selected, onClick, title, subtitle, badge }) => (
        <button
            type="button"
            onClick={onClick}
            className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-right rounded-2xl border transition ${selected ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-100' : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'}`}
        >
            <div className="min-w-0">
                <div className="font-black text-gray-900">{title}</div>
                {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
            </div>
            {badge && <span className={`shrink-0 rounded-xl px-3 py-1.5 text-sm font-black ${selected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>{badge}</span>}
        </button>
    );

    return (
        <BodyPortal>
            <div className="fixed inset-0 bg-black/60 z-[10050] flex items-center justify-center p-3" dir="rtl">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[94vh] overflow-hidden flex flex-col">
                    <div className="bg-gradient-to-l from-indigo-700 to-blue-700 text-white px-5 py-4 flex items-start justify-between gap-3">
                        <div>
                            <h2 className="text-xl font-black">إنشاء سفرة جديدة</h2>
                            <p className="text-sm text-white/80 mt-1">اختر بيانات السفرة خطوة بخطوة، ثم راجعها قبل الحفظ.</p>
                        </div>
                        <button type="button" onClick={onClose} className="p-2 rounded-full bg-white/15 hover:bg-white/25"><X size={21} /></button>
                    </div>

                    <div className="px-4 md:px-6 pt-4">
                        <div className="grid grid-cols-5 gap-1 md:gap-2">
                            {steps.map((item, index) => {
                                const completed = index < step;
                                const active = index === step;
                                return (
                                    <div key={item.label} className="min-w-0 text-center">
                                        <div className={`mx-auto w-9 h-9 rounded-full flex items-center justify-center font-black border-2 transition ${completed ? 'bg-green-600 border-green-600 text-white' : active ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-white border-gray-200 text-gray-400'}`}>
                                            {completed ? '✓' : item.icon}
                                        </div>
                                        <div className={`text-[10px] md:text-xs mt-1 truncate ${active ? 'text-blue-700 font-black' : completed ? 'text-green-700 font-bold' : 'text-gray-400'}`}>{item.label}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="h-1 bg-gray-100 rounded-full mt-3 overflow-hidden">
                            <div className="h-full bg-gradient-to-l from-blue-600 to-indigo-600 transition-all duration-300" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
                        </div>
                    </div>

                    <div className="p-4 md:p-6 overflow-y-auto flex-1">
                        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 mb-5">
                            <div className="text-xs text-blue-600 font-bold">العميل</div>
                            <div className="font-black text-gray-900 mt-1">{clientName || 'بدون اسم'}</div>
                            {currentCompany && <div className="text-sm text-gray-600 mt-2">الشركة الحالية: <span className="font-bold">{currentCompany.CompanyName}</span></div>}
                        </div>

                        {step === 0 && (
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-black text-gray-900">اختر شركة السفرة الجديدة</h3>
                                    <p className="text-sm text-gray-500 mt-1">كود الشركة المختارة هو الذي سيظهر في الباركود الجديد.</p>
                                </div>
                                <div className="relative">
                                    <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="ابحث باسم الشركة أو الكود..." className="w-full border border-gray-300 rounded-2xl py-3 pr-10 pl-3 focus:ring-2 focus:ring-blue-500" autoFocus />
                                </div>
                                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                                    {visibleCompanies.length ? visibleCompanies.map(company => {
                                        const value = String(company.CompanyID);
                                        const code = cleanCompanyCodeForBarcode(company?.CompanyCode || company?.Code || company?.Symbol || company?.CompanySymbol || company?.ShortCode) || 'بدون كود';
                                        return <ChoiceButton key={company.CompanyID} selected={String(selectedCompanyId) === value} onClick={() => setSelectedCompanyId(value)} title={company.CompanyName} subtitle={`رقم الشركة: ${company.CompanyID}`} badge={code} />;
                                    }) : <div className="p-8 text-center text-gray-500 border border-dashed rounded-2xl">لا توجد شركة مطابقة.</div>}
                                </div>
                            </div>
                        )}

                        {step === 1 && (
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-black text-gray-900">اختر مدة السفر</h3>
                                    <p className="text-sm text-gray-500 mt-1">سيتم حفظها مباشرة داخل الملف الجديد.</p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {(travelDurations || []).map(value => <ChoiceButton key={value} selected={selectedTravelDuration === value} onClick={() => setSelectedTravelDuration(value)} title={value} badge={selectedTravelDuration === value ? 'مختار' : ''} />)}
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-black text-gray-900">اختر نتيجة اختبار القدرات</h3>
                                    <p className="text-sm text-gray-500 mt-1">اختيارك سيُسجل على السفرة الجديدة فقط.</p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {(abilityTests || []).map(value => <ChoiceButton key={value} selected={selectedAbilityTest === value} onClick={() => setSelectedAbilityTest(value)} title={value} badge={selectedAbilityTest === value ? 'مختار' : ''} />)}
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-black text-gray-900">اختر المندوب</h3>
                                    <p className="text-sm text-gray-500 mt-1">يمكنك اختيار مندوب، أو تسجيل السفرة بدون مندوب حاليًا.</p>
                                </div>
                                <ChoiceButton selected={selectedDelegateId === 'none'} onClick={() => setSelectedDelegateId('none')} title="بدون مندوب حاليًا" subtitle="يمكن تعيين المندوب لاحقًا من تعديل الجواز" badge="اختياري" />
                                <div className="relative">
                                    <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input type="text" value={delegateSearchTerm} onChange={(e) => setDelegateSearchTerm(e.target.value)} placeholder="ابحث باسم المندوب..." className="w-full border border-gray-300 rounded-2xl py-3 pr-10 pl-3 focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                    {visibleDelegates.length ? visibleDelegates.map(delegate => {
                                        const value = String(delegate.DelegateID);
                                        return <ChoiceButton key={delegate.DelegateID} selected={String(selectedDelegateId) === value} onClick={() => setSelectedDelegateId(value)} title={delegate.DelegateName || `مندوب ${delegate.DelegateID}`} subtitle={`رقم المندوب: ${delegate.DelegateID}`} />;
                                    }) : <div className="p-7 text-center text-gray-500 border border-dashed rounded-2xl">لا يوجد مندوب مطابق.</div>}
                                </div>
                            </div>
                        )}

                        {step === 4 && (
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-black text-gray-900">راجع بيانات السفرة الجديدة</h3>
                                    <p className="text-sm text-gray-500 mt-1">بعد التأكيد سيطلب النظام كلمة السر ثم ينشئ رقم ملف جديد.</p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {[
                                        ['🏢 الشركة', selectedCompany?.CompanyName || 'غير محددة', selectedCode || 'بدون كود'],
                                        ['⏱️ مدة السفر', selectedTravelDuration || 'غير محددة', ''],
                                        ['✅ نتيجة القدرات', selectedAbilityTest || 'غير محددة', ''],
                                        ['👤 المندوب', selectedDelegateId === 'none' ? 'بدون مندوب حاليًا' : (selectedDelegate?.DelegateName || 'غير محدد'), '']
                                    ].map(([label, value, badge]) => (
                                        <div key={label} className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                                            <div className="text-xs font-bold text-gray-500">{label}</div>
                                            <div className="mt-2 flex items-center justify-between gap-2">
                                                <span className="font-black text-gray-900">{value}</span>
                                                {badge && <span className="bg-blue-600 text-white rounded-lg px-2.5 py-1 text-sm font-black">{badge}</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                                    سيتم أرشفة السفرة الحالية، وإنشاء ملف جديد بهذه البيانات، ثم طباعة الباركود تلقائيًا إذا كان الخيار مفعّلًا.
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="border-t bg-white px-4 md:px-6 py-4 flex items-center justify-between gap-3">
                        <button type="button" onClick={step === 0 ? onClose : goBack} className="px-4 md:px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                            {step === 0 ? 'إلغاء' : 'السابق'}
                        </button>
                        {step < steps.length - 1 ? (
                            <button type="button" disabled={!canContinue} onClick={goNext} className={`px-5 py-2.5 rounded-xl text-white font-black flex items-center justify-center gap-2 ${canContinue ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'}`}>
                                التالي
                            </button>
                        ) : (
                            <button type="button" onClick={finish} className="px-5 py-2.5 rounded-xl text-white font-black flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700">
                                <Plane size={18} /> تأكيد وإنشاء السفرة
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </BodyPortal>
    );
};

const PassportManagementContent = ({ barcodeSettingsPage = false } = {}) => {
    const { hasPermission, canViewOnly, canDeliver, canRevert } = usePermissions();
        const canChangeDelegate = hasPermission("تغيير المندوب");
    const canSmartScan      = hasPermission("المسح الذكي");
    const canManageWarnings = hasPermission("إعدادات التحذيرات والإشعارات");
    const canManageBarcodePrinting = hasPermission("ضبط الباركود وطرق الطباعة");
    const canViewActivityLog = hasPermission("عرض زر سجل الجوازات");
    const canDeliverySecure = hasPermission("أمان التسليم");
    const canControlStatuses= hasPermission("التحكم في الحالات");
    const canExtractData    = hasPermission("فك بيانات");
    const canManageBlacklist = hasPermission("إدارة قائمة المنع");
    const canShowPassportProfessionModeSetting = hasPermission("طريقة حقل المهنة بالجواز");

    // ===== نظام اللصق الذكي - activeImageField =====
    // نتتبع آخر حقل صور دخل عليه المستخدم
    const activeImageFieldRef = React.useRef(null);
    const [cropModal, setCropModal] = React.useState({ show: false, imageSrc: null, imageType: null });
    const [dragOverField, setDragOverField] = React.useState(null); // حقل السحب النشط

    // سحب صورة موجودة (dataURL أو path) وإسقاطها في حقل آخر
    const handleDropImage = React.useCallback(async (targetField, sourceUrl) => {
        if (!targetField || !sourceUrl) return;
        try {
            const res = await fetch(sourceUrl, { credentials: 'include' });
            const blob = await res.blob();
            const ext = blob.type.includes('png') ? 'png' : 'jpg';
            const file = new File([blob], `dropped.${ext}`, { type: blob.type });
            await handleImageUpload(targetField, file);
            showAlert(`✅ تم نسخ الصورة إلى ${getFieldLabel(targetField)}`, 'success');
        } catch {
            showAlert('تعذّر نسخ الصورة', 'error');
        }
    }, []);

    const getFieldLabel = (field) => {
       const labels = { PassportImage: 'صورة الجواز', PersonalImage: 'الصورة الشخصية', LicenseImage: 'صورة الرخصة (أمامي)', LicenseBackImage: 'صورة الرخصة (خلفي)', IDImage: 'صورة البطاقة (أمامي)', IDBackImage: 'صورة البطاقة (خلفي)' };
        return labels[field] || field;
    };

    const setActiveField = (field) => { activeImageFieldRef.current = field; };

    // اقتصاص الصور
    const openCropModal = (imageType) => {
     const pathMap = { PassportImage:'PassportImagePath', PersonalImage:'PersonalImagePath', LicenseImage:'LicenseImagePath', LicenseBackImage:'LicenseBackImagePath', IDImage:'IDImagePath', IDBackImage:'IDBackImagePath' };
        const pathKey = pathMap[imageType];
        if (!pathKey || !formData[pathKey]) return;
        setCropModal({ show: true, imageSrc: buildImageUrl(formData[pathKey]), imageType });
    };

    const handleCropComplete = async (croppedFile) => {
        if (!cropModal.imageType) return;
        await handleImageUpload(cropModal.imageType, croppedFile);
        setCropModal({ show: false, imageSrc: null, imageType: null });
    };

    // فك البيانات (إعادة المسح)
    const handleReExtract = () => {
        if (!formData?.PassportImagePath) { showAlert('لا توجد صورة لإعادة المسح', 'error'); return; }
        fetch(buildImageUrl(formData.PassportImagePath), { credentials: 'include' })
            .then(r => r.blob())
            .then(blob => {
                const file = new File([blob], 'passport.jpg', { type: blob.type || 'image/jpeg' });
                handleAdvancedOCR(file);
            })
            .catch(() => showAlert('تعذّر إعادة المسح', 'error'));
    };

    // أمان التسليم
    const [secureConfirmModal, setSecureConfirmModal] = React.useState({ show:false, action:null, title:'', description:'', inputValue:'' });
    const showSecureConfirm = (title, description, action) => setSecureConfirmModal({ show:true, action, title, description, inputValue:'' });
    const handleSecureConfirmChange = (val) => setSecureConfirmModal(prev => ({ ...prev, inputValue: val }));
    const handleSecureConfirmSubmit = async () => {
        if (await checkDeleteCode(secureConfirmModal.inputValue)) {
            secureConfirmModal.action && secureConfirmModal.action();
            setSecureConfirmModal({ show:false, action:null, title:'', description:'', inputValue:'' });
        } else { alert('رمز التأكيد غير صحيح'); }
    };
    const handleSecureConfirmCancel = () => setSecureConfirmModal({ show:false, action:null, title:'', description:'', inputValue:'' });
    const makeSampleBarcodePassport = () => ({
        PassportID: lastPassportID ? lastPassportID + 1 : 110,
        NameArabic: 'محمد أحمد السيد عامر',
        PassportNumber: 'A12345678',
        CompanyID: companies?.[0]?.CompanyID || '',
        PassportProfessionID: professions?.[0]?.ProfessionID || '',
        DelegateID: delegates?.[0]?.DelegateID || '',
        PhoneNumbers: ['01000000000'],
        PreviousCompanies: [],
        MedicalExamStatus: 'لائق',
        NetWorkDate: new Date().toISOString(),
        ApplicationNumber: '12345',
        FingerprintDate: '',
        VisaDate: '',
        TravelDate: ''
    });

    const handlePrintBarcode = (passport) => {
        setBarcodeSettingsOnly(false);
        setBarcodeModalPassport(passport);
    };

    const handleOpenBarcodeSettings = () => {
        if (!barcodeSettingsPage) {
            navigate('/passports/barcode-settings');
            return;
        }
        setBarcodeSettingsOnly(true);
        setBarcodeModalPassport(makeSampleBarcodePassport());
    };

    const handleOpenSavedPrintProfiles = (passport) => {
        setSavedPrintPassport(passport);
    };

      const gradientButtonStyle = {
        background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 4px 15px 0 rgba(116, 75, 162, 0.4)'
    };

    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [passports, setPassports] = useState([]);

    const getCurrentUserId = () => {
        try {
            const userData = localStorage.getItem('user');
            if (!userData) return null;
            const parsed = JSON.parse(userData);
            return parsed?.UserId || parsed?.UserID || parsed?.userId || null;
        } catch {
            return null;
        }
    };

    useEffect(() => {
        const rows = Array.isArray(passports) ? passports : [];
        const ids = rows
            .map(p => p && (p.PassportID || p.passportId || p.id))
            .filter(Boolean);

        if (membershipsAbortRef.current) {
            membershipsAbortRef.current.abort();
        }

        if (ids.length === 0) {
            setListMemberships({});
            return;
        }

        const controller = new AbortController();
        membershipsAbortRef.current = controller;

        // تحميل عضويات القوائم بعد عرض الجدول مباشرة، حتى لا يعطل فتح صفحة الجوازات.
        const timeoutId = setTimeout(() => {
            fetch(`${config.API_BASE_URL}/api/net-registrations/memberships/by-passport-ids`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ passportIds: ids, includeArchived: true }),
                signal: controller.signal
            })
                .then(r => r.json())
                .then(({ success, data }) => {
                    if (!controller.signal.aborted) {
                        if (success && data) setListMemberships(data);
                        else setListMemberships({});
                    }
                })
                .catch(err => {
                    if (err.name === 'AbortError') return;
                    console.error('Membership load error', err);
                    setListMemberships({});
                });
        }, 150);

        return () => {
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, [passports]);
    

    const [listMemberships, setListMemberships] = useState({});

    
    // دوال المعالجة المجمعة
const handleBatchUpload = (files) => {
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
        showAlert('يرجى اختيار صور صالحة', 'error');
        return;
    }
    
    if (imageFiles.length > 50) {
        showAlert('الحد الأقصى 50 صورة في المرة الواحدة', 'error');
        return;
    }
    
    const batchData = imageFiles.map((file, index) => ({
        id: Date.now() + index,
        file: file,
        name: file.name,
        status: 'waiting',
        extractedData: null,
        error: null,
        passportId: null
    }));
    
    setBatchImages(batchData);
    showAlert(`تم تحميل ${imageFiles.length} صورة للمعالجة`, 'success');
};

const startBatchProcessing = async () => {
    if (batchImages.length === 0) {
        showAlert('لا توجد صور للمعالجة', 'error');
        return;
    }
    
    setBatchProcessing(true);
    setBatchProgress(0);
    setCurrentBatchIndex(0);
    setBatchResults([]);
    
    const results = [];
    let shouldStop = false;
    
    const stopProcessing = () => {
        shouldStop = true;
        setBatchProcessing(false);
        showAlert('تم إيقاف المعالجة بواسطة المستخدم', 'warning');
    };
    
    window.stopBatchProcessing = stopProcessing;
    
    for (let i = 0; i < batchImages.length && !shouldStop; i++) {
        setCurrentBatchIndex(i);
        const image = batchImages[i];
        
        setBatchImages(prev => prev.map((img, idx) => 
            idx === i ? { ...img, status: 'processing' } : img
        ));
        
        try {
            console.log(`🚀 معالجة الصورة ${i + 1} من ${batchImages.length}: ${image.name}`);
            
            if (i > 0) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
            
            // تحويل الصورة إلى base64 لحفظها
            const imageBase64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(image.file);
            });
            
            let ocrResult;
            let merged = {};
            
            try {
                console.log('📸 بدء OCR للصورة:', image.name);
                ocrResult = await performOCROnFile(image.file, 25000);
                console.log('✅ انتهى OCR للصورة:', image.name, 'نجح:', ocrResult.success);
            } catch (ocrError) {
                console.error('❌ خطأ في OCR:', ocrError);
                ocrResult = {
                    success: false,
                    error: 'فشل في استخراج البيانات: ' + ocrError.message,
                    data: {}
                };
            }
            
            if (!ocrResult.success) {
                console.log('⚠️ فشل OCR، استخدام البيانات الافتراضية فقط');
                merged = mergeWithDefaultSettings({});
             merged.NameArabic = cleanArabicName(merged.NameArabic || `عميل مجمع ${i + 1}`);
                merged.PassportNumber = merged.PassportNumber || `A${Date.now().toString().slice(-8)}`;
                merged.NationalID = merged.NationalID || '';
            } else {
                console.log('✅ نجح OCR، دمج البيانات');
                merged = mergeWithDefaultSettings(ocrResult.data);
            }
            if (merged.NameArabic) {
    merged.NameArabic = cleanArabicName(merged.NameArabic);
}
            
            // ✨ إضافة صورة الجواز للبيانات
          // رفع الصورة أولاً
console.log('📷 رفع صورة الجواز...');
try {
    const imageFormData = new FormData();
    imageFormData.append('PassportImage', image.file);
    imageFormData.append('passportId', 'temp_' + Date.now());
    
    const uploadResponse = await fetch(`${config.PASSPORT_API_URL}/api/passports/upload-images`, {
        method: 'POST',
        body: imageFormData
    });
    
    if (uploadResponse.ok) {
        const uploadResult = await uploadResponse.json();
        merged.PassportImagePath = uploadResult.imagePaths?.PassportImagePath || '';
        console.log('✅ تم رفع الصورة:', merged.PassportImagePath);
    } else {
        console.warn('⚠️ فشل رفع الصورة، سيتم المتابعة بدونها');
        merged.PassportImagePath = '';
    }
} catch (uploadError) {
    console.error('❌ خطأ في رفع الصورة:', uploadError);
    merged.PassportImagePath = '';
}
            if (!merged.NameArabic || !merged.PassportNumber) {
                throw new Error('بيانات غير كافية للحفظ');
            }
            
            console.log('💾 محاولة حفظ الجواز:', merged.NameArabic);
            
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);
            
            const response = await fetch(`${config.PASSPORT_API_URL}/api/passports`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
              body: JSON.stringify({
    ...merged,
    CreatedByUserID: (() => {
        try {
            const userData = localStorage.getItem('user');
            if (!userData) return null;
            const parsed = JSON.parse(userData);
            return parsed?.UserId || null;
        } catch { return null; }
    })()
}),
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `خطأ HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            console.log('💚 تم حفظ الجواز بنجاح:', result.passportId);

            try {
                const batchPassportForPrint = buildNewPassportAutoPrintPayload({
                    ...merged,
                    PhoneNumbers: merged.PhoneNumbers || [],
                    PreviousCompanies: merged.PreviousCompanies || []
                }, result);
                await autoPrintNewPassportIfEnabled({
                    passport: batchPassportForPrint,
                    companies,
                    delegates,
                    professions,
                    showAlert,
                    sourceLabel: `المعالجة المجمعة ${i + 1}`
                });
            } catch (autoPrintError) {
                console.error('خطأ في الطباعة التلقائية أثناء المعالجة المجمعة:', autoPrintError);
                showAlert('تم حفظ جواز من المعالجة المجمعة ولكن فشلت طباعة الباركود: ' + autoPrintError.message, 'warning');
            }
            
            setBatchImages(prev => prev.map((img, idx) => 
                idx === i ? { 
                    ...img, 
                    status: 'completed',
                    passportId: result.passportId || result.id,
                    extractedData: ocrResult.data || {}
                } : img
            ));
            
            results.push({
                index: i,
                name: image.name,
                success: true,
                passportId: result.passportId || result.id,
                officeSerial: result.officeSerial || result.OfficeSerial || '',
                extractedFields: Object.keys(ocrResult.data || {}).length,
                ocrSuccess: ocrResult.success
            });
            
        } catch (error) {
            console.error(`❌ فشل في معالجة ${image.name}:`, error);
            
            setBatchImages(prev => prev.map((img, idx) => 
                idx === i ? { 
                    ...img, 
                    status: 'error', 
                    error: error.message
                } : img
            ));
            
            results.push({
                index: i,
                name: image.name,
                success: false,
                error: error.message
            });
        }
        
        const progressPercent = ((i + 1) / batchImages.length) * 100;
        setBatchProgress(progressPercent);
        
        await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    setBatchProcessing(false);
    setBatchResults(results);
    
    const successCount = results.filter(r => r.success).length;
    const errorCount = results.filter(r => !r.success).length;
    const ocrSuccessCount = results.filter(r => r.success && r.ocrSuccess).length;
    
    showAlert(
        `انتهت المعالجة!\n✅ نجح: ${successCount}\n❌ فشل: ${errorCount}\n🤖 OCR نجح: ${ocrSuccessCount}`, 
        shouldStop ? 'warning' : 'success'
    );
    
    if (successCount > 0) {
        await fetchData();
    }
    
    delete window.stopBatchProcessing;
};



        const [checkTimeout, setCheckTimeout] = useState(null);

        const [enlargedImage, setEnlargedImage] = useState(null);


const [showBatchUpload, setShowBatchUpload] = useState(false);
const [showSmartRepeatModal, setShowSmartRepeatModal] = useState(false);
const [smartRepeatPassportData, setSmartRepeatPassportData] = useState([]);
const [batchImages, setBatchImages] = useState([]);
const [batchProcessing, setBatchProcessing] = useState(false);
const [batchProgress, setBatchProgress] = useState(0);
const [batchResults, setBatchResults] = useState([]);
const [currentBatchIndex, setCurrentBatchIndex] = useState(0);

        // أضف هذا useEffect مع باقي useEffects
useEffect(() => {
    const handleEscKey = (event) => {
        if (event.key === 'Escape' && enlargedImage) {
            setEnlargedImage(null);
        }
    };

    if (enlargedImage) {
        document.addEventListener('keydown', handleEscKey);
        // منع scroll في الخلفية
        document.body.style.overflow = 'hidden';
    } else {
        document.body.style.overflow = 'unset';
    }

    return () => {
        document.removeEventListener('keydown', handleEscKey);
        document.body.style.overflow = 'unset';
    };
}, [enlargedImage]);

    // أضف هذه الـ states في بداية المكون
const [ocrData, setOcrData] = useState({}); // البيانات المستخرجة
const [isOcrProcessing, setIsOcrProcessing] = useState(false); // حالة المعالجة

    const formatDateSafe = (dateString) => {
        if (!dateString) return '';
        try {
            let date;
            
            // التعامل مع التنسيق المصري DD/MM/YYYY
            if (typeof dateString === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString.trim())) {
                const [day, month, year] = dateString.trim().split('/').map(Number);
                if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
                    date = new Date(year, month - 1, day);
                } else {
                    return '';
                }
            } 
            // التعامل مع التنسيق DD-MM-YYYY (من OCR القديم)
            else if (typeof dateString === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(dateString.trim())) {
                const [day, month, year] = dateString.trim().split('-').map(Number);
                if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
                    date = new Date(year, month - 1, day);
                } else {
                    return '';
                }
            }
            // أي تنسيق آخر
            else {
                date = new Date(dateString);
            }
            
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
        } catch (error) {
            console.error('خطأ في تنسيق التاريخ:', error);
            return '';
        }
    };


const showAlert = (message, type = 'error') => {
        const existingAlert = alerts.find(alert => alert.message === message);
        if (existingAlert) {
            return;
        }
    
        const id = Date.now();
        
        setAlerts(prev => {
            const updatedAlerts = [...prev, { id, message, type }];
            return updatedAlerts.slice(-1);
        });
    
        if (type === 'error' && alerts.length === 0) {
            playSound('error');
        } else if (type === 'warning' && alerts.length === 0) {
            playSound('warning');
        }
    
        setTimeout(() => {
            setAlerts(prev => prev.filter(alert => alert.id !== id));
        }, 2000);
    };




    const sendWhatsAppNotification = async (phoneNumber, message) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/whatsapp/send-message`, { // تغيير من localhost:5006 إلى config
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    phone: phoneNumber,
                    message: message
                })
            });
    
            const result = await response.json();
            if (!result.success) {
                throw new Error(result.error || 'فشل في إرسال الرسالة');
            }
            
            return result;
        } catch (error) {
            console.error('خطأ في إرسال رسالة الواتساب:', error);
            showAlert('فشل في إرسال رسالة الواتساب: ' + error.message, 'warning');
            return null;
        }
    };

    const [companies, setCompanies] = useState([]);
    const [newTripRequest, setNewTripRequest] = useState(null);
    const [professions, setProfessions] = useState([]);
    const [delegates, setDelegates] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [lastPassportID, setLastPassportID] = useState(null);
    const [selectedPreviousCompany, setSelectedPreviousCompany] = useState('');
    const [showPreviousCompaniesSelect, setShowPreviousCompaniesSelect] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageViewer, setShowImageViewer] = useState(false);
    const [barcodeModalPassport, setBarcodeModalPassport] = useState(null);
    const [barcodeSettingsOnly, setBarcodeSettingsOnly] = useState(!!barcodeSettingsPage);
    const [savedPrintPassport, setSavedPrintPassport] = useState(null);

    useEffect(() => {
        if (!barcodeSettingsPage || !canManageBarcodePrinting) return;
        setBarcodeSettingsOnly(true);
        setBarcodeModalPassport(prev => prev || makeSampleBarcodePassport());
    }, [barcodeSettingsPage, canManageBarcodePrinting, companies, professions, delegates, lastPassportID]);
    const [blacklistMap, setBlacklistMap] = useState({});
    const [showBlacklistModal, setShowBlacklistModal] = useState(false);
    const [blacklistModalPassport, setBlacklistModalPassport] = useState(null);
    const [isViewMode, setIsViewMode] = useState(false);
    const [isMobileScreen, setIsMobileScreen] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.innerWidth < 768;
    });

    useEffect(() => {
        const handleResize = () => setIsMobileScreen(window.innerWidth < 768);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
 

    // إضافة states للإدخال السريع
const [showQuickEntry, setShowQuickEntry] = useState(false);
const [quickEntryStep, setQuickEntryStep] = useState(0);
const [quickEntryData, setQuickEntryData] = useState({});
const [quickEntryError, setQuickEntryError] = useState('');
const [searchTerm, setSearchTerm] = useState('');
const [inputValue, setInputValue] = useState('');
    // Cache للبيانات الثابتة
const [dataCache, setDataCache] = useState({
    companies: [],
    delegates: [],
    professions: [],
    lastFetch: null
});

// مدة صلاحية الـ cache (5 دقائق)
const CACHE_DURATION = 5 * 60 * 1000;

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);

const [totalItems, setTotalItems] = useState(0);
const [searchFilters, setSearchFilters] = useState({
    search: '',
    companyId: '',
    delegateId: '',
    passportStatus: '',
    receiveStatus: '',
    listId: '',
    excludeList: false
});

const [savedLists, setSavedLists] = useState([]);
const [showActivityLog, setShowActivityLog] = useState(false);
const [activityLogEntries, setActivityLogEntries] = useState([]);
const [activityLogUsers, setActivityLogUsers] = useState([]);
const [activityLogLoading, setActivityLogLoading] = useState(false);
const [activityLogCleanupSettings, setActivityLogCleanupSettings] = useState({
    cleanupMode: 'off',
    retentionDays: null,
    lastCleanupAt: null
});
const [activityLogFilters, setActivityLogFilters] = useState({
    userId: '',
    date: '',
    actionType: '',
    search: ''
});


// ===== listener عالمي للصق =====
    React.useEffect(() => {
        const handleGlobalPaste = (e) => {
            const tag = document.activeElement?.tagName?.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
            const field = activeImageFieldRef.current;
            if (!field || !showForm) return;
            const items = e.clipboardData?.items;
            if (!items) return;
            for (const item of items) {
                if (item.type.startsWith('image/')) {
                    e.preventDefault();
                    const file = item.getAsFile();
                    if (file) {
                        handleImageUpload(field, file);
                        showAlert(`✅ تم لصق الصورة في: ${getFieldLabel(field)}`, 'success');
                    }
                    break;
                }
            }
        };
        document.addEventListener('paste', handleGlobalPaste);
        return () => document.removeEventListener('paste', handleGlobalPaste);
    }, [showForm]);

    useEffect(() => {
    // تحميل القوائم المحفوظة للاستخدام كفلتر
    fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists?includeArchived=false`)
        .then(r => r.json())
        .then(data => setSavedLists(Array.isArray(data) ? data : []))
        .catch(() => setSavedLists([]));
}, []);





// Hook للـ debouncing
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

// استخدام الـ debouncing للبحث
const debouncedSearchFilters = useDebounce(searchFilters, 300); // تقليل الوقت إلى 300ms


    const [showSettings, setShowSettings] = useState(false);
    const [ocrProgress, setOcrProgress] = useState(0);
const [selectedImagePreview, setSelectedImagePreview] = useState(null);

const [showOcrModal, setShowOcrModal] = useState(false);
    const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
    const [lastUserActivity, setLastUserActivity] = useState(new Date());
    const autoRefreshStateRef = React.useRef({});
    const fetchDataRef = React.useRef(null);
    const fetchDataRunIdRef = React.useRef(0);
    const membershipsAbortRef = React.useRef(null);
    
// أضف هذه في بداية المكون مع باقي الـ states
const [fieldErrors, setFieldErrors] = useState({
    NameArabic: '',
    PassportNumber: '',
    NationalID: ''
});

const [fieldValidation, setFieldValidation] = useState({
    NameArabic: null,
    PassportNumber: null,
    NationalID: null
});


 
const checkWhatsAppStatus = async () => {
    try {
        const response = await fetch(`${config.API_BASE_URL}/api/whatsapp/status`); // تغيير من localhost:5006 إلى config
        const status = await response.json();
        
        if (status.status !== 'connected') {
            showAlert('تحذير: خدمة الواتساب غير متصلة، لن يتم إرسال الرسائل', 'warning');
            return false;
        }
        return true;
    } catch (error) {
        console.error('خطأ في التحقق من حالة الواتساب:', error);
        showAlert('تحذير: لا يمكن الوصول لخدمة الواتساب', 'warning');
        return false;
    }
};

 








const [warningSettings, setWarningSettings] = useState({
    enablePassportWarnings: true,
    enableLicenseWarnings: true,
    
    // إعدادات الجوازات
    passportSettings: {
        '3 أشهر': { minMonths: 3 },
        '4 أشهر': { minMonths: 4 },
        '6 أشهر': { minMonths: 6 },
        'عقد': { minMonths: 12 },
        'تعقيب': { minMonths: 6 }
    },
    
    // إعدادات الرخص (مختلفة ومستقلة)
    licenseSettings: {
        '3 أشهر': { minMonths: 1 },   // رخصة شهر واحد فقط لسفر 3 أشهر
        '4 أشهر': { minMonths: 2 },   // رخصة شهرين لسفر 4 أشهر
        '6 أشهر': { minMonths: 3 },   // رخصة 3 أشهر لسفر 6 أشهر  
        'عقد': { minMonths: 6 },       // رخصة 6 أشهر للعقد
        'تعقيب': { minMonths: 2 }     // رخصة شهرين للتعقيب
    }
});



// إعدادات النموذج الافتراضي - أضف هذا الكود هنا
const [showDefaultSettings, setShowDefaultSettings] = useState(false);
const [defaultSettings, setDefaultSettings] = useState(() => createSafeDefaultSettings());

// في PassportManagement.js - استبدل دالة performOCROnFile بهذه النسخة المحسنة
const performOCROnFile = async (file) => {
    try {
        console.log('🔍 بدء استخراج البيانات من:', file.name);
        
        const formData = new FormData();
        formData.append('passportImage', file);

        const ocrController = new AbortController();
        const ocrTimeout = setTimeout(() => {
            ocrController.abort();
            console.error('⏱️ انتهت مهلة OCR (180 ثانية)');
        }, 180000);
        
        let response;
        try {
            response = await fetch(`${config.PASSPORT_API_URL}/api/passports/ocr-scan`, {
                method: 'POST',
                body: formData,
                signal: ocrController.signal
            });
        } finally {
            clearTimeout(ocrTimeout);
        }
        
        if (!response.ok) {
            throw new Error(`خطأ الشبكة: ${response.status}`);
        }
        
        const result = await response.json();
        
        console.log('📋 نتيجة OCR لـ', file.name, ':', result);
        
        // التحقق من وجود بيانات مستخرجة
        if (result.success && result.data) {
            const extractedFieldsCount = Object.keys(result.data).filter(key => 
                result.data[key] && result.data[key] !== '' && result.data[key] !== null
            ).length;
            
            console.log(`✅ تم استخراج ${extractedFieldsCount} حقل من ${file.name}`);
            
            if (extractedFieldsCount === 0) {
                console.warn('⚠️ لم يتم استخراج أي بيانات، سيتم استخدام البيانات الافتراضية فقط');
            }
        }
        
        return result;
    } catch (error) {
        if (error.name === 'AbortError') {
            console.error('⏱️ انتهت مهلة OCR - الطلب استغرق أكثر من 180 ثانية');
            showAlert('⏱️ انتهت مهلة الاستخراج بعد 3 دقائق. الصورة محفوظة، لكن لم تصل نتيجة التحليل.', 'error');
            return { success: false, error: 'انتهت المهلة' };
        }
        console.error('❌ خطأ في OCR:', error);
        return {
            success: false,
            error: error.message,
            data: {}
        };
    }
};

const mergeWithDefaultSettings = (extractedData) => {
    console.log('🔄 دمج البيانات المستخرجة مع الافتراضية:', extractedData);

    const preparedOcr = prepareOcrPassportData(extractedData, defaultSettings);
    const merged = { ...preparedOcr.data };
    
    // تطبيق الإعدادات الافتراضية المفعّلة
    Object.keys(defaultSettings.enabled).forEach(fieldKey => {
        if (defaultSettings.enabled[fieldKey] && defaultSettings.values[fieldKey]) {
            if (!merged[fieldKey] || 
                (Array.isArray(merged[fieldKey]) && merged[fieldKey].length === 0)) {
                merged[fieldKey] = defaultSettings.values[fieldKey];
                console.log(`✅ تم تطبيق القيمة الافتراضية لـ ${fieldKey}:`, defaultSettings.values[fieldKey]);
            }
        }
    });
    
    const professionMode = getPassportProfessionInputMode(defaultSettings);
    if (professionMode === 'text') {
        merged.PassportProfessionID = '';
        merged.PassportProfessionText = String(merged.PassportProfessionText || '').replace(/\s+/g, ' ').trim().slice(0, 255);
    } else {
        merged.PassportProfessionText = '';
    }

    // إضافة البيانات المحسوبة فقط من رقم قومي اجتاز التحقق الصارم
    if (merged.NationalID) {
        const nationalIdCheck = validateEgyptianNationalID(merged.NationalID, merged.DateOfBirth);
        if (nationalIdCheck.isValid) {
            if (!merged.DateOfBirth) merged.DateOfBirth = nationalIdCheck.DateOfBirth;
            if (!merged.Age) merged.Age = nationalIdCheck.Age;
            if (!merged.Governorate) merged.Governorate = nationalIdCheck.Governorate;
        } else {
            console.warn('تم تجاهل رقم قومي غير موثوق أثناء الدمج:', nationalIdCheck.error);
            merged.NationalID = '';
        }
    }
    
    // ضمان وجود القيم الافتراضية الأساسية
    merged.PassportReceiveStatus = merged.PassportReceiveStatus || 'اختر الحالة';
    merged.PhoneNumbers = merged.PhoneNumbers || [''];
    merged.PreviousCompanies = merged.PreviousCompanies || [];
    
    // تطبيق الملاحظة الافتراضية إذا كانت مفعّلة
    if (defaultSettings.enabled.DefaultNote &&
        defaultSettings.values.DefaultNote &&
        defaultSettings.values.DefaultNote.trim()) {
        const defaultNoteObj = {
            id: Date.now(),
            text: defaultSettings.values.DefaultNote.trim(),
            date: new Date().toISOString(),
            user: 'المستخدم الحالي'
        };
        // دمج مع أي ملاحظات موجودة من OCR
        let existingNotes = [];
        if (merged.Notes) {
            try {
                const parsed = typeof merged.Notes === 'string'
                    ? JSON.parse(merged.Notes)
                    : merged.Notes;
                existingNotes = Array.isArray(parsed)
                    ? parsed.filter(n => n.text && n.text.trim())
                    : [];
            } catch (e) {
                existingNotes = [];
            }
        }
        merged.Notes = JSON.stringify([defaultNoteObj, ...existingNotes]);
    }

    console.log('🎯 البيانات النهائية المدموجة:', merged);
    return merged;
};

// دالة لحفظ الإعدادات
const saveWarningSettings = (newSettings) => {
    setWarningSettings(newSettings);
    localStorage.setItem('passportWarningSettings', JSON.stringify(newSettings));
    showAlert('تم حفظ الإعدادات بنجاح', 'success');
};

// تحميل الإعدادات عند بدء التطبيق
useEffect(() => {
    let cancelled = false;

    const loadDefaultSettings = async () => {
        let initialSettings = createSafeDefaultSettings();

        const savedDefaults = localStorage.getItem('passportDefaultSettings');
        if (savedDefaults) {
            try {
                initialSettings = createSafeDefaultSettings(JSON.parse(savedDefaults));
            } catch (error) {
                console.error('خطأ في تحميل الإعدادات الافتراضية المحلية:', error);
            }
        }

        if (!cancelled) {
            setDefaultSettings(initialSettings);
        }

        try {
            const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/settings/passport-profession-mode`);
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(result.error || 'تعذر تحميل طريقة حقل المهنة بالجواز من قاعدة البيانات');
            }

            const sharedMode = result.data?.mode === 'text' ? 'text' : 'select';
            if (cancelled) return;

            setDefaultSettings(prev => {
                const next = createSafeDefaultSettings({
                    ...prev,
                    values: {
                        ...(prev?.values || {}),
                        PassportProfessionInputMode: sharedMode
                    }
                });
                localStorage.setItem('passportDefaultSettings', JSON.stringify(next));
                return next;
            });
        } catch (error) {
            console.warn('تعذر تحميل طريقة حقل المهنة بالجواز العامة، سيتم استخدام النسخة المحلية مؤقتًا:', error);
        }
    };

    loadDefaultSettings();

    return () => {
        cancelled = true;
    };
}, []);

// دالة حفظ الإعدادات الافتراضية
const saveDefaultSettings = async (newSettings) => {
    const cleanedSettings = createSafeDefaultSettings(newSettings);
    const passportProfessionMode = getPassportProfessionInputMode(cleanedSettings);

    setDefaultSettings(cleanedSettings);
    localStorage.setItem('passportDefaultSettings', JSON.stringify(cleanedSettings));

    try {
        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/settings/passport-profession-mode`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                mode: passportProfessionMode,
                UpdatedByUserID: getCurrentUserId ? getCurrentUserId() : null
            })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) {
            throw new Error(result.error || 'فشل حفظ طريقة حقل المهنة بالجواز في قاعدة البيانات');
        }

        showAlert('تم حفظ ضبط النموذج، وطريقة حقل المهنة بالجواز أصبحت مفعّلة لكل المستخدمين', 'success');
    } catch (error) {
        console.error('خطأ في حفظ طريقة حقل المهنة بالجواز العامة:', error);
        showAlert('تم حفظ باقي ضبط النموذج محليًا، لكن فشل حفظ طريقة حقل المهنة بالجواز لكل المستخدمين: ' + error.message, 'error');
        throw error;
    }
};

const getPassportProfessionInputMode = (settings = defaultSettings) => {
    const cleanedSettings = createSafeDefaultSettings(settings);
    return cleanedSettings.values.PassportProfessionInputMode === 'text' ? 'text' : 'select';
};

const prepareOcrPassportData = (rawData = {}, settings = defaultSettings) => {
    const data = { ...(rawData || {}) };
    const warnings = [];
    const professionMode = getPassportProfessionInputMode(settings);
    const extractedProfession = String(
        data.PassportProfessionText || data.PassportProfession || data.PassportProfessionName || data.Profession || data.Occupation || ''
    ).replace(/\s+/g, ' ').trim().slice(0, 255);

    if (professionMode === 'text') {
        data.PassportProfessionID = '';
        data.PassportProfessionText = extractedProfession;
    } else {
        delete data.PassportProfessionID;
        delete data.PassportProfessionText;
        delete data.PassportProfession;
        delete data.PassportProfessionName;
        delete data.Profession;
        delete data.Occupation;
    }

    ['PassportIssueDate', 'PassportExpiryDate', 'DateOfBirth'].forEach(field => {
        if (data[field]) data[field] = normalizeDateOnly(data[field]);
    });

    if (data.PassportIssueDate && data.PassportIssueDate > new Date().toISOString().slice(0, 10)) {
        warnings.push('تم تجاهل تاريخ إصدار الجواز لأنه في المستقبل');
        data.PassportIssueDate = '';
    }
    if (data.PassportIssueDate && data.PassportExpiryDate && data.PassportIssueDate >= data.PassportExpiryDate) {
        warnings.push('تم تجاهل تاريخ إصدار الجواز لأنه لا يسبق تاريخ الانتهاء');
        data.PassportIssueDate = '';
    }

    if (data.NationalID) {
        const check = validateEgyptianNationalID(data.NationalID, data.DateOfBirth);
        if (check.isValid) {
            data.NationalID = check.nationalID;
            data.DateOfBirth = data.DateOfBirth || check.DateOfBirth;
            data.Age = data.Age || check.Age;
            data.Governorate = data.Governorate || check.Governorate;
        } else {
            warnings.push('لم يتم اعتماد الرقم القومي المستخرج: ' + check.error);
            data.NationalID = '';
        }
    }

    return { data, warnings };
};

// دالة تطبيق الإعدادات الافتراضية عند إنشاء جواز جديد
// دالة تطبيق الإعدادات الافتراضية عند إنشاء جواز جديد
const applyDefaultSettings = () => {
    console.log('تطبيق الإعدادات الافتراضية...');
    
    setFormData(prev => {
        const updatedData = { ...prev };
        let appliedCount = 0;
        
        // تطبيق كل إعداد مفعل
        if (defaultSettings.enabled.CompanyID && defaultSettings.values.CompanyID) {
            updatedData.CompanyID = defaultSettings.values.CompanyID;
            appliedCount++;
        }
        
        if (defaultSettings.enabled.TravelDuration && defaultSettings.values.TravelDuration) {
            updatedData.TravelDuration = defaultSettings.values.TravelDuration;
            appliedCount++;
        }
        
        if (defaultSettings.enabled.PassportStatus && defaultSettings.values.PassportStatus) {
            updatedData.PassportStatus = defaultSettings.values.PassportStatus;
            appliedCount++;
            
            // إظهار/إخفاء الشركات السابقة حسب حالة الجواز
            setShowPreviousCompaniesSelect(defaultSettings.values.PassportStatus === 'قديم شركات');
        }
        
        if (defaultSettings.enabled.AbilityTest && defaultSettings.values.AbilityTest) {
            updatedData.AbilityTest = defaultSettings.values.AbilityTest;
            appliedCount++;
        }
        
        const passportProfessionMode = getPassportProfessionInputMode();
        if (passportProfessionMode === 'text' && defaultSettings.enabled.PassportProfessionText && defaultSettings.values.PassportProfessionText) {
            updatedData.PassportProfessionID = '';
            updatedData.PassportProfessionText = defaultSettings.values.PassportProfessionText;
            appliedCount++;
        } else if (passportProfessionMode === 'select' && defaultSettings.enabled.PassportProfessionID && defaultSettings.values.PassportProfessionID) {
            updatedData.PassportProfessionID = defaultSettings.values.PassportProfessionID;
            updatedData.PassportProfessionText = '';
            appliedCount++;
        }
        
        if (defaultSettings.enabled.TravelProfessionID && defaultSettings.values.TravelProfessionID) {
            updatedData.TravelProfessionID = defaultSettings.values.TravelProfessionID;
            appliedCount++;
        }
        
        if (defaultSettings.enabled.MilitaryStatus && defaultSettings.values.MilitaryStatus) {
            updatedData.MilitaryStatus = defaultSettings.values.MilitaryStatus;
            appliedCount++;
        }
        
        if (defaultSettings.enabled.DelegateID && defaultSettings.values.DelegateID) {
            updatedData.DelegateID = defaultSettings.values.DelegateID;
            appliedCount++;
        }
        
        if (defaultSettings.enabled.LicenseType && defaultSettings.values.LicenseType) {
            updatedData.LicenseType = defaultSettings.values.LicenseType;
            appliedCount++;
        }

        // تطبيق الملاحظة الافتراضية
        if (defaultSettings.enabled.DefaultNote && defaultSettings.values.DefaultNote && defaultSettings.values.DefaultNote.trim()) {
            const defaultNoteObj = {
                id: Date.now(),
                text: defaultSettings.values.DefaultNote.trim(),
                date: new Date().toISOString(),
                user: 'المستخدم الحالي'
            };
            const existingNotes = Array.isArray(prev.Notes)
                ? prev.Notes.filter(n => n.text && n.text.trim())
                : [];
            updatedData.Notes = [defaultNoteObj, ...existingNotes];
            appliedCount++;
        }

        // تطبيق الشركات السابقة فقط إذا كانت حالة الجواز "قديم شركات"
        if (defaultSettings.enabled.PreviousCompanies && 
            defaultSettings.values.PassportStatus === 'قديم شركات' &&
            Array.isArray(defaultSettings.values.PreviousCompanies) && 
            defaultSettings.values.PreviousCompanies.length > 0) {
            updatedData.PreviousCompanies = defaultSettings.values.PreviousCompanies;
            appliedCount++;
        }
        
        if (appliedCount > 0) {
            showAlert(`تم تطبيق ${appliedCount} إعداد افتراضي`, 'success');
        }
        
        return updatedData;
    });
};



const [formData, setFormData] = useState(() => ({
    PassportID: null,
    OfficeSerial: '',
    SerialNumber: '',
    TravelDuration: '',
    CompanyID: '',
    PassportStatus: '',
    PreviousCompanies: [],
    AbilityTest: '',
    NameArabic: '',
    NameEnglish: '',
    PassportNumber: 'A',
    NationalID: '',
    PassportProfessionID: '',
    PassportProfessionText: '',
    TravelProfessionID: '',
    MilitaryStatus: '',
    PassportIssueDate: '',
    PassportExpiryDate: '',
    Address: '',
    Governorate: '',
    DateOfBirth: '',
    Age: '',
    DelegateID: '',
    PhoneNumbers: [''],
    LicenseType: '',
    LicenseExpiryDate: '',
    MedicalExamStatus: '',
    MedicalExamDate: '',
    NetWorkDate: '',
    VisaDate: '',
    TravelDate: '',
    BorderNumber: '',
    ViolationNotes: '',
    ApplicationNumber: '',
     FingerprintDate: '',
    MedicalInsuranceDate: '',
    Notes: [{ id: Date.now(), text: '', date: new Date().toISOString(), user: 'المستخدم الحالي' }],
    PassportReceiveStatus: 'اختر الحالة',
    ReceiveDate: '',
    FriendName: '',
    FriendPhone: '',
    // ✅ إضافة مسارات الصور مع ضمان عدم undefined
PassportImagePath: '',
    PersonalImagePath: '',
    LicenseImagePath: '',
    LicenseBackImagePath: '',
    IDImagePath: '',
    IDBackImagePath: '',
    CompanyContractPath: '',
    MinistryContractPath: '',
    VisaFilePath: '',
    OtherAttachmentsPath: ''
}));


const isUserActive = () => {
    const now = new Date();
    const timeSinceLastActivity = now - lastUserActivity;
    const oneMinute = 60 * 1000; // دقيقة واحدة
    
    return timeSinceLastActivity < oneMinute;
};

// دالة لتحديث آخر نشاط للمستخدم
const updateUserActivity = () => {
    const now = new Date();
    setLastUserActivity(now);
    console.log('🎯 تم تحديث آخر نشاط للمستخدم:', now.toLocaleTimeString('ar-EG'));
};

// تحقق صارم من الرقم القومي المصري قبل الاعتماد أو الحساب منه
const EGYPTIAN_GOVERNORATES = {
    '01': 'القاهرة', '02': 'الإسكندرية', '03': 'بورسعيد', '04': 'السويس',
    '11': 'دمياط', '12': 'الدقهلية', '13': 'الشرقية', '14': 'القليوبية',
    '15': 'كفر الشيخ', '16': 'الغربية', '17': 'المنوفية', '18': 'البحيرة',
    '19': 'الإسماعيلية', '21': 'الجيزة', '22': 'بني سويف', '23': 'الفيوم',
    '24': 'المنيا', '25': 'أسيوط', '26': 'سوهاج', '27': 'قنا',
    '28': 'أسوان', '29': 'الأقصر', '31': 'البحر الأحمر', '32': 'الوادي الجديد',
    '33': 'مطروح', '34': 'شمال سيناء', '35': 'جنوب سيناء', '88': 'خارج الجمهورية'
};

const normalizeDateOnly = (value) => {
    if (!value) return '';
    const text = String(value).trim();
    let year;
    let month;
    let day;
    let match = text.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/);
    if (match) {
        year = Number(match[1]); month = Number(match[2]); day = Number(match[3]);
    } else {
        match = text.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
        if (!match) return '';
        day = Number(match[1]); month = Number(match[2]); year = Number(match[3]);
    }
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return '';
    return date.toISOString().slice(0, 10);
};

const validateEgyptianNationalID = (value, expectedDateOfBirth = '') => {
    const nationalID = String(value || '').replace(/\D/g, '');
    if (!/^\d{14}$/.test(nationalID)) return { isValid: false, error: 'الرقم القومي يجب أن يكون 14 رقمًا' };
    if (!['2', '3'].includes(nationalID[0])) return { isValid: false, error: 'كود القرن في الرقم القومي غير صحيح' };

    const year = (nationalID[0] === '2' ? 1900 : 2000) + Number(nationalID.slice(1, 3));
    const month = Number(nationalID.slice(3, 5));
    const day = Number(nationalID.slice(5, 7));
    const birthDate = new Date(Date.UTC(year, month - 1, day));
    if (birthDate.getUTCFullYear() !== year || birthDate.getUTCMonth() !== month - 1 || birthDate.getUTCDate() !== day) {
        return { isValid: false, error: 'تاريخ الميلاد داخل الرقم القومي غير حقيقي' };
    }

    const dateOfBirth = birthDate.toISOString().slice(0, 10);
    const today = new Date();
    const todayText = today.toISOString().slice(0, 10);
    if (dateOfBirth > todayText) return { isValid: false, error: 'تاريخ الميلاد داخل الرقم القومي في المستقبل' };

    let age = today.getUTCFullYear() - year;
    const currentMonth = today.getUTCMonth() + 1;
    const currentDay = today.getUTCDate();
    if (currentMonth < month || (currentMonth === month && currentDay < day)) age -= 1;
    if (age < 0 || age > 120) return { isValid: false, error: 'العمر الناتج من الرقم القومي غير منطقي' };

    const governorateCode = nationalID.slice(7, 9);
    const governorate = EGYPTIAN_GOVERNORATES[governorateCode];
    if (!governorate) return { isValid: false, error: 'كود المحافظة داخل الرقم القومي غير صحيح' };

    const expected = normalizeDateOnly(expectedDateOfBirth);
    if (expected && expected !== dateOfBirth) {
        return { isValid: false, error: 'تاريخ الميلاد داخل الرقم القومي لا يطابق تاريخ الميلاد الموجود بالجواز' };
    }

    return { isValid: true, nationalID, DateOfBirth: dateOfBirth, Age: age, Governorate: governorate };
};

const calculateDataFromNationalID = (nationalID) => {
    const result = validateEgyptianNationalID(nationalID);
    return result.isValid
        ? { DateOfBirth: result.DateOfBirth, Age: result.Age, Governorate: result.Governorate }
        : {};
};


    // القوائم المنسدلة
    const travelDurations = ['3 أشهر', '4 أشهر', '6 أشهر', 'عقد', 'تعقيب'];
    const passportStatuses = ['جديد', 'قديم شركات'];
    const abilityTests = ['لم يختبر','تحت الاختبار','لائق', 'غير لائق', 'اساسى', 'احتياطي', 'سائق ثاني'];
    const militaryStatuses = ['في سن التجنيد', 'غير مطلوب'];
    const licenseTypes = ['أولى', 'ثانية', 'ثالثة', 'خاصة', 'لا يوجد'];
   const medicalExamStatuses = ['في الانتظار', 'لائق', 'غير لائق', 'دفع فلوس الكشف', 'لا يوجد'];
    const receiveStatuses = ['اختر الحالة', 'لم يستلم', 'استلم العميل', 'استلم المندوب', 'استلم صديق'];

    // ✅ حقول مصدرها المتابعة/التأشيرات داخل نموذج الجوازات
    // العرض يتم من نفس صلاحية اسم الحقل، والتعديل اليدوي يتم بصلاحية مستقلة.
    const managedPassportFields = {
        MedicalExamStatus: { view: 'حالة الكشف الطبي', edit: 'تعديل يدوي - حالة الكشف الطبي', source: 'من قسم المتابعة الدورية' },
        MedicalExamDate: { view: 'تاريخ الكشف الطبي', edit: 'تعديل يدوي - تاريخ الكشف الطبي', source: 'من قسم المتابعة الدورية' },
        NetWorkDate: { view: 'تاريخ عمل النت', edit: 'تعديل يدوي - تاريخ عمل النت', source: 'من قسم التأشيرات' },
        ApplicationNumber: { view: 'رقم الطلب', edit: 'تعديل يدوي - رقم الطلب', source: 'من قسم التأشيرات' },
        FingerprintDate: { view: 'تاريخ البصمة', edit: 'تعديل يدوي - تاريخ البصمة', source: 'من قسم التأشيرات' },
        MedicalInsuranceDate: { view: 'تاريخ التأمين الطبي', edit: 'تعديل يدوي - تاريخ التأمين الطبي', source: 'من قسم التأشيرات' },
        VisaDate: { view: 'تاريخ التأشير', edit: 'تعديل يدوي - تاريخ التأشير', source: 'من قسم المتابعة الدورية' },
        TravelDate: { view: 'تاريخ السفر', edit: 'تعديل يدوي - تاريخ السفر', source: 'من قسم المتابعة الدورية' },
        BorderNumber: { view: 'رقم الحدود', edit: 'تعديل يدوي - رقم الحدود', source: 'من قسم المتابعة الدورية' },
        ViolationNotes: { view: 'المخالفة / ملاحظتها', edit: 'تعديل يدوي - المخالفة / ملاحظتها', source: 'من صفحة الجوازات فقط' }
    };

    const canShowManagedPassportField = (fieldName) => {
        const config = managedPassportFields[fieldName];
        return !!config && hasPermission(config.view);
    };

    const canEditManagedPassportField = (fieldName) => {
        const config = managedPassportFields[fieldName];
        return !!config && !isViewMode && hasPermission(config.edit);
    };

    const getManagedPassportInputClass = (fieldName, extra = '') => {
        const editable = canEditManagedPassportField(fieldName);
        return `w-full border rounded-lg px-4 py-2.5 ${editable ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 bg-white' : 'border-gray-300 bg-gray-50 cursor-not-allowed text-gray-600'} ${extra}`;
    };

    const renderManagedSourceBadge = (fieldName) => {
        const config = managedPassportFields[fieldName];
        if (!config) return null;
        const editable = canEditManagedPassportField(fieldName);
        return (
            <span className={`text-xs px-2 py-0.5 rounded ml-2 ${editable ? 'text-green-700 bg-green-50' : 'text-orange-600 bg-orange-50'}`}>
                {editable ? 'مسموح تعديل يدوي' : config.source}
            </span>
        );
    };


    const handleOCR = async (file) => {
        try {
            showAlert('جاري معالجة الصورة...', 'info');
    
            const result = await Tesseract.recognize(
                file,
                'ara+eng',
                {
                    logger: m => {
                        if (m.status === 'recognizing text') {
                            const progress = Math.round(m.progress * 100);
                            showAlert(`جاري استخراج النصوص... ${progress}%`, 'warning');
                        }
                    }
                }
            );
    
            const extractedData = processExtractedText(result.data.text);
            const preparedOcr = prepareOcrPassportData(extractedData, defaultSettings);
            setOcrData(preparedOcr.data);

            if (preparedOcr.data.PassportNumber || preparedOcr.data.NameArabic || preparedOcr.data.NameEnglish) {
                showAlert('تم استخراج البيانات بنجاح', 'success');
            } else {
                showAlert('تم استخراج البيانات بشكل جزئي، يرجى التحقق من صحة البيانات', 'warning');
            }
        } catch (error) {
            console.error('خطأ في OCR:', error);
            showAlert('حدث خطأ أثناء معالجة الصورة', 'error');
        }
    };



    // دالة المسح الضوئي المتقدم باستخدام Gemini
// أضف هذه الدالة في بداية المكون
const compressImageForOCR = (file) => {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
            // تحسين الحجم للسرعة مع الحفاظ على الجودة
            const maxWidth = 1200;
            const maxHeight = 900;
            let { width, height } = img;
            
            if (width > height) {
                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }
            } else {
                if (height > maxHeight) {
                    width = (width * maxHeight) / height;
                    height = maxHeight;
                }
            }
            
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);
            
            // ضغط معتدل للحفاظ على جودة النص
            canvas.toBlob(resolve, 'image/jpeg', 0.75);
        };
        
        img.src = URL.createObjectURL(file);
    });
};

// تحويل الأرقام العربية/الفارسية إلى إنجليزية قبل مقارنة نتائج OCR
const normalizeOcrDigits = (value = '') => String(value)
    .replace(/[٠-٩]/g, digit => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/[۰-۹]/g, digit => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)));

// قراءة مستقلة محلية للصورة. الغرض منها تأكيد الرقم القومي فقط، وليس استبدال Gemini.
// لا يتم اعتماد الرقم تلقائياً إلا عندما تتفق القراءتان على نفس الـ 14 رقماً.
const extractLocalNationalIdEvidence = async (file) => {
    try {
        const result = await Tesseract.recognize(file, 'ara+eng', { logger: () => {} });
        const normalizedText = normalizeOcrDigits(result?.data?.text || '');
        const candidates = new Set();

        normalizedText.split(/\r?\n/).forEach(line => {
            const directMatches = line.match(/\d{14}/g) || [];
            directMatches.forEach(value => candidates.add(value));

            // يسمح بمسافات أو فواصل بسيطة بين أرقام الهوية، لكن لا يدمج أسطر الجواز معاً.
            const spacedMatches = line.match(/(?:\d[\s\-–—_:./]*){14}/g) || [];
            spacedMatches.forEach(value => {
                const digits = value.replace(/\D/g, '');
                if (digits.length === 14) candidates.add(digits);
            });
        });

        return { candidates: Array.from(candidates), failed: false };
    } catch (error) {
        console.warn('تعذر تنفيذ التحقق البصري المحلي للرقم القومي:', error);
        return { candidates: [], failed: true };
    }
};

const handleAdvancedOCR = async (file) => {
    try {
        setIsOcrProcessing(true);
        setOcrProgress(0);
        
        console.log('🚀 بداية الاستخراج مع حفظ الصورة...');
        const startTime = Date.now();
        
        // 📷 خطوة 1: حفظ الصورة فوراً
        console.log('📷 حفظ الصورة...');
        setOcrProgress(20);
        await handleImageUpload('PassportImage', file);
        
        // ⚡ خطوة 2: استخراج البيانات
        console.log('🤖 استخراج البيانات...');
        setOcrProgress(40);
        
        const progressInterval = setInterval(() => {
            setOcrProgress(prev => prev >= 95 ? 95 : prev + Math.random() * 15);
        }, 200);

        showAlert('📷 تم حفظ الصورة | 🤖 جاري استخراج البيانات...', 'info');

        const formDataToSend = new FormData();
        formDataToSend.append('passportImage', file);

        // يبدأ بالتوازي مع Gemini حتى لا يضيف زمناً كبيراً للعملية.
        const localNationalIdEvidencePromise = extractLocalNationalIdEvidence(file);

        const ocrController2 = new AbortController();
        const ocrTimeout2 = setTimeout(() => {
            ocrController2.abort();
        }, 195000);

        let response;
        try {
            response = await fetch(`${config.PASSPORT_API_URL}/api/passports/ocr-scan`, {
                method: 'POST',
                body: formDataToSend,
                signal: ocrController2.signal
            });
        } finally {
            clearTimeout(ocrTimeout2);
        }

        clearInterval(progressInterval);
        setOcrProgress(100);

        const result = await response.json();
        const totalTime = Date.now() - startTime;
        
        console.log(`⚡ اكتمل في ${totalTime}ms`);

        if (result.success) {
            const rawOcrData = { ...(result.data || {}) };
            const verificationWarnings = [];
            const nationalIdCandidate = String(rawOcrData.NationalIDCandidate || '').replace(/\D/g, '');

            if (rawOcrData.NationalIDRequiresVerification && nationalIdCandidate) {
                const localEvidence = await Promise.race([
                    localNationalIdEvidencePromise,
                    new Promise(resolve => setTimeout(() => resolve({ candidates: [], failed: true, timedOut: true }), 30000))
                ]);
                const candidateCheck = validateEgyptianNationalID(nationalIdCandidate, rawOcrData.DateOfBirth);
                const independentlyConfirmed = candidateCheck.isValid && localEvidence.candidates.includes(nationalIdCandidate);

                if (independentlyConfirmed) {
                    rawOcrData.NationalID = nationalIdCandidate;
                    verificationWarnings.push('تم تأكيد الرقم القومي بتطابق قراءتين مستقلتين للصورة.');
                } else {
                    rawOcrData.NationalID = '';
                    verificationWarnings.push(
                        localEvidence.failed
                            ? 'لم يتم اعتماد الرقم القومي المحتمل لأن التحقق البصري المستقل لم يكتمل. راجعه واكتبه يدوياً.'
                            : 'لم يتم اعتماد الرقم القومي المحتمل لأن القراءة المحلية لم تتطابق معه حرفياً. راجعه واكتبه يدوياً.'
                    );
                }
            }

            delete rawOcrData.NationalIDCandidate;
            delete rawOcrData.NationalIDRequiresVerification;
            delete rawOcrData.NationalIDVerified;
            delete rawOcrData.NationalIDReviewRequired;
            delete rawOcrData.NationalIDWarning;

            const preparedOcr = prepareOcrPassportData(rawOcrData, defaultSettings);
            const cleanedData = { ...preparedOcr.data };
            if (cleanedData.NameArabic) cleanedData.NameArabic = cleanArabicName(cleanedData.NameArabic);
            setOcrData(cleanedData);

            const extractedCount = Object.keys(cleanedData).filter(key => cleanedData[key] !== '' && cleanedData[key] !== null && cleanedData[key] !== undefined).length;
            const allWarnings = [...new Set([
                ...(result.warnings || []),
                ...(result.validation?.warnings || []),
                ...(preparedOcr.warnings || []),
                ...verificationWarnings
            ].filter(Boolean))];

            showAlert(
                `✅ العملية مكتملة!\n` +
                `📷 تم حفظ الصورة\n` +
                `📊 تم استخراج ${extractedCount} حقل بدقة ${result.confidence}%` +
                (allWarnings.length ? `\n⚠️ ${allWarnings.join('\n⚠️ ')}` : ''),
                allWarnings.length ? 'warning' : 'success'
            );
        } else {
            showAlert(`📷 تم حفظ الصورة | ❌ فشل استخراج البيانات: ${result.error}`, 'warning');
            setOcrData({});
        }

    } catch (error) {
        console.error('❌ خطأ في العملية:', error);
        if (error?.name === 'AbortError') {
            showAlert('⏱️ انتهت مهلة المسح الذكي. تم حفظ صورة الجواز، لكن لم تصل نتيجة القراءة، لذلك لم تُطبق أي بيانات تلقائياً.', 'error');
        } else {
            showAlert('❌ حدث خطأ أثناء العملية: ' + (error?.message || 'خطأ غير معروف'), 'error');
        }
        setOcrData({});
    } finally {
        setIsOcrProcessing(false);
        setTimeout(() => setOcrProgress(0), 1000);
    }
};

// دالة تنظيف الاسم العربي: إزالة الهمزات، إزالة الفاصل بين عبد والاسم، تحويل ي إلى ى
const cleanArabicName = (name) => {
    if (!name) return name;
    // استبدال أشكال الهمزة بـ ا بدون همزة
    let cleaned = name
        .replace(/[إأآ]/g, 'ا')
        .replace(/[ئ]/g, 'ي')
        .replace(/[ؤ]/g, 'و');
    // إزالة المسافة بين "عبد" وباقي الاسم
    cleaned = cleaned.replace(/عبد\s+/g, 'عبد');
    // تحويل ي إلى ى في جميع الكلمات
    cleaned = cleaned.replace(/ي(?=\s|$)/g, 'ى');
    return cleaned.trim();
};

const applyOcrData = () => {
    if (Object.keys(ocrData).length === 0) {
        showAlert('لا توجد بيانات مستخرجة لتطبيقها', 'warning');
        return;
    }

    // دالة لتحويل التاريخ من DD/MM/YYYY أو DD-MM-YYYY إلى YYYY-MM-DD
    const convertDateFormat = (dateStr) => {
        if (!dateStr) return '';
        
        // إذا كان التاريخ بالفعل بتنسيق YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            return dateStr;
        }
        
        // إذا كان بتنسيق DD/MM/YYYY أو DD-MM-YYYY
        if (/^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/.test(dateStr)) {
            const parts = dateStr.split(/[\/-]/);
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];
            return `${year}-${month}-${day}`;
        }
        
        return dateStr;
    };

    // تحويل البيانات مع معالجة خاصة للتواريخ
    let processedData = { ...ocrData };
    
    // قائمة حقول التواريخ التي نحتاج تحويلها
    const dateFields = [
        'PassportIssueDate',
        'PassportExpiryDate', 
        'DateOfBirth', 
        'LicenseExpiryDate',
        'MedicalExamDate',
        'NetWorkDate',
        'VisaDate',
        'TravelDate',
        'ReceiveDate',
        'FingerprintDate'
    ];
    
    // تحويل كل حقول التواريخ
    dateFields.forEach(field => {
        if (processedData[field]) {
            processedData[field] = convertDateFormat(processedData[field]);
        }
    });

// تنظيف الاسم العربي
    if (processedData.NameArabic) processedData.NameArabic = cleanArabicName(processedData.NameArabic);

    const preparedOcr = prepareOcrPassportData(processedData, defaultSettings);
    processedData = preparedOcr.data;
    if (preparedOcr.warnings.length) {
        showAlert(preparedOcr.warnings.join('\n'), 'warning');
    }

    setFormData(prev => ({
        ...prev,
        ...processedData
    }));
    
    // إخفاء البيانات المستخرجة بعد التطبيق
    setOcrData({});
    
    showAlert('✅ تم تطبيق جميع البيانات المستخرجة', 'success');
};



    
    function processExtractedText(text) {
        const cleanText = text.replace(/\s+/g, ' ').trim();
        
        const extractedData = {
            NationalID: null,
            NameArabic: null,
            NameEnglish: null,
            PassportNumber: null,
            Address: null,
            PassportProfessionText: null,
            PassportIssueDate: null,
            PassportExpiryDate: null,
            MilitaryStatus: null,
        };
    
        // تحسين التعرف على رقم الجواز
        const passportNumberRegex = /[A-Z]\d{8}/g;
        const passportMatch = cleanText.match(passportNumberRegex);
        if (passportMatch) {
            extractedData.PassportNumber = passportMatch[0];
        }
    
        // تحسين التعرف على الرقم القومي
        const nationalIDRegex = /\b\d{14}\b/g;
        const nationalIDMatch = cleanText.match(nationalIDRegex);
        if (nationalIDMatch) {
            extractedData.NationalID = nationalIDMatch[0];
        }
    
        // تحسين التعرف على الاسم باللغة العربية
        const arabicNameRegex = /[\u0600-\u06FF\s]{7,50}/g;
        const arabicMatches = cleanText.match(arabicNameRegex);
        if (arabicMatches) {
            extractedData.NameArabic = arabicMatches.sort((a, b) => b.length - a.length)[0];
        }
    
        // تحسين التعرف على الاسم باللغة الإنجليزية
        const englishNameRegex = /[A-Za-z\s]{5,100}/g;
        const englishMatches = cleanText.match(englishNameRegex);
        if (englishMatches) {
            extractedData.NameEnglish = englishMatches.sort((a, b) => b.length - a.length)[0].toUpperCase();
        }
    
        // استخراج المهنة النصية عند ظهور عنوان المهنة في النص
        const professionMatch = cleanText.match(/(?:المهنة|Profession|Occupation)\s*[:\-]?\s*([\u0600-\u06FFA-Za-z][\u0600-\u06FFA-Za-z\s]{1,60}?)(?=\s+(?:تاريخ|Date|الجنسية|Nationality|الرقم|Passport|$))/i);
        if (professionMatch) extractedData.PassportProfessionText = professionMatch[1].replace(/\s+/g, ' ').trim();

        const issueDateMatch = cleanText.match(/(?:تاريخ\s*(?:الإصدار|الاصدار)|Date\s*of\s*Issue|Issue\s*Date)\s*[:\-]?\s*(\d{1,4}[\/-]\d{1,2}[\/-]\d{1,4})/i);
        if (issueDateMatch) extractedData.PassportIssueDate = normalizeDateOnly(issueDateMatch[1]);

        // تحسين التعرف على تاريخ انتهاء الصلاحية
        const dateRegex = /\b\d{2,4}[\/-]\d{2}[\/-]\d{2,4}\b/g;
        const dateMatches = cleanText.match(dateRegex);
        if (dateMatches) {
            // أخذ آخر تاريخ في النص (عادة يكون تاريخ الانتهاء)
            let dateStr = dateMatches[dateMatches.length - 1];
            
            // تحويل التاريخ من DD/MM/YYYY أو DD-MM-YYYY إلى YYYY-MM-DD
            if (/^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/.test(dateStr)) {
                const parts = dateStr.split(/[\/-]/);
                const day = parts[0].padStart(2, '0');
                const month = parts[1].padStart(2, '0');
                const year = parts[2];
                // حفظ بتنسيق YYYY-MM-DD
                extractedData.PassportExpiryDate = `${year}-${month}-${day}`;
            } else {
                // إذا كان بتنسيق آخر، احتفظ به كما هو
                extractedData.PassportExpiryDate = dateStr;
            }
        }
    
        // تحسين التعرف على العنوان
        const addressRegex = /(?:العنوان|Address)[:\s]*([\u0600-\u06FF\s\d،,\/\-]+)/i;
        const addressMatch = cleanText.match(addressRegex);
        if (addressMatch && addressMatch[1]) {
            extractedData.Address = addressMatch[1].trim();
        }
    
        // تحسين التعرف على الحالة العسكرية
        if (/معاف|إعفاء|غير مطلوب/i.test(cleanText)) {
            extractedData.MilitaryStatus = 'غير مطلوب';
        } else if (/مؤجل|طالب|في سن التجنيد|غير لائق/i.test(cleanText)) {
            extractedData.MilitaryStatus = 'في سن التجنيد';
        } else if (/مجند|خدمة|خاضع للتجنيد/i.test(cleanText)) {
            extractedData.MilitaryStatus = 'مطلوب للخدمة';
        }
    
        console.log('البيانات المستخرجة النهائية:', extractedData);
        return extractedData;
    }
    
    
    
    
    
    
    

 
    // دالة التحقق من اللغة العربية
    const handleArabicInputChange = (e, field) => {
        if (isViewMode) return;

        const value = e.target.value;
        const isAddressField = field === 'Address';
        const isValidValue = isAddressField ? isArabicAddress(value) : isArabic(value);

        if (value && !isValidValue) {
            showAlert(isAddressField ? 'العنوان يقبل العربية والأرقام وعلامات العنوان فقط' : 'يرجى الكتابة باللغة العربية فقط', 'error');
            return;
        }
        setFormData(prev => ({ ...prev, [field]: value }));
    };


    
    // دالة التحقق من اللغة الإنجليزية
// دالة التحقق من اللغة الإنجليزية
const handleEnglishNameChange = (e) => {
    if (isViewMode) return;

    let value = e.target.value;

    // التحقق من الكتابة باللغة الإنجليزية فقط
    if (value && !isEnglish(value)) {
        showAlert('يرجى الكتابة باللغة الإنجليزية فقط', 'error');
        return;
    }

    // تحويل الأحرف إلى أحرف كبيرة
    value = value.toUpperCase();

    setFormData(prev => ({ ...prev, NameEnglish: value }));
};


    // التحقق من اللغة العربية
    const isArabic = (text) => {
        if (isViewMode) return;

        const arabicPattern = /^[\u0600-\u06FF\s]+$/;
        return arabicPattern.test(text);
    };

    const isArabicAddress = (text) => {
        if (isViewMode) return;
        const addressPattern = /^[\u0600-\u06FF\s0-9٠-٩،,\/\-.#()]+$/;
        return addressPattern.test(text);
    };

    const normalizeProfessionText = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

    const findProfessionByInput = (value) => {
        const text = normalizeProfessionText(value);
        if (!text) return null;
        return professions.find(profession => String(profession.ProfessionID) === text)
            || professions.find(profession => normalizeProfessionText(profession.ProfessionName).toLowerCase() === text.toLowerCase())
            || null;
    };


    const getPassportProfessionPayload = (idValue, textValue = '', mode = getPassportProfessionInputMode()) => {
        if (mode === 'text') {
            const manualText = normalizeProfessionText(textValue || idValue);
            return {
                PassportProfessionID: null,
                PassportProfessionText: manualText ? manualText.slice(0, 255) : ''
            };
        }

        const text = normalizeProfessionText(idValue);
        if (!text) {
            return { PassportProfessionID: null, PassportProfessionText: '' };
        }

        const matchedProfession = findProfessionByInput(text);
        if (matchedProfession?.ProfessionID) {
            return { PassportProfessionID: parseInt(matchedProfession.ProfessionID, 10), PassportProfessionText: '' };
        }

        // في وضع الاختيار فقط لا نحفظ نصًا يدويًا حتى لا يجتمع النص والقائمة معًا.
        return { PassportProfessionID: null, PassportProfessionText: '' };
    };

    const handlePassportProfessionInputChange = (value) => {
        if (isViewMode) return;
        setFormData(prev => ({ ...prev, PassportProfessionID: value, PassportProfessionText: '' }));
    };

    const handlePassportProfessionTextChange = (value) => {
        if (isViewMode) return;
        setFormData(prev => ({ ...prev, PassportProfessionID: '', PassportProfessionText: value }));
    };

    // التحقق من اللغة الإنجليزية
    const isEnglish = (text) => {
        if (isViewMode) return;

        const englishPattern = /^[A-Za-z\s]+$/;
        return englishPattern.test(text);
    };

    // تغيير لغة لوحة المفاتيح إلى العربية

    // التحقق من التكرار
    const checkDuplicate = async (field, value) => {
        if (isViewMode) return;

        if (!value || editingId) return false;

        try {
            const existingPassport = passports.find(p => 
                p[field]?.toString().toLowerCase() === value.toString().toLowerCase() && 
                p.PassportID !== editingId
            );

            if (existingPassport) {
                let fieldName;
                switch(field) {
                    case 'NameArabic':
                        fieldName = 'الاسم بالعربي';
                        break;
                    case 'NameEnglish':
                        fieldName = 'الاسم بالإنجليزي';
                        break;
                    case 'PassportNumber':
                        fieldName = 'رقم الجواز';
                        break;
                    case 'NationalID':
                        fieldName = 'الرقم القومي';
                        break;
                    default:
                        fieldName = field;
                        break;
                }
                showAlert(`${fieldName} موجود مسبقاً`);
                return true;
            }
            return false;
        } catch (error) {
            console.error('خطأ في التحقق من التكرار:', error);
            return false;
        }
    };


const handleFieldBlur = async (field, value) => {
    if (isViewMode) return;

    setFieldErrors(prev => ({ ...prev, [field]: '' }));
    setFieldValidation(prev => ({ ...prev, [field]: null }));

    if (!value || value.trim() === '') return;

    // التحقق من صحة التنسيق أولاً
    let validationError = '';
    
    switch(field) {
        case 'PassportNumber':
            if (!value.match(/^[A][0-9]{8}$/)) {
                validationError = 'رقم الجواز يجب أن يبدأ بحرف A متبوعاً بـ 8 أرقام';
            }
            break;
        case 'NationalID': {
            const nationalIdCheck = validateEgyptianNationalID(value, formData.DateOfBirth);
            if (!nationalIdCheck.isValid) validationError = nationalIdCheck.error;
            break;
        }
        case 'NameArabic':
            if (!isArabic(value)) {
                validationError = 'يجب استخدام اللغة العربية فقط';
            }
            break;
    }

    if (validationError) {
        setFieldErrors(prev => ({ ...prev, [field]: validationError }));
        setFieldValidation(prev => ({ ...prev, [field]: 'invalid' }));
        // تشغيل صوت خطأ للتنسيق الخاطئ
        playSound('error');
        return;
    }

    // التحقق من التكرار مع تأخير بسيط
    const fieldsToCheck = ['NameArabic', 'PassportNumber', 'NationalID'];
    
    if (fieldsToCheck.includes(field)) {
        // إلغاء التحقق السابق لو موجود
        if (checkTimeout) {
            clearTimeout(checkTimeout);
        }

        // تأخير نصف ثانية قبل التحقق
        const newTimeout = setTimeout(async () => {
            setFieldValidation(prev => ({ ...prev, [field]: 'checking' }));
            
            try {
                const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/check-duplicate`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        field: field,
                        value: value,
                        excludeId: editingId
                    })
                });

                if (!response.ok) {
                    throw new Error(`خطأ الشبكة: ${response.status}`);
                }

                const result = await response.json();
                
                if (result.isDuplicate) {
                    const fieldNames = {
                        'NameArabic': 'الاسم بالعربي',
                        'PassportNumber': 'رقم الجواز',
                        'NationalID': 'الرقم القومي'
                    };
                    
                    const errorMessage = `❌ ${fieldNames[field]} موجود في الجواز رقم ${result.duplicateInfo?.existingPassportId || 'غير معروف'}`;
                    setFieldErrors(prev => ({ ...prev, [field]: errorMessage }));
                    setFieldValidation(prev => ({ ...prev, [field]: 'invalid' }));
                    setFormData(prev => ({ ...prev, [field]: '' }));
                    
                    // 🔊 تشغيل صوت تحذير للتكرار
                    playSound('error');
                    
                } else {
                    setFieldErrors(prev => ({ ...prev, [field]: '' }));
                    setFieldValidation(prev => ({ ...prev, [field]: 'valid' }));
                    
                    // 🔊 تشغيل صوت نجاح (اختياري)
                    // playSound('success'); // إلغي التعليق لو عايز صوت عند النجاح
                }
            } catch (error) {
                console.error('خطأ في التحقق:', error);
                setFieldErrors(prev => ({ 
                    ...prev, 
                    [field]: `خطأ في التحقق: ${error.message}` 
                }));
                setFieldValidation(prev => ({ ...prev, [field]: 'invalid' }));
                
                // 🔊 تشغيل صوت خطأ عند فشل الشبكة
                playSound('error');
            }
        }, 500);

        setCheckTimeout(newTimeout);
    }
};


const fetchBlacklistStatuses = async (passportRows = []) => {
    try {
        const rows = Array.isArray(passportRows) ? passportRows : [];
        const ids = rows.map(p => p?.PassportID).filter(Boolean);
        if (ids.length === 0) {
            setBlacklistMap({});
            return;
        }

        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/blacklist/status-by-passport-ids`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ passportIds: ids })
        });
        const result = await response.json();
        if (response.ok && result.success) {
            setBlacklistMap(result.data || {});
        } else {
            setBlacklistMap({});
        }
    } catch (error) {
        console.error('خطأ في جلب ملخص قائمة المنع:', error);
        setBlacklistMap({});
    }
};

const handleOpenBlacklist = (passport = null) => {
    if (!canManageBlacklist) {
        showAlert && showAlert('ليس لديك صلاحية إدارة قائمة المنع', 'error');
        return;
    }
    setBlacklistModalPassport(passport || null);
    setShowBlacklistModal(true);
};

const refreshBlacklistAndData = async () => {
    await fetchData(currentPage, searchFilters);
};

const fetchData = async (page = 1, filters = searchFilters, signal = null) => {
    const runId = ++fetchDataRunIdRef.current;

    try {
        setIsLoading(true);

        const rawParams = {
            page: page.toString(),
            limit: itemsPerPage.toString(),
        };
        const { excludeList, ...restFilters } = filters || {};
        Object.entries(restFilters).forEach(([k, v]) => {
            if (v !== '' && v !== null && v !== undefined) rawParams[k] = String(v);
        });
        if (excludeList) rawParams.excludeList = 'true';

        const queryParams = new URLSearchParams(rawParams);
        const fetchSignal = signal && typeof signal === 'object' ? signal : null;
        const fetchOpts = () => (fetchSignal ? { signal: fetchSignal } : {});

        const now = Date.now();
        const needsRefresh = !dataCache.lastFetch || (now - dataCache.lastFetch) > CACHE_DURATION;

        const passportsPromise = fetch(
            `${config.PASSPORT_API_URL}/api/passports/paginated?${queryParams}`,
            fetchOpts()
        );

        const staticDataPromises = needsRefresh ? [
            fetch(`${config.PASSPORT_API_URL}/api/companies`, fetchOpts()),
            fetch(`${config.PASSPORT_API_URL}/api/professions`, fetchOpts()),
            fetch(`${config.PASSPORT_API_URL}/api/delegates`, fetchOpts())
        ] : [];

        const [passportsResponse, ...staticResponses] = await Promise.allSettled([
            passportsPromise,
            ...staticDataPromises
        ]);

        if (runId !== fetchDataRunIdRef.current) return;

        if (passportsResponse.status === 'fulfilled' && passportsResponse.value.ok) {
            const passportsData = await passportsResponse.value.json();
            if (runId !== fetchDataRunIdRef.current) return;

            const loadedPassports = passportsData.data || [];
            setPassports(loadedPassports);
            setTotalItems(passportsData.total || 0);

            // ملخص قائمة المنع لا يعطل فتح الجدول؛ يتم تحميله بعد عرض البيانات.
            setTimeout(() => {
                if (runId === fetchDataRunIdRef.current) {
                    fetchBlacklistStatuses(loadedPassports);
                }
            }, 100);
        } else {
            throw new Error('فشل في جلب بيانات الجوازات');
        }

        if (needsRefresh && staticResponses.length > 0) {
            const [companiesRes, professionsRes, delegatesRes] = staticResponses;
            let nextCompanies = companies;
            let nextProfessions = professions;
            let nextDelegates = delegates;

            if (companiesRes.status === 'fulfilled' && companiesRes.value.ok) {
                const companiesData = await companiesRes.value.json();
                nextCompanies = Array.isArray(companiesData) ? companiesData : [];
                if (runId === fetchDataRunIdRef.current) setCompanies(nextCompanies);
            }

            if (professionsRes.status === 'fulfilled' && professionsRes.value.ok) {
                const professionsData = await professionsRes.value.json();
                nextProfessions = Array.isArray(professionsData) ? professionsData : [];
                if (runId === fetchDataRunIdRef.current) setProfessions(nextProfessions);
            }

            if (delegatesRes.status === 'fulfilled' && delegatesRes.value.ok) {
                const delegatesData = await delegatesRes.value.json();
                nextDelegates = Array.isArray(delegatesData) ? delegatesData : [];
                if (runId === fetchDataRunIdRef.current) setDelegates(nextDelegates);
            }

            if (runId === fetchDataRunIdRef.current) {
                setDataCache({
                    companies: nextCompanies,
                    delegates: nextDelegates,
                    professions: nextProfessions,
                    lastFetch: now
                });
            }
        }

        if (runId === fetchDataRunIdRef.current) {
            setLastUpdateTime(new Date());
        }

    } catch (error) {
        if (error?.name === 'AbortError' || signal?.aborted) return;
        if (runId !== fetchDataRunIdRef.current) return;

        console.error('خطأ في جلب البيانات:', error);
        const isNetworkError = !window.navigator.onLine || error.message === 'Failed to fetch';
        if (isNetworkError) {
            showAlert('لا يوجد اتصال بالشبكة. تحقق من الاتصال وأعد المحاولة.', 'error');
        } else {
            showAlert('حدث خطأ في الاتصال بالسيرفر.', 'warning');
        }
    } finally {
        if (runId === fetchDataRunIdRef.current) {
            setIsLoading(false);
        }
    }
};

const fetchActivityLogUsers = async () => {
    try {
        const response = await fetch(`${config.API_BASE_URL}/api/users`);
        if (!response.ok) return;
        const data = await response.json();
        setActivityLogUsers(Array.isArray(data) ? data : []);
    } catch (error) {
        console.error('خطأ في جلب مستخدمي السجل:', error);
    }
};

const fetchActivityLog = async (filters = activityLogFilters) => {
    try {
        setActivityLogLoading(true);
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== '' && value !== null && value !== undefined) params.append(key, value);
        });
        params.append('limit', '300');
        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/activity-log?${params.toString()}`);
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.error || 'فشل في جلب السجل');
        setActivityLogEntries(result.data || []);
    } catch (error) {
        console.error('خطأ في جلب سجل الجوازات:', error);
        showAlert('حدث خطأ أثناء تحميل سجل الجوازات', 'error');
    } finally {
        setActivityLogLoading(false);
    }
};

const fetchActivityLogCleanupSettings = async () => {
    try {
        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/activity-log/cleanup-settings`);
        const result = await response.json();
        if (response.ok && result.success) {
            setActivityLogCleanupSettings(result.data || { cleanupMode: 'off', retentionDays: null, lastCleanupAt: null });
        }
    } catch (error) {
        console.error('خطأ في جلب إعدادات تنظيف السجل:', error);
    }
};

const saveActivityLogCleanupSettings = async (cleanupMode) => {
    try {
        setActivityLogLoading(true);
        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/activity-log/cleanup-settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cleanupMode, UpdatedByUserID: getCurrentUserId() })
        });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.error || 'فشل في حفظ إعدادات تنظيف السجل');
        showAlert('تم حفظ إعدادات تنظيف السجل بنجاح', 'success');
        await fetchActivityLogCleanupSettings();
        await fetchActivityLog(activityLogFilters);
    } catch (error) {
        console.error('خطأ في حفظ إعدادات تنظيف السجل:', error);
        showAlert('حدث خطأ في حفظ إعدادات تنظيف السجل: ' + error.message, 'error');
    } finally {
        setActivityLogLoading(false);
    }
};

const manualCleanupActivityLog = async (deleteMode) => {
    try {
        setActivityLogLoading(true);
        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/activity-log/cleanup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deleteMode, DeletedByUserID: getCurrentUserId() })
        });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.error || 'فشل في حذف السجل');
        showAlert(`تم حذف ${result.deletedCount || 0} حركة من السجل`, 'success');
        await fetchActivityLogCleanupSettings();
        await fetchActivityLog(activityLogFilters);
    } catch (error) {
        console.error('خطأ في حذف سجل الجوازات:', error);
        showAlert('حدث خطأ أثناء حذف السجل: ' + error.message, 'error');
    } finally {
        setActivityLogLoading(false);
    }
};

useEffect(() => {
    if (showActivityLog) {
        fetchActivityLog(activityLogFilters);
        fetchActivityLogUsers();
        fetchActivityLogCleanupSettings();
    }
}, [showActivityLog]);

useEffect(() => {
    if (!showActivityLog) return;
    const timeoutId = setTimeout(() => fetchActivityLog(activityLogFilters), 300);
    return () => clearTimeout(timeoutId);
}, [activityLogFilters, showActivityLog]);


// دالة buildImageUrl الآمنة - محدثة
const buildImageUrl = (imagePath) => {
    // حماية شاملة من undefined و null
    if (!imagePath || 
        imagePath === null || 
        imagePath === undefined || 
        typeof imagePath !== 'string' || 
        imagePath.trim() === '') {
        console.warn('مسار صورة غير صحيح:', imagePath);
        return null;
    }
    
    try {
        // إذا كان المسار يحتوي على year/month (المسار الجديد)
        if (imagePath.includes('/')) {
            const parts = imagePath.split('/').filter(part => part && part.trim() !== '');
            if (parts.length >= 4) {
                const year = parts[0];
                const month = parts[1];
                const folder = parts[2];
                const filename = parts[3];
                
                // تحويل اسم المجلد إلى النوع
                const typeMap = {
                    'passport_images': 'passport',
                    'personal_images': 'personal',
                    'license_images': 'license',
                    'license_back_images': 'license_back_images',
                    'id_images': 'id',
                    'id_back_images': 'id_back_images',
                    'company_contracts': 'company_contracts',
                    'ministry_contracts': 'ministry_contracts',
                    'visa_files': 'visa_files',
                    'other_attachments': 'other_attachments'
                };
                
                const type = typeMap[folder] || folder;
                return `${config.PASSPORT_API_URL}/api/passports/images/${year}/${month}/${type}/${filename}`;
            }
        }
        
        // المسار القديم (احتياطي)
        return `${config.PASSPORT_API_URL}/api/passports/images/passport/${imagePath}`;
        
    } catch (error) {
        console.error('خطأ في بناء رابط الصورة:', error);
        return null;
    }
};

const buildCustomDownloadUrl = (filePath, personName, documentName = 'تأشيرة') => {
    const fileUrl = buildImageUrl(filePath);

    if (!fileUrl) return null;

    const safePersonName = String(personName || 'بدون اسم')
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    const downloadName = `${safePersonName || 'بدون اسم'}-${documentName}`;

    return fileUrl.replace('/api/passports/images/', '/api/passports/download/')
        + `?name=${encodeURIComponent(downloadName)}`;
};

// في دالة fetchPassports
const fetchPassports = async () => {
    try {
        setIsLoading(true);
        console.log('جلب بيانات الجوازات...');
        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports`);
        const data = await response.json();
        console.log('بيانات الجوازات المستلمة:', data);
        setPassports(data);
    } catch (error) {
        console.error('خطأ في جلب الجوازات:', error);
        showAlert('خطأ في جلب البيانات', 'error');
    } finally {
        setIsLoading(false);
    }
};

    // جلب آخر رقم ملف ظاهر للمستخدم
    const fetchLastPassportID = async () => {
        try {
            const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/last`);
            const data = await response.json();
            setLastPassportID(data.lastID);
        } catch (error) {
            console.error('خطأ في جلب آخر رقم ملف:', error);
        }
    };

    fetchDataRef.current = fetchData;

    useEffect(() => {
        autoRefreshStateRef.current = {
            currentPage,
            searchFilters,
            lastUserActivity,
            showForm,
            showQuickEntry,
            showBatchUpload,
            showSettings,
            showDefaultSettings,
            showActivityLog,
            showImageViewer
        };
    }, [currentPage, searchFilters, lastUserActivity, showForm, showQuickEntry, showBatchUpload, showSettings, showDefaultSettings, showActivityLog, showImageViewer]);

    useEffect(() => {
        fetchLastPassportID();

        // تحديث تلقائي كل 4 دقائق بدون إعادة إنشاء interval مع كل حركة أو فتح نافذة.
        const intervalId = setInterval(async () => {
            const state = autoRefreshStateRef.current || {};
            const now = new Date();
            const lastActivity = state.lastUserActivity || now;
            const timeSinceLastActivity = now - lastActivity;
            const fourMinutes = 4 * 60 * 1000;

            if (timeSinceLastActivity >= fourMinutes &&
                !state.showForm &&
                !state.showQuickEntry &&
                !state.showBatchUpload &&
                !state.showSettings &&
                !state.showDefaultSettings &&
                !state.showActivityLog &&
                !state.showImageViewer) {

                try {
                    const loader = fetchDataRef.current || fetchData;
                    await loader(state.currentPage || 1, state.searchFilters || searchFilters);
                    await fetchLastPassportID();
                    showAlert('🔄 تم تحديث البيانات تلقائياً', 'success');
                } catch (error) {
                    console.error('خطأ في التحديث التلقائي:', error);
                }
            }
        }, 30000);

        return () => clearInterval(intervalId);
    }, []);



// useEffect للبحث التلقائي مع debouncing محسن
useEffect(() => {
    const abortController = new AbortController();
    
    const performSearch = async () => {
        try {
            await fetchData(1, debouncedSearchFilters, abortController.signal);
            setCurrentPage(1);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('خطأ في البحث:', error);
            }
        }
    };

    // تأخير قصير لتجنب البحث المفرط
    const timeoutId = setTimeout(performSearch, 100);

    return () => {
        clearTimeout(timeoutId);
        abortController.abort();
    };
}, [debouncedSearchFilters]);


// دالة لتحميل الإعدادات
useEffect(() => {
    const savedSettings = localStorage.getItem('passportWarningSettings');
    if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        // دمج الإعدادات المحفوظة مع الافتراضية لضمان ظهور أي مدد جديدة مثل "4 أشهر"
        setWarningSettings(prev => ({
            ...prev,
            ...parsed,
            passportSettings: {
                ...prev.passportSettings,   // القيم الافتراضية (تشمل 4 أشهر)
                ...parsed.passportSettings  // القيم المحفوظة تطغى على الافتراضية
            },
            licenseSettings: {
                ...prev.licenseSettings,
                ...parsed.licenseSettings
            }
        }));
    }
}, []);


    // معالجة تحديث حالة الاستلام السريع
// معالجة تحديث حالة الاستلام السريع
const handleQuickReceiveUpdate = async (passportId, newStatus, friendName = '', friendPhone = '') => {
    if (newStatus === 'استلم صديق') {
        if (!friendName || !friendPhone) {
            showAlert('يرجى إدخال اسم ورقم هاتف المستلم');
            return;
        }

        // تصحيح التحقق من رقم الهاتف - 11 رقم فقط
        if (!/^01[0-9]{9}$/.test(friendPhone)) {
            showAlert('رقم الهاتف يجب أن يبدأ بـ 01 ويكون 11 رقم', 'error');
            return;
        }
    }

    try {
        // تحديث حالة الاستلام في قاعدة البيانات
        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/${passportId}/receive-status`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                PassportReceiveStatus: newStatus,
                ReceiveDate: new Date().toISOString(),
                FriendName: friendName,
                FriendPhone: friendPhone,
                DeliveredByUserID: (() => {
                    try {
                        const userData = localStorage.getItem('user');
                        if (!userData) return null;
                        const parsed = JSON.parse(userData);
                        return parsed?.UserId || null;
                    } catch { return null; }
                })()
            })
        });

        if (!response.ok) {
            throw new Error('فشل تحديث حالة الاستلام');
        }

        // باقي الكود يفضل زي ما هو...
        const passport = passports.find(p => p.PassportID === passportId);
        if (passport) {
            let phoneNumbers = [];
            try {
                phoneNumbers = JSON.parse(passport.PhoneNumbers || '[]');
            } catch (e) {
                phoneNumbers = [];
            }

            let messages = [];

            switch (newStatus) {
                case 'استلم العميل':
                    if (phoneNumbers[0]) {
                        messages.push({
                            phone: phoneNumbers[0],
                            message: `السلام عليكم ورحمة الله وبركاته
عزيزي/ة ${passport.NameArabic}
نود إعلامكم بأنه تم تحديث حالة جوازكم إلى: استلم العميل
رقم الجواز: ${passport.PassportNumber}
تاريخ الاستلام: ${new Date().toLocaleDateString('ar-EG')}
نشكركم لثقتكم بخدماتنا
مع تحيات فريق خدمة العملاء`
                        });
                    }
                    break;
                    
                case 'استلم المندوب':
                    const delegate = delegates.find(d => d.DelegateID === passport.DelegateID);
                    if (delegate && delegate.DelegatePhone) {
                        messages.push({
                            phone: delegate.DelegatePhone,
                            message: `السلام عليكم ورحمة الله وبركاته
عزيزي ${delegate.DelegateName}
تم تسليم جواز العميل ${passport.NameArabic} إليك
رقم الجواز: ${passport.PassportNumber}
تاريخ التسليم: ${new Date().toLocaleDateString('ar-EG')}
يرجى التأكد من التسليم للعميل في أقرب وقت
مع تحيات فريق خدمة العملاء`
                        });
                    }
                    break;
                    
                case 'استلم صديق':
                    // رسالة للمستلم - استخدام الرقم كما هو (11 رقم)
                    messages.push({
                        phone: friendPhone, // الرقم كما أدخله المستخدم (11 رقم)
                        message: `السلام عليكم ورحمة الله وبركاته
عزيزي/ة ${friendName}
تم تسليم جواز العميل ${passport.NameArabic} إليك
رقم الجواز: ${passport.PassportNumber}
تاريخ التسليم: ${new Date().toLocaleDateString('ar-EG')}
يرجى التأكد من التسليم لصاحب الجواز
مع تحيات فريق خدمة العملاء`
                    });
                    
                    // رسالة لصاحب الجواز
                    if (phoneNumbers[0]) {
                        messages.push({
                            phone: phoneNumbers[0],
                            message: `السلام عليكم ورحمة الله وبركاته
عزيزي/ة ${passport.NameArabic}
نود إعلامكم بأنه تم تحديث حالة جوازكم إلى: استلم صديق
تم الاستلام بواسطة: ${friendName}
رقم الهاتف: ${friendPhone}
رقم الجواز: ${passport.PassportNumber}
تاريخ التسليم: ${new Date().toLocaleDateString('ar-EG')}
مع تحيات فريق خدمة العملاء`
                        });
                    }
                    break;
                    
                case 'لم يستلم':
                    if (phoneNumbers[0]) {
                        messages.push({
                            phone: phoneNumbers[0],
                            message: `السلام عليكم ورحمة الله وبركاته
عزيزي/ة ${passport.NameArabic}
نود إعلامكم بأنه تم تحديث حالة جوازكم إلى: لم يستلم
رقم الجواز: ${passport.PassportNumber}

مع تحيات فريق خدمة العملاء`
                        });
                    }
                    break;
            }

            // إرسال جميع الرسائل
            let successCount = 0;
            let failCount = 0;
            
            for (const msg of messages) {
                try {
                    await sendWhatsAppNotification(msg.phone, msg.message);
                    successCount++;
                } catch (error) {
                    console.error(`فشل إرسال رسالة إلى ${msg.phone}:`, error);
                    failCount++;
                }
            }

            if (successCount > 0 && failCount === 0) {
                showAlert('تم تحديث حالة الاستلام وإرسال الرسائل بنجاح', 'success');
            } else if (successCount > 0 && failCount > 0) {
                showAlert(`تم تحديث حالة الاستلام وإرسال ${successCount} رسالة، فشل في إرسال ${failCount} رسالة`, 'warning');
            } else if (failCount > 0) {
                showAlert('تم تحديث حالة الاستلام ولكن فشل في إرسال الرسائل', 'warning');
            } else {
                showAlert('تم تحديث حالة الاستلام بنجاح', 'success');
            }
        }
        
        await fetchData();
    } catch (error) {
        console.error('خطأ في تحديث حالة الاستلام:', error);
        showAlert('حدث خطأ في تحديث حالة الاستلام');
    }
};


// كان بيفتح برنامج الرسام على جهاز السيرفر (نسخة الديسكتوب). في المتصفح: بيختار الصورة بعد تعديلها على جهازك.
const openPaint = async (imageType) => {
    const openFileDialog = document.createElement('input');
    openFileDialog.type = 'file';
    openFileDialog.accept = 'image/*';
    openFileDialog.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleImageUpload(imageType, file);
            
            // تشغيل OCR إذا كانت الصورة هي صورة الجواز
            if (imageType === 'PassportImage') {
                handleOCR(file);
            }
        }
    };
    openFileDialog.click();
};




const handleImageUpload = async (imageType, file) => {
    if (!file) return;
    
    try {
        // إظهار حالة التحميل
        showAlert('جاري رفع الصورة...', 'info');
        
        const formData = new FormData();
        formData.append(imageType, file);
        formData.append('passportId', editingId || 'temp');
        formData.append('UploadedByUserID', getCurrentUserId() || '');
        
        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/upload-images`, {
            method: 'POST',
            body: formData
        });
        
        if (!response.ok) {
            throw new Error(`HTTP Error: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
            setFormData(prev => ({
                ...prev,
                [`${imageType}Path`]: result.imagePaths[`${imageType}Path`]
            }));
            
            showAlert('تم رفع الصورة بنجاح', 'success');
        } else {
            throw new Error(result.error || 'فشل في رفع الصورة');
        }
        
    } catch (error) {
        console.error('خطأ في رفع الصورة:', error);
        showAlert('خطأ في رفع الصورة: ' + error.message, 'error');
    }
};

    // معالجة تغيير حالة استلام الجواز
    const handleReceiveStatusChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            PassportReceiveStatus: value,
            ReceiveDate: value !== 'اختر الحالة' && value !== 'لم يستلم' ? 
                new Date().toISOString().split('T')[0] : '',
            FriendName: '',
            FriendPhone: ''
        }));
    };

    // معالجة تغيير حالة الجواز
    const handlePassportStatusChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            PassportStatus: value,
            PreviousCompanies: value === 'قديم شركات' ? prev.PreviousCompanies : []
        }));
        setShowPreviousCompaniesSelect(value === 'قديم شركات');
    };
    // معالجة تغيير الرقم القومي
    const handleNationalIDChange = (e) => {
        if (isViewMode) return;

        const value = e.target.value.replace(/\D/g, '').slice(0, 14);
        setFormData(prev => ({ ...prev, NationalID: value }));
        setFieldErrors(prev => ({ ...prev, NationalID: '' }));
        setFieldValidation(prev => ({ ...prev, NationalID: null }));

        if (value.length === 14) {
            const check = validateEgyptianNationalID(value);
            if (!check.isValid) {
                setFieldErrors(prev => ({ ...prev, NationalID: check.error }));
                setFieldValidation(prev => ({ ...prev, NationalID: 'invalid' }));
                return;
            }

            setFormData(prev => ({
                ...prev,
                NationalID: check.nationalID,
                DateOfBirth: check.DateOfBirth,
                Age: check.Age,
                Governorate: check.Governorate
            }));
            setFieldValidation(prev => ({ ...prev, NationalID: 'valid' }));
        }
    };

    // دالة تغيير رقم الجواز
const handlePassportNumberChange = (e) => {
    if (isViewMode) return;

    let value = e.target.value.toUpperCase();
    
    // التحقق من أن القيمة تبدأ دائمًا بحرف "A" ومنع أي تغيير له
    if (!value.startsWith("A")) {
        value = "A" + value.replace(/[^0-9]/g, ""); // إجبار القيمة على البدء بـ "A" ورفض أي رموز غير الأرقام
    } else {
        value = "A" + value.slice(1).replace(/[^0-9]/g, ""); // إزالة أي رموز غير الأرقام بعد "A"
    }

    if (value.length > 9) {
        showAlert("رقم الجواز يجب أن يكون 9 أحرف فقط (حرف واحد و 8 أرقام)");
        return;
    }
    setFormData((prev) => ({ ...prev, PassportNumber: value }));
};


// معالجة تغيير أرقام الهواتف
const handlePhoneNumberChange = (index, value) => {
    if (isViewMode) return;

    if (value && !/^[0-9]*$/.test(value)) {
        showAlert('يرجى إدخال أرقام فقط');
        return;
    }
    if (value && value.length > 11) {
        showAlert('رقم الهاتف يجب أن يكون 11 رقم فقط');
        return;
    }

    const newPhoneNumbers = [...formData.PhoneNumbers];
    newPhoneNumbers[index] = value;

    // السماح بإضافة حقل جديد عند إدخال رقم
    if (index === newPhoneNumbers.length - 1 && value && newPhoneNumbers.length < 3) {
        newPhoneNumbers.push('');
    }

    setFormData(prev => ({
        ...prev,
        PhoneNumbers: newPhoneNumbers
    }));
};
// دالة للتحقق من طول رقم الهاتف عند الانتقال إلى الحقل التالي
const handlePhoneNumberBlur = (index) => {
    if (isViewMode) return;

    const value = formData.PhoneNumbers[index];
    if (value && value.length !== 11) {
        showAlert('رقم الهاتف يجب أن يكون 11 رقم بالضبط');
    }
};





    // معالجة تغيير رقم هاتف المستلم
const handleFriendPhoneChange = (e) => {
    if (isViewMode) return;

    const value = e.target.value;
    if (!/^[0-9]*$/.test(value)) {
        showAlert('يرجى إدخال أرقام فقط');
        return;
    }
    if (value.length > 11) {
        showAlert('رقم الهاتف يجب أن يكون 11 رقم فقط');
        return;
    }
    setFormData(prev => ({ ...prev, FriendPhone: value }));
};
// دالة لإضافة الشركة السابقة تلقائيًا عند اختيارها
const handlePreviousCompanyChange = (e) => {
    const selectedCompanyId = e.target.value;
    
    if (selectedCompanyId && !formData.PreviousCompanies.includes(selectedCompanyId)) {
        setFormData((prev) => ({
            ...prev,
            PreviousCompanies: [...prev.PreviousCompanies, selectedCompanyId],
        }));
        setSelectedPreviousCompany(''); // إعادة تعيين القائمة بعد الإضافة
    }
};

    // معالجة إزالة شركة سابقة
    const handleRemovePreviousCompany = (companyId) => {
        setFormData(prev => ({
            ...prev,
            PreviousCompanies: prev.PreviousCompanies.filter(id => id !== companyId)
        }));
    };

    // الحصول على الشركات المتاحة
    const getAvailableCompanies = () => {
        return companies.filter(company => 
            !formData.PreviousCompanies.includes(company.CompanyID.toString())
        );
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        updateUserActivity(); // ✅ تحديث النشاط
    
        // تعطيل زر الحفظ مؤقتاً لمنع الضغط المتكرر
        if (isLoading) return;
        setIsLoading(true);
    
        // التحقق من البيانات المطلوبة
        if (formData.PassportReceiveStatus === 'استلم صديق' && (!formData.FriendName || !formData.FriendPhone)) {
            showAlert('يرجى إدخال اسم ورقم هاتف المستلم', 'error');
            setIsLoading(false);
            return;
        }
    
        try {
            if (formData.NationalID) {
                const nationalIdCheck = validateEgyptianNationalID(formData.NationalID, formData.DateOfBirth);
                if (!nationalIdCheck.isValid) {
                    showAlert('لا يمكن الحفظ: ' + nationalIdCheck.error, 'error');
                    setIsLoading(false);
                    return;
                }
            }

            const url = editingId 
                ? `${config.PASSPORT_API_URL}/api/passports/${editingId}`
                : `${config.PASSPORT_API_URL}/api/passports`;
                
            const method = editingId ? 'PUT' : 'POST';
    
            // معالجة الملاحظات قبل الإرسال
            const processedNotes = Array.isArray(formData.Notes) 
                ? formData.Notes.filter(note => note.text && note.text.trim() !== '') 
                : [];
    
            // جلب معرف المستخدم الحالي تلقائياً
            const currentUserId = (() => {
                try {
                    const userData = localStorage.getItem('user');
                    if (!userData) return null;
                    const parsed = JSON.parse(userData);
                    return parsed?.UserId || null;
                } catch { return null; }
            })();

            const passportProfessionPayload = getPassportProfessionPayload(formData.PassportProfessionID, formData.PassportProfessionText);

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                },
             body: JSON.stringify({
        ...formData,
        CompanyID: formData.CompanyID && !isNaN(parseInt(formData.CompanyID)) ? parseInt(formData.CompanyID) : null,
        PassportProfessionID: passportProfessionPayload.PassportProfessionID,
        PassportProfessionText: passportProfessionPayload.PassportProfessionText,
        TravelProfessionID: formData.TravelProfessionID && !isNaN(parseInt(formData.TravelProfessionID)) ? parseInt(formData.TravelProfessionID) : null,
        DelegateID: formData.DelegateID && !isNaN(parseInt(formData.DelegateID)) ? parseInt(formData.DelegateID) : null,
        Age: formData.Age && !isNaN(parseInt(formData.Age)) ? parseInt(formData.Age) : null,
        PhoneNumbers: formData.PhoneNumbers?.filter(phone => phone) || [],
        PreviousCompanies: formData.PreviousCompanies || [],
        Notes: JSON.stringify(processedNotes),
        CreatedByUserID: !editingId ? currentUserId : undefined,
        LastModifiedByUserID: editingId ? currentUserId : undefined,
    })
            });
    
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'حدث خطأ في العملية');
            }
    
            const result = await response.json();
            showAlert(editingId ? 'تم تحديث الجواز بنجاح' : 'تم إضافة الجواز بنجاح', 'success');

            // طباعة تلقائية بعد تسجيل جواز جديد حسب إعدادات الباركود المحفوظة
            if (!editingId && result.success) {
                try {
                    const newPassportForPrint = buildNewPassportAutoPrintPayload({
                        ...formData,
                        PhoneNumbers: formData.PhoneNumbers?.filter(phone => phone) || [],
                        PreviousCompanies: formData.PreviousCompanies || []
                    }, result);
                    await autoPrintNewPassportIfEnabled({
                        passport: newPassportForPrint,
                        companies,
                        delegates,
                        professions,
                        showAlert,
                        sourceLabel: 'إضافة جواز جديد'
                    });
                } catch (autoPrintError) {
                    console.error('خطأ في الطباعة التلقائية للباركود:', autoPrintError);
                    showAlert('تم حفظ الجواز ولكن فشلت الطباعة التلقائية: ' + autoPrintError.message, 'warning');
                }
            }
                
                // إرسال رسالة ترحيب للجواز الجديد فقط
                if (!editingId && result.success && formData.PhoneNumbers[0]) {
                    try {
                        const welcomeMessage = `السلام عليكم ورحمة الله وبركاته
        عزيزي/ة ${formData.NameArabic}
        نرحب بكم في مكتبنا ونشكركم لثقتكم بخدماتنا
        تم تسجيل جوازكم بنجاح
        رقم الجواز: ${formData.PassportNumber}
        الشركة: ${companies.find(c => c.CompanyID.toString() === formData.CompanyID)?.CompanyName || 'غير محدد'}
        سيتم إشعاركم بأي تحديثات على حالة الجواز
        مع تحيات فريق خدمة العملاء`;
        
                        await sendWhatsAppNotification(formData.PhoneNumbers[0], welcomeMessage);
                        showAlert('تم إرسال رسالة الترحيب بنجاح', 'success');
                    } catch (welcomeError) {
                        console.error('خطأ في إرسال رسالة الترحيب:', welcomeError);
                        showAlert('تم حفظ الجواز ولكن فشل في إرسال رسالة الترحيب', 'warning');
                    }
                }
                
                // تحديث البيانات فوراً بعد الحفظ
                await fetchLastPassportID();
                await fetchData(currentPage, searchFilters);
                resetForm();
                updateUserActivity(); // ✅ تحديث النشاط مرة أخرى
                
            } catch (error) {
                console.error('خطأ:', error);
                showAlert('حدث خطأ: ' + error.message, 'error');
            } finally {
                setIsLoading(false);
            }
        };

    const handleView = (passport) => {
        updateUserActivity();
        if (!passport) {
            console.error('بيانات الجواز غير موجودة');
            return;
        }
    
        setFormData({
       // البيانات الأساسية
       PassportID: passport.PassportID, // معرف داخلي للحفظ والاستدعاءات فقط
       OfficeSerial: passport.OfficeSerial ?? '',
       SerialNumber: passport.SerialNumber ?? '',
       
       // المعرفات - تحويل آمن
       CompanyID: passport.CompanyID ? passport.CompanyID.toString() : '',
       PassportProfessionID: passport.PassportProfessionID ? passport.PassportProfessionID.toString() : '',
       PassportProfessionText: passport.PassportProfessionText || (!passport.PassportProfessionID ? (passport.PassportProfessionName || '') : ''),
       TravelProfessionID: passport.TravelProfessionID ? passport.TravelProfessionID.toString() : '',
       DelegateID: passport.DelegateID ? passport.DelegateID.toString() : '',
       
       // النصوص - ضمان عدم وجود null أو undefined
       NameArabic: passport.NameArabic || '',
       NameEnglish: passport.NameEnglish || '',
       PassportNumber: passport.PassportNumber || 'A',
       NationalID: passport.NationalID || '',
       Address: passport.Address || '',
       Governorate: passport.Governorate || '',
       
       // الحالات والخيارات
       TravelDuration: passport.TravelDuration || '',
       PassportStatus: passport.PassportStatus || '',
       AbilityTest: passport.AbilityTest || '',
       MilitaryStatus: passport.MilitaryStatus || '',
       LicenseType: passport.LicenseType || '',
       MedicalExamStatus: passport.MedicalExamStatus || '',
       PassportReceiveStatus: passport.PassportReceiveStatus || 'اختر الحالة',
       
       // التواريخ - تنسيق آمن
       PassportIssueDate: formatDateSafe(passport.PassportIssueDate),
       PassportExpiryDate: formatDateSafe(passport.PassportExpiryDate),
       DateOfBirth: formatDateSafe(passport.DateOfBirth),
       LicenseExpiryDate: formatDateSafe(passport.LicenseExpiryDate),
       MedicalExamDate: formatDateSafe(passport.MedicalExamDate),
       NetWorkDate: formatDateSafe(passport.NetWorkDate),
       VisaDate: formatDateSafe(passport.VisaDate),
       TravelDate: formatDateSafe(passport.TravelDate),
       BorderNumber: passport.BorderNumber || '',
       ViolationNotes: passport.ViolationNotes || '',
       ApplicationNumber: passport.ApplicationNumber || '',
      FingerprintDate: formatDateSafe(passport.FingerprintDate),
       MedicalInsuranceDate: formatDateSafe(passport.MedicalInsuranceDate),
       ReceiveDate: formatDateSafe(passport.ReceiveDate),
       
       // العمر - تحويل آمن
       Age: passport.Age ? passport.Age.toString() : '',
       
       // معلومات المستلم
       FriendName: passport.FriendName || '',
       FriendPhone: passport.FriendPhone || '',
       
       // سجل الإنشاء والتعديل والتسليم
       CreatedByUserName: passport.CreatedByUserName || '',
       CreatedAt_Display: passport.CreatedAt || null,
       LastModifiedByUserName: passport.LastModifiedByUserName || '',
       LastModifiedAt_Display: passport.LastModifiedAt || null,
       DeliveredByUserName: passport.DeliveredByUserName || '',
       DeliveredAt_Display: passport.ReceiveDate || null,
       
     // مسارات الصور الجديدة بس
PassportImagePath: passport.PassportImagePath || '',
PersonalImagePath: passport.PersonalImagePath || '',
LicenseImagePath: passport.LicenseImagePath || '',
LicenseBackImagePath: passport.LicenseBackImagePath || '',
IDImagePath: passport.IDImagePath || '',
IDBackImagePath: passport.IDBackImagePath || '',
CompanyContractPath: passport.CompanyContractPath || '',
MinistryContractPath: passport.MinistryContractPath || '',
VisaFilePath: passport.VisaFilePath || '',
OtherAttachmentsPath: passport.OtherAttachmentsPath || '',
       
       // أرقام الهواتف - معالجة محسنة
       PhoneNumbers: (() => {
           try {
               if (!passport.PhoneNumbers) return [''];
               if (typeof passport.PhoneNumbers === 'string') {
                   // إذا كانت نص فارغ
                   if (passport.PhoneNumbers.trim() === '') return [''];
                   // إذا كانت تبدأ برقم (نص عادي)
                   if (/^\d/.test(passport.PhoneNumbers.trim())) {
                       return [passport.PhoneNumbers.trim()];
                   }
                   // إذا كانت JSON
                   const parsed = JSON.parse(passport.PhoneNumbers);
                   return Array.isArray(parsed) && parsed.length > 0 ? parsed : [''];
               }
               return Array.isArray(passport.PhoneNumbers) && passport.PhoneNumbers.length > 0 ? passport.PhoneNumbers : [''];
           } catch (error) {
               console.error('خطأ في معالجة أرقام الهواتف:', error);
               return [''];
           }
       })(),
       
       // الشركات السابقة - معالجة خاصة محسنة
       PreviousCompanies: (() => {
           try {
               console.log('الشركات السابقة الخام:', passport.PreviousCompanies);
               
               if (!passport.PreviousCompanies) return [];
               
               if (typeof passport.PreviousCompanies === 'string') {
                   if (passport.PreviousCompanies === '' || passport.PreviousCompanies === '[]') return [];
                   const parsed = JSON.parse(passport.PreviousCompanies);
                   return Array.isArray(parsed) ? parsed : [];
               }
               
               return Array.isArray(passport.PreviousCompanies) ? passport.PreviousCompanies : [];
           } catch (error) {
               console.error('خطأ في معالجة الشركات السابقة:', error);
               return [];
           }
       })(),
       
       // الملاحظات - معالجة محسنة
       Notes: (() => {
           try {
               if (!passport.Notes || passport.Notes === '') {
                   return [{ 
                       id: Date.now(), 
                       text: '', 
                       date: new Date().toISOString(), 
                       user: 'المستخدم الحالي' 
                   }];
               }
               
               if (typeof passport.Notes === 'string') {
                   // إذا كانت تبدأ بـ [ فهي JSON
                   if (passport.Notes.startsWith('[')) {
                       const parsed = JSON.parse(passport.Notes);
                       return Array.isArray(parsed) && parsed.length > 0 ? parsed : 
                           [{ id: Date.now(), text: '', date: new Date().toISOString(), user: 'المستخدم الحالي' }];
                   } else {
                       // إذا كانت نص عادي
                       return [{ 
                           id: Date.now(), 
                           text: passport.Notes, 
                           date: new Date().toISOString(), 
                           user: 'مستخدم سابق' 
                       }];
                   }
               }
               
               return Array.isArray(passport.Notes) && passport.Notes.length > 0 ? passport.Notes :
                   [{ id: Date.now(), text: '', date: new Date().toISOString(), user: 'المستخدم الحالي' }];
                   
           } catch (error) {
               console.error('خطأ في معالجة الملاحظات:', error);
               return [{ 
                   id: Date.now(), 
                   text: '', 
                   date: new Date().toISOString(), 
                   user: 'المستخدم الحالي' 
               }];
           }
       })()
   });

   // طباعة البيانات المعالجة للتشخيص
   console.log('البيانات بعد المعالجة:', {
       PassportStatus: passport.PassportStatus,
       AbilityTest: passport.AbilityTest,
       NationalID: passport.NationalID,
       MilitaryStatus: passport.MilitaryStatus,
       Address: passport.Address,
       Governorate: passport.Governorate,
       DateOfBirth: passport.DateOfBirth,
       Age: passport.Age,
       MedicalExamStatus: passport.MedicalExamStatus,
       LicenseType: passport.LicenseType,
       PreviousCompanies: passport.PreviousCompanies,
    
       PhoneNumbers: passport.PhoneNumbers
   });

   // تنظيف بيانات OCR السابقة
   setOcrData({});
   setIsOcrProcessing(false);
   setOcrProgress(0);

   // تعيين الحالات
   setIsViewMode(true);
   setShowForm(true);
   setShowPreviousCompaniesSelect(passport.PassportStatus === 'قديم شركات');
};

const handleEdit = (passport) => {
    updateUserActivity();
    console.log('البيانات الخام للتعديل:', passport);
    
    // نفس الكود تماماً كما في handleView
    setFormData({
        PassportID: passport.PassportID, // معرف داخلي للحفظ والاستدعاءات فقط
        OfficeSerial: passport.OfficeSerial ?? '',
        SerialNumber: passport.SerialNumber ?? '',
        CompanyID: passport.CompanyID ? passport.CompanyID.toString() : '',
        PassportProfessionID: passport.PassportProfessionID ? passport.PassportProfessionID.toString() : '',
       PassportProfessionText: passport.PassportProfessionText || (!passport.PassportProfessionID ? (passport.PassportProfessionName || '') : ''),
        TravelProfessionID: passport.TravelProfessionID ? passport.TravelProfessionID.toString() : '',
        DelegateID: passport.DelegateID ? passport.DelegateID.toString() : '',
        NameArabic: passport.NameArabic || '',
        NameEnglish: passport.NameEnglish || '',
        PassportNumber: passport.PassportNumber || 'A',
        NationalID: passport.NationalID || '',
        Address: passport.Address || '',
        Governorate: passport.Governorate || '',
        TravelDuration: passport.TravelDuration || '',
        PassportStatus: passport.PassportStatus || '',
        AbilityTest: passport.AbilityTest || '',
        MilitaryStatus: passport.MilitaryStatus || '',
        LicenseType: passport.LicenseType || '',
        MedicalExamStatus: passport.MedicalExamStatus || '',
        PassportReceiveStatus: passport.PassportReceiveStatus || 'اختر الحالة',
        PassportIssueDate: formatDateSafe(passport.PassportIssueDate),
        PassportExpiryDate: formatDateSafe(passport.PassportExpiryDate),
        DateOfBirth: formatDateSafe(passport.DateOfBirth),
        LicenseExpiryDate: formatDateSafe(passport.LicenseExpiryDate),
        MedicalExamDate: formatDateSafe(passport.MedicalExamDate),
        NetWorkDate: formatDateSafe(passport.NetWorkDate),
        VisaDate: formatDateSafe(passport.VisaDate),
        TravelDate: formatDateSafe(passport.TravelDate),
        BorderNumber: passport.BorderNumber || '',
        ViolationNotes: passport.ViolationNotes || '',
        ApplicationNumber: passport.ApplicationNumber || '',
FingerprintDate: formatDateSafe(passport.FingerprintDate),
        MedicalInsuranceDate: formatDateSafe(passport.MedicalInsuranceDate),
        ReceiveDate: formatDateSafe(passport.ReceiveDate),
        Age: passport.Age ? passport.Age.toString() : '',
        FriendName: passport.FriendName || '',
        FriendPhone: passport.FriendPhone || '',
      PassportImagePath: passport.PassportImagePath || '',
        PersonalImagePath: passport.PersonalImagePath || '',
        LicenseImagePath: passport.LicenseImagePath || '',
        LicenseBackImagePath: passport.LicenseBackImagePath || '',
        IDImagePath: passport.IDImagePath || '',
        IDBackImagePath: passport.IDBackImagePath || '',
        CompanyContractPath: passport.CompanyContractPath || '',
        MinistryContractPath: passport.MinistryContractPath || '',
        VisaFilePath: passport.VisaFilePath || '',
        OtherAttachmentsPath: passport.OtherAttachmentsPath || '',
        
        // سجل الإنشاء والتعديل والتسليم
        CreatedByUserName: passport.CreatedByUserName || '',
        CreatedAt_Display: passport.CreatedAt || null,
        LastModifiedByUserName: passport.LastModifiedByUserName || '',
        LastModifiedAt_Display: passport.LastModifiedAt || null,
        DeliveredByUserName: passport.DeliveredByUserName || '',
        DeliveredAt_Display: passport.ReceiveDate || null,
        
        // نفس معالجة الهواتف والشركات والملاحظات...
        PhoneNumbers: (() => {
            try {
                if (!passport.PhoneNumbers) return [''];
                if (typeof passport.PhoneNumbers === 'string') {
                    const parsed = JSON.parse(passport.PhoneNumbers);
                    return Array.isArray(parsed) && parsed.length > 0 ? parsed : [''];
                }
                return Array.isArray(passport.PhoneNumbers) && passport.PhoneNumbers.length > 0 ? passport.PhoneNumbers : [''];
            } catch {
                return [''];
            }
        })(),
        
        PreviousCompanies: (() => {
            try {
                if (!passport.PreviousCompanies) return [];
                if (typeof passport.PreviousCompanies === 'string') {
                    if (passport.PreviousCompanies === '') return [];
                    const parsed = JSON.parse(passport.PreviousCompanies);
                    return Array.isArray(parsed) ? parsed : [];
                }
                return Array.isArray(passport.PreviousCompanies) ? passport.PreviousCompanies : [];
            } catch {
                return [];
            }
        })(),
        
        Notes: (() => {
            try {
                if (!passport.Notes || passport.Notes === '') {
                    return [{ 
                        id: Date.now(), 
                        text: '', 
                        date: new Date().toISOString(), 
                        user: 'المستخدم الحالي' 
                    }];
                }
                if (typeof passport.Notes === 'string') {
                    if (passport.Notes.startsWith('[')) {
                        const parsed = JSON.parse(passport.Notes);
                        return Array.isArray(parsed) && parsed.length > 0 ? parsed : 
                            [{ id: Date.now(), text: '', date: new Date().toISOString(), user: 'المستخدم الحالي' }];
                    } else {
                        return [{ 
                            id: Date.now(), 
                            text: passport.Notes, 
                            date: new Date().toISOString(), 
                            user: 'مستخدم سابق' 
                        }];
                    }
                }
                return Array.isArray(passport.Notes) && passport.Notes.length > 0 ? passport.Notes :
                    [{ id: Date.now(), text: '', date: new Date().toISOString(), user: 'المستخدم الحالي' }];
            } catch {
                return [{ 
                    id: Date.now(), 
                    text: '', 
                    date: new Date().toISOString(), 
                    user: 'المستخدم الحالي' 
                }];
            }
        })()
    });

    setEditingId(passport.PassportID);
    setShowForm(true);
    setShowPreviousCompaniesSelect(passport.PassportStatus === 'قديم شركات');
    setIsViewMode(false);
};

const handleSmartRepeat = (passport) => {
    updateUserActivity();
    
    // تحضير بيانات الجواز للتكرار الذكي
    const passportData = [{
        NameArabic: passport.NameArabic,
        PassportNumber: passport.PassportNumber,
        NationalID: passport.NationalID,
        Age: passport.Age,
        PassportProfessionName: passport.PassportProfessionName || passport.PassportProfessionText || professions.find(p => p.ProfessionID === passport.PassportProfessionID)?.ProfessionName || '',
        CompanyName: companies.find(c => c.CompanyID === passport.CompanyID)?.CompanyName || '',
        DelegateName: delegates.find(d => d.DelegateID === passport.DelegateID)?.DelegateName || '',
        PhoneNumbers: (() => {
            try {
                if (!passport.PhoneNumbers) return '';
                if (/^\d/.test(passport.PhoneNumbers.trim())) {
                    return passport.PhoneNumbers.trim();
                }
                const parsed = JSON.parse(passport.PhoneNumbers);
                return Array.isArray(parsed) ? (parsed[0] || '') : passport.PhoneNumbers;
            } catch (error) {
                return passport.PhoneNumbers || '';
            }
        })(),
        Governorate: passport.Governorate,
        MedicalExamDate: passport.MedicalExamDate,
        VisaDate: passport.VisaDate,
        TravelDate: passport.TravelDate,
        PassportPhotoUrl: passport.PassportPhotoUrl,
        PassportImagePath: passport.PassportPhotoUrl,
    }];

    // فتح النافذة السريعة بدلاً من الانتقال لصفحة التكرار الذكي
    setSmartRepeatPassportData(passportData);
    setShowSmartRepeatModal(true);
};
    // معالجة الحذف
// استبدال دالة handleDelete في PassportManagement.js
// تحديث دالة handleDelete في PassportManagement.js
// تحديث دالة handleDelete في PassportManagement.js
const handleDelete = async (passportId) => {
    if (!passportId || isNaN(passportId)) {
        showAlert('معرف الجواز غير صحيح', 'error');
        return;
    }

    try {
        setIsLoading(true);
        
        // أولاً: معاينة ما سيتم حذفه
        showAlert('جاري فحص البيانات المرتبطة...', 'warning');
        
        const previewResponse = await fetch(`${config.PASSPORT_API_URL}/api/passports/${passportId}/delete-preview`);
        const previewData = await previewResponse.json();

        if (!previewResponse.ok) {
            throw new Error(previewData.error || 'خطأ في فحص البيانات');
        }

        // إنشاء رسالة تأكيد مفصلة
        let confirmMessage = `هل أنت متأكد من حذف:\n`;
        confirmMessage += `📋 الاسم: ${previewData.passport.NameArabic}\n`;
        confirmMessage += `🆔 رقم الجواز: ${passportId}\n\n`;

        if (previewData.totalRelatedRecords > 0) {
            confirmMessage += `⚠️ تحذير خطير: سيتم حذف ${previewData.totalRelatedRecords} سجل مرتبط:\n\n`;
            
            Object.entries(previewData.relatedData).forEach(([table, count]) => {
                if (count > 0) {
                    const tableNames = {
                        'VisaLogs': 'سجلات التأشيرات',
                        'PassportVisaActions': 'إجراءات التأشيرات',
                        'ClientTrips': 'رحلات العملاء',
                        'ClientRequiredPaymentHistory': 'تاريخ المدفوعات',
                        'ClientTransfers': 'تحويلات العملاء',
                        'DelegateCommissions': 'عمولات المناديب',
                        'FlightListDetails': 'تفاصيل الرحلات',
                        'ClientAccounts': 'حسابات العملاء',
                        'transactions': 'المعاملات المالية',
                        'PassportVisas': 'تأشيرات الجواز'
                    };
                    
                    confirmMessage += `• ${tableNames[table] || table}: ${count} سجل\n`;
                }
            });
            
            confirmMessage += `\n❌ هذا الإجراء نهائي ولا يمكن التراجع عنه!\n`;
            confirmMessage += `💰 قد يؤثر على الحسابات المالية والتقارير!\n\n`;
            confirmMessage += `هل أنت متأكد 100% من المتابعة؟`;
        } else {
            confirmMessage += `✅ ${previewData.warning}\n\nهل تريد المتابعة؟`;
        }

        const confirmed = window.confirm(confirmMessage);
        
        if (!confirmed) {
            setIsLoading(false);
            return;
        }

        // ثانياً: تنفيذ الحذف
        showAlert('جاري حذف الجواز والبيانات المرتبطة... يرجى الانتظار', 'warning');

        const deleteResponse = await fetch(`${config.PASSPORT_API_URL}/api/passports/${passportId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ DeletedByUserID: getCurrentUserId() })
        });
        
        const deleteResult = await deleteResponse.json();
        
        if (!deleteResponse.ok) {
            throw new Error(deleteResult.error || `خطأ HTTP: ${deleteResponse.status}`);
        }

        // إظهار تفاصيل ما تم حذفه
        let successMessage = deleteResult.message;
        if (deleteResult.details && deleteResult.details.deletedRelatedRecords > 0) {
            successMessage += `\n\nتم حذف ${deleteResult.details.deletedRelatedRecords} سجل مرتبط من ${deleteResult.details.affectedTables} جدول`;
        }

        showAlert(successMessage, 'success');
        
        // تحديث البيانات
        await fetchData();
        
    } catch (error) {
        console.error('خطأ في حذف الجواز:', error);
        showAlert('حدث خطأ في حذف الجواز: ' + error.message, 'error');
    } finally {
        setIsLoading(false);
    }
};

    // دالة إزالة التواريخ والحالة
// احذف دالة handleClearDates واستبدلها بهذه الدالة
const handleAddNote = async (passportId, passportName) => {
    const noteText = window.prompt(`إضافة ملاحظة للعميل: ${passportName}\n\nاكتب الملاحظة:`);
    
    if (!noteText || noteText.trim() === '') {
        return; // المستخدم ألغى أو لم يكتب شيء
    }

    try {
        setIsLoading(true);
        
        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/${passportId}/add-note`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                note: noteText.trim(),
                noteType: 'ملاحظة يدوية',
                NoteByUserID: getCurrentUserId()
            })
        });

        if (!response.ok) {
            throw new Error('فشل في إضافة الملاحظة');
        }

        const result = await response.json();
        
        if (result.success) {
            showAlert('تم إضافة الملاحظة بنجاح', 'success');
            await fetchData(); // تحديث البيانات
        } else {
            throw new Error(result.error || 'فشل في إضافة الملاحظة');
        }
        
    } catch (error) {
        console.error('خطأ في إضافة الملاحظة:', error);
        showAlert('حدث خطأ في إضافة الملاحظة: ' + error.message, 'error');
    } finally {
        setIsLoading(false);
    }
};

// إضافة الحقول المطلوبة للإدخال السريع
const quickEntryFields = [
    {
        key: 'CompanyID',
        title: 'الشركة',
        type: 'select',
        required: true,
        options: () => companies.map((company, index) => ({
            value: company.CompanyID,
            label: `${index + 1}. ${company.CompanyName}`
        }))
    },
    {
        key: 'TravelDuration',
        title: 'مدة السفر',
        type: 'select',
        required: true,
        options: () => travelDurations.map((duration, index) => ({
            value: duration,
            label: `${index + 1}. ${duration}`
        }))
    },
    {
        key: 'PassportStatus',
        title: 'حالة الجواز',
        type: 'select',
        required: true,
        options: () => passportStatuses.map((status, index) => ({
            value: status,
            label: `${index + 1}. ${status}`
        }))
    },
    {
        key: 'PreviousCompanies',
        title: 'الشركات السابقة',
        type: 'multiselect',
        required: false,
        condition: (data) => data.PassportStatus === 'قديم شركات',
        options: () => companies.map((company, index) => ({
            value: company.CompanyID.toString(),
            label: `${index + 1}. ${company.CompanyName}`
        }))
    },
    {
        key: 'AbilityTest',
        title: 'نتيجة اختبار القدرات',
        type: 'select',
        required: true,
        options: () => abilityTests.map((test, index) => ({
            value: test,
            label: `${index + 1}. ${test}`
        }))
    },
    {
        key: 'NameArabic',
        title: 'الاسم بالعربي',
        type: 'text',
        required: true,
        placeholder: 'أدخل الاسم الكامل بالعربي',
        validation: (value) => {
            if (!value) return 'الاسم مطلوب';
            if (!/^[\u0600-\u06FF\s]+$/.test(value)) return 'يجب استخدام العربية فقط';
            return null;
        }
    },
    {
        key: 'NameEnglish',
        title: 'الاسم بالإنجليزي',
        type: 'text',
        required: true,
        placeholder: 'Enter full name in English',
        validation: (value) => {
            if (!value) return 'Name is required';
            if (!/^[A-Za-z\s]+$/.test(value)) return 'English letters only';
            return null;
        }
    },
    {
        key: 'PassportNumber',
        title: 'رقم الجواز',
        type: 'text',
        required: true,
        placeholder: 'A12345678',
        validation: (value) => {
            if (!value) return 'رقم الجواز مطلوب';
            if (!/^A[0-9]{8}$/.test(value)) return 'يجب أن يبدأ بـ A و8 أرقام';
            return null;
        }
    },
    {
        key: 'NationalID',
        title: 'الرقم القومي',
        type: 'text',
        required: true,
        placeholder: '12345678901234',
        validation: (value) => {
            if (!value) return 'الرقم القومي مطلوب';
            if (!/^[0-9]{14}$/.test(value)) return 'يجب أن يكون 14 رقم';
            return null;
        }
    },
    {
        key: 'PassportProfessionID',
        title: 'المهنة بالجواز',
        type: 'select',
        required: true,
        searchable: true,
        condition: () => getPassportProfessionInputMode() === 'select',
        placeholder: 'ابحث واختر المهنة من القائمة',
        helpText: 'وضع مهنة الجواز مضبوط على الاختيار من القائمة فقط.',
        options: () => professions.map((profession, index) => ({
            value: profession.ProfessionID,
            label: `${index + 1}. ${profession.ProfessionName}`,
            searchText: profession.ProfessionName
        })),
        validation: (value) => {
            if (!value) return 'المهنة بالجواز مطلوبة';
            return null;
        }
    },
    {
        key: 'PassportProfessionText',
        title: 'المهنة بالجواز',
        type: 'text',
        required: true,
        condition: () => getPassportProfessionInputMode() === 'text',
        placeholder: 'اكتب المهنة بالجواز نصيًا',
        helpText: 'وضع مهنة الجواز مضبوط على الكتابة النصية فقط، ولن يتم إضافتها لقائمة المهن.',
        validation: (value) => {
            if (!value || !String(value).trim()) return 'المهنة بالجواز مطلوبة';
            return null;
        }
    },
    {
        key: 'TravelProfessionID',
        title: 'مهنة السفر',
        type: 'select',
        required: true,
        options: () => professions.map((profession, index) => ({
            value: profession.ProfessionID,
            label: `${index + 1}. ${profession.ProfessionName}`
        }))
    },
    {
        key: 'MilitaryStatus',
        title: 'الموقف من التجنيد',
        type: 'select',
        required: true,
        options: () => militaryStatuses.map((status, index) => ({
            value: status,
            label: `${index + 1}. ${status}`
        }))
    },
    {
        key: 'PassportIssueDate',
        title: 'تاريخ إصدار الجواز',
        type: 'date',
        required: false,
        placeholder: 'اختر تاريخ إصدار الجواز (اختياري)'
    },
    {
        key: 'PassportExpiryDate',
        title: 'تاريخ انتهاء الجواز',
        type: 'date',
        required: true,
        placeholder: 'اختر التاريخ'
    },
    {
        key: 'Address',
        title: 'العنوان',
        type: 'text',
        required: true,
        placeholder: 'أدخل العنوان كاملاً',
        validation: (value) => {
            if (!value) return 'العنوان مطلوب';
            if (!/^[\u0600-\u06FF\s\d،,\/\-]+$/.test(value)) return 'يجب استخدام العربية فقط';
            return null;
        }
    },
    {
        key: 'DelegateID',
        title: 'المندوب',
        type: 'select',
        required: true,
        options: () => delegates.map((delegate, index) => ({
            value: delegate.DelegateID,
            label: `${index + 1}. ${delegate.DelegateName}`
        }))
    },
    {
        key: 'PhoneNumbers',
        title: 'رقم الهاتف',
        type: 'phone',
        required: true,
        placeholder: '01xxxxxxxxx',
        validation: (value) => {
            if (!value) return 'رقم الهاتف مطلوب';
            if (!/^01[0-9]{9}$/.test(value)) return 'يجب أن يبدأ بـ 01 و11 رقم';
            return null;
        }
    },

// في مصفوفة quickEntryFields، استبدل حقول الصور الأربعة بهذا الحقل الواحد:
{
    key: 'Images',
    title: 'رفع الصور',
    type: 'images',
    required: false,
    description: 'اختر الصور التي تريد رفعها (كلها اختيارية)'
},
];

// استبدل دالة getVisibleQuickEntryFields الموجودة بهذه النسخة المحدثة:
const getVisibleQuickEntryFields = () => {
    const passportProfessionMode = getPassportProfessionInputMode();
    return quickEntryFields.filter(field => {
        // تطبيق شرط الرؤية العادي (مثل الشركات السابقة أو وضع مهنة الجواز)
        if (field.condition && !field.condition(quickEntryData)) {
            return false;
        }

        // لو مهنة الجواز لها قيمة افتراضية في نفس الوضع الحالي، نخفي خطوة المهنة من الإدخال السريع
        if (passportProfessionMode === 'text' && field.key === 'PassportProfessionText' && defaultSettings.enabled.PassportProfessionText && defaultSettings.values.PassportProfessionText) {
            return false;
        }
        if (passportProfessionMode === 'select' && field.key === 'PassportProfessionID' && defaultSettings.enabled.PassportProfessionID && defaultSettings.values.PassportProfessionID) {
            return false;
        }
        
        // إخفاء الحقول التي لها قيم افتراضية مفعّلة
        if (defaultSettings.enabled[field.key] && defaultSettings.values[field.key]) {
            // للحقول التي قد تكون arrays (مثل الشركات السابقة)
            if (Array.isArray(defaultSettings.values[field.key])) {
                return defaultSettings.values[field.key].length === 0;
            }
            // للحقول العادية
            return false;
        }
        
        return true;
    });
};

// دالة التنقل للحقل التالي
const goToNextField = () => {
    const visibleFields = getVisibleQuickEntryFields();
    
    if (quickEntryStep < visibleFields.length - 1) {
        setQuickEntryStep(prev => prev + 1);
        setQuickEntryError('');
        setSearchTerm(''); // مسح البحث عند الانتقال
    } else {
        handleQuickEntrySubmit();
    }
};

// دالة التنقل للحقل السابق
const goToPreviousField = () => {
    if (quickEntryStep > 0) {
        setQuickEntryStep(quickEntryStep - 1);
        setQuickEntryError('');
    }
};

// دالة معالجة إدخال البيانات
const handleQuickEntryInput = (value) => {
    const visibleFields = getVisibleQuickEntryFields();
    
    // التحقق من صحة الخطوة الحالية
    if (quickEntryStep >= visibleFields.length || quickEntryStep < 0) {
        console.error('خطوة غير صحيحة:', quickEntryStep);
        return;
    }
    
    const currentField = visibleFields[quickEntryStep];
    
    // التحقق من وجود الحقل
    if (!currentField || !currentField.key) {
        console.error('حقل غير صحيح:', currentField);
        return;
    }
    
    let processedValue = value;
    
    // معالجة القيم حسب نوع الحقل
    if (currentField.key === 'NameEnglish') {
        processedValue = value.toUpperCase();
    } else if (currentField.key === 'PassportNumber') {
        processedValue = value.toUpperCase();
    } else if (currentField.key === 'NationalID') {
        processedValue = value.replace(/\D/g, '');
    } else if (currentField.key === 'PhoneNumbers') {
        processedValue = value.replace(/\D/g, '');
    }
    
    setQuickEntryData(prev => ({
        ...prev,
        [currentField.key]: processedValue
    }));
    
    setQuickEntryError('');
};

// دالة معالجة الاختيار المتعدد
const handleMultiSelectInput = (value) => {
    const currentArray = quickEntryData.PreviousCompanies || [];
    let newArray;
    
    if (currentArray.includes(value)) {
        newArray = currentArray.filter(item => item !== value);
    } else {
        newArray = [...currentArray, value];
    }
    
    setQuickEntryData({
        ...quickEntryData,
        PreviousCompanies: newArray
    });
};

// تحديث دالة validateCurrentField
const validateCurrentField = (directValue = null) => {
    const visibleFields = getVisibleQuickEntryFields();
    
    if (quickEntryStep >= visibleFields.length || quickEntryStep < 0) {
        return false;
    }
    
    const currentField = visibleFields[quickEntryStep];
    if (!currentField) return false;
    
    const value = directValue !== null ? directValue : quickEntryData[currentField.key];
    
    // حقول الصور ليست مطلوبة
    if (currentField.type === 'images' || currentField.type === 'textarea') {
        return true;
    }
    
    if (currentField.required && (!value || value === '')) {
        setQuickEntryError(`${currentField.title} مطلوب`);
        return false;
    }
    
    if (currentField.validation && value) {
        const error = currentField.validation(value);
        if (error) {
            setQuickEntryError(error);
            return false;
        }
    }
    
    return true;
};


const handleQuickEntryKeyDown = (e) => {
    const visibleFields = getVisibleQuickEntryFields();
    
    if (quickEntryStep >= visibleFields.length || quickEntryStep < 0) {
        return;
    }
    
    const currentField = visibleFields[quickEntryStep];
    if (!currentField) return;
    
    if (e.key === 'Enter') {
        e.preventDefault();
        
        // معالجة خاصة للملاحظات
        if (currentField.type === 'textarea') {
            if (!e.shiftKey) { // Enter بدون Shift = حفظ
                console.log('حفظ من معالج المفاتيح');
                setQuickEntryError('');
                setTimeout(() => {
                    handleQuickEntrySubmit(); // استدعاء دالة الحفظ
                }, 100);
            }
            return; // إيقاف المعالجة هنا للملاحظات
        }
        
        if (currentField.type === 'images') {
            setQuickEntryError('');
            setTimeout(() => {
                goToNextField();
            }, 100);
            return;
        }
        
        if (currentField.type === 'multiselect') {
            setQuickEntryError('');
            setTimeout(() => {
                goToNextField();
            }, 100);
            return;
        }
        
        // للحقول العادية والـ select
        if (currentField.type === 'select') {
            const typedValue = String(e.target.value || '').trim();
            const selectedIndex = parseInt(typedValue, 10) - 1;
            const options = currentField.options();
            
            if (selectedIndex >= 0 && selectedIndex < options.length) {
                const newValue = options[selectedIndex].value;
                setQuickEntryData(prev => ({
                    ...prev,
                    [currentField.key]: newValue
                }));
                
                e.target.value = ''; // مسح الحقل
                setQuickEntryError('');
                setTimeout(() => {
                    goToNextField();
                }, 100);
            } else if (currentField.allowCustom && typedValue) {
                setQuickEntryData(prev => ({
                    ...prev,
                    [currentField.key]: typedValue
                }));
                e.target.value = '';
                setQuickEntryError('');
                setTimeout(() => {
                    goToNextField();
                }, 100);
            } else {
                setQuickEntryError(currentField.allowCustom ? 'اكتب قيمة صحيحة أو اختر رقم من 1 إلى ' + options.length : 'رقم غير صحيح، اختر من 1 إلى ' + options.length);
            }
            return;
        }
        
        // للحقول النصية
        const currentValue = quickEntryData[currentField.key];
        if (validateCurrentField(currentValue)) {
            setQuickEntryError('');
            setTimeout(() => {
                goToNextField();
            }, 100);
        }
    } else if (e.key === 'Escape') {
        resetQuickEntry();
    }
};



// دالة معالجة إدخال رقم الجواز - محدثة
const handlePassportNumberInput = (value) => {
    // السماح بالأرقام فقط، حد أقصى 8 أرقام
    const numbersOnly = value.replace(/\D/g, '').substring(0, 8);
    
    setQuickEntryData({
        ...quickEntryData,
        PassportNumber: 'A' + numbersOnly
    });
    
    setQuickEntryError('');
};

// دالة إعادة تعيين الإدخال السريع
// دالة إعادة تعيين الإدخال السريع - محدثة
// في دالة resetQuickEntry، أضف تحديد القيم الافتراضية:
const resetQuickEntry = () => {
    setShowQuickEntry(false);
    setQuickEntryStep(0);
    setQuickEntryData({});
    setQuickEntryError('');
    setSearchTerm('');
    
    // إضافة تنبيه إذا كانت هناك إعدادات افتراضية
    const defaultFieldsCount = Object.keys(defaultSettings.enabled)
        .filter(key => defaultSettings.enabled[key] && defaultSettings.values[key]).length;
    
    if (defaultFieldsCount > 0) {
        console.log(`🔧 سيتم تطبيق ${defaultFieldsCount} إعداد افتراضي تلقائياً`);
    }
};

const handleQuickEntrySubmit = async () => {
    try {
        console.log('بدء عملية الحفظ...');
        setIsLoading(true);
        
        // دمج البيانات المُدخلة مع القيم الافتراضية
        const mergedData = { ...quickEntryData };
        
        // تطبيق القيم الافتراضية المفعّلة (ما عدا DefaultNote تتعالج بشكل منفصل)
        Object.keys(defaultSettings.enabled).forEach(fieldKey => {
            if (fieldKey === 'DefaultNote') return; // تُعالج بشكل منفصل أدناه
            if (defaultSettings.enabled[fieldKey] && defaultSettings.values[fieldKey]) {
                if (!mergedData[fieldKey] || 
                    (Array.isArray(mergedData[fieldKey]) && mergedData[fieldKey].length === 0)) {
                    mergedData[fieldKey] = defaultSettings.values[fieldKey];
                }
            }
        });
        
        console.log('البيانات للحفظ:', mergedData);

        if (mergedData.NationalID) {
            const nationalIdCheck = validateEgyptianNationalID(mergedData.NationalID, mergedData.DateOfBirth);
            if (!nationalIdCheck.isValid) throw new Error('الرقم القومي غير صحيح: ' + nationalIdCheck.error);
        }
        
        // تحضير البيانات للإرسال
        const dataToSubmit = {
            ...mergedData,
            // إضافة البيانات المحسوبة من الرقم القومي
            ...(mergedData.NationalID ? calculateDataFromNationalID(mergedData.NationalID) : {}),
            // تحويل رقم الهاتف إلى array
            // معالجة أرقام الهواتف بشكل صحيح
PhoneNumbers: (() => {
    if (!mergedData.PhoneNumbers) {
        return ['', '', ''];
    }
    
    let phones;
    if (Array.isArray(mergedData.PhoneNumbers)) {
        // إذا كانت array من الإدخال السريع
        phones = mergedData.PhoneNumbers.filter(phone => phone && phone.trim());
    } else {
        // إذا كانت string واحد
        phones = [mergedData.PhoneNumbers];
    }
    
    // التأكد من وجود 3 عناصر كما يتوقع النظام
    while (phones.length < 3) {
        phones.push('');
    }
    
    return phones.slice(0, 3); // أقصى 3 أرقام
})(),
            // تحويل الملاحظات إلى format مناسب مع دمج الملاحظة الافتراضية
            Notes: (() => {
                const notesList = [];
                // الملاحظة الافتراضية أولاً (إن وجدت)
                if (defaultSettings.enabled.DefaultNote &&
                    defaultSettings.values.DefaultNote &&
                    defaultSettings.values.DefaultNote.trim()) {
                    notesList.push({
                        id: Date.now(),
                        text: defaultSettings.values.DefaultNote.trim(),
                        date: new Date().toISOString(),
                        user: 'المستخدم الحالي'
                    });
                }
                // ملاحظة الإدخال السريع (إن وجدت)
                if (mergedData.Notes && mergedData.Notes.trim()) {
                    notesList.push({
                        id: Date.now() + 1,
                        text: mergedData.Notes.trim(),
                        date: new Date().toISOString(),
                        user: 'الإدخال السريع'
                    });
                }
                return JSON.stringify(notesList);
            })(),
            // إضافة الحقول الافتراضية
            PassportReceiveStatus: 'اختر الحالة',
            PreviousCompanies: mergedData.PreviousCompanies || []
        };

        const quickPassportProfessionPayload = getPassportProfessionPayload(
            dataToSubmit.PassportProfessionID,
            dataToSubmit.PassportProfessionText,
            getPassportProfessionInputMode()
        );
        dataToSubmit.PassportProfessionID = quickPassportProfessionPayload.PassportProfessionID;
        dataToSubmit.PassportProfessionText = quickPassportProfessionPayload.PassportProfessionText;
        


        ['PassportImage', 'PersonalImage', 'LicenseImage', 'LicenseBackImage',
 'IDImage', 'IDBackImage', 'CompanyContract', 'MinistryContract',
 'VisaFile', 'OtherAttachments'].forEach(key => delete dataToSubmit[key]);
 
        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
           body: JSON.stringify({
    ...dataToSubmit,
    CreatedByUserID: (() => {
        try {
            const userData = localStorage.getItem('user');
            if (!userData) return null;
            const parsed = JSON.parse(userData);
            return parsed?.UserId || null;
        } catch { return null; }
    })()
})
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'حدث خطأ في العملية');
        }
        const result = await response.json();
if (result.passportId) {
    const attachmentKeys = [
        'PassportImage', 'PersonalImage', 'LicenseImage', 'LicenseBackImage',
        'IDImage', 'IDBackImage', 'CompanyContract', 'MinistryContract',
        'VisaFile', 'OtherAttachments'
    ];
    const presentKeys = attachmentKeys.filter(
        key => quickEntryData[key] && quickEntryData[key].startsWith('data:')
    );
    if (presentKeys.length > 0) {
        const uploadFormData = new FormData();
        presentKeys.forEach(key => {
            const dataUrl = quickEntryData[key];
            const [header, base64Data] = dataUrl.split(',');
            const mimeType = header.match(/data:(.*?);base64/)?.[1] || 'application/octet-stream';
            const byteString = atob(base64Data);
            const byteArray = new Uint8Array(byteString.length);
            for (let i = 0; i < byteString.length; i++) {
                byteArray[i] = byteString.charCodeAt(i);
            }
            const extension = mimeType === 'application/pdf' ? 'pdf' : (mimeType.split('/')[1] || 'jpg');
            const file = new File([byteArray], `${key}.${extension}`, { type: mimeType });
            uploadFormData.append(key, file);
        });
        uploadFormData.append('passportId', result.passportId);
        uploadFormData.append('UploadedByUserID', getCurrentUserId() || '');
        await fetch(`${config.PASSPORT_API_URL}/api/passports/upload-images`, {
            method: 'POST',
            body: uploadFormData
        });
    }
}
        
        try {
            const quickPassportForPrint = buildNewPassportAutoPrintPayload({
                ...dataToSubmit,
                PhoneNumbers: dataToSubmit.PhoneNumbers || [],
                PreviousCompanies: dataToSubmit.PreviousCompanies || []
            }, result);
            await autoPrintNewPassportIfEnabled({
                passport: quickPassportForPrint,
                companies,
                delegates,
                professions,
                showAlert,
                sourceLabel: 'الإدخال السريع'
            });
        } catch (autoPrintError) {
            console.error('خطأ في الطباعة التلقائية بعد الإدخال السريع:', autoPrintError);
            showAlert('تم حفظ الجواز من الإدخال السريع ولكن فشلت الطباعة التلقائية: ' + autoPrintError.message, 'warning');
        }

        const manualFields = Object.keys(quickEntryData).length;
        const defaultFields = Object.keys(defaultSettings.enabled)
            .filter(key => defaultSettings.enabled[key] && defaultSettings.values[key]).length;
        
        showAlert(
            `تم حفظ الجواز بنجاح! 🎉\n` +
            `📝 حقول مُدخلة: ${manualFields}\n` +
            `⚙️ حقول افتراضية: ${defaultFields}`, 
            'success'
        );
        
        resetQuickEntry();
        await fetchData();
        await fetchLastPassportID();
        
    } catch (error) {
        console.error('خطأ في الإدخال السريع:', error);
        showAlert('حدث خطأ: ' + error.message, 'error');
    } finally {
        setIsLoading(false);
    }
};



const resetForm = () => {
    // مسح جميع البيانات الموجودة
    setFormData({
        PassportID: null,
        OfficeSerial: '',
        SerialNumber: '',
        TravelDuration: '',
        CompanyID: '',
        PassportStatus: '',
        PreviousCompanies: [],
        AbilityTest: '',
        NameArabic: '',
        NameEnglish: '',
        PassportNumber: 'A',
        NationalID: '',
        PassportProfessionID: '',
        PassportProfessionText: '',
        TravelProfessionID: '',
        MilitaryStatus: '',
        PassportIssueDate: '',
        PassportExpiryDate: '',
        Address: '',
        Governorate: '',
        DateOfBirth: '',
        Age: '',
        DelegateID: '',
        PhoneNumbers: [''],
        LicenseType: '',
        LicenseExpiryDate: '',
        MedicalExamStatus: '',
        MedicalExamDate: '',
        NetWorkDate: '',
        VisaDate: '',
        TravelDate: '',
        BorderNumber: '',
        ViolationNotes: '',
        ApplicationNumber: '',
         FingerprintDate: '',
    MedicalInsuranceDate: '',
    Notes: [{ id: Date.now(), text: '', date: new Date().toISOString(), user: 'المستخدم الحالي' }],
        PassportReceiveStatus: 'اختر الحالة',
        ReceiveDate: '',
        FriendName: '',
        FriendPhone: '',
        PassportImagePath: '',
        PersonalImagePath: '',
        LicenseImagePath: '',
        LicenseBackImagePath: '',
        IDImagePath: '',
        IDBackImagePath: '',
        CompanyContractPath: '',
        MinistryContractPath: '',
        VisaFilePath: '',
        OtherAttachmentsPath: ''
      
    });
    
    // مسح رسائل الأخطاء والتحقق
    setFieldErrors({
        NameArabic: '',
        PassportNumber: '',
        NationalID: ''
    });
    
    setFieldValidation({
        NameArabic: null,
        PassportNumber: null,
        NationalID: null
    });
    
    setEditingId(null);
    setShowForm(false);
    setShowPreviousCompaniesSelect(false);
    setSelectedPreviousCompany('');
    setIsViewMode(false);
    
    // مسح بيانات OCR
    setOcrData({});
    setIsOcrProcessing(false);
    setOcrProgress(0);
};



// دالة إنشاء سفرة جديدة مع حماية بكلمة سر
const executeNewTrip = async (passportId, clientName, newTripDetails = {}) => {
    const newCompanyId = newTripDetails?.companyId;
    const newTravelDuration = String(newTripDetails?.travelDuration || '').trim();
    const newAbilityTest = String(newTripDetails?.abilityTest || '').trim();
    const newDelegateId = newTripDetails?.delegateId ? Number(newTripDetails.delegateId) : null;

    if (!newCompanyId) {
        showAlert('اختار الشركة التي ستُسجل عليها السفرة الجديدة أولًا', 'warning');
        return;
    }
    if (!newTravelDuration) {
        showAlert('اختار مدة السفر للسفرة الجديدة', 'warning');
        return;
    }
    if (!newAbilityTest) {
        showAlert('اختار نتيجة اختبار القدرات للسفرة الجديدة', 'warning');
        return;
    }
    try {
        const checkResponse = await fetch(`${config.PASSPORT_API_URL}/api/passports/blacklist/check`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ PassportID: passportId, CompanyID: newCompanyId })
        });
        const checkResult = await checkResponse.json();
        if (checkResponse.ok && checkResult.success) {
            if (checkResult.officeBlocks?.length) {
                const item = checkResult.officeBlocks[0];
                const proceed = window.confirm(
                    `⛔ تنبيه قوي: هذا العميل ممنوع على مستوى المكتب بالكامل.\n\n` +
                    `السبب: ${item.ReasonDetails || 'غير مسجل'}\n` +
                    `تاريخ الإضافة: ${item.AddedAt ? new Date(item.AddedAt).toLocaleDateString('ar-EG') : 'غير مسجل'}\n\n` +
                    `هل تريد إنشاء سفرة جديدة رغم المنع؟`
                );
                if (!proceed) return;
            } else if (checkResult.companyBlocks?.length) {
                const reasons = checkResult.companyBlocks.map(item => `- ${item.CompanyName || 'شركة غير محددة'}: ${item.ReasonDetails || 'غير مسجل'}`).join('\n');
                const proceed = window.confirm(`⚠️ هذا العميل عليه منع نشط على الشركة الحالية أو شركة مرتبطة به:\n${reasons}\n\nهل تريد المتابعة رغم التحذير؟`);
                if (!proceed) return;
            } else if (checkResult.otherCompanyBlocks?.length || checkResult.warnings?.length) {
                const notes = [...(checkResult.otherCompanyBlocks || []), ...(checkResult.warnings || [])]
                    .slice(0, 5)
                    .map(item => `- ${item.ScopeLabel || 'تنبيه'} ${item.CompanyName ? '(' + item.CompanyName + ')' : ''}: ${item.ReasonDetails || 'غير مسجل'}`)
                    .join('\n');
                window.alert(`⚠️ تنبيه قبل إنشاء السفرة الجديدة:\n${notes}`);
            }
        }
    } catch (error) {
        console.warn('تعذر فحص قائمة المنع قبل إنشاء سفرة جديدة:', error);
    }

    // نافذة إدخال كلمة السر
    const password = window.prompt(
        `🔒 تأكيد إنشاء سفرة جديدة للعميل: ${clientName}\n\n` +
        `⚠️ هذا الإجراء سيؤدي إلى:\n` +
        `✅ أرشفة البيانات الحالية\n` +
        `✅ إنشاء ملف جديد للعميل\n` +
        `✅ نقل الصور الأساسية فقط\n` +
        `❌ عدم نقل المستندات القديمة: عقد الشركة / عقد الوزارة / التأشيرة / المرفقات الأخرى\n` +
        `❌ مسح بيانات السفر والمتابعة السابقة مثل رقم الطلب والبصمة والتأمين\n` +
        `🏢 نقل الشركة الحالية تلقائيًا إلى الشركات السابقة إذا لم تكن موجودة\n\n` +
        `بيانات السفرة الجديدة:\n` +
        `• مدة السفر: ${newTravelDuration}\n` +
        `• نتيجة القدرات: ${newAbilityTest}\n` +
        `• المندوب: ${newDelegateId ? (delegates.find(d => String(d.DelegateID) === String(newDelegateId))?.DelegateName || newDelegateId) : 'بدون مندوب'}\n\n` +
        `🔐 لتأكيد العملية، أدخل كلمة السر:\n` +
        `(اكتب: 123321)`
    );
    
    // التحقق من إلغاء العملية
    if (password === null) {
        return; // المستخدم ضغط Cancel
    }
    
    // التحقق من كلمة السر
    if (password !== "123321") {
        alert("❌ كلمة السر غير صحيحة!\n\nتم إلغاء العملية لأسباب أمنية.");
        return;
    }
    
    // تأكيد نهائي
    const finalConfirm = window.confirm(
        `✅ تم التحقق من كلمة السر بنجاح!\n\n` +
        `هل أنت متأكد 100% من إنشاء سفرة جديدة للعميل: ${clientName}؟\n\n` +
        `⚠️ هذا الإجراء لا يمكن التراجع عنه!`
    );
    
    if (!finalConfirm) {
        return;
    }
    
    try {
        setIsLoading(true);
        showAlert('🔄 جاري إنشاء سفرة جديدة مع نقل الصور فقط وتفريغ المستندات ونقل الشركة الحالية للشركات السابقة...', 'warning');
        
        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/${passportId}/new-trip`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                NewTripByUserID: getCurrentUserId(),
                NewCompanyID: Number(newCompanyId),
                NewTravelDuration: newTravelDuration,
                NewAbilityTest: newAbilityTest,
                NewDelegateID: newDelegateId
            })
        });
        
        const result = await response.json();
        
        if (!response.ok) {
            throw new Error(result.error || 'فشل في إنشاء سفرة جديدة');
        }
        
        const newTripOfficeSerial = result.data?.newOfficeSerial;
        const newTripPassportId = result.data?.newPassportId;
        const newTripCompanyId = result.data?.newCompanyId || Number(newCompanyId);
        const newTripCompany = companies.find(company => String(company?.CompanyID ?? '') === String(newTripCompanyId));
        const newTripCompanyName = result.data?.newCompanyName || newTripCompany?.CompanyName || '';
        const newTripCompanyCode = result.data?.newCompanyCode || cleanCompanyCodeForBarcode(
            newTripCompany?.CompanyCode || newTripCompany?.Code || newTripCompany?.Symbol || newTripCompany?.CompanySymbol || newTripCompany?.ShortCode
        );
        const savedTravelDuration = result.data?.travelDuration || newTravelDuration;
        const savedAbilityTest = result.data?.abilityTest || newAbilityTest;
        const savedDelegateId = result.data?.delegateId ?? newDelegateId;
        const savedDelegateName = result.data?.delegateName || delegates.find(d => String(d?.DelegateID ?? '') === String(savedDelegateId ?? ''))?.DelegateName || '';

        showAlert(
            `✅ تم إنشاء سفرة جديدة بنجاح للعميل: ${clientName}\n` +
            `🆔 رقم الملف الجديد: ${newTripOfficeSerial || newTripPassportId}\n` +
            `🔄 رقم الدورة: ${result.data.tripCycle}\n` +
            `📷 تم نقل الصور فقط، والمستندات القديمة لم تنتقل للسفرة الجديدة` +
            `\n🏢 الشركة الجديدة: ${newTripCompanyName || 'غير محددة'}${newTripCompanyCode ? ` (${newTripCompanyCode})` : ''}` +
            `\n⏱️ مدة السفر: ${savedTravelDuration}` +
            `\n✅ نتيجة القدرات: ${savedAbilityTest}` +
            `\n👤 المندوب: ${savedDelegateName || 'بدون مندوب'}` +
            `${result.data?.companyWasAddedToPrevious ? '\n🏢 تم إضافة الشركة الحالية إلى الشركات السابقة تلقائيًا' : ''}`, 
            'success'
        );

        // إذا كانت الطباعة التلقائية بعد تسجيل جواز جديد مفعلة،
        // نطبع السفرة الجديدة نفسها برقم OfficeSerial الجديد، وليس رقم الملف القديم أو PassportID الداخلي.
        try {
            const sourcePassport = Array.isArray(passports)
                ? passports.find(item => String(item?.PassportID ?? '') === String(passportId))
                : null;

            const newTripPassportForPrint = {
                ...(sourcePassport || {}),
                PassportID: newTripPassportId || null, // داخلي للعلاقات فقط
                OfficeSerial: newTripOfficeSerial || '',
                SerialNumber: newTripOfficeSerial ? String(newTripOfficeSerial) : '',
                TripCycle: result.data?.tripCycle || ((sourcePassport?.TripCycle || 0) + 1),
                IsActive: 1,
                Status: 'نشط',
                PassportStatus: result.data?.passportStatus ?? sourcePassport?.PassportStatus ?? '',
                PreviousCompanies: result.data?.previousCompanies ?? sourcePassport?.PreviousCompanies ?? [],

                // هذه الحقول تبدأ فارغة في السفرة الجديدة طبقًا لمسار إنشاء السفرة في السيرفر.
                CompanyID: newTripCompanyId,
                CompanyName: newTripCompanyName,
                DelegateID: savedDelegateId || null,
                DelegateName: savedDelegateName,
                TravelDuration: savedTravelDuration,
                AbilityTest: savedAbilityTest,
                MedicalExamStatus: '',
                MedicalExamDate: null,
                NetWorkDate: null,
                NetworkDate: null,
                ApplicationNumber: '',
                FingerprintDate: null,
                VisaDate: null,
                TravelDate: null,
                MedicalInsuranceDate: null,
                PassportReceiveStatus: 'اختر الحالة',
                ReceiveDate: null,
                FriendName: '',
                FriendPhone: '',
                CompanyContractPath: '',
                MinistryContractPath: '',
                VisaFilePath: '',
                OtherAttachmentsPath: ''
            };

            await autoPrintNewPassportIfEnabled({
                passport: newTripPassportForPrint,
                companies,
                delegates,
                professions,
                showAlert,
                sourceLabel: `السفرة الجديدة - رقم الملف ${newTripOfficeSerial || 'غير متاح'}`
            });
        } catch (autoPrintError) {
            console.error('خطأ في الطباعة التلقائية بعد إنشاء السفرة الجديدة:', autoPrintError);
            showAlert(
                `تم إنشاء السفرة الجديدة ولكن فشلت الطباعة التلقائية: ${autoPrintError.message}`,
                'warning'
            );
        }
        
        // تحديث البيانات
        await fetchData();
        await fetchLastPassportID();
        
    } catch (error) {
        console.error('خطأ في إنشاء سفرة جديدة:', error);
        showAlert('❌ حدث خطأ في إنشاء سفرة جديدة: ' + error.message, 'error');
    } finally {
        setIsLoading(false);
    }
};

const handleNewTrip = (passportId, clientName) => {
    const sourcePassport = Array.isArray(passports)
        ? passports.find(item => String(item?.PassportID ?? '') === String(passportId))
        : null;

    setNewTripRequest({
        passportId,
        clientName,
        currentCompanyId: sourcePassport?.CompanyID || ''
    });
};

// دالة عرض تاريخ العميل
const handleViewHistory = async (passportId) => {
    try {
        setIsLoading(true);
        showAlert('جاري جلب تاريخ العميل...', 'warning');
        
        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/${passportId}/history`);
        const result = await response.json();
        
        if (!response.ok) {
            throw new Error(result.error || 'فشل في جلب تاريخ العميل');
        }
        
        // إنشاء نافذة منبثقة لعرض التاريخ
        let historyText = `تاريخ العميل: ${result.currentPassport.NameArabic}\n`;
        historyText += `الرقم القومي: ${result.currentPassport.NationalID}\n`;
        historyText += `عدد السفرات: ${result.totalTrips}\n\n`;
        
        result.history.forEach((trip, index) => {
            historyText += `--- السفرة رقم ${trip.TripCycle || index + 1} ---\n`;
            historyText += `رقم الملف: ${trip.OfficeSerial || trip.SerialNumber || trip.PassportID}\n`;
            historyText += `الحالة: ${trip.Status || 'غير محدد'}\n`;
            historyText += `الشركة: ${trip.CompanyName || 'غير محدد'}\n`;
            historyText += `المندوب: ${trip.DelegateName || 'غير محدد'}\n`;
            historyText += `تاريخ السفر: ${trip.TravelDate ? new Date(trip.TravelDate).toLocaleDateString('ar-EG') : 'لم يسافر بعد'}\n`;
            historyText += `تاريخ الإنشاء: ${new Date(trip.CreatedAt).toLocaleDateString('ar-EG')}\n\n`;
        });
        
        alert(historyText);
        
    } catch (error) {
        console.error('خطأ في جلب تاريخ العميل:', error);
        showAlert('حدث خطأ في جلب تاريخ العميل: ' + error.message, 'error');
    } finally {
        setIsLoading(false);
    }
};



// دالة البحث المحسن (بدون زر)
const handleSearch = (newFilters) => {
    updateUserActivity();
    setSearchFilters(newFilters);
    setCurrentPage(1);
};


const normalizeScannerAction = (action) => {
    const value = String(action || '').trim().toLowerCase();
    if (['edit', 'تعديل', 'e'].includes(value)) return 'edit';
    if (['open', 'view', 'فتح', 'عرض', 'v', 'o'].includes(value)) return 'view';
    return 'search';
};

const cleanScannerPayload = (rawText = '') => String(rawText || '')
    .replace(/^\uFEFF/, '')
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .trim();

const parseScannerCommand = (rawText = '') => {
    const raw = cleanScannerPayload(rawText);
    if (!raw) return { action: '', value: '', raw: '', ignored: true };

    // الوضع الآمن الجديد: أي مسح لازم يكون بأمر صريح داخل الباركود/QR مثل SEARCH:B-1384 أو EDIT:1384.
    const commandMatch = raw.match(/^\s*(SEARCH|FIND|OPEN|VIEW|EDIT|بحث|فتح|عرض|تعديل)\s*[:：]\s*(.+?)\s*$/i);
    if (commandMatch) {
        return {
            action: normalizeScannerAction(commandMatch[1]),
            value: cleanScannerPayload(commandMatch[2]),
            raw
        };
    }

    try {
        const canBeUrl = raw.startsWith('?') || /^https?:\/\//i.test(raw) || raw.startsWith('/');
        if (canBeUrl) {
            const url = raw.startsWith('?')
                ? new URL(`${window.location.origin}${window.location.pathname}${raw}`)
                : raw.startsWith('/')
                    ? new URL(`${window.location.origin}${raw}`)
                    : new URL(raw);
            const action = url.searchParams.get('barcodeAction') || url.searchParams.get('action') || url.searchParams.get('mode');
            const value = url.searchParams.get('barcode') || url.searchParams.get('code') || url.searchParams.get('id');
            if (action && value) {
                return { action: normalizeScannerAction(action), value: cleanScannerPayload(value), raw };
            }
        }
    } catch {}

    return { action: '', value: '', raw, ignored: true };
};

const extractLookupValueFromScannerText = (text = '') => {
    const raw = cleanScannerPayload(text);
    if (!raw) return '';
    const decoded = (() => {
        try { return decodeURIComponent(raw); } catch { return raw; }
    })();

    const barcodeParam = decoded.match(/[?&](?:barcode|code|id)=([^&\s]+)/i);
    if (barcodeParam) return cleanScannerPayload(barcodeParam[1]);

    const prefixed = decoded.match(/(?:كود|الرمز|barcode|code|id|رقم\s*الملف|ملف)\s*[:=]\s*([A-Z0-9\-_/]+)/i);
    if (prefixed) return cleanScannerPayload(prefixed[1]);

    const companyIdCode = decoded.match(/\b([A-Z0-9]{1,12}-\d{1,10})\b/i);
    if (companyIdCode) return companyIdCode[1].toUpperCase();

    const passportNumber = decoded.match(/\b([A-Z][0-9]{6,10})\b/i);
    if (passportNumber) return passportNumber[1].toUpperCase();

    const nationalId = decoded.match(/\b([23]\d{13})\b/);
    if (nationalId) return nationalId[1];

    const shortNumber = decoded.match(/^\s*#?\s*(\d{1,10})\s*$/);
    if (shortNumber) return shortNumber[1];

    return raw;
};

const getPassportIdFromLookupValue = (lookupValue = '') => {
    const text = cleanScannerPayload(lookupValue);
    const codeMatch = text.match(/\b[A-Z0-9]{1,12}-(\d{1,10})\b/i);
    if (codeMatch) return codeMatch[1];
    const idMatch = text.match(/^#?\s*(\d{1,10})\s*$/);
    return idMatch ? idMatch[1] : '';
};

const looksLikeScannerCommandPrefix = (text = '') => {
    const value = cleanScannerPayload(text).toUpperCase();
    if (!value) return false;
    const commands = ['SEARCH', 'FIND', 'OPEN', 'VIEW', 'EDIT'];
    return commands.some(command => command.startsWith(value) || value.startsWith(`${command}:`) || value.startsWith(`${command}：`));
};

const looksLikeScannerText = (text = '') => {
    const value = cleanScannerPayload(text);
    if (value.length < 6) return false;

    // لا نعتبر أي أرقام أو أكواد خام باركود. التنفيذ فقط عند وجود أمر صريح.
    return /^\s*(SEARCH|FIND|OPEN|VIEW|EDIT|بحث|فتح|عرض|تعديل)\s*[:：]\s*.+$/i.test(value)
        || ((/^https?:\/\//i.test(value) || value.startsWith('/') || value.startsWith('?'))
            && /[?&](barcodeAction|action|mode)=/i.test(value)
            && /[?&](barcode|code|id)=/i.test(value));
};

const processedBarcodeRouteRef = React.useRef('');
React.useEffect(() => {
    try {
        const params = new URLSearchParams(window.location.search || '');
        let action = params.get('barcodeAction') || params.get('action') || params.get('mode');
        let barcode = params.get('barcode') || params.get('code') || params.get('id');
        const rawScan = params.get('masarScan') || params.get('scan') || params.get('scanner');
        if ((!action || !barcode) && rawScan) {
            const parsedRouteScan = parseScannerCommand(rawScan);
            action = parsedRouteScan.action;
            barcode = parsedRouteScan.value;
        }
        if (!action || !barcode || !Array.isArray(passports) || passports.length === 0) return;
        action = normalizeScannerAction(action);
        const routeKey = `${action}:${barcode}`;
        if (processedBarcodeRouteRef.current === routeKey) return;
        const match = String(barcode).match(/-(\d+)$/) || String(barcode).match(/^(\d+)$/);
        const officeSerial = match ? match[1] : '';
        const target = passports.find(p => String(getPassportFileNumber(p)) === String(officeSerial));
        if (!target) return;
        processedBarcodeRouteRef.current = routeKey;
        if (action === 'edit') {
            handleEdit(target);
            setTimeout(() => {
                setEditingId(target?.PassportID || null);
                setIsViewMode(false);
                setShowForm(true);
            }, 0);
        } else if (action === 'view' || action === 'open') {
            handleView(target);
            setTimeout(() => {
                setEditingId(null);
                setIsViewMode(true);
                setShowForm(true);
            }, 0);
        } else {
            setShowForm(false);
            setEditingId(null);
            setIsViewMode(false);
            handleSearch({ ...searchFilters, search: officeSerial || barcode });
        }
        window.history.replaceState(null, '', window.location.pathname);
    } catch (error) {
        console.warn('تعذر تنفيذ رابط الباركود:', error);
    }
}, [passports]);

const findPassportForScannedValue = React.useCallback(async (lookupValue) => {
    const lookup = extractLookupValueFromScannerText(lookupValue);
    const passportId = getPassportIdFromLookupValue(lookup); // المقصود هنا رقم الملف الظاهر OfficeSerial وليس PassportID الداخلي
    const normalize = (value) => String(value ?? '').trim().toLowerCase();
    const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '');

    const localMatch = Array.isArray(passports) ? passports.find(p => {
        const code = buildPassportBarcodeCode(p, companies);
        return (passportId && String(getPassportFileNumber(p)) === String(passportId))
            || normalize(code) === normalize(lookup)
            || normalize(p.PassportNumber) === normalize(lookup)
            || digitsOnly(p.NationalID) === digitsOnly(lookup);
    }) : null;
    if (localMatch) return { passport: localMatch, lookup, passportId };

    try {
        const searchValue = passportId || lookup;
        const params = new URLSearchParams({ page: '1', limit: '5', search: searchValue });
        const response = await fetch(`${config.PASSPORT_API_URL}/api/passports/paginated?${params.toString()}`);
        const result = await response.json().catch(() => ({}));
        const rows = Array.isArray(result?.data) ? result.data : [];
        const serverMatch = rows.find(p => {
            const code = buildPassportBarcodeCode(p, companies);
            return (passportId && String(getPassportFileNumber(p)) === String(passportId))
                || normalize(code) === normalize(lookup)
                || normalize(p.PassportNumber) === normalize(lookup)
                || digitsOnly(p.NationalID) === digitsOnly(lookup);
        }) || rows[0] || null;
        return { passport: serverMatch, lookup, passportId };
    } catch (error) {
        return { passport: null, lookup, passportId, error };
    }
}, [passports, companies]);

const scannerForcedModeRef = React.useRef({ mode: '', passportId: null });

const applyScannerForcedMode = React.useCallback((mode, targetPassport = null) => {
    const passportId = targetPassport?.PassportID || targetPassport?.id || scannerForcedModeRef.current?.passportId || null;
    if (!mode) {
        scannerForcedModeRef.current = { mode: '', passportId: null };
        return;
    }

    scannerForcedModeRef.current = { mode, passportId };

    if (mode === 'edit') {
        if (passportId) setEditingId(passportId);
        setIsViewMode(false);
        setShowForm(true);
    } else if (mode === 'view') {
        setEditingId(null);
        setIsViewMode(true);
        setShowForm(true);
    } else if (mode === 'search') {
        setShowForm(false);
        setEditingId(null);
        setIsViewMode(false);
        setShowPreviousCompaniesSelect(false);
    }
}, []);

React.useEffect(() => {
    const forced = scannerForcedModeRef.current;
    if (!forced?.mode || !showForm) return;

    const currentPassportId = formData?.PassportID || editingId || null;
    if (forced.passportId && currentPassportId && String(currentPassportId) !== String(forced.passportId)) return;

    if (forced.mode === 'edit') {
        if (forced.passportId && String(editingId || '') !== String(forced.passportId)) {
            setEditingId(forced.passportId);
        }
        if (isViewMode) setIsViewMode(false);
    } else if (forced.mode === 'view') {
        if (editingId) setEditingId(null);
        if (!isViewMode) setIsViewMode(true);
    }
}, [showForm, formData?.PassportID, editingId, isViewMode]);

const executeScannerCommand = React.useCallback(async (rawText) => {
    const parsed = parseScannerCommand(rawText);
    if (!parsed.action || !parsed.value) {
        console.log('[Masar Scanner] تم تجاهل إدخال غير موجه بأمر صريح:', rawText);
        return;
    }
    const lookupSource = parsed.value;
    const { passport, lookup, passportId } = await findPassportForScannedValue(lookupSource);
    const searchValue = passportId || lookup;
    const action = parsed.action;
    console.log('[Masar Scanner]', { rawText, parsed, action, searchValue, foundPassportId: passport?.PassportID });

    const closeFormForScannerSearch = () => {
        // SEARCH لازم يرجع لقائمة الجوازات فقط، ولا يسيب آخر معاينة مفتوحة على الشاشة.
        applyScannerForcedMode('search');
    };

    const forceScannerViewMode = (targetPassport) => {
        scannerForcedModeRef.current = { mode: 'view', passportId: targetPassport?.PassportID || null };
        handleView(targetPassport);
        // تأكيد نهائي على مراحل لأن بعض تحديثات React القديمة قد تأتي بعد handleView.
        const forceView = () => applyScannerForcedMode('view', targetPassport);
        forceView();
        requestAnimationFrame(forceView);
        setTimeout(forceView, 0);
        setTimeout(forceView, 80);
        setTimeout(forceView, 200);
    };

    const forceScannerEditMode = (targetPassport) => {
        scannerForcedModeRef.current = { mode: 'edit', passportId: targetPassport?.PassportID || null };
        handleEdit(targetPassport);
        // تأكيد نهائي على مراحل: EDIT = تعديل فعلي وليس معاينة.
        const forceEdit = () => applyScannerForcedMode('edit', targetPassport);
        forceEdit();
        requestAnimationFrame(forceEdit);
        setTimeout(forceEdit, 0);
        setTimeout(forceEdit, 80);
        setTimeout(forceEdit, 200);
    };

    if (action === 'edit') {
        if (passport) {
            forceScannerEditMode(passport);
            showAlert(`تم فتح الجواز للتعديل من المسح: ${buildPassportBarcodeCode(passport, companies)}`, 'success');
        } else {
            closeFormForScannerSearch();
            handleSearch({ ...searchFilters, search: searchValue });
            showAlert(`تم البحث عن ${searchValue} لكن لم أجد جوازًا مفتوحًا للتعديل مباشرة`, 'warning');
        }
        return;
    }

    if (action === 'view') {
        if (passport) {
            forceScannerViewMode(passport);
            showAlert(`تم فتح الجواز للمعاينة من المسح: ${buildPassportBarcodeCode(passport, companies)}`, 'success');
        } else {
            closeFormForScannerSearch();
            handleSearch({ ...searchFilters, search: searchValue });
            showAlert(`تم البحث عن ${searchValue} لكن لم أجد الجواز لفتحه مباشرة`, 'warning');
        }
        return;
    }

    closeFormForScannerSearch();
    handleSearch({ ...searchFilters, search: searchValue });
    showAlert(`تم تنفيذ البحث من المسح: ${searchValue}`, 'success');
}, [findPassportForScannedValue, companies, searchFilters, applyScannerForcedMode]);

const scannerBufferRef = React.useRef({
    text: '',
    firstAt: 0,
    lastAt: 0,
    events: 0,
    fastEvents: 0,
    target: null,
    originalValue: null
});

React.useEffect(() => {
    const resetBuffer = () => {
        scannerBufferRef.current = { text: '', firstAt: 0, lastAt: 0, events: 0, fastEvents: 0, target: null, originalValue: null };
    };

    const restoreFocusedInputIfNeeded = (buffer) => {
        const target = buffer?.target;
        if (!target || buffer.originalValue === null || buffer.originalValue === undefined) return;
        const tag = target.tagName?.toLowerCase();
        if (!['input', 'textarea'].includes(tag)) return;
        try {
            if (target.value !== buffer.originalValue) {
                target.value = buffer.originalValue;
                target.dispatchEvent(new Event('input', { bubbles: true }));
            }
        } catch {}
    };

    const handleScannerKeyDown = (event) => {
        if (event.ctrlKey || event.altKey || event.metaKey) return;
        const now = Date.now();
        const key = event.key;
        const buffer = scannerBufferRef.current;

        if (key === 'Escape') {
            resetBuffer();
            return;
        }

        if (key === 'Enter') {
            const text = cleanScannerPayload(buffer.text);
            const isCommand = looksLikeScannerText(text);
            if (text && isCommand) {
                event.preventDefault();
                event.stopPropagation();
                if (event.stopImmediatePropagation) event.stopImmediatePropagation();
                restoreFocusedInputIfNeeded(buffer);
                resetBuffer();
                executeScannerCommand(text);
            } else {
                resetBuffer();
            }
            return;
        }

        if (key === 'Backspace' || key === 'Delete' || key === 'Tab' || key.startsWith('Arrow')) {
            resetBuffer();
            return;
        }

        if (key.length !== 1) return;

        const gap = buffer.lastAt ? now - buffer.lastAt : 0;
        if (!buffer.text || gap > 250) {
            const active = document.activeElement;
            const tag = active?.tagName?.toLowerCase();
            scannerBufferRef.current = {
                text: key,
                firstAt: now,
                lastAt: now,
                events: 1,
                fastEvents: 0,
                target: active || null,
                originalValue: ['input', 'textarea'].includes(tag) ? active.value : null
            };
            return;
        }

        buffer.text += key;
        buffer.events += 1;
        if (gap <= 80) buffer.fastEvents += 1;
        buffer.lastAt = now;

        // لا نمنع الكتابة العادية إطلاقًا. نمنع فقط قراءة Scanner التي تبدأ بأمر صريح مثل SEARCH: أو EDIT:.
        if (buffer.events >= 3 && buffer.fastEvents >= 2 && looksLikeScannerCommandPrefix(buffer.text)) {
            event.preventDefault();
            event.stopPropagation();
            if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        }
    };

    document.addEventListener('keydown', handleScannerKeyDown, true);
    return () => document.removeEventListener('keydown', handleScannerKeyDown, true);
}, [executeScannerCommand]);

// دالة تغيير الصفحة
const handlePageChange = (newPage) => {
    updateUserActivity(); // أضف هنا

    setCurrentPage(newPage);
    fetchData(newPage, searchFilters);
};

// حساب إجمالي الصفحات
const totalPages = Math.ceil(totalItems / itemsPerPage);



    const handleTableRowClick = () => {
        const today = new Date().toISOString().split('T')[0];
        setFormData(prev => ({
            ...prev,
            PassportIssueDate: today,
            PassportExpiryDate: today,
            LicenseExpiryDate: today,
            DateOfBirth: today,
            MedicalExamDate: today,
            NetWorkDate: today,
            VisaDate: today,
            TravelDate: today,
            ReceiveDate: today
        }));
    };

    if (barcodeSettingsPage && !canManageBarcodePrinting) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir="rtl">
                <div className="bg-white border border-red-100 rounded-3xl shadow-xl p-6 max-w-md text-center">
                    <div className="text-4xl mb-3">🔒</div>
                    <h2 className="text-xl font-black text-gray-900 mb-2">لا توجد صلاحية لضبط الباركود</h2>
                    <p className="text-sm text-gray-500 mb-4">فعّل صلاحية "ضبط الباركود وطرق الطباعة" من صفحة المستخدمين ثم جرّب مرة أخرى.</p>
                    <button onClick={() => navigate('/passports')} className="bg-blue-700 hover:bg-blue-800 text-white px-5 py-2.5 rounded-xl font-bold">رجوع للجوازات</button>
                </div>
            </div>
        );
    }

    if (barcodeSettingsPage && canManageBarcodePrinting && !barcodeModalPassport) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
                <div className="text-center bg-white border border-gray-100 rounded-3xl shadow-xl p-6">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-700 mx-auto"></div>
                    <p className="mt-4 text-gray-600 font-bold">جاري فتح صفحة ضبط الباركود...</p>
                </div>
            </div>
        );
    }

    const closeBarcodeSettingsWorkspace = () => {
        setBarcodeModalPassport(null);
        setBarcodeSettingsOnly(false);
        if (barcodeSettingsPage) navigate('/passports');
    };

    if (barcodeSettingsOnly && barcodeModalPassport) {
        return (
            <>
                <div className="fixed top-4 right-4 z-[10080] space-y-2" dir="rtl">
                    {alerts.map(alert => (
                        <AlertMessage
                            key={alert.id}
                            message={alert.message}
                            type={alert.type}
                            onClose={() => setAlerts(prev => prev.filter(a => a.id !== alert.id))}
                        />
                    ))}
                </div>
                <BarcodePrintModal
                    isOpen={!!barcodeModalPassport}
                    onClose={closeBarcodeSettingsWorkspace}
                    passport={barcodeModalPassport}
                    companies={companies}
                    delegates={delegates}
                    professions={professions}
                    showAlert={showAlert}
                    settingsOnly={barcodeSettingsOnly}
                    pageMode
                />
            </>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-6 max-md:py-0" dir="rtl">
            {/* عرض التنبيهات */}
            <div className="fixed top-4 right-4 z-50 space-y-2">
                {alerts.map(alert => (
                    <AlertMessage
                        key={alert.id}
                        message={alert.message}
                        type={alert.type}
                        onClose={() => setAlerts(prev => prev.filter(a => a.id !== alert.id))}
                    />
                ))}
            </div>

            <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 max-md:px-0">
        {/* رأس الصفحة */}
<div className="hidden md:block bg-white rounded-lg shadow-md p-6 max-xl:p-4 mb-2 sticky top-0 z-30">

    <div className="flex flex-wrap gap-3 justify-between items-center max-md:flex-col max-md:items-stretch max-md:gap-3 [&_button]:whitespace-nowrap">
        <div className="flex items-center gap-3 max-md:flex-wrap max-md:justify-between">
            <h1 className="text-3xl max-xl:text-2xl font-bold text-gray-900 whitespace-nowrap max-md:text-2xl max-md:leading-8">إدارة جوازات السفر</h1>
            
            {/* زر الإعدادات مباشرة بجانب العنوان */}
            {canManageWarnings && (
            <button 
                onClick={() => setShowSettings(true)}
                className="bg-gray-600 hover:bg-gray-700 text-white p-2.5 rounded-lg transition duration-200"
                title="إعدادات التحذيرات"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            </button>
            )}
            {canManageBarcodePrinting && (
            <button
                onClick={handleOpenBarcodeSettings}
                className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2.5 rounded-lg transition duration-200 flex items-center gap-2"
                title="ضبط الباركود وطرق الطباعة"
            >
                <Barcode size={18} />
                الباركود
            </button>
            )}
            {canViewActivityLog && (
            <button
                onClick={() => setShowActivityLog(true)}
                className="bg-slate-800 hover:bg-slate-900 text-white px-4 py-2.5 rounded-lg transition duration-200 flex items-center gap-2"
                title="سجل حركات الجوازات"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                السجل
            </button>
            )}
            {canManageBlacklist && (
            <button
                onClick={() => handleOpenBlacklist(null)}
                className="bg-red-700 hover:bg-red-800 text-white px-4 py-2.5 rounded-lg transition duration-200 flex items-center gap-2"
                title="إدارة قائمة المنع الذكية"
            >
                <span>⛔</span>
                قائمة المنع
            </button>
            )}
        </div>
        
       

        <div className="flex flex-wrap gap-3 items-center max-md:grid max-md:grid-cols-2 max-md:gap-2">
    {!editingId && lastPassportID && (
        <div className="text-lg max-xl:text-base font-semibold text-blue-600 bg-blue-50 px-4 py-2 rounded-lg whitespace-nowrap max-md:text-sm max-md:text-center max-md:px-3">
            رقم الملف القادم: {lastPassportID + 1}
        </div>
    )}
    
    <div className="flex items-center gap-2 text-sm text-gray-600 max-md:col-span-2 max-md:flex-wrap max-md:text-xs max-md:bg-gray-50 max-md:rounded-xl max-md:p-2">
        <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
        <span>آخر تحديث: {lastUpdateTime.toLocaleTimeString('ar-EG')}</span>
        
        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
            تحديث تلقائي كل 4 دقائق
        </span>
        
        <button
            onClick={async () => {
                updateUserActivity();
                setIsLoading(true);
                try {
                    await fetchData(currentPage, searchFilters);
                    await fetchLastPassportID();
                    showAlert('تم تحديث البيانات', 'success');
                } catch (error) {
                    showAlert('خطأ في التحديث', 'error');
                } finally {
                    setIsLoading(false);
                }
            }}
            disabled={isLoading}
            className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded text-xs flex items-center gap-1 disabled:opacity-50"
        >
            <svg className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {isLoading ? 'جاري التحديث...' : 'تحديث الآن'}
        </button>
        
        <span className="text-xs text-gray-500">
            {(() => {
                const timeSinceActivity = Math.floor((new Date() - lastUserActivity) / 1000 / 60);
                const remainingMinutes = Math.max(0, 4 - timeSinceActivity);
                return timeSinceActivity >= 4 ? 'سيحدث تلقائياً' : `${remainingMinutes} دقيقة للتحديث التلقائي`;
            })()}
        </span>
    </div>

            <button 
                onClick={() => navigate('/')}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2.5 rounded-lg transition duration-200 max-md:col-span-2 max-md:w-full"
            >
                العودة للرئيسية
            </button>
        </div>
    </div>
</div>



{/* رأس الصفحة للموبايل - شكل تطبيق مضغوط */}
<div className="md:hidden bg-white border-b border-gray-100 shadow-sm sticky top-0 z-30 px-3 pt-3 pb-3">
    <div className="flex items-center justify-between gap-2 mb-3">
        {canManageWarnings ? (
            <button 
                onClick={() => setShowSettings(true)}
                className="w-11 h-11 rounded-2xl bg-slate-700 text-white flex items-center justify-center shadow-sm active:scale-95 transition"
                title="إعدادات التحذيرات"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            </button>
        ) : <div className="w-11" />}

        <div className="flex-1 min-w-0 text-center">
            <h1 className="text-xl font-black text-gray-900 leading-7">إدارة جوازات السفر</h1>
            <p className="text-[11px] text-gray-500 mt-0.5">كروت الجوازات والإجراءات السريعة</p>
        </div>

        {canViewActivityLog ? (
            <button
                onClick={() => setShowActivityLog(true)}
                className="w-11 h-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center shadow-sm active:scale-95 transition"
                title="سجل حركات الجوازات"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </button>
        ) : <div className="w-11" />}
    </div>

    <div className="grid grid-cols-2 gap-2 mb-2">
        {!editingId && lastPassportID && (
            <div className="rounded-2xl bg-blue-50 border border-blue-100 px-3 py-2 text-center">
                <div className="text-[11px] text-blue-500 font-semibold">رقم الملف القادم</div>
                <div className="text-lg font-black text-blue-700 leading-6">{lastPassportID + 1}</div>
            </div>
        )}
        <div className={`${!editingId && lastPassportID ? '' : 'col-span-2'} rounded-2xl bg-slate-50 border border-slate-100 px-3 py-2 text-center`}>
            <div className="text-[11px] text-slate-500 font-semibold">آخر تحديث</div>
            <div className="text-sm font-bold text-slate-800 leading-6">{lastUpdateTime.toLocaleTimeString('ar-EG')}</div>
        </div>
    </div>

    {canManageBarcodePrinting && (
    <div className="grid grid-cols-2 gap-2 mb-2">
        <button
            onClick={handleOpenBarcodeSettings}
            className="col-span-2 rounded-2xl bg-blue-700 text-white py-2.5 font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition"
        >
            <Barcode size={16} />
            ضبط الباركود وطرق الطباعة
        </button>
    </div>
    )}

    <div className="grid grid-cols-2 gap-2">
        <button
            onClick={async () => {
                updateUserActivity();
                setIsLoading(true);
                try {
                    await fetchData(currentPage, searchFilters);
                    await fetchLastPassportID();
                    showAlert('تم تحديث البيانات', 'success');
                } catch (error) {
                    showAlert('خطأ في التحديث', 'error');
                } finally {
                    setIsLoading(false);
                }
            }}
            disabled={isLoading}
            className="rounded-2xl bg-blue-600 text-white py-2.5 font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition"
        >
            <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {isLoading ? 'تحديث...' : 'تحديث الآن'}
        </button>
        <button 
            onClick={() => navigate('/')}
            className="rounded-2xl bg-slate-700 text-white py-2.5 font-bold text-sm active:scale-[0.98] transition"
        >
            العودة للرئيسية
        </button>
    </div>

    <div className="mt-2 flex items-center justify-center gap-1 text-[11px] text-gray-500">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
        <span>تحديث تلقائي كل 4 دقائق</span>
        <span>•</span>
        <span>{(() => {
            const timeSinceActivity = Math.floor((new Date() - lastUserActivity) / 1000 / 60);
            const remainingMinutes = Math.max(0, 4 - timeSinceActivity);
            return timeSinceActivity >= 4 ? 'جاهز للتحديث' : `متبقي ${remainingMinutes} د`;
        })()}</span>
    </div>
</div>

{/* شريط البحث والفلاتر المحسن - بحث تلقائي */}
<div className="bg-white rounded-lg shadow-md p-6 mb-6 sticky top-20 z-20 max-md:static max-md:rounded-none max-md:p-4 max-md:mb-4">


   <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">


        {/* البحث النصي */}
        <div className="md:col-span-2">
            <div className="relative">
                <Search className="absolute right-3 top-3 h-5 w-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="البحث بالاسم أو رقم الملف (#123) أو رقم الجواز أو الرقم القومي أو رقم الهاتف..."

                    value={searchFilters.search}
                    onChange={(e) => handleSearch({ ...searchFilters, search: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-2.5 px-10 focus:ring-2 focus:ring-blue-500"
                />
                {/* مؤشر البحث */}
                {searchFilters.search && searchFilters.search !== debouncedSearchFilters.search && (
                    <div className="absolute left-3 top-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                    </div>
                )}
            </div>
            <p className="text-xs text-gray-500 mt-1">💡 للبحث بالـ ID فقط: اكتب # ثم الرقم (مثال: #123)</p>
        </div>

        {/* فلتر الشركة */}
        <div>
            <select
                value={searchFilters.companyId}
                onChange={(e) => handleSearch({ ...searchFilters, companyId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 focus:ring-2 focus:ring-blue-500"
            >
                <option value="">كل الشركات</option>
                {companies.map(company => (
                    <option key={company.CompanyID} value={company.CompanyID}>
                        {company.CompanyName}
                    </option>
                ))}
            </select>
        </div>

        {/* فلتر المندوب */}
        <div>
            <select
                value={searchFilters.delegateId}
                onChange={(e) => handleSearch({ ...searchFilters, delegateId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 focus:ring-2 focus:ring-blue-500"
            >
                <option value="">كل المناديب</option>
                {delegates.map(delegate => (
                    <option key={delegate.DelegateID} value={delegate.DelegateID}>
                        {delegate.DelegateName}
                    </option>
                ))}
            </select>
        </div>

        {/* فلتر حالة الاستلام */}
        <div>
            <select
                value={searchFilters.receiveStatus}
                onChange={(e) => handleSearch({ ...searchFilters, receiveStatus: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 focus:ring-2 focus:ring-blue-500"
            >
                <option value="">كل الحالات</option>
                <option value="لم يستلم">لم يستلم</option>
                <option value="استلم العميل">استلم العميل</option>
                <option value="استلم المندوب">استلم المندوب</option>
                <option value="استلم صديق">استلم صديق</option>
            </select>
        </div>
        {/* فلتر القائمة المحفوظة */}
<div className="flex items-center gap-2">
    <select
        value={searchFilters.listId}
        onChange={(e) => handleSearch({ ...searchFilters, listId: e.target.value })}
        className="w-full border border-gray-300 rounded-lg py-2.5 px-3 focus:ring-2 focus:ring-blue-500"
    >
        <option value="">كل القوائم</option>
        {savedLists.map(list => (
            <option key={list.ListID} value={list.ListID}>
                {list.ListName}
            </option>
        ))}
    </select>

   
</div>


    </div>

    {/* أزرار الإضافة وعدد العناصر في الصفحة */}
    <div className="flex flex-wrap gap-3 justify-between items-center max-md:flex-col max-md:items-stretch max-md:gap-3">

    <div className="flex flex-wrap items-center gap-3 max-md:grid max-md:grid-cols-2 max-md:gap-2 [&_button]:whitespace-nowrap">
            <PermissionGate permissions="إضافة جواز جديد">
                <button 
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 transition duration-200 max-md:justify-center max-md:px-3 max-md:text-sm"
                    onClick={() => {
                        setShowForm(true);
                        setTimeout(() => {
                            applyDefaultSettings();
                        }, 1000);
                    }}
                >
                    <Plus size={20} />
                    إضافة جواز جديد
                </button>
            </PermissionGate>

            <PermissionGate permissions="الإدخال السريع">
                <button 
                    className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 transition duration-200 shadow-lg max-md:justify-center max-md:px-3 max-md:text-sm"
                    onClick={() => setShowQuickEntry(true)}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    إدخال سريع
                </button>
            </PermissionGate>

            <PermissionGate permissions="المعالجة المجمعة">
                <button 
                    className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 transition duration-200 shadow-lg max-md:justify-center max-md:px-3 max-md:text-sm"
                    onClick={() => setShowBatchUpload(true)}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    معالجة مجمعة
                </button>
            </PermissionGate>

            <PermissionGate permissions="ضبط النموذج">
                <button 
                    onClick={() => setShowDefaultSettings(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-lg flex items-center gap-2 transition duration-200 max-md:justify-center max-md:px-3 max-md:text-sm"
                    title="ضبط الإعدادات الافتراضية للنموذج"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                    </svg>
                    ضبط النموذج
                </button>
            </PermissionGate>
            
            <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">عدد العناصر:</label>
                <select
                    value={itemsPerPage}
                    onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                        fetchData(1, searchFilters);
                    }}
                    className="border border-gray-300 rounded px-3 py-1 text-sm"
                >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                </select>
            </div>

            {/* زر مسح البحث */}
            {(searchFilters.search 
 || searchFilters.companyId 
 || searchFilters.delegateId 
 || searchFilters.receiveStatus
 || searchFilters.passportStatus
 || searchFilters.listId
 || searchFilters.excludeList) && (
                <button
               onClick={() => handleSearch({
    search: '',
    companyId: '',
    delegateId: '',
    passportStatus: '',
    receiveStatus: '',
    listId: '',
    excludeList: false
})}

                    className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 py-2.5 rounded-lg flex items-center gap-2"
                >
                    <X size={16} />
                    مسح البحث
                </button>
            )}
        </div>

        {/* معلومات الصفحة الحالية */}
        <div className="text-sm text-gray-600">
            {isLoading ? 'جاري البحث...' : 
                `عرض ${((currentPage - 1) * itemsPerPage) + 1} إلى ${Math.min(currentPage * itemsPerPage, totalItems)} من أصل ${totalItems} جواز`
            }
        </div>
    </div>
</div>


                {/* مؤشر التحميل */}
                {isLoading && (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        <span className="mr-4 text-gray-600">جاري تحميل البيانات...</span>
                    </div>
                )}
      {/* جدول الجوازات - يظهر على الكمبيوتر فقط كما هو بدون تعديل */}
<div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
    <div className="overflow-x-auto">
        <table className="w-full text-sm">
            <thead className="bg-gradient-to-l from-[#1b3163] to-[#0f1d3d] text-white">
                <tr>
                    <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">رقم ID</th>
                    <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">الاسم بالعربي</th>
                    <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">الشركة</th>
                    <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">مهنة السفر</th>
                    <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">رقم الهاتف</th>
                    <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">المندوب</th>
                    <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">رقم الجواز</th>
                    <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">حالة الاستلام</th>
                    {canControlStatuses && <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">الحالة</th>}
                    <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">الإجراءات</th>
                </tr>
            </thead>
            

            <tbody className="divide-y divide-gray-200 bg-gray-50">
{passports.map(passport => (
    <PassportRow listMemberships={listMemberships}
        key={passport.PassportID}
        passport={passport}
        companies={companies}
        delegates={delegates}
        professions={professions}
        warningSettings={warningSettings}
        onView={handleView}
        onEdit={handleEdit}
        onSmartRepeat={handleSmartRepeat}
        onPrintBarcode={handlePrintBarcode}
        onSavedPrintProfiles={handleOpenSavedPrintProfiles}
        showAlert={showAlert}
        onNewTrip={handleNewTrip}
        onViewHistory={handleViewHistory}
        onAddNote={handleAddNote}  // تغيير من onClearDates إلى onAddNote
        onQuickReceiveUpdate={handleQuickReceiveUpdate}
        blacklistInfo={blacklistMap[passport.PassportID] || []}
        onOpenBlacklist={handleOpenBlacklist}
        canManageBlacklist={canManageBlacklist}
        showStatusColumn={canControlStatuses}
        onSecureDelivery={showSecureConfirm}
        secureDeliveryEnabled={canDeliverySecure}
    />
))}
</tbody>
</table>

    </div>
</div>

{/* كروت الجوازات - موبايل فقط، ولا تظهر على الكمبيوتر */}
<div className="md:hidden space-y-4">
{passports.map(passport => (
    <PassportMobileCard
        key={passport.PassportID}
        passport={passport}
        companies={companies}
        delegates={delegates}
        professions={professions}
        warningSettings={warningSettings}
        onView={handleView}
        onEdit={handleEdit}
        onSmartRepeat={handleSmartRepeat}
        onPrintBarcode={handlePrintBarcode}
        onSavedPrintProfiles={handleOpenSavedPrintProfiles}
        showAlert={showAlert}
        onNewTrip={handleNewTrip}
        onViewHistory={handleViewHistory}
        onAddNote={handleAddNote}
        onQuickReceiveUpdate={handleQuickReceiveUpdate}
        listMemberships={listMemberships}
        blacklistInfo={blacklistMap[passport.PassportID] || []}
        onOpenBlacklist={handleOpenBlacklist}
        canManageBlacklist={canManageBlacklist}
        showStatusColumn={canControlStatuses}
        onSecureDelivery={showSecureConfirm}
        secureDeliveryEnabled={canDeliverySecure}
    />
))}
</div>




{/* مكون Pagination */}
{totalPages > 1 && (
    <div className="bg-white rounded-lg shadow-md p-4 mt-6">
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    الأولى
                </button>
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    السابقة
                </button>
            </div>

            <div className="flex items-center gap-1">
                {/* عرض أرقام الصفحات */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                        pageNum = i + 1;
                    } else if (currentPage <= 3) {
                        pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                    } else {
                        pageNum = currentPage - 2 + i;
                    }

                    return (
                        <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 border rounded-lg ${
                                currentPage === pageNum
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {pageNum}
                        </button>
                    );
                })}
            </div>

            <div className="flex items-center gap-2">
                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    التالية
                </button>
                <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    الأخيرة
                </button>
            </div>
        </div>
    </div>
)}





{showForm && (
    <BodyPortal enabled={isMobileScreen}>
    <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998] flex items-center justify-center p-2 overflow-auto max-md:block max-md:p-0 max-md:overflow-hidden"
        style={isMobileScreen ? { width: '100vw', height: '100dvh' } : undefined}
    >
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-[95vw] max-h-[95vh] overflow-hidden relative max-md:rounded-none max-md:max-w-none max-md:max-h-none max-md:w-screen max-md:h-[100dvh] max-md:flex max-md:flex-col">
            {/* رأس النافذة */}
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 z-50 max-md:px-4 max-md:py-3 max-md:flex-shrink-0">
                <div className="flex justify-between items-center max-md:items-start max-md:gap-2">
                    <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2 max-md:flex-1 max-md:min-w-0">
                        <h2 className="text-2xl font-bold text-gray-900 max-md:text-lg max-md:leading-7">
                            {editingId ? 'تعديل بيانات الجواز' : isViewMode ? 'عرض بيانات الجواز' : 'إضافة جواز جديد'}
                        </h2>
                        
                        {/* أزرار المسح الضوئي */}
                        {!isViewMode && canSmartScan && (
                            <div className="flex gap-2 max-md:grid max-md:grid-cols-2">
                                <label className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white px-4 py-2 rounded-lg transition duration-200 cursor-pointer shadow-lg max-md:justify-center max-md:text-sm max-md:px-3">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                handleAdvancedOCR(file);
                                            }
                                        }}
                                        className="hidden"
                                    />
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                                    </svg>
                                    🤖 مسح ذكي
                                </label>
                                
                                <label className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white px-4 py-2 rounded-lg transition duration-200 cursor-pointer shadow-lg md:hidden max-md:justify-center max-md:text-sm max-md:px-3">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                handleAdvancedOCR(file);
                                            }
                                        }}
                                        className="hidden"
                                    />
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                        <circle cx="12" cy="13" r="4"/>
                                    </svg>
                                    📱 كاميرا
                                </label>
                            </div>
                        )}
                        
                        {/* عرض رقم الملف الظاهر للمستخدم في المعاينة والتعديل */}
                        {(isViewMode || editingId) ? (
                            <div className={`text-lg font-semibold px-4 py-2 rounded-lg max-md:text-sm max-md:px-3 max-md:text-center ${isViewMode ? 'text-green-600 bg-green-50' : 'text-blue-600 bg-blue-50'}`}>
                                رقم الملف: {getPassportFileNumber(formData) || '—'}
                            </div>
                        ) : lastPassportID && (
                            <div className="text-lg font-semibold text-blue-600 bg-blue-50 px-4 py-2 rounded-lg max-md:text-sm max-md:px-3 max-md:text-center">
                                رقم الملف القادم: {lastPassportID + 1}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={resetForm}
                        className="text-gray-400 hover:text-gray-600 transition duration-200 max-md:bg-gray-100 max-md:rounded-full max-md:p-2 max-md:flex-shrink-0"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* شريط المعلومات المميز */}
                <div className="mt-4 flex gap-4 flex-wrap max-md:hidden">

                           {/* إشارة الشركة الحالية */}
                    {formData.CompanyID && (
                        <div className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-4 py-2 rounded-lg shadow-lg">
                            <div className="flex items-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <span className="font-bold">الشركة الحالية:</span>
                                <span className="font-semibold">
                                    {companies.find(c => c.CompanyID.toString() === formData.CompanyID.toString())?.CompanyName || 'غير محدد'}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* إشارة المندوب */}
                    {formData.DelegateID && (
                        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg shadow-lg">
                            <div className="flex items-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <span className="font-bold">المندوب:</span>
                                <span className="font-semibold">
                                    {delegates.find(d => d.DelegateID.toString() === formData.DelegateID.toString())?.DelegateName || 'غير محدد'}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* إشارة الشركات السابقة */}
                    {formData.PassportStatus === 'قديم شركات' && Array.isArray(formData.PreviousCompanies) && formData.PreviousCompanies.length > 0 && (
                        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-lg shadow-lg">
                            <div className="flex items-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <span className="font-bold">الشركات السابقة:</span>
                                <span className="font-semibold">
                                    {formData.PreviousCompanies.map(companyId => {
                                        const company = companies.find(c => c.CompanyID.toString() === companyId.toString());
                                        return company?.CompanyName;
                                    }).filter(Boolean).join(' • ')}
                                </span>
                            </div>
                        </div>
                    )}

                      {/* إشارة أرقام الهواتف */}
    {formData.PhoneNumbers && formData.PhoneNumbers.filter(phone => phone && phone.trim()).length > 0 && (
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 py-2 rounded-lg shadow-lg">
            <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="font-bold">الهاتف:</span>
                <div className="flex items-center gap-1">
                    {formData.PhoneNumbers.filter(phone => phone && phone.trim()).map((phone, index) => (
                        <span key={index} className="font-semibold">
                            {phone}
                            {index < formData.PhoneNumbers.filter(p => p && p.trim()).length - 1 && (
                                <span className="mx-1 text-emerald-200">•</span>
                            )}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    )}


      {/* إشارة الملاحظات - الجديد */}
      {Array.isArray(formData.Notes) && formData.Notes.some(note => note.text && note.text.trim()) && (
        <div className="bg-gradient-to-r from-pink-500 to-rose-600 text-white px-4 py-2 rounded-lg shadow-lg">
            <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span className="font-bold">الملاحظات:</span>
                <div className="flex items-center gap-1">
                    {(() => {
                        const validNotes = formData.Notes.filter(note => note.text && note.text.trim());
                        const latestNote = validNotes[validNotes.length - 1];
                        
                        if (latestNote) {
                            const shortText = latestNote.text.length > 30 
                                ? latestNote.text.substring(0, 30) + '...' 
                                : latestNote.text;
                            
                            return (
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold">{shortText}</span>
                                    {validNotes.length > 1 && (
                                        <span className="bg-pink-400 text-pink-100 px-2 py-1 rounded-full text-xs font-bold">
                                            +{validNotes.length - 1} أخرى
                                        </span>
                                    )}
                                </div>
                            );
                        }
                        
                        return null;
                    })()}
                </div>
            </div>
        </div>
    )}
             
                </div>

                <div className="hidden max-md:grid grid-cols-2 gap-2 mt-3">
                    <button
                        type="button"
                        onClick={() => document.getElementById('passport-mobile-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                        className="bg-blue-50 text-blue-700 border border-blue-100 rounded-xl py-2 text-sm font-bold"
                    >
                        البيانات
                    </button>
                    <button
                        type="button"
                        onClick={() => document.getElementById('passport-mobile-images-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                        className="bg-purple-50 text-purple-700 border border-purple-100 rounded-xl py-2 text-sm font-bold"
                    >
                        الصور والمستندات
                    </button>
                </div>
            </div>

            {/* المحتوى الرئيسي - تخطيط جديد */}
            <div className="flex h-[calc(95vh-180px)] max-md:flex-col max-md:h-auto max-md:flex-1 max-md:min-h-0 max-md:overflow-y-auto">
                {/* الجانب الأيسر - النموذج */}
                <div id="passport-mobile-form-section" className="flex-1 overflow-y-auto p-6 max-md:overflow-visible max-md:p-3 max-md:order-1">
                    <form onSubmit={handleSubmit} className="space-y-6 max-md:space-y-4">
                        {/* البيانات الأساسية */}
                        <div className="bg-gray-50 p-4 rounded-lg max-md:bg-white max-md:border max-md:border-gray-200 max-md:rounded-2xl max-md:shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-700 mb-4 max-md:bg-blue-50 max-md:text-blue-700 max-md:px-3 max-md:py-2 max-md:rounded-xl">البيانات الأساسية</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 min-[1700px]:grid-cols-4 gap-4">
                                
                                {/* الشركة */}
                                <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
        الشركة *
    </label>
    <SearchableSelect
        options={companies.map(company => ({
            value: company.CompanyID,
            label: company.CompanyName,
            subtitle: `ID: ${company.CompanyID}` // معلومة إضافية اختيارية
        }))}
        value={formData.CompanyID}
        onChange={(value) => setFormData({...formData, CompanyID: value})}
        placeholder="ابحث عن الشركة..."
        disabled={isViewMode}
        emptyMessage="لا توجد شركات"
    />
</div>

                                {/* مدة السفر */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        مدة السفر *
                                    </label>
                                    <select
                                        value={formData.TravelDuration}
                                        onChange={(e) => setFormData({...formData, TravelDuration: e.target.value})}
                                        className={`w-full border rounded-lg px-4 py-2.5 ${
                                            isViewMode 
                                            ? 'bg-gray-50 cursor-default border-gray-200' 
                                            : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                        }`}
                                        disabled={isViewMode}
                                        required
                                        dir="rtl"
                                        autoComplete="off"
                                    >
                                        <option value="">اختر المدة</option>
                                        {travelDurations.map(duration => (
                                            <option key={duration} value={duration}>{duration}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* نتيجة اختبار القدرات */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        نتيجة اختبار القدرات *
                                    </label>
                                    <select
                                        value={formData.AbilityTest}
                                        onChange={(e) => setFormData({...formData, AbilityTest: e.target.value})}
                                        className={`w-full border rounded-lg px-4 py-2.5 ${
                                            isViewMode 
                                            ? 'bg-gray-50 cursor-default border-gray-200' 
                                            : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                        }`}
                                        disabled={isViewMode}
                                        required
                                        autoComplete="off"
                                    >
                                        <option value="">اختر النتيجة</option>
                                        {abilityTests.map(test => (
                                            <option key={test} value={test}>{test}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* الاسم بالعربي */}
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        الاسم بالعربي *
                                        {ocrData.NameArabic && (
                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded ml-2">
                                                مستخرج ✓
                                            </span>
                                        )}
                                    </label>
                                    <input
                                        type="text"
                                        name="NameArabic"
                                        value={formData.NameArabic}
                                        onChange={(e) => {
                                            if (fieldErrors.NameArabic) {
                                                setFieldErrors(prev => ({ ...prev, NameArabic: '' }));
                                                setFieldValidation(prev => ({ ...prev, NameArabic: null }));
                                            }
                                            handleArabicInputChange(e, 'NameArabic');
                                        }}
                                        onBlur={(e) => handleFieldBlur('NameArabic', e.target.value)}
                                        autoComplete="off"
                                        className={`w-full border rounded-lg px-4 py-2.5 transition-all duration-200 ${
                                            fieldValidation.NameArabic === 'invalid' || fieldErrors.NameArabic
                                                ? 'border-red-400 bg-red-50 focus:ring-2 focus:ring-red-500' 
                                                : fieldValidation.NameArabic === 'valid'
                                                ? 'border-green-400 bg-green-50 focus:ring-2 focus:ring-green-500'
                                                : fieldValidation.NameArabic === 'checking'
                                                ? 'border-blue-400 bg-blue-50 focus:ring-2 focus:ring-blue-500'
                                                : ocrData.NameArabic && formData.NameArabic === ocrData.NameArabic 
                                                ? 'border-green-400 bg-green-50' 
                                                : isViewMode 
                                                ? 'bg-gray-50 cursor-default border-gray-200' 
                                                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                        }`}
                                        readOnly={isViewMode}
                                        required
                                        dir="rtl"
                                        placeholder="أدخل الاسم بالعربي"
                                    />
                                    
                                    <FieldMessage 
                                        message={fieldErrors.NameArabic || 
                                                (fieldValidation.NameArabic === 'checking' ? 'جاري التحقق من التكرار...' : 
                                                 fieldValidation.NameArabic === 'valid' ? 'الاسم متاح للاستخدام' : '')}
                                        type={fieldValidation.NameArabic || (fieldErrors.NameArabic ? 'error' : '')}
                                        isVisible={fieldErrors.NameArabic || fieldValidation.NameArabic}
                                    />
                                </div>

                                {/* الاسم بالإنجليزي */}
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        الاسم بالإنجليزي
                                        {ocrData.NameEnglish && (
                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded ml-2">
                                                مستخرج ✓
                                            </span>
                                        )}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.NameEnglish}
                                        onChange={handleEnglishNameChange}
                                        autoComplete="off"
                                        className={`w-full border rounded-lg px-4 py-2.5 ${
                                            ocrData.NameEnglish && formData.NameEnglish === ocrData.NameEnglish 
                                                ? 'border-green-400 bg-green-50' 
                                                : isViewMode 
                                                ? 'bg-gray-50 cursor-default border-gray-200' 
                                                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                        }`}
                                        readOnly={isViewMode}
                                        required
                                        dir="ltr"
                                    />
                                </div>

                                {/* رقم الجواز */}
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        رقم الجواز *
                                        {ocrData.PassportNumber && (
                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded ml-2">
                                                مستخرج ✓
                                            </span>
                                        )}
                                    </label>
                                    <input
                                        type="text"
                                        name="PassportNumber"
                                        value={formData.PassportNumber}
                                        onChange={(e) => {
                                            if (fieldErrors.PassportNumber) {
                                                setFieldErrors(prev => ({ ...prev, PassportNumber: '' }));
                                                setFieldValidation(prev => ({ ...prev, PassportNumber: null }));
                                            }
                                            handlePassportNumberChange(e);
                                        }}
                                        onBlur={(e) => handleFieldBlur('PassportNumber', e.target.value)}
                                        autoComplete="off"
                                        className={`w-full border rounded-lg px-4 py-2.5 transition-all duration-200 ${
                                            fieldValidation.PassportNumber === 'invalid' || fieldErrors.PassportNumber
                                                ? 'border-red-400 bg-red-50 focus:ring-2 focus:ring-red-500' 
                                                : fieldValidation.PassportNumber === 'valid'
                                                ? 'border-green-400 bg-green-50 focus:ring-2 focus:ring-green-500'
                                                : fieldValidation.PassportNumber === 'checking'
                                                ? 'border-blue-400 bg-blue-50 focus:ring-2 focus:ring-blue-500'
                                                : ocrData.PassportNumber && formData.PassportNumber === ocrData.PassportNumber 
                                                ? 'border-green-400 bg-green-50' 
                                                : isViewMode 
                                                ? 'bg-gray-50 cursor-default border-gray-200' 
                                                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                        }`}
                                        readOnly={isViewMode}
                                        required
                                        maxLength={9}
                                        dir="ltr"
                                        placeholder="A12345678"
                                    />
                                    
                                    <FieldMessage 
                                        message={fieldErrors.PassportNumber || 
                                                (fieldValidation.PassportNumber === 'checking' ? 'جاري التحقق من التكرار...' : 
                                                 fieldValidation.PassportNumber === 'valid' ? 'رقم الجواز متاح للاستخدام' : '')}
                                        type={fieldValidation.PassportNumber || (fieldErrors.PassportNumber ? 'error' : '')}
                                        isVisible={fieldErrors.PassportNumber || fieldValidation.PassportNumber}
                                    />
                                </div>

                                {/* الرقم القومي */}
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        الرقم القومي *
                                        {ocrData.NationalID && (
                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded ml-2">
                                                مستخرج ✓
                                            </span>
                                        )}
                                    </label>
                                    <input
                                        type="text"
                                        name="NationalID"
                                        value={formData.NationalID}
                                        onChange={(e) => {
                                            if (fieldErrors.NationalID) {
                                                setFieldErrors(prev => ({ ...prev, NationalID: '' }));
                                                setFieldValidation(prev => ({ ...prev, NationalID: null }));
                                            }
                                            handleNationalIDChange(e);
                                        }}
                                        onBlur={(e) => handleFieldBlur('NationalID', e.target.value)}
                                        autoComplete="off"
                                        className={`w-full border rounded-lg px-4 py-2.5 transition-all duration-200 ${
                                            fieldValidation.NationalID === 'invalid' || fieldErrors.NationalID
                                                ? 'border-red-400 bg-red-50 focus:ring-2 focus:ring-red-500' 
                                                : fieldValidation.NationalID === 'valid'
                                                ? 'border-green-400 bg-green-50 focus:ring-2 focus:ring-green-500'
                                                : fieldValidation.NationalID === 'checking'
                                                ? 'border-blue-400 bg-blue-50 focus:ring-2 focus:ring-blue-500'
                                                : ocrData.NationalID && formData.NationalID === ocrData.NationalID 
                                                ? 'border-green-400 bg-green-50' 
                                                : isViewMode 
                                                ? 'bg-gray-50 cursor-default border-gray-200' 
                                                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                        }`}
                                        readOnly={isViewMode}
                                        required
                                        maxLength={14}
                                        placeholder="12345678901234"
                                    />
                                    
                                    <FieldMessage 
                                        message={fieldErrors.NationalID || 
                                                (fieldValidation.NationalID === 'checking' ? 'جاري التحقق من التكرار...' : 
                                                 fieldValidation.NationalID === 'valid' ? 'الرقم القومي متاح للاستخدام' : '')}
                                        type={fieldValidation.NationalID || (fieldErrors.NationalID ? 'error' : '')}
                                        isVisible={fieldErrors.NationalID || fieldValidation.NationalID}
                                    />
                                </div>

                              {/* المهنة بالجواز */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    المهنة بالجواز *
  </label>
  {getPassportProfessionInputMode() === 'text' ? (
    <>
      <input
        type="text"
        value={formData.PassportProfessionText || ''}
        onChange={(e) => handlePassportProfessionTextChange(e.target.value)}
        readOnly={isViewMode}
        required
        placeholder="اكتب المهنة بالجواز نصيًا"
        className={`w-full border rounded-lg px-4 py-2.5 ${
          isViewMode ? 'bg-gray-50 cursor-default border-gray-200' : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
        }`}
      />
      {!isViewMode && <p className="text-xs text-gray-500 mt-1">الوضع الحالي من ضبط النموذج: كتابة نصية فقط، ولن يتم إضافتها لقائمة المهن.</p>}
    </>
  ) : (
    <>
      <SearchableSelect
        options={professions.map(profession => ({
          value: profession.ProfessionID,
          label: profession.ProfessionName,
          subtitle: `ID: ${profession.ProfessionID}`
        }))}
        value={formData.PassportProfessionID}
        onChange={handlePassportProfessionInputChange}
        placeholder="ابحث واختر المهنة من القائمة..."
        disabled={isViewMode}
        emptyMessage="لا توجد مهن"
      />
      {!isViewMode && <p className="text-xs text-gray-500 mt-1">الوضع الحالي من ضبط النموذج: اختيار من قائمة فقط مع إتاحة البحث داخل الحقل.</p>}
    </>
  )}
</div>

{/* مهنة السفر */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    مهنة السفر *
  </label>
  <SearchableSelect
    options={professions.map(profession => ({
      value: profession.ProfessionID,
      label: profession.ProfessionName,
      subtitle: `ID: ${profession.ProfessionID}`
    }))}
    value={formData.TravelProfessionID}
    onChange={(value) => setFormData({ ...formData, TravelProfessionID: value })}
    placeholder="ابحث عن المهنة..."
    disabled={isViewMode}
    emptyMessage="لا توجد مهن"
  />
</div>


                                {/* الموقف من التجنيد */}
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        الموقف من التجنيد *
                                        {ocrData.MilitaryStatus && (
                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded ml-2">
                                                مستخرج ✓
                                            </span>
                                        )}
                                    </label>
                                    <select
                                        value={formData.MilitaryStatus}
                                        onChange={(e) => setFormData({...formData, MilitaryStatus: e.target.value})}
                                        className={`w-full border rounded-lg px-4 py-2.5 ${
                                            ocrData.MilitaryStatus && formData.MilitaryStatus === ocrData.MilitaryStatus 
                                                ? 'border-green-400 bg-green-50' 
                                                : isViewMode 
                                                ? 'bg-gray-50 cursor-default border-gray-200' 
                                                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                        }`}
                                        disabled={isViewMode}
                                        required
                                        autoComplete="off"
                                    >
                                        <option value="">اختر الموقف</option>
                                        {militaryStatuses.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* تاريخ إصدار الجواز */}
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        تاريخ إصدار الجواز
                                    </label>
                                    <CustomDateInput
                                        value={formData.PassportIssueDate || ''}
                                        onChange={(e) => setFormData({...formData, PassportIssueDate: e.target.value})}
                                        isReadOnly={isViewMode}
                                        showTodayButton={false}
                                        className={`w-full border rounded-lg px-4 py-2.5 ${
                                            isViewMode
                                                ? 'bg-gray-50 cursor-default border-gray-200'
                                                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                        }`}
                                    />
                                    {!isViewMode && (
                                        <p className="text-xs text-gray-500 mt-1">اختياري، ويظهر في فورم تجديد التأشيرات وكشف الجوازات عند تفعيل العمود.</p>
                                    )}
                                </div>

                                {/* تاريخ انتهاء الجواز */}
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        تاريخ انتهاء الجواز *
                                        {ocrData.PassportExpiryDate && (
                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded ml-2">
                                                مستخرج ✓
                                            </span>
                                        )}
                                        {(() => {
                                            const warning = checkExpiryWarning(
                                                formData.PassportExpiryDate, 
                                                formData.TravelDuration, 
                                                warningSettings, 
                                                'passport'
                                            );
                                            if (warningSettings.enablePassportWarnings && warning.hasWarning) {
                                                return (
                                                    <span className={`ml-2 text-xs font-medium ${
                                                        warning.level === 'critical' ? 'text-red-600' : 'text-yellow-600'
                                                    }`}>
                                                        ⚠️ {warning.message}
                                                    </span>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </label>
                                    <CustomDateInput
                                        value={formData.PassportExpiryDate}
                                        onChange={(e) => setFormData({...formData, PassportExpiryDate: e.target.value})}
                                        isReadOnly={isViewMode}
                                        showTodayButton={false}
                                        className={`w-full border rounded-lg px-4 py-2.5 ${
                                            (() => {
                                                if (ocrData.PassportExpiryDate && formData.PassportExpiryDate === ocrData.PassportExpiryDate) {
                                                    return 'border-green-400 bg-green-50';
                                                }
                                                const warning = checkExpiryWarning(
                                                    formData.PassportExpiryDate, 
                                                    formData.TravelDuration, 
                                                    warningSettings, 
                                                    'passport'
                                                );
                                                if (warningSettings.enablePassportWarnings && warning.hasWarning) {
                                                    return warning.level === 'critical' 
                                                        ? 'border-red-400 bg-red-50' 
                                                        : 'border-yellow-400 bg-yellow-50';
                                                }
                                                return isViewMode 
                                                    ? 'bg-gray-50 cursor-default border-gray-200' 
                                                    : 'border-gray-300 focus:ring-2 focus:ring-blue-500';
                                            })()
                                        }`}
                                    />
                                </div>

                                {/* العنوان */}
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        العنوان
                                        {ocrData.Address && (
                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded ml-2">
                                                مستخرج ✓
                                            </span>
                                        )}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.Address}
                                        onChange={(e) => handleArabicInputChange(e, 'Address')}
                                        inputMode="text"
                                        placeholder="اكتب العنوان  "
                                        autoComplete="off"
                                        className={`w-full border rounded-lg px-4 py-2.5 ${
                                            ocrData.Address && formData.Address === ocrData.Address 
                                                ? 'border-green-400 bg-green-50' 
                                                : isViewMode 
                                                ? 'bg-gray-50 cursor-default border-gray-200' 
                                                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                        }`}
                                        readOnly={isViewMode}
                                        required
                                        dir="rtl"
                                    />
                                </div>

                                {/* المحافظة */}
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        المحافظة
                                        {ocrData.Governorate && (
                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded ml-2">
                                                محسوبة ✓
                                            </span>
                                        )}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.Governorate}
                                        readOnly
                                        className={`w-full border rounded-lg px-4 py-2.5 ${
                                            ocrData.Governorate && formData.Governorate === ocrData.Governorate 
                                                ? 'border-green-400 bg-green-50' 
                                                : 'border-gray-300 bg-gray-50'
                                        }`}
                                    />
                                </div>

                                {/* تاريخ الميلاد */}
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        تاريخ الميلاد
                                        {ocrData.DateOfBirth && (
                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded ml-2">
                                                محسوب ✓
                                            </span>
                                        )}
                                    </label>
                                    <CustomDateInput
                                        value={formData.DateOfBirth}
                                        onChange={(e) => setFormData({...formData, DateOfBirth: e.target.value})}
                                        isReadOnly={isViewMode}
                                        showTodayButton={false}
                                        className={`w-full border rounded-lg px-4 py-2.5 ${
                                            ocrData.DateOfBirth && formData.DateOfBirth === ocrData.DateOfBirth 
                                                ? 'border-green-400 bg-green-50' 
                                               : isViewMode 
                                               ? 'bg-gray-50 cursor-default border-gray-200' 
                                               : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                       }`}
                                   />
                               </div>

                               {/* العمر */}
                               <div className="relative">
                                   <label className="block text-sm font-medium text-gray-700 mb-2">
                                       العمر
                                       {ocrData.Age && (
                                           <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded ml-2">
                                               محسوب ✓
                                           </span>
                                       )}
                                   </label>
                                  <input
    type="number"
    value={formData.Age || ''}
                                       readOnly
                                       className={`w-full border rounded-lg px-4 py-2.5 ${
                                           ocrData.Age && formData.Age === ocrData.Age?.toString() 
                                               ? 'border-green-400 bg-green-50' 
                                               : 'border-gray-300 bg-gray-50'
                                       }`}
                                   />
                               </div>

                               {/* المندوب - مميز */}
                               <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-3 rounded-lg border-2 border-purple-200">
    <label className="block text-sm font-medium text-purple-800 mb-2">
        المندوب *
        <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs ml-2">
            🎯 حقل مهم
        </span>
    </label>
    <SearchableSelect
        options={delegates.map(delegate => ({
            value: delegate.DelegateID,
            label: delegate.DelegateName,
            subtitle: delegate.DelegatePhone ? `📱 ${delegate.DelegatePhone}` : ''
        }))}
        value={formData.DelegateID}
        onChange={(value) => setFormData({...formData, DelegateID: value})}
        placeholder="ابحث عن المندوب..."
        disabled={
    isViewMode                  // لو View فقط → مقفول
    || (editingId && !canChangeDelegate) // لو بنعدّل وجواز قديم والمستخدم معندوش صلاحية تغيير المندوب
}

        emptyMessage="لا توجد مناديب"
    />
</div>

                               

                               {/* أرقام الهواتف */}
                               <div>
                                   <label className="block text-sm font-medium text-gray-700 mb-2">
                                       أرقام الهواتف
                                   </label>
                                   {formData.PhoneNumbers.map((phone, index) => (
                                       <div key={index} className="flex gap-2 mb-2">
                                           <input
                                               type="text"
                                               value={phone}
                                               onChange={(e) => handlePhoneNumberChange(index, e.target.value)}
                                               onBlur={() => handlePhoneNumberBlur(index)}
                                               autoComplete="off"
                                               className={`w-full border rounded-lg px-4 py-2.5 ${
                                                   isViewMode 
                                                   ? 'bg-gray-50 cursor-default border-gray-200' 
                                                   : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                               }`}
                                               readOnly={isViewMode}
                                               maxLength={12}
                                               placeholder={`رقم الهاتف ${index + 1}`}
                                           />
                                           {!isViewMode && index > 0 && (
                                               <button
                                                   type="button"
                                                   onClick={() => {
                                                       const newPhones = formData.PhoneNumbers.filter((_, i) => i !== index);
                                                       setFormData({ ...formData, PhoneNumbers: newPhones });
                                                   }}
                                                   className="bg-red-100 text-red-600 p-2 rounded-lg hover:bg-red-200"
                                               >
                                                   <X size={16} />
                                               </button>
                                           )}
                                       </div>
                                   ))}
                                   {!isViewMode && formData.PhoneNumbers.length < 3 && formData.PhoneNumbers[formData.PhoneNumbers.length - 1] && (
                                       <button
                                           type="button"
                                           onClick={() => setFormData({ 
                                               ...formData, 
                                               PhoneNumbers: [...formData.PhoneNumbers, ''] 
                                           })}
                                           className="mt-2 text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                                       >
                                           <Plus size={16} />
                                           إضافة رقم هاتف آخر
                                       </button>
                                   )}
                               </div>

                                    {/* حالة الجواز والشركات السابقة */}
                               <div className={showPreviousCompaniesSelect ? 'col-span-2' : ''}>
                                   <div className="flex gap-4">
                                       {/* حالة الجواز */}
                                       <div className="flex-1">
                                           <label className="block text-sm font-medium text-gray-700 mb-2">
                                               حالة الجواز *
                                           </label>
                                           <select
                                               value={formData.PassportStatus}
                                               onChange={handlePassportStatusChange}
                                               className={`w-full border rounded-lg px-4 py-2.5 ${
                                                   isViewMode 
                                                   ? 'bg-gray-50 cursor-default border-gray-200' 
                                                   : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                               }`}
                                               disabled={isViewMode}
                                               required
                                               dir="rtl"
                                               autoComplete="off"
                                           >
                                               <option value="">اختر الحالة</option>
                                               {passportStatuses.map(status => (
                                                   <option key={status} value={status}>{status}</option>
                                               ))}
                                           </select>
                                       </div>

                                       {/* الشركات السابقة */}
                                       {(showPreviousCompaniesSelect || (isViewMode && Array.isArray(formData.PreviousCompanies) && formData.PreviousCompanies?.length > 0)) && (
    <div className="col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">
            الشركات السابقة
        </label>
        
        {!isViewMode && showPreviousCompaniesSelect && (
            <SearchableSelect
                options={companies.map(company => ({
                    value: company.CompanyID.toString(),
                    label: company.CompanyName,
                    subtitle: `ID: ${company.CompanyID}`
                }))}
                multiple={true}
                selectedValues={formData.PreviousCompanies || []}
                onMultiChange={(values) => setFormData({...formData, PreviousCompanies: values})}
                placeholder="ابحث واختر الشركات السابقة..."
                emptyMessage="لا توجد شركات متاحة"
            />
        )}

        {/* عرض الشركات المختارة في وضع العرض */}
        {isViewMode && Array.isArray(formData.PreviousCompanies) && formData.PreviousCompanies.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
                {formData.PreviousCompanies.map((companyId) => {
                    const company = companies.find((c) => c.CompanyID.toString() === companyId?.toString());
                    if (!company) return null;
                    return (
                        <span 
                            key={companyId} 
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
                        >
                            {company.CompanyName}
                        </span>
                    );
                })}
            </div>
        )}
    </div>
)}
                                   </div>
                               </div>

                               {/* نوع الرخصة */}
                               {hasPermission("نوع الرخصة") && (
                               <div>
                                   <label className="block text-sm font-medium text-gray-700 mb-2">
                                       نوع الرخصة
                                   </label>
                                   <select
                                       value={formData.LicenseType}
                                       onChange={(e) => setFormData({...formData, LicenseType: e.target.value})}
                                       className={`w-full border rounded-lg px-4 py-2.5 ${
                                           isViewMode 
                                           ? 'bg-gray-50 cursor-default border-gray-200' 
                                           : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                       }`}
                                       disabled={isViewMode}
                                       autoComplete="off"
                                   >
                                       <option value="">اختر نوع الرخصة</option>
                                       {licenseTypes.map(type => (
                                           <option key={type} value={type}>{type}</option>
                                       ))}
                                   </select>
                               </div>
                               )}

                          

                               {/* تاريخ انتهاء الرخصة */}
                               {hasPermission("تاريخ انتهاء الرخصة") && (
                               <div>
                                   <label className="block text-sm font-medium text-gray-700 mb-2">
                                       تاريخ انتهاء الرخصة
                                       {(() => {
                                           const warning = checkExpiryWarning(
                                               formData.LicenseExpiryDate, 
                                               formData.TravelDuration, 
                                               warningSettings, 
                                               'license'
                                           );
                                           if (warningSettings.enableLicenseWarnings && warning.hasWarning) {
                                               return (
                                                   <span className={`ml-2 text-xs font-medium ${
                                                       warning.level === 'critical' ? 'text-red-600' : 'text-yellow-600'
                                                   }`}>
                                                       ⚠️ {warning.message}
                                                   </span>
                                               );
                                           }
                                           return null;
                                       })()}
                                   </label>
                                   <CustomDateInput
                                       value={formData.LicenseExpiryDate}
                                       onChange={(e) => setFormData({...formData, LicenseExpiryDate: e.target.value})}
                                       isReadOnly={isViewMode}
                                       showTodayButton={false}
                                       className={`w-full border rounded-lg px-4 py-2.5 ${
                                           (() => {
                                               const warning = checkExpiryWarning(
                                                   formData.LicenseExpiryDate, 
                                                   formData.TravelDuration, 
                                                   warningSettings, 
                                                   'license'
                                               );
                                               if (warningSettings.enableLicenseWarnings && warning.hasWarning) {
                                                   return warning.level === 'critical' 
                                                       ? 'border-red-400 bg-red-50' 
                                                       : 'border-yellow-400 bg-yellow-50';
                                               }
                                               return isViewMode 
                                                   ? 'bg-gray-50 cursor-default border-gray-200' 
                                                   : 'border-gray-300 focus:ring-2 focus:ring-blue-500';
                                           })()
                                       }`}
                                   />
                               </div>
                               )}

                               {/* حالة الكشف الطبي */}
                               {canShowManagedPassportField("MedicalExamStatus") && (
                               <div>
                                   <label className="block text-sm font-medium text-gray-700 mb-2">
                                       حالة الكشف الطبي
                                       {renderManagedSourceBadge('MedicalExamStatus')}
                                   </label>
                                   <input
                                       type="text"
                                       value={formData.MedicalExamStatus || ''}
                                       onChange={(e) => setFormData({...formData, MedicalExamStatus: e.target.value})}
                                       className={getManagedPassportInputClass('MedicalExamStatus')}
                                       readOnly={!canEditManagedPassportField('MedicalExamStatus')}
                                       placeholder={canEditManagedPassportField('MedicalExamStatus') ? 'اكتب حالة الكشف الطبي' : 'يتم التحديث من قسم المتابعة الدورية'}
                                   />
                               </div>
                               )}

                               {/* تاريخ الكشف الطبي */}
                               {canShowManagedPassportField("MedicalExamDate") && (
                               <div>
                                   <label className="block text-sm font-medium text-gray-700 mb-2">
                                       تاريخ الكشف الطبي
                                       {renderManagedSourceBadge('MedicalExamDate')}
                                   </label>
                                   <input
                                       type="date"
                                       value={formData.MedicalExamDate || ''}
                                       onChange={(e) => setFormData({...formData, MedicalExamDate: e.target.value})}
                                       className={getManagedPassportInputClass('MedicalExamDate')}
                                       readOnly={!canEditManagedPassportField('MedicalExamDate')}
                                       disabled={!canEditManagedPassportField('MedicalExamDate')}
                                   />
                               </div>
                               )}

                               {/* تاريخ عمل النت */}
                               {canShowManagedPassportField("NetWorkDate") && (
                               <div>
                                   <label className="block text-sm font-medium text-gray-700 mb-2">
                                       تاريخ عمل النت
                                       {renderManagedSourceBadge('NetWorkDate')}
                                   </label>
                                   <input
                                       type="date"
                                       value={formData.NetWorkDate || ''}
                                       onChange={(e) => setFormData({...formData, NetWorkDate: e.target.value})}
                                       className={getManagedPassportInputClass('NetWorkDate')}
                                       readOnly={!canEditManagedPassportField('NetWorkDate')}
                                       disabled={!canEditManagedPassportField('NetWorkDate')}
                                       placeholder="سيتم تحديثه من قسم التأشيرات"
                                   />
                               </div>
                               )}

                               {/* رقم الطلب */}
                               {canShowManagedPassportField("ApplicationNumber") && (
                               <div>
                                   <label className="block text-sm font-medium text-gray-700 mb-2">
                                       رقم الطلب
                                       {renderManagedSourceBadge('ApplicationNumber')}
                                   </label>
                                   <input
                                       type="text"
                                       value={formData.ApplicationNumber || ''}
                                       onChange={(e) => setFormData({...formData, ApplicationNumber: e.target.value})}
                                       className={getManagedPassportInputClass('ApplicationNumber')}
                                       readOnly={!canEditManagedPassportField('ApplicationNumber')}
                                       placeholder={canEditManagedPassportField('ApplicationNumber') ? 'اكتب رقم الطلب' : 'سيتم إدخاله من قسم التأشيرات'}
                                       dir="ltr"
                                   />
                               </div>
                               )}

                               {/* تاريخ البصمة */}
                               {canShowManagedPassportField("FingerprintDate") && (
                               <div>
                                   <label className="block text-sm font-medium text-gray-700 mb-2">
                                       تاريخ البصمة
                                       {renderManagedSourceBadge('FingerprintDate')}
                                   </label>
                                   <input
                                       type="date"
                                       value={formData.FingerprintDate || ''}
                                       onChange={(e) => setFormData({...formData, FingerprintDate: e.target.value})}
                                       className={getManagedPassportInputClass('FingerprintDate')}
                                       readOnly={!canEditManagedPassportField('FingerprintDate')}
                                       disabled={!canEditManagedPassportField('FingerprintDate')}
                                   />
                               </div>
                               )}

                               {/* التأمين الطبي */}
                               {canShowManagedPassportField("MedicalInsuranceDate") && (
                               <div>
                                   <label className="block text-sm font-medium text-gray-700 mb-2">
                                       🏥 التأمين الطبي
                                       {renderManagedSourceBadge('MedicalInsuranceDate')}
                                   </label>
                                   <input
                                       type="date"
                                       value={formData.MedicalInsuranceDate || ''}
                                       onChange={(e) => setFormData({...formData, MedicalInsuranceDate: e.target.value})}
                                       className={getManagedPassportInputClass('MedicalInsuranceDate')}
                                       readOnly={!canEditManagedPassportField('MedicalInsuranceDate')}
                                       disabled={!canEditManagedPassportField('MedicalInsuranceDate')}
                                   />
                                   {formData.MedicalInsuranceDate ? (
                                       <p className="text-xs text-green-600 mt-1 font-medium">✅ تم التأمين الطبي</p>
                                   ) : (
                                       <p className="text-xs text-red-400 mt-1">❌ لم يتم التأمين الطبي</p>
                                   )}
                               </div>
                               )}

                               {/* تاريخ التأشير */}
                               {canShowManagedPassportField("VisaDate") && (
                               <div>
                                   <label className="block text-sm font-medium text-gray-700 mb-2">
                                       تاريخ التأشير
                                       {renderManagedSourceBadge('VisaDate')}
                                   </label>
                                   <input
                                       type="date"
                                       value={formData.VisaDate || ''}
                                       onChange={(e) => setFormData({...formData, VisaDate: e.target.value})}
                                       className={getManagedPassportInputClass('VisaDate')}
                                       readOnly={!canEditManagedPassportField('VisaDate')}
                                       disabled={!canEditManagedPassportField('VisaDate')}
                                       placeholder="سيتم تحديثه من قسم المتابعة الدورية"
                                   />
                               </div>
                               )}

                               {/* تاريخ السفر */}
                               {canShowManagedPassportField("TravelDate") && (
                               <div className="mt-4">
                                   <label className="block text-sm font-medium text-gray-700 mb-2">
                                       تاريخ السفر
                                       {renderManagedSourceBadge('TravelDate')}
                                   </label>
                                   <input
                                       type="date"
                                       value={formData.TravelDate || ''}
                                       onChange={(e) => setFormData({...formData, TravelDate: e.target.value})}
                                       className={getManagedPassportInputClass('TravelDate')}
                                       readOnly={!canEditManagedPassportField('TravelDate')}
                                       disabled={!canEditManagedPassportField('TravelDate')}
                                       placeholder="سيتم تحديثه من قسم المتابعة الدورية"
                                   />
                               </div>
                               )}

                               {/* رقم الحدود والمخالفة */}
                               {(canShowManagedPassportField("BorderNumber") || canShowManagedPassportField("ViolationNotes")) && (
                               <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                   {canShowManagedPassportField("BorderNumber") && (
                                   <div>
                                       <label className="block text-sm font-medium text-gray-700 mb-2">
                                           🧾 رقم الحدود
                                           {renderManagedSourceBadge('BorderNumber')}
                                       </label>
                                       <input
                                           type="text"
                                           value={formData.BorderNumber || ''}
                                           onChange={(e) => setFormData({...formData, BorderNumber: e.target.value})}
                                           className={getManagedPassportInputClass('BorderNumber')}
                                           readOnly={!canEditManagedPassportField('BorderNumber')}
                                           placeholder={canEditManagedPassportField('BorderNumber') ? 'اكتب رقم الحدود' : 'سيتم إدخاله من المتابعة الدورية'}
                                           dir="ltr"
                                       />
                                   </div>
                                   )}
                                   {canShowManagedPassportField("ViolationNotes") && (
                                   <div>
                                       <label className="block text-sm font-medium text-gray-700 mb-2">
                                           ⚠️ المخالفة / ملاحظتها
                                           {renderManagedSourceBadge('ViolationNotes')}
                                       </label>
                                       <textarea
                                           value={formData.ViolationNotes || ''}
                                           onChange={(e) => setFormData({...formData, ViolationNotes: e.target.value})}
                                           className={getManagedPassportInputClass('ViolationNotes', 'min-h-[80px]')}
                                           readOnly={!canEditManagedPassportField('ViolationNotes')}
                                           placeholder={canEditManagedPassportField('ViolationNotes') ? 'اكتب المخالفة أو ملاحظتها' : 'لا توجد مخالفة أو ملاحظة'}
                                       />
                                   </div>
                                   )}
                               </div>
                               )}

{/* ─── سجل الإنشاء والتعديل والتسليم ─── */}
{hasPermission("سجل الإنشاء والتعديل والتسليم") && (
<div className="mt-4 rounded-xl border border-gray-200 overflow-hidden">
    {/* Header */}
    <div className="bg-gradient-to-r from-slate-700 to-slate-600 px-4 py-2.5 flex items-center gap-2">
        <span className="text-white text-sm font-bold">📋 سجل الإنشاء والتعديل والتسليم</span>
    </div>

    <div className="bg-white divide-y divide-gray-100">

        {/* ── صف الإنشاء ── */}
        <div className="px-4 py-3 grid grid-cols-2 gap-3">
            <div>
                <p className="text-[11px] text-gray-400 mb-1 flex items-center gap-1">👤 أضافه</p>
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-sm text-blue-800 font-medium min-h-[36px] flex items-center">
                    {formData.CreatedByUserName || <span className="text-gray-400 font-normal text-xs">—</span>}
                </div>
            </div>
            <div>
                <p className="text-[11px] text-gray-400 mb-1 flex items-center gap-1">📅 تاريخ الإضافة</p>
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-sm text-blue-800 min-h-[36px] flex items-center">
                    {formData.CreatedAt_Display
                        ? new Date(formData.CreatedAt_Display).toLocaleString('ar-EG')
                        : <span className="text-gray-400 text-xs">—</span>}
                </div>
            </div>
        </div>

        {/* ── صف آخر تعديل ── */}
        <div className="px-4 py-3 grid grid-cols-2 gap-3">
            <div>
                <p className="text-[11px] text-gray-400 mb-1 flex items-center gap-1">✏️ آخر تعديل بواسطة</p>
                <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-sm text-amber-800 font-medium min-h-[36px] flex items-center">
                    {formData.LastModifiedByUserName || <span className="text-gray-400 font-normal text-xs">—</span>}
                </div>
            </div>
            <div>
                <p className="text-[11px] text-gray-400 mb-1 flex items-center gap-1">📅 تاريخ آخر تعديل</p>
                <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-sm text-amber-800 min-h-[36px] flex items-center">
                    {formData.LastModifiedAt_Display
                        ? new Date(formData.LastModifiedAt_Display).toLocaleString('ar-EG')
                        : <span className="text-gray-400 text-xs">—</span>}
                </div>
            </div>
        </div>

        {/* ── صف التسليم (يظهر فقط إذا سُلِّم) ── */}
        {formData.PassportReceiveStatus && 
         formData.PassportReceiveStatus !== 'اختر الحالة' && 
         formData.PassportReceiveStatus !== 'لم يستلم' && (
            <div className="px-4 py-3 grid grid-cols-2 gap-3 bg-green-50">
                <div>
                    <p className="text-[11px] text-gray-500 mb-1 flex items-center gap-1">
                        🤝 سلّمه
                        <span className="bg-green-200 text-green-800 text-[10px] px-1.5 py-0.5 rounded-full mr-1">
                            {formData.PassportReceiveStatus}
                        </span>
                    </p>
                    <div className="bg-white border border-green-200 rounded-lg px-3 py-2 text-sm text-green-800 font-medium min-h-[36px] flex items-center">
                        {formData.DeliveredByUserName || <span className="text-gray-400 font-normal text-xs">—</span>}
                    </div>
                </div>
                <div>
                    <p className="text-[11px] text-gray-500 mb-1 flex items-center gap-1">📅 تاريخ التسليم</p>
                    <div className="bg-white border border-green-200 rounded-lg px-3 py-2 text-sm text-green-800 min-h-[36px] flex items-center">
                        {formData.DeliveredAt_Display
                            ? new Date(formData.DeliveredAt_Display).toLocaleString('ar-EG')
                            : <span className="text-gray-400 text-xs">—</span>}
                    </div>
                </div>
            </div>
        )}

    </div>
</div>
)}


                               {/* حالة استلام الجواز */}
                               <div>
                                   <label className="block text-sm font-medium text-gray-700 mb-2">
                                       حالة استلام الجواز *
                                   </label>
                                   <select
                                       value={formData.PassportReceiveStatus}
                                       onChange={handleReceiveStatusChange}
                                       className={`w-full border rounded-lg px-4 py-2.5 ${
                                           isViewMode 
                                           ? 'bg-gray-50 cursor-default border-gray-200' 
                                           : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                       }`}
                                       disabled={isViewMode}
                                       autoComplete="off"                                                
                                   >
                                       {receiveStatuses.map(status => (
                                           <option key={status} value={status}>{status}</option>
                                       ))}
                                   </select>
                               </div>

                               {/* تاريخ الاستلام */}
                               {formData.PassportReceiveStatus !== 'اختر الحالة' && 
                                formData.PassportReceiveStatus !== 'لم يستلم' && (
                                   <div>
                                       <label className="block text-sm font-medium text-gray-700 mb-2">
                                           تاريخ الاستلام *
                                       </label>
                                       <CustomDateInput
                                           value={formData.ReceiveDate}
                                           onChange={(e) => setFormData({...formData, ReceiveDate: e.target.value})}
                                           isReadOnly={isViewMode}
                                           allowManualEdit={false}
                                           className={`w-full border rounded-lg px-4 py-2.5 ${
                                               isViewMode 
                                               ? 'bg-gray-50 cursor-default border-gray-200' 
                                               : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                           }`}
                                       />
                                   </div>
                               )}

                               {/* بيانات المستلم في حالة استلام صديق */}
                               {formData.PassportReceiveStatus === 'استلم صديق' && (
                                   <>
                                       <div>
                                           <label className="block text-sm font-medium text-gray-700 mb-2">
                                               اسم المستلم *
                                           </label>
                                           <input
                                               type="text"
                                               value={formData.FriendName}
                                               onChange={(e) => handleArabicInputChange(e, 'FriendName')}
                                               autoComplete="off"
                                               className={`w-full border rounded-lg px-4 py-2.5 ${
                                                   isViewMode 
                                                   ? 'bg-gray-50 cursor-default border-gray-200' 
                                                   : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                               }`}
                                               readOnly={isViewMode}                                                        
                                               dir="rtl"
                                           />
                                       </div>
                                       
                                       <div>
                                           <label className="block text-sm font-medium text-gray-700 mb-2">
                                               رقم هاتف المستلم * (11 رقم يبدأ بـ 01)
                                           </label>
                                           <input
                                               type="text"
                                               value={formData.FriendPhone}
                                               onChange={handleFriendPhoneChange}
                                               onBlur={() => {
                                                   if (formData.FriendPhone && !/^01[0-9]{9}$/.test(formData.FriendPhone)) {
                                                       showAlert('رقم الهاتف يجب أن يبدأ بـ 01 ويكون 11 رقم', 'error');
                                                   }
                                               }}
                                               autoComplete="off"
                                               className={`w-full border rounded-lg px-4 py-2.5 ${
                                                   isViewMode 
                                                   ? 'bg-gray-50 cursor-default border-gray-200' 
                                                   : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                                               }`}
                                               readOnly={isViewMode}
                                               maxLength={11}
                                               placeholder="01xxxxxxxxx"
                                               dir="ltr"
                                           />
                                           <p className="text-xs text-gray-500 mt-1">
                                               مثال: 01234567890
                                           </p>
                                       </div>
                                   </>
                               )}
                           </div>
                       </div>

                       {/* الملاحظات */}
                       <div className="bg-gray-50 p-4 rounded-lg">
                           <NotesSection 
                               notes={formData.Notes} 
                               setNotes={(notes) => setFormData(prev => ({ ...prev, Notes: notes }))}
                               isViewMode={isViewMode}
                           />
                       </div>

                       {/* أزرار التحكم */}
                       <div className="flex justify-end gap-4 mt-6 pt-6 border-t border-gray-200 max-md:sticky max-md:bottom-0 max-md:bg-white/95 max-md:backdrop-blur max-md:p-3 max-md:-mx-3 max-md:flex-col-reverse max-md:gap-2 max-md:z-40">
                           {!isViewMode ? (
                               <button
                                   type="submit"
                                   disabled={isLoading}
                                   className={`px-8 py-3 rounded-lg flex items-center gap-2 transition duration-200 max-md:w-full max-md:justify-center ${
                                       isLoading 
                                           ? 'bg-gray-400 cursor-not-allowed text-white' 
                                           : 'bg-blue-600 hover:bg-blue-700 text-white'
                                   }`}
                               >
                                   <Save size={20} />
                                   {editingId ? 'تحديث البيانات' : 'حفظ البيانات'}
                               </button>
                           ) : null}
                           <button
                               type="button"
                               onClick={resetForm}
                               className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-8 py-3 rounded-lg transition duration-200 max-md:w-full"
                           >
                               {isViewMode ? 'إغلاق' : 'إلغاء'}
                           </button>
                       </div>
                   </form>
               </div>



{/* الجانب الأيمن - الصور والمستندات */}
<div id="passport-mobile-images-section" className="border-l border-gray-200 bg-gray-50 p-4 overflow-y-auto max-md:border-l-0 max-md:border-t max-md:p-3 max-md:overflow-visible max-md:w-full max-md:min-w-0 max-md:order-2" style={isMobileScreen ? {} : {width:'clamp(340px, 32vw, 520px)', minWidth:'clamp(340px, 32vw, 520px)', flexShrink:0}}>
    <h3 className="text-lg font-semibold text-gray-700 mb-4">الصور والمستندات</h3>
    
{/* صورة الجواز */}
<div className="mb-6">
    <label className="block text-sm font-medium text-gray-700 mb-2">
        صورة الجواز
        <span className="text-xs text-gray-400 ml-2">انقر للتكبير</span>
    </label>
    
    {formData?.PassportImagePath ? (


<div
    className={`relative transition-all duration-150 ${dragOverField==='PassportImage' ? 'ring-4 ring-blue-400 ring-offset-1 rounded-lg' : ''}`}
    onMouseEnter={() => setActiveField('PassportImage')}
    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect='copy'; setDragOverField('PassportImage'); }}
    onDragLeave={(e) => { if(!e.currentTarget.contains(e.relatedTarget)) setDragOverField(null); }}
    onDrop={(e) => {
        e.preventDefault(); setDragOverField(null);
        // سحب ملف من الجهاز
        if (e.dataTransfer.files?.length > 0) {
            const file = e.dataTransfer.files[0];
            if (file.type.startsWith('image/')) { handleImageUpload('PassportImage', file); return; }
        }
        // سحب من صورة أخرى
        const srcUrl = e.dataTransfer.getData('sourceUrl');
        const srcField = e.dataTransfer.getData('sourceField');
        if (srcUrl && srcField !== 'PassportImage') handleDropImage('PassportImage', srcUrl);
    }}
>
    <div
        className="relative cursor-grab"
        draggable={!isViewMode && !!formData?.PassportImagePath}
        onDragStart={(e) => {
            e.dataTransfer.setData('sourceField', 'PassportImage');
            e.dataTransfer.setData('sourceUrl', buildImageUrl(formData?.PassportImagePath));
            e.dataTransfer.effectAllowed = 'copy';
        }}
        onClick={() => {
            if (enlargedImage === 'PassportImage') {
                setEnlargedImage(null);
            } else {
                setEnlargedImage('PassportImage');
            }
        }}
    >
        {enlargedImage === 'PassportImage' ? (
            // وضع التكبير - يملأ الـ panel
            <div className="relative w-full bg-white rounded-xl border-2 border-blue-400 shadow-2xl overflow-hidden">
                <img
                    src={buildImageUrl(formData?.PassportImagePath)}
                    alt="صورة الجواز"
                    className="max-md:max-h-[80vh]"
                    style={{width:'100%', height:'auto', objectFit:'contain', display:'block', cursor:'zoom-out'}}
                    onClick={(e) => { e.stopPropagation(); setEnlargedImage(null); }}
                />
                <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-full">
                    انقر للتصغير ✕
                </div>
                        
                    </div>
                ) : (
                    // الوضع العادي - حجم طبيعي
                    <div className="relative">
                        <ImageMagnifier
                            src={buildImageUrl(formData?.PassportImagePath)}
                            alt="صورة الجواز"
                            className="w-full object-contain rounded-lg border border-gray-300 bg-white hover:shadow-lg transition-all duration-200 max-md:max-h-56"
                            magnifierHeight={200}
                            magnifierWidth={200}
                            zoomLevel={2}
                        />
                        {dragOverField === 'PassportImage' && (
                            <div className="absolute inset-0 bg-blue-500 bg-opacity-25 rounded-lg flex items-center justify-center pointer-events-none border-4 border-blue-400 border-dashed">
                                <span className="text-blue-800 font-bold text-base bg-white px-4 py-2 rounded-full shadow-lg">📥 أسقط الصورة هنا</span>
                            </div>
                        )}
                        
                        <div className="absolute top-2 right-2 flex gap-1">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedImage({
                                        url: buildImageUrl(formData?.PassportImagePath),
                                        title: 'صورة الجواز'
                                    });
                                    setShowImageViewer(true);
                                }}
                                className="bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-opacity"
                            >
                                <Eye size={16} className="text-white" />
                            </button>
                            
                            {!isViewMode && (
                                <>
                                    <label className="bg-blue-500 bg-opacity-80 rounded-full p-2 hover:bg-opacity-100 transition-opacity cursor-pointer">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    handleImageUpload('PassportImage', file);
                                                }
                                            }}
                                            className="hidden"
                                        />
                                        <Upload size={16} className="text-white" />
                                    </label>
                                    
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.confirm('هل أنت متأكد من حذف صورة الجواز؟')) {
                                                setFormData({...formData, PassportImagePath: ''
                                                });
                                                showAlert('تم حذف صورة الجواز', 'success');
                                            }
                                        }}
                                        className="bg-red-500 bg-opacity-80 rounded-full p-2 hover:bg-opacity-100 transition-opacity"
                                    >
                                        <X size={16} className="text-white" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    ) : (
        <div
            className={`w-full border-2 rounded-lg bg-white transition-all duration-150 ${
                dragOverField==='PassportImage' ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-300' : 'border-dashed border-gray-300'
            }`}
            style={isMobileScreen ? {height:'180px'} : {height:'400px'}}
            onMouseEnter={() => setActiveField('PassportImage')}
        >
            <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors rounded-lg">
                <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                            handleImageUpload('PassportImage', file);
                        }
                    }}
                    className="hidden"
                    disabled={isViewMode}
                />
                {dragOverField === 'PassportImage' ? (
                    <div className="text-center text-blue-500">
                        <div className="text-5xl mb-2">📥</div>
                        <p className="font-bold text-sm">أسقط الصورة هنا</p>
                    </div>
                ) : (
                    <div className="text-center text-gray-500">
                        <Upload size={48} className="mx-auto mb-2" />
                        <p className="font-medium">اختر صورة الجواز</p>
                        <p className="text-xs mt-1 text-gray-400">رفع • سحب وإسقاط • Ctrl+V</p>
                    </div>
                )}
            </label>
        </div>
    )}

    {/* شريط التقدم أسفل الصورة مباشرة */}
    {isOcrProcessing && (
        <div className="mt-2 mb-3">
            <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-blue-600">
                    {ocrProgress < 30 ? '📤 رفع الصورة...' :
                     ocrProgress < 60 ? '🔍 تحليل المحتوى...' :
                     ocrProgress < 90 ? '📝 استخراج البيانات...' :
                     '✅ اكتمل!'}
                </span>
                <span className="text-sm text-gray-500">{Math.round(ocrProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                    className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${ocrProgress}%` }}
                ></div>
            </div>
        </div>
    )}

    {/* أزرار تحت الصورة */}
    {!isViewMode && (
        <div className="mt-2 flex flex-wrap gap-1.5">
            <label className="flex-1 cursor-pointer bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg p-2 text-center text-sm transition border border-blue-200" style={{minWidth:'70px'}}>
                <input type="file" accept="image/*" onChange={(e) => { const f=e.target.files[0]; if(f) handleImageUpload('PassportImage',f); }} className="hidden"/>
                <Upload className="inline mr-1" size={13}/> رفع
            </label>
            {canSmartScan && (
            <label className="flex-1 cursor-pointer bg-purple-50 hover:bg-purple-100 text-purple-600 rounded-lg p-2 text-center text-sm transition border border-purple-200" style={{minWidth:'70px'}}>
                <input type="file" accept="image/*" onChange={(e) => { const f=e.target.files[0]; if(f) handleAdvancedOCR(f); }} className="hidden"/>
                <svg className="inline mr-1" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                مسح ذكي
            </label>
            )}
            {formData?.PassportImagePath && (
            <button type="button" onClick={() => openCropModal('PassportImage')}
                className="flex-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg p-2 text-sm transition border border-indigo-200" style={{minWidth:'70px'}}>
                ✂️ اقتصاص
            </button>
            )}
            {formData?.PassportImagePath && canExtractData && (
            <button type="button" onClick={handleReExtract}
                className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg p-2 text-sm transition border border-green-200" style={{minWidth:'80px'}}>
                🔄 فك بيانات
            </button>
            )}
        </div>
    )}

    {/* البيانات المستخرجة أسفل الصورة مباشرة - زي الصورة */}
    {Object.keys(ocrData).length > 0 && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-green-700 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    البيانات المستخرجة
                </h4>
                <span className="text-xs text-green-600 bg-green-200 px-2 py-1 rounded-full">
                    {Object.keys(ocrData).length} حقل
                </span>
            </div>
            
            <div className="space-y-2 mb-4">
                {Object.entries(ocrData).map(([key, value]) => (
                    value && (
                        <div key={key} className="flex justify-between items-center bg-white p-2 rounded text-sm">
                            <span className="font-medium text-green-800">{key}:</span>
                            <span className="text-green-600 flex-1 text-right mr-2">{value}</span>
                        </div>
                    )
                ))}
            </div>
            
            <button
                type="button"
                onClick={applyOcrData}
                className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2 font-medium"
            >
                <Download size={18} />
                تطبيق جميع البيانات
            </button>
            
            <div className="mt-3 pt-3 border-t border-green-200">
                <h5 className="text-xs font-medium text-green-700 mb-2">إحصائيات الاستخراج</h5>
                <div className="grid grid-cols-2 gap-4 text-xs">
                    <div className="text-center">
                        <div className="font-bold text-lg text-green-600">{Object.keys(ocrData).length}</div>
                        <div className="text-green-600">الحقول المستخرجة</div>
                    </div>
                    <div className="text-center">
                        <div className="font-bold text-lg text-green-600">
                            {Object.entries(ocrData).filter(([key, value]) => 
                                formData[key] === value
                            ).length}
                        </div>
                        <div className="text-green-600">الحقول المطابقة</div>
                    </div>
                </div>
            </div>
        </div>
    )}
</div>

{/* الصور الأخرى والمستندات */}
<div className="mt-3">
    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide border-b pb-1 mb-2">📷 الصور</h4>
    <div className="grid grid-cols-2 gap-2">
        {[
            { key: 'PersonalImage', title: 'الصورة الشخصية' },
            { key: 'LicenseImage', title: 'رخصة (أمامي)' },
            { key: 'LicenseBackImage', title: 'رخصة (خلفي)' },
            { key: 'IDImage', title: 'بطاقة (أمامي)' },
            { key: 'IDBackImage', title: 'بطاقة (خلفي)' },
        ].map(({ key, title }) => (
            <div key={key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{title}</label>
                {formData?.[key + 'Path'] ? (
                    <div
                        className={`relative transition-all duration-150 ${dragOverField===key ? 'ring-2 ring-blue-400 ring-offset-1 rounded-lg' : ''}`}
                        onMouseEnter={() => setActiveField(key)}
                        draggable={!isViewMode}
                        onDragStart={(e) => {
                            e.dataTransfer.setData('sourceField', key);
                            e.dataTransfer.setData('sourceUrl', buildImageUrl(formData[key+'Path']));
                            e.dataTransfer.effectAllowed = 'copy';
                        }}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect='copy'; setDragOverField(key); }}
                        onDragLeave={() => setDragOverField(null)}
                        onDrop={(e) => {
                            e.preventDefault(); setDragOverField(null);
                            const srcUrl = e.dataTransfer.getData('sourceUrl');
                            const srcField = e.dataTransfer.getData('sourceField');
                            if (srcUrl && srcField !== key) handleDropImage(key, srcUrl);
                        }}
                    >
                        <LazyImage
                            src={buildImageUrl(formData[key + 'Path'])}
                            alt={title}
                            className="w-full h-24 object-cover rounded-lg border border-gray-300 cursor-grab hover:shadow-md transition-shadow"
                            onClick={() => {
                                setSelectedImage({ url: buildImageUrl(formData[key + 'Path']), title });
                                setShowImageViewer(true);
                            }}
                        />
                        {dragOverField === key && (
                            <div className="absolute inset-0 bg-blue-500 bg-opacity-20 rounded-lg flex items-center justify-center pointer-events-none">
                                <span className="text-blue-700 font-bold text-xs bg-white px-2 py-1 rounded-full shadow">📥 أسقط هنا</span>
                            </div>
                        )}
                        {!isViewMode && (
                            <div className="absolute top-1 right-1 flex gap-1">
                                <label className="bg-blue-500 bg-opacity-80 rounded-full p-1 hover:bg-opacity-100 transition-opacity cursor-pointer" title="رفع صورة">
                                    <input type="file" accept="image/*" onChange={(e) => { const f=e.target.files[0]; if(f) handleImageUpload(key,f); }} className="hidden"/>
                                    <Upload size={10} className="text-white" />
                                </label>
                                <button type="button" onClick={() => openCropModal(key)}
                                    className="bg-indigo-500 bg-opacity-80 rounded-full p-1 hover:bg-opacity-100 transition-opacity text-white text-xs leading-none" title="اقتصاص">✂</button>
                                <button type="button"
                                    onClick={() => { if(window.confirm(`حذف ${title}؟`)) { setFormData({...formData,[key+'Path']:''}); showAlert(`تم حذف ${title}`,'success'); }}}
                                    className="bg-red-500 bg-opacity-80 rounded-full p-1 hover:bg-opacity-100 transition-opacity">
                                    <X size={10} className="text-white" />
                                </button>
                            </div>
                        )}
                    </div>
                ) : !isViewMode ? (
                    <div
                        className={`w-full h-24 border-2 rounded-lg flex items-center justify-center bg-white transition-all duration-150 ${
                            dragOverField===key ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-300' : 'border-dashed border-gray-300'
                        }`}
                        onMouseEnter={() => setActiveField(key)}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect='copy'; setDragOverField(key); }}
                        onDragLeave={() => setDragOverField(null)}
                        onDrop={(e) => {
                            e.preventDefault(); setDragOverField(null);
                            const srcUrl = e.dataTransfer.getData('sourceUrl');
                            const srcField = e.dataTransfer.getData('sourceField');
                            if (srcUrl && srcField !== key) handleDropImage(key, srcUrl);
                        }}
                    >
                        <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors rounded-lg">
                            <input type="file" accept="image/*" onChange={(e) => { const f=e.target.files[0]; if(f) handleImageUpload(key,f); }} className="hidden"/>
                            {dragOverField===key ? (
                                <div className="text-center text-blue-500">
                                    <div className="text-2xl">📥</div>
                                    <p className="text-xs font-bold">أسقط هنا</p>
                                </div>
                            ) : (
                                <div className="text-center text-gray-400">
                                    <Upload size={16} className="mx-auto mb-1" />
                                    <p className="text-xs">{title}</p>
                                </div>
                            )}
                        </label>
                    </div>
                ) : (
                    <div className="w-full h-24 border border-gray-300 rounded-lg flex items-center justify-center bg-gray-100">
                        <span className="text-xs text-gray-400">لا توجد صورة</span>
                    </div>
                )}
            </div>
        ))}
    </div>

    {/* المستندات PDF */}
    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide border-b pb-1 mb-2 mt-4">📄 المستندات</h4>
    <div className="space-y-2">
        {[
            { key: 'CompanyContract', pathKey: 'CompanyContractPath', title: 'عقد الشركة', icon: '🏢' },
            { key: 'MinistryContract', pathKey: 'MinistryContractPath', title: 'عقد الوزارة', icon: '🏛️' },
            { key: 'VisaFile', pathKey: 'VisaFilePath', title: 'التأشيرة', icon: '✈️' },
            { key: 'OtherAttachments', pathKey: 'OtherAttachmentsPath', title: 'مرفقات أخرى', icon: '📎' },
        ].map(({ key, pathKey, title, icon }) => (
            <div key={key} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2 border border-gray-200">
                <span className="text-base">{icon}</span>
                <span className="text-xs font-medium text-gray-700 flex-1">{title}</span>
                {formData?.[pathKey] ? (
                    <div className="flex items-center gap-1">
                       {key === 'VisaFile' ? (
    <a
        href={buildCustomDownloadUrl(formData[pathKey], formData.NameArabic, 'تأشيرة')}
        className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-200 transition flex items-center gap-1"
        title="تحميل التأشيرة باسم العميل"
    >
        <Download size={11} />
        عرض التأشيرة
    </a>
) : (
    <a
        href={buildImageUrl(formData[pathKey])}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-200 transition flex items-center gap-1"
    >
        <Eye size={11} />
        عرض
    </a>
)}
                        {!isViewMode && (
                            <>
                                <label className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded hover:bg-green-200 transition cursor-pointer">
                                    <input type="file" accept="image/*,application/pdf" onChange={(e) => { const f=e.target.files[0]; if(f) handleImageUpload(key,f); }} className="hidden"/>
                                    تغيير
                                </label>
                                <button type="button"
                                    onClick={() => { if(window.confirm(`حذف ${title}؟`)) { setFormData({...formData,[pathKey]:''}); showAlert(`تم حذف ${title}`,'success'); }}}
                                    className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded hover:bg-red-200 transition">
                                    حذف
                                </button>
                            </>
                        )}
                    </div>
                ) : !isViewMode ? (
                    <label className="bg-blue-50 text-blue-600 text-xs px-2 py-1 rounded hover:bg-blue-100 transition cursor-pointer border border-blue-200 flex items-center gap-1">
                        <input type="file" accept="image/*,application/pdf" onChange={(e) => { const f=e.target.files[0]; if(f) handleImageUpload(key,f); }} className="hidden"/>
                        <Upload size={11}/>رفع
                    </label>
                ) : (
                    <span className="text-xs text-gray-400">لا يوجد</span>
                )}
            </div>
        ))}
    </div>
</div>

    {/* نتائج الاستخراج */}
    {Object.keys(ocrData).length > 0 && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                البيانات المستخرجة
            </h4>
            <div className="space-y-1 mb-3">
                {Object.entries(ocrData).map(([key, value]) => (
                    value && (
                        <div key={key} className="text-xs text-green-600 bg-white p-2 rounded">
                            <strong>{key}:</strong> {value}
                        </div>
                    )
                ))}
            </div>
            <button
                type="button"
                onClick={applyOcrData}
                className="w-full bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700 transition flex items-center justify-center gap-2"
            >
                <Download size={16} />
                تطبيق جميع البيانات
            </button>
        </div>
    )}

    {/* إحصائيات الاستخراج */}
    {Object.keys(ocrData).length > 0 && (
        <div className="mt-4 p-3 bg-gray-100 rounded-lg">
            <h4 className="text-xs font-medium text-gray-600 mb-2">إحصائيات الاستخراج</h4>
            <div className="text-xs text-gray-500">
                <div className="flex justify-between">
                    <span>الحقول المستخرجة:</span>
                    <span className="font-medium">{Object.keys(ocrData).length}</span>
                </div>
                <div className="flex justify-between">
                    <span>الحقول المطابقة:</span>
                    <span className="font-medium text-green-600">
                        {Object.entries(ocrData).filter(([key, value]) => 
                            formData[key] === value
                        ).length}
                    </span>
                </div>
            </div>
        </div>
    )}
</div>
           </div>
       </div>
   </div>
   </BodyPortal>
)}




                {/* رسالة عند عدم وجود نتائج */}
                {!isLoading && passports.length === 0 && (
                    <div className="bg-white rounded-lg shadow-md p-8 text-center">
                        <div className="text-gray-500 mb-4">لا توجد جوازات مضافة</div>
                        <button 
                            onClick={() => setShowForm(true)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg inline-flex items-center gap-2"
                        >
                            <Plus size={20} />
                            إضافة جواز جديد
                        </button>
                    </div>
                )}

         

{/* نافذة تقدم المسح الضوئي */}
{showOcrModal && (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-[200] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">🤖 تحليل الجواز</h3>
                    <button onClick={() => setShowOcrModal(false)} className="text-gray-400 hover:text-gray-600">
                        <X size={24} />
                    </button>
                </div>

                {selectedImagePreview && (
                    <div className="mb-6">
                        <LazyImage 
                            src={selectedImagePreview} 
                            alt="معاينة الجواز" 
                            className="w-full max-h-96 border"
                        />
                    </div>
                )}

                <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium">
                            {ocrProgress < 30 ? '📤 رفع الصورة...' :
                             ocrProgress < 60 ? '🔍 تحليل المحتوى...' :
                             ocrProgress < 90 ? '📝 استخراج البيانات...' :
                             '✅ اكتمل!'}
                        </span>
                        <span className="text-sm text-gray-500">{Math.round(ocrProgress)}%</span>
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                            className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${ocrProgress}%` }}
                        ></div>
                    </div>
                </div>

                <div className="text-sm text-gray-600 text-center">
                    <p>⚡ تحليل ذكي تلقائي للصورة</p>
                </div>
            </div>
        </div>
    </div>
)}



<NewTripCompanyModal
    isOpen={!!newTripRequest}
    onClose={() => setNewTripRequest(null)}
    companies={companies}
    delegates={delegates}
    travelDurations={travelDurations}
    abilityTests={abilityTests}
    clientName={newTripRequest?.clientName || ''}
    currentCompanyId={newTripRequest?.currentCompanyId || ''}
    onConfirm={(tripDetails) => {
        const request = newTripRequest;
        setNewTripRequest(null);
        if (request) executeNewTrip(request.passportId, request.clientName, tripDetails);
    }}
/>

<SettingsModal 
    isOpen={showSettings}
    onClose={() => setShowSettings(false)}
    settings={warningSettings}
    onSave={saveWarningSettings}
/>



<PassportBlacklistModal
    isOpen={showBlacklistModal}
    onClose={() => { setShowBlacklistModal(false); setBlacklistModalPassport(null); }}
    passport={blacklistModalPassport}
    companies={companies}
    showAlert={showAlert}
    getCurrentUserId={getCurrentUserId}
    onChanged={refreshBlacklistAndData}
    apiBaseUrl={config.PASSPORT_API_URL}
/>

<PassportActivityLogModal
    isOpen={showActivityLog}
    onClose={() => setShowActivityLog(false)}
    logs={activityLogEntries}
    users={activityLogUsers}
    companies={companies}
    delegates={delegates}
    professions={professions}
    cleanupSettings={activityLogCleanupSettings}
    onSaveCleanupSettings={saveActivityLogCleanupSettings}
    onManualCleanup={manualCleanupActivityLog}
    filters={activityLogFilters}
    setFilters={setActivityLogFilters}
    onRefresh={() => fetchActivityLog(activityLogFilters)}
    isLoading={activityLogLoading}
/>

<DefaultSettingsModal 
    isOpen={showDefaultSettings}
    onClose={() => setShowDefaultSettings(false)}
    defaultSettings={defaultSettings}
    setDefaultSettings={setDefaultSettings}
    companies={companies}
    professions={professions}
    delegates={delegates}
    travelDurations={travelDurations}
    passportStatuses={passportStatuses}
    abilityTests={abilityTests}
    militaryStatuses={militaryStatuses}
    licenseTypes={licenseTypes}
    canShowPassportProfessionMode={canShowPassportProfessionModeSetting}
    onSave={saveDefaultSettings}
/>

<BatchUploadModal 
    isOpen={showBatchUpload}
    onClose={() => setShowBatchUpload(false)}
    batchImages={batchImages}
    setBatchImages={setBatchImages}
    batchProcessing={batchProcessing}
    batchProgress={batchProgress}
    batchResults={batchResults}
    currentBatchIndex={currentBatchIndex}
    onUpload={handleBatchUpload}
    onStartProcessing={startBatchProcessing}
    onViewPassport={(passportId) => {
        const passport = { PassportID: passportId };
        handleView(passport);
        setShowBatchUpload(false);
    }}
/>

<QuickEntryModal 
    isOpen={showQuickEntry}
    onClose={resetQuickEntry}
    quickEntryStep={quickEntryStep}
    setQuickEntryStep={setQuickEntryStep}
    quickEntryData={quickEntryData}
    setQuickEntryData={setQuickEntryData}
    quickEntryError={quickEntryError}
    setQuickEntryError={setQuickEntryError}
    searchTerm={searchTerm}
    setSearchTerm={setSearchTerm}
    isLoading={isLoading}
    companies={companies}
    professions={professions}
    delegates={delegates}
    travelDurations={travelDurations}
    passportStatuses={passportStatuses}
    abilityTests={abilityTests}
    militaryStatuses={militaryStatuses}
    licenseTypes={licenseTypes}
    defaultSettings={defaultSettings}
    onSubmit={handleQuickEntrySubmit}
    getVisibleQuickEntryFields={getVisibleQuickEntryFields}
    onReset={resetQuickEntry}
    onValidateField={validateCurrentField}
    onHandleInput={handleQuickEntryInput}
    onHandleKeyDown={handleQuickEntryKeyDown}
    onGoToNext={goToNextField}
    onGoToPrevious={goToPreviousField}
/>

<BarcodePrintModal
    isOpen={!!barcodeModalPassport}
    onClose={() => { setBarcodeModalPassport(null); setBarcodeSettingsOnly(false); }}
    passport={barcodeModalPassport}
    companies={companies}
    delegates={delegates}
    professions={professions}
    showAlert={showAlert}
    settingsOnly={barcodeSettingsOnly}
/>

<BarcodeSavedProfilesModal
    isOpen={!!savedPrintPassport}
    onClose={() => setSavedPrintPassport(null)}
    passport={savedPrintPassport}
    companies={companies}
    delegates={delegates}
    professions={professions}
    showAlert={showAlert}
    onOpenSettings={(passport) => { setBarcodeSettingsOnly(true); setBarcodeModalPassport(passport); }}
/>

{showImageViewer && (
    <BodyPortal>
        {/* ضمان ظهور عارض الصور فوق نموذج بيانات الجواز وأي Modal آخر */}
        <div className="fixed inset-0 z-[100000]">
            <ImageViewerModal 
                isOpen={showImageViewer}
                onClose={() => setShowImageViewer(false)}
                imageUrl={selectedImage?.url}
                imageTitle={selectedImage?.title}
                formData={formData}
            />
        </div>
    </BodyPortal>
)}

<SmartRepeatQuickModal
    isOpen={showSmartRepeatModal}
    onClose={() => setShowSmartRepeatModal(false)}
    passportData={smartRepeatPassportData}
/>

<ImageCropModal
    isOpen={cropModal.show}
    onClose={() => setCropModal({show:false,imageSrc:null,imageType:null})}
    imageSrc={cropModal.imageSrc}
    onCropComplete={handleCropComplete}
/>

{secureConfirmModal.show && (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999]" dir="rtl">
        <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-2xl">🔒</div>
                <div>
                    <h3 className="text-lg font-bold text-gray-800">{secureConfirmModal.title}</h3>
                    <p className="text-sm text-gray-500">{secureConfirmModal.description}</p>
                </div>
            </div>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                اكتب <strong className="text-lg">123</strong> لتأكيد هذا الإجراء
            </p>
            <input
                type="text" value={secureConfirmModal.inputValue}
                onChange={(e) => handleSecureConfirmChange(e.target.value)}
                onKeyDown={(e) => { if(e.key==='Enter') handleSecureConfirmSubmit(); }}
                placeholder="_ _ _"
                className="w-full border-2 border-gray-300 focus:border-blue-500 rounded-xl p-3 text-center text-2xl font-bold mb-4 outline-none tracking-[1rem]"
                autoFocus maxLength={3}
            />
            <div className="flex gap-3">
                <button onClick={handleSecureConfirmSubmit} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl font-medium transition">✅ تأكيد</button>
                <button onClick={handleSecureConfirmCancel} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl transition">إلغاء</button>
            </div>
        </div>
    </div>
)}

            </div>
        </div>
    );
     
};

const PassportManagement = (props = {}) => {
    return (
        <PermissionsProvider>
            <PassportManagementContent {...props} />
        </PermissionsProvider>
    );
};


// خدمات طباعة الباركود المشتركة مُصدّرة مباشرة عند تعريفها أعلى الملف.

export default function PassportManagementWithErrorBoundary(props) {
    return (
        <PassportErrorBoundary>
            <PassportManagement {...props} />
        </PassportErrorBoundary>
    );
}